import { useState, useRef, useCallback, useEffect } from "react";

// ── Constants ─────────────────────────────────────────────────────────────────
const GRID_SIZE = 20;
const CANVAS_W = 4000;
const CANVAS_H = 3000;

const ENTITY_COLORS = [
  "#c0392b","#e67e22","#f39c12","#27ae60","#1abc9c",
  "#2980b9","#8e44ad","#e91e63","#d35400","#16a085",
  "#2c3e50","#c2185b","#0288d1","#7b1fa2","#558b2f",
];
const KINGDOM_COLORS = ENTITY_COLORS;
const CHAR_COLORS    = ENTITY_COLORS;
const ARMY_COLORS    = ENTITY_COLORS;
const MAGE_COLORS    = ENTITY_COLORS;
const DRAGON_COLORS  = ENTITY_COLORS;
const DRAGON_SIZES   = ["Wyrmling","Young","Adult","Ancient"];
const MAGE_SCHOOLS   = ["Fire","Ice","Lightning","Necromancy","Arcane","Nature","Blood","Shadow","Holy","Wind"];

const MAGIC_DISCIPLINES = [
  { key:"flamecalling",  label:"Flamecalling",  icon:"🔥", color:"#ef4444", desc:"Raw fire and heat" },
  { key:"stormweaving",  label:"Stormweaving",  icon:"⚡", color:"#3b82f6", desc:"Lightning, wind, pressure" },
  { key:"tidecalling",   label:"Tidecalling",   icon:"🌊", color:"#06b6d4", desc:"Water, tides, and currents" },
  { key:"voidweaving",   label:"Voidweaving",   icon:"🌑", color:"#6b21a8", desc:"Darkness, silence, negation" },
  { key:"lightforging",  label:"Lightforging",  icon:"✦",  color:"#eab308", desc:"Radiant energy constructs" },
  { key:"soulbinding",   label:"Soulbinding",   icon:"👁", color:"#be185d", desc:"Will and spirit magic" },
  { key:"earthshaping",  label:"Earthshaping",  icon:"⛰", color:"#78716c", desc:"Stone, metal, crystal, tectonics" },
  { key:"runebinding",   label:"Runebinding",   icon:"🔷", color:"#1d4ed8", desc:"Wards, glyphs, enchantment" },
  { key:"frostbringer",  label:"Frostbringer",  icon:"❄️", color:"#7dd3fc", desc:"Deep cold, permafrost, glacial force" },
  { key:"forcebinding",  label:"Forcebinding",  icon:"🕳️", color:"#312e81", desc:"Gravity, telekinesis, spatial force" },
];
const DISCIPLINE_LABELS = ["","Spark","Flicker","Novice","Apprentice","Adept","Channeler","Arcanist","Master","Archon","Mythic","Transcendent","Ascendant"];

// ── Ability System ──────────────────────────────────────────────────────────
// Skill points per discipline level-up (tight economy)
// Primary discipline: full SP. Non-primary: HALF SP (floored).
function skillPointsForLevel(lvl) {
  if (lvl <= 0) return 0;
  if (lvl <= 4) return 1;   // 4 SP from levels 1-4
  return 2;                  // 16 SP from levels 5-12
}                            // Total at max: 20 SP (primary), 10 SP (non-primary)
function totalSkillPointsAtLevel(lvl) {
  let total = 0;
  for (let i = 1; i <= lvl; i++) total += skillPointsForLevel(i);
  return total;
}

// Calculate available SP — primary gets 1.5x, non-primary gets 0.5x
function calcAvailableSkillPoints(discLevel, isPrimary) {
  const raw = totalSkillPointsAtLevel(discLevel);
  if (isPrimary === true) return Math.floor(raw * 1.5);
  if (isPrimary === false) return Math.floor(raw / 2);
  return raw; // no primary set — full points
}

// Ability tiers — increased costs for tight economy
// Active: basic(2), advanced(4), ultimate(6)
// Passive: single choice at Lv.11 primary discipline, no SP cost, only 1 allowed globally
// Total to learn ALL actives = (4×2)+(3×4)+(2×6) = 8+12+12 = 32 SP
// Max SP at Lv12 = 20 → can learn ~63% of actives
const ABILITY_TIERS = {
  basic:            { label:"Basic",      color:"#94a3b8", cost:2, reqLevel:1 },
  advanced:         { label:"Advanced",   color:"#a855f7", cost:4, reqLevel:5 },
  ultimate:         { label:"Ultimate",   color:"#f59e0b", cost:6, reqLevel:9 },
  passive_master:   { label:"Passive",    color:"#f97316", cost:0, reqLevel:11 },
};

// Types: "damage", "defense", "buff", "passive"
// Passives have type:"passive" — always active when learned, no cooldown, no selection needed
// Only ONE passive can be learned globally, must be from primary discipline, requires Lv.11

const ABILITIES = {
flamecalling:[
{key:"fc_fireball",name:"Fireball",icon:"🔥",type:"damage",tier:"basic",basePower:0.5,cooldown:0,desc:"Hurl a ball of concentrated flame"},
{key:"fc_ignite",name:"Ignite",icon:"🔥",type:"damage",tier:"basic",basePower:0.25,cooldown:0,desc:"Set the enemy ablaze — burns over time",dot:{damage:11,rounds:3}},
{key:"fc_backdraft",name:"Backdraft",icon:"🔥",type:"damage",tier:"basic",basePower:0.45,cooldown:0,desc:"Explosive burst of superheated air"},
{key:"fc_kindlestrength",name:"Kindle Strength",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Stoke inner flame — boosts attack power for 2 rounds",effects:{selfAtkUp:true}},
{key:"fc_pyrobeam",name:"Pyrobeam",icon:"🔥",type:"damage",tier:"advanced",basePower:0.8,cooldown:2,desc:"Sustained stream of intense fire — leaves lingering burns",dot:{damage:18,rounds:2}},
{key:"fc_moltensurge",name:"Molten Surge",icon:"🔥",type:"damage",tier:"advanced",basePower:0.85,cooldown:2,desc:"Wave of liquid fire — burns on contact and keeps burning",dot:{damage:17,rounds:3}},
{key:"fc_infernalrage",name:"Infernal Rage",icon:"✦",type:"buff",tier:"advanced",basePower:0.4,cooldown:3,desc:"Fury burns away restraint — greatly boosts attack power for 2 rounds",effects:{selfAtkUp:true}},
{key:"fc_hellstorm",name:"Hellstorm",icon:"🔥",type:"damage",tier:"ultimate",basePower:1.45,cooldown:4,desc:"Raging fire vortex — scorched ground keeps burning",dot:{damage:31,rounds:3}},
{key:"fc_sunflare",name:"Sunflare Cataclysm",icon:"🔥",type:"damage",tier:"ultimate",basePower:1.4,cooldown:4,desc:"Detonate a blinding eruption of solar fire — devastating damage"},
{key:"fc_p_innerflame",name:"Inner Flame",icon:"🔥",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+80% fire damage. +30 max HP. Burns hotter with every breath.",passiveEffect:{atkBonus:0.80,hpBonus:30}},
{key:"fc_p_ashborn",name:"Ashborn Resilience",icon:"🔥",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+8% damage per round in combat, stacks up to 6. Heal 8 HP per round.",passiveEffect:{atkPerStack:0.08,maxStacks:6,hpRegen:8}},
{key:"fc_p_inferno",name:"Inferno Incarnate",icon:"🔥",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Become fire itself — +25% attack. +35% defense. Heal 8 HP per round. +35 max HP.",passiveEffect:{atkBonus:0.25,defBonus:0.35,hpRegen:8,hpBonus:35}},
],
stormweaving:[
{key:"sw_bolt",name:"Kinetic Blast",icon:"⚡",type:"damage",tier:"basic",basePower:0.65,cooldown:0,desc:"Blast of focused air"},
{key:"sw_windslash",name:"Wind Slash",icon:"⚡",type:"damage",tier:"basic",basePower:0.35,cooldown:0,desc:"Razor-sharp gust cuts through defenses"},
{key:"sw_stormcloak",name:"Storm Cloak",icon:"🛡",type:"defense",tier:"basic",basePower:0.4,cooldown:1,desc:"Swirling winds deflect incoming attacks"},
{key:"sw_static",name:"Static Charge",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Build electrical charge — boosts attack power for 2 rounds",effects:{selfAtkUp:true}},
{key:"sw_lightningbolt",name:"Lightning Bolt",icon:"⚡",type:"damage",tier:"advanced",basePower:1.4,cooldown:2,desc:"Call down a devastating lightning bolt from the heavens"},
{key:"sw_thunderstrike",name:"Thunderstrike",icon:"⚡",type:"damage",tier:"advanced",basePower:0.9,cooldown:2,desc:"Call down a focused lightning strike from the heavens"},
{key:"sw_galvanize",name:"Galvanize",icon:"✦",type:"buff",tier:"advanced",basePower:0.35,cooldown:3,desc:"Overcharge your body — greatly boosts attack power for 2 rounds",effects:{selfAtkUp:true}},
{key:"sw_cataclysm",name:"Cataclysm",icon:"⚡",type:"damage",tier:"ultimate",basePower:1.45,cooldown:4,desc:"Summon a full hurricane — absolute electrical devastation"},
{key:"sw_shockwave",name:"Shockwave",icon:"⚡",type:"damage",tier:"ultimate",basePower:1.8,cooldown:4,desc:"Condense the storm into a singularity — massive concentrated strike"},
{key:"sw_p_aerofocus",name:"Aero Focus",icon:"⚡",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+45% attack. +30 max HP. Wind-sharpened strikes hit harder.",passiveEffect:{atkBonus:0.45,hpBonus:30}},
{key:"sw_p_conductor",name:"Storm Conductor",icon:"⚡",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+10% attack per round in combat, stacks up to 6. Builds charge over time.",passiveEffect:{atkPerStack:0.10,maxStacks:6}},
{key:"sw_p_eye",name:"Eye of the Storm",icon:"⚡",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Master of the tempest — +80% attack. +30% evasion. +70 max HP.",passiveEffect:{evasion:0.30,atkBonus:0.80,hpBonus:70, hpRegen:30}},
],
tidecalling:[
{key:"tc_tidalwave",name:"Tidal Wave",icon:"🌊",type:"damage",tier:"basic",basePower:0.5,cooldown:0,desc:"Crash a wall of water upon your foe"},
{key:"tc_soothingcurrents",name:"Soothing Currents",icon:"🌊",type:"buff",tier:"basic",basePower:0.25,cooldown:1,desc:"Healing waters mend and continue to soothe",heal:25,hot:{heal:8,rounds:3}},
{key:"tc_tidewall",name:"Tidewall",icon:"🛡",type:"buff",tier:"basic",basePower:0.3,cooldown:2,desc:"Water barrier grants shield and boosts defense",shield:30,effects:{selfDefUp:true}},
{key:"tc_tidalblessing",name:"Tidal Blessing",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Ocean's blessing — instant heal and lingering restoration",heal:30,hot:{heal:6,rounds:3}},
{key:"tc_maelstrom",name:"Maelstrom",icon:"🌊",type:"damage",tier:"advanced",basePower:0.85,cooldown:2,desc:"Vortex of crushing water — immense pressure damage"},
{key:"tc_abyssalembrace",name:"Abyssal Embrace",icon:"🌊",type:"buff",tier:"advanced",basePower:0.4,cooldown:2,desc:"Deep-sea barrier absorbs damage and heals over time",shield:60,hot:{heal:10,rounds:2}},
{key:"tc_drown",name:"Drown",icon:"✦",type:"buff",tier:"advanced",basePower:0.4,cooldown:3,desc:"Drown yourself in healing waters — strong heal and defense boost",heal:45,effects:{selfDefUp:true}},
{key:"tc_tsunami",name:"Tsunami",icon:"🌊",type:"damage",tier:"ultimate",basePower:1.3,cooldown:4,desc:"Summon an ocean-scale wave — obliterating force"},
{key:"tc_depthsheart",name:"Depth's Heart",icon:"🌊",type:"buff",tier:"ultimate",basePower:0.7,cooldown:5,desc:"Become one with the ocean — massive shield, heal, and restoration",shield:80,heal:50,hot:{heal:15,rounds:3}},
{key:"tc_p_seaborn",name:"Seaborn",icon:"🌊",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+25% defense. Heal 18 HP per round. +20 max HP. The ocean sustains you.",passiveEffect:{defBonus:0.25,hpRegen:18,hpBonus:20}},
{key:"tc_p_tidalflow",name:"Tidal Flow",icon:"🌊",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+7% defense per round in combat, stacks up to 6. +35 max HP.",passiveEffect:{defPerStack:0.07,maxStacks:6,hpBonus:35}},
{key:"tc_p_ocean",name:"Ocean's Embrace",icon:"🌊",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"One with the sea — +50% attack. +40% defense, +15% evasion. Heal 18 HP per round. +30 max HP.",passiveEffect:{atkBonus:0.50,defBonus:0.40,evasion:0.15,hpRegen:18,hpBonus:30}},
],
voidweaving:[
{key:"vw_shadowbolt",name:"Shadow Bolt",icon:"🌑",type:"damage",tier:"basic",basePower:0.5,cooldown:0,desc:"Dark bolt drains vitality on impact",lifesteal:0.20},
{key:"vw_lifedrain",name:"Life Drain",icon:"🌑",type:"damage",tier:"basic",basePower:0.35,cooldown:0,desc:"Siphon life force from the enemy — high drain",lifesteal:0.35},
{key:"vw_darkpact",name:"Dark Pact",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Sacrifice shadow to shred enemy defenses",effects:{enemyDefDown:true}},
{key:"vw_corrupt",name:"Corrupt",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Inflict corruption — shreds enemy defense for 2 rounds",effects:{enemyDefDown:true}},
{key:"vw_nightmare",name:"Nightmare Strike",icon:"🌑",type:"damage",tier:"advanced",basePower:0.8,cooldown:2,desc:"Terror-infused strike steals life force",lifesteal:0.25},
{key:"vw_abyssdrain",name:"Abyssal Drain",icon:"🌑",type:"damage",tier:"advanced",basePower:0.65,cooldown:2,desc:"Channel the abyss to rip life from the enemy",lifesteal:0.30},
{key:"vw_despair",name:"Aura of Despair",icon:"✦",type:"buff",tier:"advanced",basePower:0.35,cooldown:3,desc:"Crushing dread — greatly shreds enemy defense for 2 rounds",effects:{enemyDefDown:true}},
{key:"vw_eclipse",name:"Eclipse",icon:"🌑",type:"damage",tier:"ultimate",basePower:1.55,cooldown:4,desc:"Darkness engulfs and drains everything",lifesteal:0.20},
{key:"vw_shadowform",name:"Shadow Form",icon:"✦",type:"buff",tier:"ultimate",basePower:0.55,cooldown:5,desc:"Become living darkness — massively shreds enemy defense for 2 rounds",effects:{enemyDefDown:true}},
{key:"vw_p_darkheart",name:"Darkheart",icon:"🌑",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+35% dark damage. Enemy −20% defense. Heal 15 HP per round. +40 max HP.",passiveEffect:{atkBonus:0.35,enemyDefPenalty:0.20,hpRegen:15,hpBonus:40}},
{key:"vw_p_corruption",name:"Creeping Corruption",icon:"🌑",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Enemy loses −6% defense per round, stacks up to 6. Enemy −15% attack. +35 max HP.",passiveEffect:{enemyDefPerStack:0.06,maxStacks:6,enemyAtkPenalty:0.15,hpBonus:35}},
{key:"vw_p_abyss",name:"Abyssal Dominion",icon:"🌑",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Lord of shadow — +25% attack. Enemy −25% defense. Enemy −10% attack. +30 max HP.",passiveEffect:{atkBonus:0.25,enemyDefPenalty:0.25,enemyAtkPenalty:0.10,hpBonus:30}},
],
lightforging:[
{key:"lf_photonbolt",name:"Photon Bolt",icon:"✦",type:"damage",tier:"basic",basePower:0.5,cooldown:0,desc:"Fire a concentrated bolt of pure light energy"},
{key:"lf_prismburst",name:"Prism Burst",icon:"✦",type:"damage",tier:"basic",basePower:0.35,cooldown:0,desc:"Scatter light into refracted beams that strike multiple angles"},
{key:"lf_radiantward",name:"Radiant Ward",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Condense light into a protective barrier of temporary HP",shield:35},
{key:"lf_overcharge",name:"Overcharge",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Radiant energy heals and empowers — boosts attack for 2 rounds",effects:{selfAtkUp:true},heal:20},
{key:"lf_solarflare",name:"Solar Flare",icon:"✦",type:"damage",tier:"advanced",basePower:0.8,cooldown:2,desc:"Unleash a burst of solar plasma — powerful radiant damage"},
{key:"lf_radiantaegis",name:"Radiant Aegis",icon:"✦",type:"buff",tier:"advanced",basePower:0.6,cooldown:2,desc:"Layered light barrier absorbs damage and mends wounds",shield:55,hot:{heal:10,rounds:2}},
{key:"lf_energyflow",name:"Energy Flow",icon:"✦",type:"buff",tier:"advanced",basePower:0.65,cooldown:3,desc:"Stabilize energy — heals and boosts attack for 2 rounds",effects:{selfAtkUp:true},heal:35},
{key:"lf_supernova",name:"Supernova",icon:"✦",type:"damage",tier:"ultimate",basePower:1.45,cooldown:4,desc:"Detonate a star-like explosion of energy — massive area devastation"},
{key:"lf_lightsurge",name:"Lightsurge",icon:"✦",type:"damage",tier:"ultimate",basePower:2.2,cooldown:4,desc:"Release a continuous surge of radiant energy — devastating damage"},
{key:"lf_p_divinerestoration",name:"Divine Restoration",icon:"✦",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+50% defense. Heal 50 HP per round. +35 max HP. Radiant shield sustains you.",passiveEffect:{defBonus:0.50,hpRegen:50,hpBonus:35}},
{key:"lf_p_radiance",name:"Building Radiance",icon:"✦",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+5% damage per round in combat, stacks up to 8. Heal 12 HP per round.",passiveEffect:{atkPerStack:0.05,maxStacks:8,hpRegen:12}},
{key:"lf_p_core",name:"Radiant Core",icon:"✦",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Living star — +120% damage. +120 max HP.",passiveEffect:{atkBonus:1.30,hpBonus:120}},
],
soulbinding:[
{key:"sb_willstrike",name:"Will Strike",icon:"👁",type:"damage",tier:"basic",basePower:0.45,cooldown:0,desc:"Assault the enemy's mind with raw willpower"},
{key:"sb_psychiclash",name:"Psychic Lash",icon:"👁",type:"damage",tier:"basic",basePower:0.35,cooldown:0,desc:"Whip of mental energy — sharp and precise"},
{key:"sb_mindshield",name:"Mind Shield",icon:"🛡",type:"defense",tier:"basic",basePower:0.4,cooldown:1,desc:"Fortress of will — mental and physical defense"},
{key:"sb_dominate",name:"Dominate",icon:"✦",type:"buff",tier:"basic",basePower:0.45,cooldown:2,desc:"Seize partial control — weakens enemy attack for 2 rounds",effects:{enemyAtkDown:true}},
{key:"sb_soulrend",name:"Soul Rend",icon:"👁",type:"damage",tier:"advanced",basePower:0.8,cooldown:2,desc:"Tear at the enemy's essence — rips away a buff for yourself",stealBuffs:true},
{key:"sb_ironwill",name:"Iron Will",icon:"🛡",type:"defense",tier:"advanced",basePower:0.65,cooldown:2,desc:"Unbreakable mental fortress — powerful psychic defense"},
{key:"sb_puppeteer",name:"Puppeteer",icon:"✦",type:"buff",tier:"advanced",basePower:0.65,cooldown:3,desc:"Seize the enemy's will — greatly weakens enemy attack for 2 rounds",effects:{enemyAtkDown:true}},
{key:"sb_shatter",name:"Shatter Soul",icon:"👁",type:"damage",tier:"ultimate",basePower:1.2,cooldown:4,desc:"Break the enemy's will completely — devastating psychic damage"},
{key:"sb_domination",name:"Total Domination",icon:"👁",type:"damage",tier:"ultimate",basePower:1.2,cooldown:5,desc:"Seize absolute control — rip ALL buffs and shields from the enemy and claim them as your own",stealAllBuffs:true},
{key:"sb_p_willpower",name:"Indomitable Will",icon:"👁",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+45% attack. Enemy −30% attack. +25% evasion. +35 max HP. Crush their will to fight.",passiveEffect:{atkBonus:0.60,enemyAtkPenalty:0.30,evasion:0.20,hpBonus:35}},
{key:"sb_p_domination",name:"Domination Aura",icon:"👁",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Enemy loses −7% attack per round, stacks up to 6. +20% evasion. +50 max HP.",passiveEffect:{enemyAtkPerStack:0.07,maxStacks:6,evasion:0.20,hpBonus:50}},
{key:"sb_p_tyrant",name:"Tyrant's Presence",icon:"👁",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Absolute authority — +30% attack. +30% defense. Enemy −15% attack. +20% evasion. +40 max HP.",passiveEffect:{atkBonus:0.30,defBonus:0.30,enemyAtkPenalty:0.15,evasion:0.20,hpBonus:40}},
],
earthshaping:[
{key:"es_boulder",name:"Boulder Crush",icon:"⛰",type:"damage",tier:"basic",basePower:0.5,cooldown:0,desc:"Launch a massive stone at the enemy"},
{key:"es_stoneskin",name:"Stone Skin",icon:"⛰",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Coat yourself in stone — shield HP and defense boost",shield:30,effects:{selfDefUp:true}},
{key:"es_stonewall",name:"Stone Wall",icon:"🛡",type:"defense",tier:"basic",basePower:0.5,cooldown:1,desc:"Raise a wall of solid granite"},
{key:"es_ironhide",name:"Iron Hide",icon:"✦",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Coat skin in stone — boosts defense and absorbs hits",shield:25,effects:{selfDefUp:true}},
{key:"es_avalanche",name:"Avalanche",icon:"⛰",type:"damage",tier:"advanced",basePower:0.75,cooldown:2,desc:"Bring down an entire hillside on the enemy"},
{key:"es_fortress",name:"Fortress",icon:"⛰",type:"buff",tier:"advanced",basePower:0.4,cooldown:2,desc:"Encase yourself in layered stone — massive shield and defense",shield:55,effects:{selfDefUp:true}},
{key:"es_quake",name:"Quake Pulse",icon:"✦",type:"buff",tier:"advanced",basePower:0.35,cooldown:3,desc:"Seismic resonance — shield barrier and defense boost",shield:35,effects:{selfDefUp:true}},
{key:"es_worldbreaker",name:"Worldbreaker",icon:"⛰",type:"damage",tier:"ultimate",basePower:1.15,cooldown:4,desc:"Shatter the earth itself — tectonic devastation"},
{key:"es_mountainheart",name:"Mountain Heart",icon:"🛡",type:"defense",tier:"ultimate",basePower:1.0,cooldown:5,desc:"Become the mountain — nearly immovable for 1 round"},
{key:"es_p_stoneblood",name:"Stoneblood",icon:"⛰",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+60% defense. +60 max HP. The mountain does not break.",passiveEffect:{defBonus:0.60,hpBonus:60}},
{key:"es_p_tectonic",name:"Tectonic Buildup",icon:"⛰",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+5% defense per round, stacks up to 6. +20% attack. +30 max HP.",passiveEffect:{defPerStack:0.05,maxStacks:6,atkBonus:0.20,hpBonus:30}},
{key:"es_p_titan",name:"Titan's Endurance",icon:"⛰",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Immovable — +30% attack. +40% defense. Heal 10 HP per round. +40 max HP.",passiveEffect:{atkBonus:0.30,defBonus:0.40,hpRegen:10,hpBonus:40}},
],
runebinding:[
{key:"rb_runebolt",name:"Rune Bolt",icon:"🔷",type:"damage",tier:"basic",basePower:0.45,cooldown:0,desc:"Launch a glyph that detonates on contact"},
{key:"rb_sigilward",name:"Sigil of Warding",icon:"🔷",type:"buff",tier:"basic",basePower:0.25,cooldown:1,desc:"Inscribe a ward that absorbs damage",shield:25},
{key:"rb_wardpower",name:"Ward of Power",icon:"🔷",type:"buff",tier:"basic",basePower:0.25,cooldown:2,desc:"Protective ward that also sharpens offense",shield:30,effects:{selfAtkUp:true}},
{key:"rb_amplify",name:"Amplify Runes",icon:"✦",type:"buff",tier:"basic",basePower:0.3,cooldown:2,desc:"Wards mend and protect — shield, HoT, and defense",shield:20,hot:{heal:6,rounds:3},effects:{selfDefUp:true}},
{key:"rb_detonation",name:"Rune Detonation",icon:"🔷",type:"damage",tier:"advanced",basePower:0.75,cooldown:2,desc:"Overload inscribed glyphs — chain explosion"},
{key:"rb_aegis",name:"Runic Aegis",icon:"🔷",type:"buff",tier:"advanced",basePower:0.4,cooldown:2,desc:"Layered ward system — powerful shield and defense",shield:50,effects:{selfDefUp:true}},
{key:"rb_nullify",name:"Nullify",icon:"✦",type:"buff",tier:"advanced",basePower:0.4,cooldown:3,desc:"Counter-ward amplifies all combat stats — shield and dual boost",shield:30,effects:{selfAtkUp:true,selfDefUp:true}},
{key:"rb_apocalypse",name:"Rune Apocalypse",icon:"🔷",type:"damage",tier:"ultimate",basePower:1.2,cooldown:4,desc:"Detonate every glyph in the area simultaneously"},
{key:"rb_masterward",name:"Master Ward",icon:"🔷",type:"buff",tier:"ultimate",basePower:0.9,cooldown:5,desc:"The perfect ward — supreme shield and complete enhancement",shield:75,effects:{selfAtkUp:true,selfDefUp:true}},
{key:"rb_p_inscribed",name:"Inscribed Skin",icon:"🔷",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+45% defense. Heal 12 HP per round. +30 max HP. Living wardstone.",passiveEffect:{defBonus:0.45,hpRegen:12,hpBonus:30}},
{key:"rb_p_resonance",name:"Runic Resonance",icon:"🔷",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+4% attack and +4% defense per round, stacks up to 6. +20 max HP.",passiveEffect:{atkPerStack:0.04,defPerStack:0.04,maxStacks:6,hpBonus:20}},
{key:"rb_p_arcane",name:"Arcane Lattice",icon:"🔷",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Perfect wards — +30% attack. +30% defense. Enemy −10% defense. +30 max HP.",passiveEffect:{atkBonus:0.30,defBonus:0.30,enemyDefPenalty:0.10,hpBonus:30}},
],
frostbringer:[
{key:"fb_icespear",name:"Ice Spear",icon:"❄️",type:"damage",tier:"basic",basePower:0.5,cooldown:0,desc:"Razor-sharp lance of ice — leaves frozen wounds",dot:{damage:10,rounds:2}},
{key:"fb_frostbite",name:"Frostbite",icon:"❄️",type:"damage",tier:"basic",basePower:0.2,cooldown:0,desc:"Creeping cold burns and lingers",dot:{damage:11,rounds:3}},
{key:"fb_frostveil",name:"Frost Veil",icon:"✦",type:"buff",tier:"basic",basePower:0.45,cooldown:2,desc:"Chilling aura weakens enemy attacks",effects:{enemyAtkDown:true}},
{key:"fb_numbing",name:"Numbing Cold",icon:"✦",type:"buff",tier:"basic",basePower:0.45,cooldown:2,desc:"Sap enemy's warmth — weakens enemy attack for 2 rounds",effects:{enemyAtkDown:true}},
{key:"fb_blizzard",name:"Blizzard",icon:"❄️",type:"damage",tier:"advanced",basePower:0.75,cooldown:2,desc:"Howling blizzard — freezing devastation that persists",dot:{damage:17,rounds:3}},
{key:"fb_permacurse",name:"Permafrost Crush",icon:"❄️",type:"damage",tier:"advanced",basePower:0.6,cooldown:2,desc:"Coat enemy in permafrost that slowly crushes",dot:{damage:20,rounds:3}},
{key:"fb_deepfreeze",name:"Deep Freeze",icon:"✦",type:"buff",tier:"advanced",basePower:0.7,cooldown:3,desc:"Flash-freeze the enemy — greatly weakens enemy attack for 2 rounds",effects:{enemyAtkDown:true}},
{key:"fb_absolutezero",name:"Absolute Zero",icon:"❄️",type:"damage",tier:"ultimate",basePower:1.3,cooldown:4,desc:"Molecular standstill — the cold beyond cold lingers",dot:{damage:30,rounds:4}},
{key:"fb_glacierage",name:"Glacial Age",icon:"✦",type:"buff",tier:"ultimate",basePower:1.1,cooldown:5,desc:"Usher in an ice age — massively weakens enemy attack for 2 rounds",effects:{enemyAtkDown:true}},
{key:"fb_p_coldheart",name:"Coldheart",icon:"❄️",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+50% frost damage. Enemy −40% attack. +100 max HP. Cold beyond mercy.",passiveEffect:{atkBonus:0.50,enemyAtkPenalty:0.40,hpBonus:100}},
{key:"fb_p_frostburn",name:"Frostburn Aura",icon:"❄️",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Enemy loses −5% attack per round, stacks up to 6. +25% defense. +25 max HP.",passiveEffect:{enemyAtkPerStack:0.05,maxStacks:6,defBonus:0.25,hpBonus:25}},
{key:"fb_p_permafrost",name:"Permafrost Blood",icon:"❄️",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Eternal cold — +25% attack. +30% defense. Enemy −10% attack. Heal 6 HP per round.",passiveEffect:{atkBonus:0.25,defBonus:0.30,enemyAtkPenalty:0.10,hpRegen:6}},
],
forcebinding:[
{key:"gb_forcepush",name:"Force Push",icon:"🕳️",type:"damage",tier:"basic",basePower:0.45,cooldown:0,desc:"Telekinetic blast that slams the enemy backward"},
{key:"gb_gravsnare",name:"Gravity Snare",icon:"🕳️",type:"damage",tier:"basic",basePower:0.35,cooldown:0,desc:"Intensify gravity beneath the enemy, crushing them into the ground"},
{key:"gb_gravcrush",name:"Gravity Crush",icon:"✦",type:"buff",tier:"basic",basePower:0.45,cooldown:2,desc:"Amplify gravity on enemy — crushes their defenses",effects:{enemyDefDown:true}},
{key:"gb_gravwell",name:"Gravity Well",icon:"✦",type:"buff",tier:"basic",basePower:0.45,cooldown:2,desc:"Amplify local gravity — weakens enemy attack for 2 rounds",effects:{enemyAtkDown:true}},
{key:"gb_crush",name:"Crush",icon:"🕳️",type:"damage",tier:"advanced",basePower:0.8,cooldown:2,desc:"Telekinetically compress the enemy from all sides — immense force"},
{key:"gb_implosion",name:"Implosion",icon:"🕳️",type:"damage",tier:"advanced",basePower:0.95,cooldown:2,desc:"Collapse space inward — crushing force damage"},
{key:"gb_leaden",name:"Leaden Aura",icon:"✦",type:"buff",tier:"advanced",basePower:1.05,cooldown:3,desc:"Everything weighs ten times more — cripples all combat ability",effects:{enemyAtkDown:true,enemyDefDown:true}},
{key:"gb_singularity",name:"Singularity",icon:"🕳️",type:"damage",tier:"ultimate",basePower:1.45,cooldown:4,desc:"Collapse a point in space into a gravitational singularity — devastating"},
{key:"gb_spatialrend",name:"Spatial Rend",icon:"🕳️",type:"damage",tier:"ultimate",basePower:1.75,cooldown:4,desc:"Tear space itself apart — massive concentrated force damage"},
{key:"gb_p_graviton",name:"Graviton Core",icon:"🕳️",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"+60% attack. Enemy −50% attack. +50 max HP. Everything bends toward you.",passiveEffect:{atkBonus:0.70,enemyAtkPenalty:0.50,hpBonus:100}},
{key:"gb_p_tidalforce",name:"Tidal Force",icon:"🕳️",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Enemy loses −8% attack per round, stacks up to 6. +25% defense. +45 max HP. Inescapable pressure.",passiveEffect:{enemyAtkPerStack:0.08,maxStacks:6,defBonus:0.25,hpBonus:45}},
{key:"gb_p_spatial",name:"Spatial Dominion",icon:"🕳️",type:"passive",tier:"passive_master",basePower:0,cooldown:0,desc:"Master of force and space — +35% attack. +35% defense. Enemy −20% attack. +20% evasion. +40 max HP.",passiveEffect:{atkBonus:0.35,defBonus:0.35,enemyAtkPenalty:0.20,evasion:0.20,hpBonus:40}},
],
};

// ── HP System ────────────────────────────────────────────────────────────────
// Base HP by discipline primary. HP persists between duels (like fatigue).
const BASE_HP = { mage: 400, dragon: 200 };
const DISCIPLINE_HP_BONUS = {
  flamecalling:35, stormweaving:30, tidecalling:65, voidweaving:38,
  lightforging:45, soulbinding:45, earthshaping:75, runebinding:50, frostbringer:55,
  forcebinding:40,
};

// Calculate max HP for an entity considering passives and artifacts
function calcMaxHP(entity) {
  const type = entity.type || (entity.sort_key === "Dragon" ? "dragon" : "mage");
  let hp = BASE_HP[type] || 100;
  // Add discipline HP bonuses (peak discipline gives most)
  const peak = peakDiscipline(entity.disciplines, entity.primaryDiscipline);
  if (peak.key) {
    hp += (DISCIPLINE_HP_BONUS[peak.key] || 0) * peak.level;
  }
  // Add passive HP bonuses
  const learned = entity.learnedAbilities || {};
  for (const disc of Object.keys(ABILITIES)) {
    for (const ab of ABILITIES[disc]) {
      if (ab.type === "passive" && learned[ab.key] && ab.passiveEffect?.hpBonus) {
        hp += ab.passiveEffect.hpBonus;
      }
    }
  }
  // Add artifact HP bonuses
  const arts = Array.isArray(entity.artifacts) ? entity.artifacts.filter(a => a?.name && a?.effects?.hpBonus) : [];
  for (const a of arts) {
    const attuneMult = 1 + Math.min(a.attunement || 0, 10) * 0.05;
    hp += Math.round(a.effects.hpBonus * attuneMult);
  }
  // Fatigue reduces max HP by up to 30%
  const fat = entity.fatigue || 0;
  if (fat > 0.5) {
    hp *= 1 - ((fat - 0.5) / 0.5) * 0.3;
  }
  return Math.round(hp);
}

// Get current HP (stored on entity, defaults to max)
function getCurrentHP(entity) {
  const max = calcMaxHP(entity);
  if (entity.currentHP === undefined || entity.currentHP === null) return max;
  return Math.min(entity.currentHP, max);
}

// Collect all passive effects for an entity. round = current duel round (for stacking passives)
function getPassiveEffects(entity, round) {
  const effects = { atkBonus:0, defBonus:0, hpBonus:0, hpRegen:0, enemyAtkPenalty:0, enemyDefPenalty:0, evasion:0 };
  const learned = entity.learnedAbilities || {};
  const r = round || 0;
  for (const disc of Object.keys(ABILITIES)) {
    for (const ab of ABILITIES[disc]) {
      if (ab.type !== "passive" || !learned[ab.key] || !ab.passiveEffect) continue;
      const pe = ab.passiveEffect;
      // Flat bonuses
      if (pe.atkBonus) effects.atkBonus += pe.atkBonus;
      if (pe.defBonus) effects.defBonus += pe.defBonus;
      if (pe.hpBonus) effects.hpBonus += pe.hpBonus;
      if (pe.hpRegen) effects.hpRegen += pe.hpRegen;
      if (pe.enemyAtkPenalty) effects.enemyAtkPenalty += pe.enemyAtkPenalty;
      if (pe.enemyDefPenalty) effects.enemyDefPenalty += pe.enemyDefPenalty;
      if (pe.evasion) effects.evasion = Math.min(0.75, effects.evasion + pe.evasion);
      // Stacking bonuses — grow each round up to maxStacks
      if (pe.atkPerStack) {
        const stacks = Math.min(r, pe.maxStacks || 99);
        effects.atkBonus += pe.atkPerStack * stacks;
      }
      if (pe.defPerStack) {
        const stacks = Math.min(r, pe.maxStacks || 99);
        effects.defBonus += pe.defPerStack * stacks;
      }
      if (pe.enemyAtkPerStack) {
        const stacks = Math.min(r, pe.maxStacks || 99);
        effects.enemyAtkPenalty += pe.enemyAtkPerStack * stacks;
      }
      if (pe.enemyDefPerStack) {
        const stacks = Math.min(r, pe.maxStacks || 99);
        effects.enemyDefPenalty += pe.enemyDefPerStack * stacks;
      }
    }
  }

  // Add artifact effects — all possessed artifacts are always active
  const arts = Array.isArray(entity.artifacts) ? entity.artifacts.filter(a => a?.name && a?.effects) : [];
  for (const a of arts) {
    const attuneMult = 1 + Math.min(a.attunement || 0, 10) * 0.05; // +5% per attunement level
    const eff = a.effects;
    if (eff.atkBonus) effects.atkBonus += eff.atkBonus * attuneMult;
    if (eff.defBonus) effects.defBonus += eff.defBonus * attuneMult;
    if (eff.hpBonus) effects.hpBonus += Math.round(eff.hpBonus * attuneMult);
    if (eff.hpRegen) effects.hpRegen += Math.round(eff.hpRegen * attuneMult);
    if (eff.enemyAtkPenalty) effects.enemyAtkPenalty += eff.enemyAtkPenalty * attuneMult;
    if (eff.enemyDefPenalty) effects.enemyDefPenalty += eff.enemyDefPenalty * attuneMult;
    if (eff.evasion) effects.evasion = Math.min(0.75, effects.evasion + eff.evasion * attuneMult);
  }

  return effects;
}

// Mastery: 0-5, each level adds +10% to basePower. Gained by using the ability in duels.
const MASTERY_LEVELS = 5;
const MASTERY_XP_THRESHOLDS = [3, 5, 8, 12, 18];
const MASTERY_BONUS_PER_LEVEL = 0.10;
const MASTERY_LABELS = ["Untrained","Familiar","Practiced","Skilled","Expert","Mastered"];

function getAbility(abilityKey) {
  for (const disc of Object.keys(ABILITIES)) {
    const found = ABILITIES[disc].find(a => a.key === abilityKey);
    if (found) return { ...found, discipline: disc };
  }
  return null;
}

function calcSpentSkillPoints(learnedAbilities, discipline) {
  if (!learnedAbilities || !ABILITIES[discipline]) return 0;
  return ABILITIES[discipline]
    .filter(a => learnedAbilities[a.key] && a.type !== "passive")
    .reduce((sum, a) => sum + (ABILITY_TIERS[a.tier]?.cost || 0), 0);
}

function canLearnAbility(entity, abilityKey) {
  const ab = getAbility(abilityKey);
  if (!ab) return false;
  const disc = ab.discipline;
  const discLevel = (entity.disciplines || {})[disc] || 0;
  const tierInfo = ABILITY_TIERS[ab.tier];
  if (!tierInfo) return false;
  if (discLevel < tierInfo.reqLevel) return false;

  // Passive abilities: only 1 allowed globally, must be primary discipline
  if (ab.type === "passive") {
    if (entity.primaryDiscipline !== disc) return false; // must be primary
    // Check if entity already has ANY passive learned
    const learned = entity.learnedAbilities || {};
    for (const d of Object.keys(ABILITIES)) {
      for (const a of ABILITIES[d]) {
        if (a.type === "passive" && learned[a.key]) return false; // already has a passive
      }
    }
    return true; // no SP cost for passives
  }

  const isPrimary = entity.primaryDiscipline ? (entity.primaryDiscipline === disc) : undefined;
  const available = calcAvailableSkillPoints(discLevel, isPrimary);
  const spent = calcSpentSkillPoints(entity.learnedAbilities, disc);
  return (available - spent) >= tierInfo.cost;
}

function calcAbilityPower(abilityKey, entity) {
  const ab = getAbility(abilityKey);
  if (!ab) return 0;
  const discLevel = (entity.disciplines || {})[ab.discipline] || 0;
  const mastery = (entity.abilityMastery || {})[abilityKey] || 0;
  const masteryLevel = getMasteryLevel(mastery);

  // Peak discipline bonus: abilities from your highest discipline get +20% power
  const peak = peakDiscipline(entity.disciplines, entity.primaryDiscipline);
  const isPeak = peak.key && peak.key === ab.discipline;
  const peakBonus = isPeak ? 1.20 : 1.0;

  let power = ab.basePower * (1 + Math.pow(discLevel / 12, 3.0) * 3.0) * (1 + masteryLevel * MASTERY_BONUS_PER_LEVEL) * peakBonus;
  // Passive and artifact bonuses (atkBonus, defBonus, etc.) are applied in resolveRound
  return power;
}

// Discipline-level multiplier for heal, shield, DoT, HoT amounts.
// Scales from 1.0 at level 1 to ~2.5 at level 12. Peak discipline gets +20% bonus.
function discEffectMult(entity, discipline) {
  const discLevel = (entity.disciplines || {})[discipline] || 0;
  if (discLevel <= 0) return 1.0;
  const peak = peakDiscipline(entity.disciplines, entity.primaryDiscipline);
  const isPeak = peak.key && peak.key === discipline;
  const peakBonus = isPeak ? 1.20 : 1.0;
  return (1 + discLevel * 0.12) * peakBonus;
}

function getMasteryLevel(xp) {
  let lvl = 0, total = 0;
  for (let i = 0; i < MASTERY_LEVELS; i++) {
    total += MASTERY_XP_THRESHOLDS[i];
    if (xp >= total) lvl = i + 1; else break;
  }
  return lvl;
}

function getMasteryProgress(xp) {
  let total = 0;
  for (let i = 0; i < MASTERY_LEVELS; i++) {
    const needed = MASTERY_XP_THRESHOLDS[i];
    if (xp < total + needed) return { level: i, current: xp - total, needed, total: xp };
    total += needed;
  }
  return { level: MASTERY_LEVELS, current: 0, needed: 0, total: xp };
}

function getLearnedAbilitiesForDisc(entity, discipline) {
  if (!entity.learnedAbilities || !ABILITIES[discipline]) return [];
  return ABILITIES[discipline].filter(a => entity.learnedAbilities[a.key]);
}

const ABILITY_TYPE_INFO = {
  damage:  { label:"Damage",  color:"#ef4444", icon:"⚔" },
  defense: { label:"Defense", color:"#3b82f6", icon:"🛡" },
  buff:    { label:"Buff",    color:"#22c55e", icon:"✦" },
  passive: { label:"Passive", color:"#06b6d4", icon:"◈" },
};

// Format buff/debuff effect tags for ability display in duel UI
function getAbilityEffectTags(ab, entity) {
  if (!ab) return [];
  const tags = [];
  const mult = (entity && ab.discipline) ? discEffectMult(entity, ab.discipline) : 1;
  if (ab.type === "damage") {
    tags.push({ label: "DMG", color: "#ef4444", icon: "⚔" });
  }
  if (ab.type === "defense") {
    tags.push({ label: "DEF", color: "#3b82f6", icon: "🛡" });
  }
  if (ab.effects) {
    if (ab.effects.selfAtkUp) tags.push({ label: "ATK UP", color: "#22c55e", icon: "↑" });
    if (ab.effects.selfDefUp) tags.push({ label: "DEF UP", color: "#60a5fa", icon: "↑" });
    if (ab.effects.enemyAtkDown) tags.push({ label: "Enemy ATK ↓", color: "#f97316", icon: "↓" });
    if (ab.effects.enemyDefDown) tags.push({ label: "Enemy DEF ↓", color: "#fb923c", icon: "↓" });
  }
  if (ab.lifesteal) tags.push({ label: `${Math.round(ab.lifesteal*100)}% Steal`, color: "#be185d", icon: "♥" });
  if (ab.dot) { const d = Math.round(ab.dot.damage * mult); tags.push({ label: `DoT ${d}×${ab.dot.rounds}r`, color: "#a855f7", icon: "☠" }); }
  if (ab.heal) { const h = Math.round(ab.heal * mult); tags.push({ label: `Heal ${h}`, color: "#34d399", icon: "♥" }); }
  if (ab.hot) { const h = Math.round(ab.hot.heal * mult); tags.push({ label: `HoT ${h}×${ab.hot.rounds}r`, color: "#2dd4bf", icon: "♥↻" }); }
  if (ab.shield) { const s = Math.round(ab.shield * mult); tags.push({ label: `Shield ${s}`, color: "#fbbf24", icon: "🛡" }); }
  if (ab.stealBuffs) tags.push({ label: "Steal Buff", color: "#c084fc", icon: "🔮" });
  if (ab.stealAllBuffs) tags.push({ label: "Steal ALL Buffs+Shield", color: "#a855f7", icon: "🔮" });
  return tags;
}

// ── Discipline Counters ──────────────────────────────────────────────────────
// Each key is strong against the disciplines in its array.
// Counters are checked PER ABILITY PER ROUND:
//   - Attack vs enemy defense: if attack disc counters defense disc, +30% attack power
//   - Defense vs enemy attack: if defense disc counters attack disc, +30% defense power
//   - If attack disc is countered by enemy defense disc, −20% attack power
// Every discipline counters exactly 2 and is countered by exactly 2 (net zero).
const DISCIPLINE_COUNTERS = {
  flamecalling:  ["frostbringer", "earthshaping"],    // fire melts ice, shatters stone
  stormweaving:  ["flamecalling", "forcebinding"],    // storm smothers flame, scatters focus
  tidecalling:   ["voidweaving", "forcebinding"],     // water fills the void, erodes force
  voidweaving:   ["stormweaving", "lightforging"],    // void swallows storms, devours light
  lightforging:  ["voidweaving", "soulbinding"],      // light burns darkness, frees minds
  soulbinding:   ["runebinding", "tidecalling"],      // will shatters wards, dominates the sea
  earthshaping:  ["tidecalling", "frostbringer"],     // earth dams water, crushes ice
  runebinding:   ["lightforging", "flamecalling"],    // wards absorb energy, seal fire
  frostbringer:  ["stormweaving", "soulbinding"],     // cold freezes storms, numbs the will
  forcebinding:  ["runebinding", "earthshaping"],     // force shatters wards, crushes stone
};
const COUNTER_ATK_BONUS = 0.30;    // +30% attack power when your attack counters enemy defense
const COUNTER_DEF_BONUS = 0.30;    // +30% defense power when your defense counters enemy attack
const COUNTERED_PENALTY = 0.20;    // −20% attack power when enemy defense counters your attack

// Check if discipline A counters discipline B
function disciplineCounters(discA, discB) {
  if (!discA || !discB) return false;
  return (DISCIPLINE_COUNTERS[discA] || []).includes(discB);
}

// ── Discipline Synergies ─────────────────────────────────────────────────────
// Pairs of disciplines that grant a bonus when BOTH are at level 7+.
// The bonus scales with the lower of the two levels.
// Bonuses are tuned to add ~7-11% relative power at lvl 7, ~12-19% at lvl 12.
const DISCIPLINE_SYNERGIES = [
  { pair: ["flamecalling", "stormweaving"],  label: "Firestorm",       icon: "🌪", bonus: 0.04, desc: "Fire + Lightning = devastating area denial" },
  { pair: ["flamecalling", "earthshaping"],  label: "Magmaforge",      icon: "🌋", bonus: 0.035, desc: "Fire + Earth = molten stone weapons and armor" },
  { pair: ["tidecalling", "stormweaving"],   label: "Tempest",         icon: "🌀", bonus: 0.04, desc: "Water + Storm = hurricane-force devastation" },
  { pair: ["voidweaving", "soulbinding"],    label: "Dread Pact",      icon: "💀", bonus: 0.05, desc: "Void + Soul = terror and psychic annihilation" },
  { pair: ["flamecalling", "voidweaving"],   label: "Hellfire",        icon: "🕳", bonus: 0.035, desc: "Fire + Void = flames that burn reality itself" },
  { pair: ["tidecalling", "earthshaping"],   label: "Permafrost",      icon: "❄", bonus: 0.03, desc: "Water + Earth = unbreakable frozen fortifications" },
  { pair: ["flamecalling", "lightforging"],  label: "Crucible",        icon: "🔥", bonus: 0.04, desc: "Fire + Light = purifying radiant fire that burns corruption and heals allies while destroying enemies" },
  { pair: ["voidweaving", "lightforging"],   label: "Eclipse",         icon: "🌓", bonus: 0.04, desc: "Void + Light = weaponized contrast — blinding flashes followed by absolute darkness" },
  { pair: ["voidweaving", "earthshaping"],   label: "Withering",       icon: "🥀", bonus: 0.035, desc: "Void + Earth = accelerated erosion — fortresses crumble, armor rusts to dust" },
  { pair: ["frostbringer", "tidecalling"],   label: "Glacial Deluge",  icon: "🧊", bonus: 0.04,  desc: "Frost + Water = flash-frozen tidal waves that shatter on impact like a million blades" },
  { pair: ["frostbringer", "earthshaping"],  label: "Frozen Bastion",  icon: "🏔", bonus: 0.035, desc: "Frost + Earth = permafrost fortifications harder than steel, with spires of razor ice" },
  { pair: ["forcebinding", "earthshaping"],  label: "Tectonics",       icon: "🕳️", bonus: 0.04, desc: "Force + Earth = amplified gravitational pressure fused with tectonic mass — unstoppable crushing power" },
  { pair: ["forcebinding", "voidweaving"],   label: "Collapse",        icon: "⚫", bonus: 0.045, desc: "Force + Void = spatial annihilation — gravity wells that devour light and matter alike" },
];
const SYNERGY_THRESHOLD = 7; // Both disciplines must be at least this level

// ── Runebinding Passive ──────────────────────────────────────────────────────
// Runebinding grants a standalone multiplicative bonus at level 7+.
// It doesn't need a partner — wards amplify ALL magic equally.
// Scales from +3% total power at lvl 7 to +8% at lvl 12.
const RUNEBINDING_PASSIVE_MIN_LEVEL = 7;
function calcRunebindingPassive(disciplines) {
  if (!disciplines || typeof disciplines !== "object") return 0;
  const lvl = disciplines.runebinding || 0;
  if (lvl < RUNEBINDING_PASSIVE_MIN_LEVEL) return 0;
  // Linear scale: 0.03 at lvl 7, up to 0.08 at lvl 12
  return 0.03 + (lvl - 7) * 0.01;
}

// Power formula constants — mages grow steeper, dragons start higher
const MAGE_BASE   = 1.40;
const DRAGON_BASE = 1.45;

const MAGE_WEIGHTS = {
  lightforging: 0.055, voidweaving: 0.055,
  soulbinding:  0.050, stormweaving: 0.050,
  flamecalling: 0.045, tidecalling:  0.045,
  runebinding:  0.030, earthshaping: 0.040,
  frostbringer: 0.050, forcebinding: 0.048,
};
const DRAGON_WEIGHTS = {
  stormweaving: 0.175, voidweaving: 0.130,
  lightforging: 0.120, flamecalling: 0.095,
  tidecalling:  0.100, soulbinding:  0.090,
  earthshaping: 0.150, runebinding:  0.070,
  frostbringer: 0.110, forcebinding: 0.105,
};
const PEAK_MULT = 1.60; // Primary affinity bonus — highest discipline gets this multiplier

// ── Power tier system — 5 ranks, escalating visual sophistication ─────────────
const POWER_TIERS = [
  {
    key:"celestial", label:"Celestial", min:11.0,
    // Ethereal white-gold with aurora shimmer
    color:"#e0c97f",
    glow:"#fff7d6",
    gradient:"linear-gradient(135deg,#0f0c00 0%,#1a1400 30%,#2a1f00 60%,#1a1400 100%)",
    headerGrad:"linear-gradient(135deg,#1a1200,#2e2000,#3d2b00,#2e2000)",
    badge:"#e0c97f",
    badgeText:"#1a1200",
    textColor:"#fef9e7",
    subColor:"#e0c97f99",
    border:"#e0c97f",
    borderWidth:2,
    glowSize:24,
    animBorder:true,   // animated sweeping glow border
    ornament:"✦",      // decorative ornament for header
    barStyle:"linear-gradient(90deg,#c8a84b,#f0d060,#c8a84b)",
    cardBg:"linear-gradient(160deg,#1a140022 0%,#2e200018 60%,#e0c97f08 100%)",
    activeBg:"linear-gradient(160deg,#1a1400ee,#2e2000ee)",
    letterSpacing:3,
  },
  {
    key:"legendary", label:"Legendary", min:6.0,
    // Deep amber-crimson, forged-metal feel
    color:"#f97316",
    glow:"#fed7aa",
    gradient:"linear-gradient(135deg,#1c0a00 0%,#431407 40%,#7c2d12 100%)",
    headerGrad:"linear-gradient(135deg,#1c0900,#431407,#7c2d12,#431407)",
    badge:"#fb923c",
    badgeText:"#1c0a00",
    textColor:"#fff7ed",
    subColor:"#fb923c99",
    border:"#f97316",
    borderWidth:2,
    glowSize:16,
    animBorder:true,
    ornament:"🔥",
    barStyle:"linear-gradient(90deg,#c2410c,#f97316,#c2410c)",
    cardBg:"linear-gradient(160deg,#43140718 0%,#f9731608 100%)",
    activeBg:"linear-gradient(160deg,#1c0900ee,#431407ee)",
    letterSpacing:2,
  },
  {
    key:"epic", label:"Epic", min:2.5,
    // Void-purple, deep and saturated
    color:"#a855f7",
    glow:"#e9d5ff",
    gradient:"linear-gradient(135deg,#0d0017 0%,#2d0a5e 50%,#4a0f8a 100%)",
    headerGrad:"linear-gradient(135deg,#0d0017,#2d0a5e,#4a0f8a,#2d0a5e)",
    badge:"#c084fc",
    badgeText:"#0d0017",
    textColor:"#faf5ff",
    subColor:"#c084fc99",
    border:"#a855f7",
    borderWidth:1.5,
    glowSize:10,
    animBorder:false,
    ornament:"⬡",
    barStyle:"linear-gradient(90deg,#7e22ce,#a855f7,#7e22ce)",
    cardBg:"linear-gradient(160deg,#2d0a5e14 0%,#a855f708 100%)",
    activeBg:"linear-gradient(160deg,#0d0017ee,#2d0a5eee)",
    letterSpacing:1.5,
  },
  {
    key:"rare", label:"Rare", min:0.5,
    // Steel blue, cool and sharp
    color:"#38bdf8",
    glow:"#bae6fd",
    gradient:"linear-gradient(135deg,#001824 0%,#0c3a5a 50%,#155e8a 100%)",
    headerGrad:"linear-gradient(135deg,#001824,#0c3a5a,#155e8a,#0c3a5a)",
    badge:"#7dd3fc",
    badgeText:"#001824",
    textColor:"#f0f9ff",
    subColor:"#7dd3fc99",
    border:"#38bdf8",
    borderWidth:1,
    glowSize:6,
    animBorder:false,
    ornament:"◆",
    barStyle:"linear-gradient(90deg,#0369a1,#38bdf8,#0369a1)",
    cardBg:"linear-gradient(160deg,#0c3a5a12 0%,#38bdf808 100%)",
    activeBg:"linear-gradient(160deg,#001824ee,#0c3a5aee)",
    letterSpacing:1,
  },
  {
    key:"iron", label:"Iron", min:0.0,
    // Muted slate, understated
    color:"#94a3b8",
    glow:"#e2e8f0",
    gradient:"linear-gradient(135deg,#0f1117 0%,#1e2433 50%,#2d3748 100%)",
    headerGrad:"linear-gradient(135deg,#0f1117,#1e2433,#2d3748,#1e2433)",
    badge:"#94a3b8",
    badgeText:"#0f1117",
    textColor:"#f1f5f9",
    subColor:"#94a3b899",
    border:"#475569",
    borderWidth:1,
    glowSize:0,
    animBorder:false,
    ornament:"·",
    barStyle:"linear-gradient(90deg,#475569,#94a3b8,#475569)",
    cardBg:"linear-gradient(160deg,#1e243310 0%,#2d374808 100%)",
    activeBg:"linear-gradient(160deg,#0f1117ee,#1e2433ee)",
    letterSpacing:0.5,
  },
];

// ── Font & CSS injection ─────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("map-fonts")) {
  const link = document.createElement("link");
  link.id = "map-fonts";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700;800;900&family=Crimson+Pro:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap";
  document.head.appendChild(link);
  // Ensure proper mobile viewport
  if (!document.querySelector('meta[name="viewport"]')) {
    const meta = document.createElement("meta");
    meta.name = "viewport";
    meta.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
    document.head.appendChild(meta);
  }
}
const FONT_DISPLAY = "'Cinzel', 'Palatino Linotype', 'Georgia', serif";
const FONT_BODY    = "'Crimson Pro', 'Georgia', serif";

// Inject CSS for animated border glow (only once)
if (typeof document !== "undefined" && !document.getElementById("tier-anim-css")) {
  const style = document.createElement("style");
  style.id = "tier-anim-css";
  style.textContent = `
    @keyframes borderGlow {
      0%   { box-shadow: 0 0 8px 2px var(--tier-glow), inset 0 0 8px 1px var(--tier-glow-in); }
      50%  { box-shadow: 0 0 22px 6px var(--tier-glow), inset 0 0 14px 2px var(--tier-glow-in); }
      100% { box-shadow: 0 0 8px 2px var(--tier-glow), inset 0 0 8px 1px var(--tier-glow-in); }
    }
    @keyframes borderSweep {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes celestialPulse {
      0%,100% { opacity: 0.7; transform: scale(1); }
      50%      { opacity: 1;   transform: scale(1.015); }
    }
    @keyframes legendaryFlicker {
      0%,100% { opacity: 0.85; }
      40%      { opacity: 1; }
      60%      { opacity: 0.9; }
    }
    @keyframes celestialDrift {
      0%   { background-position: 0% 0%; }
      50%  { background-position: 100% 100%; }
      100% { background-position: 0% 0%; }
    }
    @keyframes emberFloat {
      0%   { background-position: 0% 100%; }
      100% { background-position: 100% 0%; }
    }
    @keyframes epicSwirl {
      0%   { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    @keyframes hpPulse {
      0%,100% { opacity: 1; }
      50%     { opacity: 0.75; }
    }
    @keyframes hpCritFlicker {
      0%,100% { opacity: 1; }
      25%     { opacity: 0.5; }
      50%     { opacity: 0.9; }
      75%     { opacity: 0.6; }
    }
    @keyframes koFade {
      from { opacity: 1; filter: saturate(1); }
      to   { opacity: 0.4; filter: saturate(0.2); }
    }
    @keyframes iconHalo {
      0%,100% { box-shadow: 0 0 4px 1px var(--tier-glow); }
      50%     { box-shadow: 0 0 10px 3px var(--tier-glow); }
    }
    @keyframes fallenText {
      from { opacity: 0; transform: translate(-50%,-50%) scale(1.3); }
      to   { opacity: 1; transform: translate(-50%,-50%) scale(1); }
    }
    @keyframes hpDeltaFlash {
      0%   { opacity: 1; transform: translateY(0); }
      70%  { opacity: 1; }
      100% { opacity: 0.5; transform: translateY(0); }
    }
    @keyframes panelSlideIn {
      from { transform: translateX(100%); opacity: 0.85; }
      to   { transform: translateX(0);    opacity: 1; }
    }
    .panel-close-btn {
      background: none; border: none; cursor: pointer; font-size: 13px;
      color: #9a8e82; width: 28px; height: 28px; border-radius: 6px;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.15s;
    }
    .panel-close-btn:hover { background: #f0ebe4; color: #6b5e52; }
    @keyframes drawerSlideIn {
      from { transform: translateX(-100%); }
      to   { transform: translateX(0); }
    }
    @keyframes drawerSlideOut {
      from { transform: translateX(0); }
      to   { transform: translateX(-100%); }
    }
    @keyframes mobileMenuFadeIn {
      from { opacity: 0; transform: translateY(-8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @media (max-width: 768px) {
      svg text { font-size: 14px !important; }
    }
  `;
  document.head.appendChild(style);
}

// Normalized power: log scale, uncapped
// +100% raw (1.0) → 50 PWR, +1000% raw (10.0) → 100 PWR
// Formula derived: f(raw) = 15.77 * log2(1 + 8 * raw)
function normalizePower(rawPower) {
  if (!rawPower || rawPower <= 0) return 0;
  return Math.round(15.773 * Math.log2(1 + 8 * rawPower));
}

function getPowerTier(rawPower) {
  for (const tier of POWER_TIERS) {
    if (rawPower >= tier.min) return tier;
  }
  return POWER_TIERS[POWER_TIERS.length - 1];
}

// ── Contained power — measures total magical presence inside a city or army ──
// Returns { totalRaw, totalNorm, peakTier, peakColor, entities } where entities
// is the array of { name, rawPower, norm, tier } for each mage/dragon inside.
function calcContainedPower(mages, dragons, containerKey, keyField) {
  const result = { totalRaw:0, totalNorm:0, peakTier:null, peakColor:null, entities:[] };
  const all = [
    ...mages.map(m => ({ ...m, _type:"mage" })),
    ...dragons.map(d => ({ ...d, _type:"dragon" })),
  ].filter(e => e[keyField] === containerKey);
  for (const e of all) {
    const raw = calcDisciplinePowerForType(e.disciplines, e._type, e.artifacts, e.fatigue || 0);
    const norm = normalizePower(raw);
    const tier = getPowerTier(raw);
    result.totalRaw += raw;
    result.entities.push({ name:e.name, rawPower:raw, norm, tier });
    if (!result.peakTier || raw > (result._peakRaw || 0)) {
      result.peakTier = tier;
      result.peakColor = tier.color;
      result._peakRaw = raw;
    }
  }
  result.totalNorm = normalizePower(result.totalRaw);
  // Clean up internal field
  delete result._peakRaw;
  return result;
}

const CITY_STATUSES  = ["normal","under_attack","besieged"];
const STATUS_COLORS  = { normal:"#22c55e", under_attack:"#f97316", besieged:"#ef4444" };
const STATUS_LABELS  = { normal:"Normal", under_attack:"Under Attack", besieged:"Besieged" };
const SKILL_LABELS   = ["","Rabble","Levy","Militia","Trained","Seasoned","Veteran","Elite","Champion","Legendary","Godlike","Mythic","Transcendent"];

// ── Helpers ───────────────────────────────────────────────────────────────────
const snapToGrid = (x, y) => [Math.round(x / GRID_SIZE) * GRID_SIZE, Math.round(y / GRID_SIZE) * GRID_SIZE];
const coordStr   = (x, y) => `${x},${y}`;
const parseCoord = (s)     => { const [x, y] = s.split(",").map(Number); return { x, y }; };

function distToSegment(px, py, [ax, ay], [bx, by]) {
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function pointInPolygon(px, py, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
    if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}

// ── API ───────────────────────────────────────────────────────────────────────
const API_BASE = "https://w5kymwgbpg.execute-api.us-east-1.amazonaws.com/prod/";

async function apiGet() {
  const res = await fetch(`${API_BASE}/data`);
  if (!res.ok) throw new Error(`GET failed: ${res.status}`);
  return res.json(); // { kingdoms, cities, characters, armies }
}

async function apiPut(item) {
  // DynamoDB rejects null values — strip them out before sending
  const clean = Object.fromEntries(
    Object.entries(item).filter(([, v]) => v !== null && v !== undefined && v !== "")
  );
  const res = await fetch(`${API_BASE}/data`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ item: clean }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`PUT failed: ${res.status} — ${detail}`);
  }
  return res.json();
}

async function apiDelete(primary_key, sort_key) {
  const res = await fetch(`${API_BASE}/data`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: primary_key, sort_key }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`DELETE failed: ${res.status} — ${detail}`);
  }
  return res.json();
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
const inputStyle = {
  background:"#f7f5f2", border:"1px solid #e2ddd6", color:"#1a1814",
  padding:"5px 8px", borderRadius:6, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box",
  fontFamily:FONT_BODY,
};
const selectStyle = { ...inputStyle, cursor:"pointer" };
const mkBtn = (bg) => ({
  background:bg, color:"#fff", border:"none", borderRadius:6,
  padding:"6px 14px", cursor:"pointer", fontSize:13, fontWeight:600, flex:1,
  fontFamily:FONT_BODY, letterSpacing:0.3,
});

function Field({ label, children }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
      <label style={{ fontSize:10, fontWeight:700, color:"#8a7e72", letterSpacing:1, textTransform:"uppercase", fontFamily:FONT_DISPLAY }}>{label}</label>
      {children}
    </div>
  );
}
function FInput(props) { return <input {...props} style={inputStyle} />; }

function Panel({ title, onClose, children, accentColor }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  return (
    <div style={{
      position: isMobile ? "fixed" : "absolute",
      top:0, right:0, bottom:0,
      left: isMobile ? 0 : "auto",
      width: isMobile ? "100%" : 300,
      background:"#faf9f7", borderLeft: isMobile ? "none" : "1px solid #e2ddd6",
      boxShadow:"-4px 0 20px #0000000f", display:"flex", flexDirection:"column", zIndex: isMobile ? 100 : 20, overflowY:"auto",
      animation:"panelSlideIn 0.25s ease-out",
    }}>
      {/* Accent stripe */}
      {accentColor && <div style={{ height:3, background:accentColor, flexShrink:0, opacity:0.7 }} />}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", borderBottom:"1px solid #ece7e0", flexShrink:0 }}>
        <span style={{ fontWeight:700, fontSize:14, color:"#1a1814", fontFamily:FONT_DISPLAY, letterSpacing:0.5 }}>{title}</span>
        <button onClick={onClose} className="panel-close-btn">✕</button>
      </div>
      <div style={{ padding:"12px 14px", display:"flex", flexDirection:"column", gap:10 }}>
        {children}
      </div>
    </div>
  );
}

// Computed read-only badge
function Badge({ label, value, color }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#f7f5f2", border:"1px solid #e2ddd6", borderRadius:6, padding:"4px 8px" }}>
      <span style={{ fontSize:12, color:"#8a7e72", fontFamily:FONT_BODY }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:600, color: color || "#1a1814", fontFamily:FONT_BODY }}>{value}</span>
    </div>
  );
}

// Artifacts editor for mages and dragons
const ARTIFACT_SLOTS = [
  { key: "crown",  label: "Crown" },
  { key: "weapon", label: "Main Weapon" },
  { key: "dagger", label: "Dagger" },
  { key: "ring",   label: "Ring" },
  { key: "amulet", label: "Amulet" },
  { key: "staff",   label: "Staff" },
  { key: "chain",   label: "Chain / Pendant" },
];

// ── Artifact Catalog ─────────────────────────────────────────────────────────
// discipline: specific key = targets that discipline, "all" = targets all
const ARTIFACT_CATALOG = [
  // ── CROWNS ───────────────────────────────────────────
  { id:"infernalix", name:"Infernalix", slot:"crown", discipline:"flamecalling", effects:{atkBonus:0.165,defBonus:0.11}, tags:[], desc:"Crown of living flame — incinerates the air around the wearer and turns their fire white-hot" },
  { id:"tempest_crown", name:"Crown of Tempests", slot:"crown", discipline:"stormweaving", effects:{atkBonus:0.18,defBonus:0.12,hpBonus:18}, tags:[], desc:"Crackling diadem that commands all storms within sight — lightning answers to thought alone" },
  { id:"depthcrown", name:"Abyssal Diadem", slot:"crown", discipline:"tidecalling", effects:{atkBonus:0.165,defBonus:0.11}, tags:[], desc:"Crown of deep-trench coral — the ocean itself rises to obey the wearer" },
  { id:"nullcrown", name:"Crown of Unmaking", slot:"crown", discipline:"voidweaving", effects:{atkBonus:0.27,defBonus:0.18,hpBonus:27}, tags:[], desc:"Crown that exists as a wound in reality — erases anything the wearer wills from existence" },
  { id:"solaris_crown", name:"Solaris", slot:"crown", discipline:"lightforging", effects:{atkBonus:0.285,defBonus:0.19,hpBonus:29}, tags:[], desc:"Blinding circlet forged from a dying star — constructs of light become permanent and unbreakable" },
  { id:"dominion", name:"Dominion", slot:"crown", discipline:"soulbinding", effects:{atkBonus:0.24,defBonus:0.16,hpBonus:24}, tags:[], desc:"Iron crown of absolute command — entire armies kneel when the wearer speaks" },
  { id:"crown_of_ages", name:"Crown of Ages", slot:"crown", discipline:"all", effects:{atkBonus:0.09,defBonus:0.06}, tags:[], desc:"Ancient crown worn by the first archmage — amplifies every school of magic equally" },

  // ── WEAPONS ──────────────────────────────────────────
  // Flamecalling
  { id:"caldenvyr", name:"Caldenvyr", slot:"weapon", discipline:"flamecalling", effects:{atkBonus:0.14}, tags:[], desc:"Volcanic greatsword that drinks fire and spits it back threefold" },
  { id:"pyrewrath", name:"Pyrewrath", slot:"weapon", discipline:"flamecalling", effects:{atkBonus:0.06}, tags:[], desc:"Blackened staff crowned with an ever-burning ember" },
  // Stormweaving
  { id:"khorathis", name:"Khorathis", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Spear of living lightning, thrown and recalled by will alone" },
  { id:"galecleaver", name:"Galecleaver", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.06}, tags:[], desc:"Twin-bladed glaive that screams with compressed wind" },
  { id:"stormbreaker", name:"Stormbreaker", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.16,enemyDefPenalty:0.04}, tags:[], desc:"Twin-bladed glaive that screams with compressed wind" },
  // Tidecalling
  { id:"nythirel", name:"Nythirel", slot:"weapon", discipline:"tidecalling", effects:{atkBonus:0.08}, tags:[], desc:"Trident forged from deep-sea ice that never melts" },
  { id:"tidesunder", name:"Tidesunder", slot:"weapon", discipline:"tidecalling", effects:{atkBonus:0.04}, tags:[], desc:"Curved blade that trails saltwater mist" },
  // Voidweaving
  { id:"drevnahkar", name:"Drevnahkar", slot:"weapon", discipline:"voidweaving", effects:{atkBonus:0.28,enemyDefPenalty:0.07}, tags:[], desc:"Sword of absolute darkness — wounds it makes cannot heal by magic" },
  { id:"voidripper", name:"Voidripper", slot:"weapon", discipline:"voidweaving", effects:{atkBonus:0.12}, tags:[], desc:"Serrated blade that cuts through wards and barriers like cloth" },
  // Lightforging
  { id:"Lightbringer", name:"Lightbringer", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.4,enemyDefPenalty:0.1}, tags:[], desc:"Radiant lance that burns hotter the more magic it absorbs" },
  { id:"dawnbreaker", name:"Dawnshatter", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.12}, tags:[], desc:"Crystalline mace that detonates with stored sunlight on impact" },
  { id:"goldryn", name:"Goldryn", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.16,enemyDefPenalty:0.04}, tags:[], desc:"Crystalline sword that detonates with stored sunlight on impact" },
  // Soulbinding
  { id:"valdryss", name:"Valdryss", slot:"weapon", discipline:"soulbinding", effects:{atkBonus:0.12}, tags:[], desc:"Whispering staff — those who hear it lose the will to fight" },
  { id:"willbreaker", name:"Willbreaker", slot:"weapon", discipline:"soulbinding", effects:{atkBonus:0.08}, tags:[], desc:"Iron scepter that paralyzes weaker minds on sight" },
  // All
  { id:"aetherall", name:"Aetherall", slot:"weapon", discipline:"all", effects:{atkBonus:0.06}, tags:[], desc:"Ethereal blade that resonates with all schools of magic" },
  { id:"ruinwalker", name:"Ruinwalker", slot:"weapon", discipline:"all", effects:{atkBonus:0.04}, tags:[], desc:"Ancient glaive recovered from a god-war battlefield" },
  { id:"godsplinter", name:"Godsplinter", slot:"weapon", discipline:"all", effects:{atkBonus:0.08}, tags:[], desc:"Fragment of a divine weapon — warps reality in the hands of any caster" },
   { id:"Aetheryn", name:"Aetheryn", slot:"weapon", discipline:"all", effects:{atkBonus:0.14}, tags:[], desc:"Fragment of a divine weapon — warps reality in the hands of any caster" },

  // ── DAGGERS ──────────────────────────────────────────
  { id:"emberfang", name:"Emberfang", slot:"dagger", discipline:"flamecalling", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Dagger that ignites anything it pierces from the inside" },
  { id:"boltfang", name:"Boltfang", slot:"dagger", discipline:"stormweaving", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Crackling knife that arcs lightning between wounds" },
  { id:"frostpiercer", name:"Frostpiercer", slot:"dagger", discipline:"tidecalling", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Ice-crystal stiletto — shatters inside the target" },
  { id:"nulledge", name:"Nulledge", slot:"dagger", discipline:"voidweaving", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Dagger that severs the target's connection to magic" },
  { id:"gleamfang", name:"Gleamfang", slot:"dagger", discipline:"lightforging", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Blade of condensed light that blinds on the draw" },
  { id:"dreadeater", name:"Souleater", slot:"dagger", discipline:"soulbinding", effects:{atkBonus:0.1,evasion:0.1}, tags:[], desc:"Feeds on the target's fear, growing sharper with terror" },
  { id:"flickerblade", name:"Flickerblade", slot:"dagger", discipline:"all", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Phase-shifting dagger — exists in two planes at once" },
  { id:"venomthorn", name:"Venomthorn", slot:"dagger", discipline:"tidecalling", effects:{atkBonus:0.06,evasion:0.06}, tags:[], desc:"Coral dagger laced with abyssal toxin — wounds weep saltwater" },
  { id:"hexfang", name:"Hexfang", slot:"dagger", discipline:"soulbinding", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Cursed fang that plants suggestions in the stabbed" },
  { id:"sparkpiercer", name:"Sparkpiercer", slot:"dagger", discipline:"stormweaving", effects:{atkBonus:0.07,evasion:0.07}, tags:[], desc:"Needle-thin blade that calls a thunderclap on every strike" },

  // ── RINGS ────────────────────────────────────────────
  { id:"cinderband", name:"Cinderband", slot:"ring", discipline:"flamecalling", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Molten ring that shields the wearer from their own flames" },
  { id:"tempestloop", name:"Tempestloop", slot:"ring", discipline:"stormweaving", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Coiled lightning frozen in silver — quickens casting" },
  { id:"depthsignet", name:"Depthsignet", slot:"ring", discipline:"tidecalling", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Deep-ocean signet that lets the wearer breathe and cast underwater" },
  { id:"wraithstone", name:"Wraithstone", slot:"ring", discipline:"voidweaving", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring of compressed absence — makes the wearer hard to perceive" },
  { id:"paldoras_gem", name:"Paldora's Gem", slot:"ring", discipline:"lightforging", effects:{evasion:0.138,enemyAtkPenalty:0.083}, tags:[], desc:"Legendary gem that amplifies radiant constructs beyond mortal limits" },
  { id:"thrallring", name:"Thrallring", slot:"ring", discipline:"soulbinding", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Iron ring that extends the wearer's domination range" },
  { id:"starstone", name:"Starstone", slot:"ring", discipline:"all", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Fallen star fragment — hums with raw universal power" },
  { id:"convergence", name:"Convergence", slot:"ring", discipline:"all", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Ring forged where six ley lines cross" },
  { id:"ashenveil_band", name:"Ashenveil Band", slot:"ring", discipline:"voidweaving", effects:{evasion:0.088,enemyAtkPenalty:0.052}, tags:[], desc:"Ring that cloaks the wearer's magic in silence — spells arrive without warning" },
  { id:"pyralis", name:"Pyralis", slot:"ring", discipline:"flamecalling", effects:{evasion:0.088,enemyAtkPenalty:0.052}, tags:[], desc:"White-hot band that lets the wearer shape magma with thought alone" },
  { id:"tidewalkers_loop", name:"Tidewalker's Loop", slot:"ring", discipline:"tidecalling", effects:{evasion:0.063,enemyAtkPenalty:0.037}, tags:[], desc:"Ancient mariner's ring — commands currents across vast distances" },

  // ── AMULETS ──────────────────────────────────────────
  { id:"ashpendant", name:"Ashpendant", slot:"amulet", discipline:"flamecalling", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Ember-heart pendant that lets fire reshape rather than destroy" },
  { id:"stormseye", name:"Storm's Eye", slot:"amulet", discipline:"stormweaving", effects:{hpRegen:6,hpBonus:12,defBonus:0.03}, tags:[], desc:"Amulet of perfect calm — the wearer becomes the eye of any storm they call" },
  { id:"abyssal_pearl", name:"Abyssal Pearl", slot:"amulet", discipline:"tidecalling", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Pearl from the deepest trench — magnifies water magic tenfold at depth" },
  { id:"voidheart", name:"Voidheart", slot:"amulet", discipline:"voidweaving", effects:{hpRegen:5,hpBonus:10}, tags:[], desc:"Crystallized fragment of true nothingness — unmakes reality around it" },
  { id:"solcrux", name:"Solcrux", slot:"amulet", discipline:"all", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Sun-forged crucible worn at the throat — channels blinding radiance" },
  { id:"dracaldyr", name:"Dracaldyr", slot:"amulet", discipline:"soulbinding", effects:{hpRegen:5,hpBonus:10}, tags:[], desc:"Dragon-bone amulet that amplifies willpower to a crushing weight" },
  { id:"primordial_eye", name:"Primordial Eye", slot:"amulet", discipline:"all", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Ancient artifact of unknown origin — enhances all magical ability" },
  { id:"echoveil", name:"Echoveil", slot:"amulet", discipline:"all", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Shimmering veil-pendant that echoes every spell cast through it" },
  { id:"krythane", name:"Krythane", slot:"amulet", discipline:"stormweaving", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Stormglass pendant — clouds gather when the wearer raises their voice" },
  { id:"oblivions_eye", name:"Oblivion's Eye", slot:"amulet", discipline:"voidweaving", effects:{hpRegen:12,hpBonus:24,defBonus:0.06}, tags:[], desc:"Obsidian eye of a dead god — erases whatever it gazes upon from existence" },
  { id:"soulforge_heart", name:"Soulforge Heart", slot:"amulet", discipline:"soulbinding", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Pulsing iron heart that syncs the wearer's will with nearby minds" },
  { id:"maelstrom_seed", name:"Maelstrom Seed", slot:"amulet", discipline:"tidecalling", effects:{hpRegen:11,hpBonus:22,defBonus:0.055}, tags:[], desc:"Seed of a primordial ocean storm — unleashes catastrophic tidal force" },

  // ── NEW WEAPONS ────────────────────────────────────────
  { id:"scorchbrand", name:"Scorchbrand", slot:"weapon", discipline:"flamecalling", effects:{atkBonus:0.08}, tags:[], desc:"Longsword that superheats the air in its wake — swings leave trails of fire" },
  { id:"galvanyss", name:"Galvanyss", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.08}, tags:[], desc:"Javelin of galvanized steel — calls down a bolt from clear sky on impact" },
  { id:"riptide", name:"Riptide", slot:"weapon", discipline:"tidecalling", effects:{atkBonus:0.06}, tags:[], desc:"Curved cutlass that drags enemies into phantom undertow" },
  { id:"hollowfang", name:"Hollowfang", slot:"weapon", discipline:"voidweaving", effects:{atkBonus:0.08}, tags:[], desc:"Rapier with no shadow — its strikes leave wounds that forget how to close" },
  { id:"dawnpiercer", name:"Dawnpiercer", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.08}, tags:[], desc:"Spear of crystallized dawn — explodes with radiance when thrown" },
  { id:"thrallbane", name:"Thrallbane", slot:"weapon", discipline:"soulbinding", effects:{atkBonus:0.08}, tags:[], desc:"Flail whose chains rattle inside the enemy's mind, not their ears" },

  // ── NEW DAGGERS ────────────────────────────────────────
  { id:"kindlefang", name:"Kindlefang", slot:"dagger", discipline:"flamecalling", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Tiny blade that plants a slow-burning ember in whatever it cuts" },
  { id:"staticshiv", name:"Staticshiv", slot:"dagger", discipline:"stormweaving", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Dagger wreathed in static — numbs nerves on contact" },
  { id:"sleetfang", name:"Sleetfang", slot:"dagger", discipline:"tidecalling", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Ice dagger that reforms itself after shattering inside a wound" },
  { id:"umbrafang", name:"Umbrafang", slot:"dagger", discipline:"voidweaving", effects:{atkBonus:0.04,evasion:0.04}, tags:[], desc:"Blade forged from the shadow of a dying star — cuts through warded flesh" },
  { id:"prismfang", name:"Prismfang", slot:"dagger", discipline:"lightforging", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Glass dagger that splits light into searing beams on impact" },
  { id:"mindthorn", name:"Mindthorn", slot:"dagger", discipline:"soulbinding", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Thorn-shaped blade — victims hear whispers for days after being cut" },

  // ── NEW RINGS ──────────────────────────────────────────
  { id:"magmacore", name:"Magmacore", slot:"ring", discipline:"flamecalling", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring with a molten core — the wearer's flames burn through magical shields" },
  { id:"cyclone_band", name:"Cyclone Band", slot:"ring", discipline:"stormweaving", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Wind-carved ring that lets the wearer redirect any incoming projectile" },
  { id:"glacial_seal", name:"Glacial Seal", slot:"ring", discipline:"tidecalling", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Frozen signet that flash-freezes anything the wearer touches" },
  { id:"eclipse_band", name:"Eclipse Band", slot:"ring", discipline:"voidweaving", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Ring of perpetual eclipse — nearby light sources dim and flicker" },
  { id:"haloband", name:"Haloband", slot:"ring", discipline:"lightforging", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring that projects a faint protective halo — absorbs minor attacks" },
  { id:"puppet_loop", name:"Puppet Loop", slot:"ring", discipline:"soulbinding", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Twisted iron loop — lets the wearer puppet a single weak-willed target" },

  // ── NEW AMULETS ────────────────────────────────────────
  { id:"cinder_heart", name:"Cinder Heart", slot:"amulet", discipline:"flamecalling", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Smoldering pendant that converts the wearer's rage into hotter flame" },
  { id:"thunderveil", name:"Thunderveil", slot:"amulet", discipline:"stormweaving", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Pendant that wraps the wearer in rolling thunder — disorients nearby enemies" },
  { id:"tidecaller_pearl", name:"Tidecaller's Pearl", slot:"amulet", discipline:"tidecalling", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Pearl that hums near water — amplifies casting when rain falls" },
  { id:"nihil_pendant", name:"Nihil Pendant", slot:"amulet", discipline:"voidweaving", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Pendant of compressed nothing — negates the first spell that hits the wearer" },
  { id:"radiant_focus", name:"Radiant Focus", slot:"amulet", discipline:"lightforging", effects:{hpRegen:5,hpBonus:10}, tags:[], desc:"Lens-shaped amulet that focuses scattered light into a devastating beam" },
  { id:"dominus_chain", name:"Dominus Chain", slot:"amulet", discipline:"soulbinding", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Chain of linked iron wills — extends domination to groups instead of individuals" },

  // ── STAVES ───────────────────────────────────────────
  // Flamecalling
  { id:"infernal_crook", name:"Infernal Crook", slot:"staff", discipline:"flamecalling", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Crooked iron staff that vents gouts of flame from cracks along its length" },
  { id:"ashen_focus", name:"Ashen Focus", slot:"staff", discipline:"flamecalling", effects:{atkBonus:0.045,hpRegen:2}, tags:[], desc:"Charred oak rod — channels fire without burning the caster's hands" },
  { id:"pyre_conduit", name:"Pyre Conduit", slot:"staff", discipline:"flamecalling", effects:{atkBonus:0.15,hpRegen:8}, tags:[], desc:"Staff forged in a volcanic vent — turns any spark into an inferno" },
  // Stormweaving
  { id:"tempest_rod", name:"Tempest Rod", slot:"staff", discipline:"stormweaving", effects:{atkBonus:0.075,hpRegen:4}, tags:[], desc:"Copper-wound staff that hums with stored static — discharges on command" },
  { id:"zephyr_cane", name:"Zephyr Cane", slot:"staff", discipline:"stormweaving", effects:{atkBonus:0.03,hpRegen:2}, tags:[], desc:"Lightweight cane carved from windwood — amplifies gale-force casting" },
  { id:"thunderspine", name:"Thunderspine", slot:"staff", discipline:"stormweaving", effects:{atkBonus:0.135,hpRegen:7}, tags:[], desc:"Staff carved from a tree struck by lightning a thousand times — every spell thunders" },
  // Tidecalling
  { id:"deepcurrent", name:"Deepcurrent", slot:"staff", discipline:"tidecalling", effects:{atkBonus:0.06,hpRegen:3}, tags:[], desc:"Driftwood staff from the abyssal floor — water obeys it like a conductor's baton" },
  { id:"mistweaver", name:"Mistweaver", slot:"staff", discipline:"tidecalling", effects:{atkBonus:0.03,hpRegen:2}, tags:[], desc:"Staff that exhales perpetual fog — obscures and chills everything nearby" },
  { id:"leviathan_spine", name:"Leviathan Spine", slot:"staff", discipline:"tidecalling", effects:{atkBonus:0.165,hpRegen:8}, tags:[], desc:"Spine of an ancient sea beast — commands tidal waves and frozen storms alike" },
  // Voidweaving
  { id:"nihilstaff", name:"Nihilstaff", slot:"staff", discipline:"voidweaving", effects:{atkBonus:0.12,hpRegen:6}, tags:[], desc:"Staff that consumes light around it — spells cast through it arrive as pure negation" },
  { id:"duskrod", name:"Duskrod", slot:"staff", discipline:"voidweaving", effects:{atkBonus:0.045,hpRegen:2}, tags:[], desc:"Rod carved from petrified shadow — muffles all sound near the caster" },
  { id:"entropy_spire", name:"Entropy Spire", slot:"staff", discipline:"voidweaving", effects:{atkBonus:0.15,hpRegen:8}, tags:[], desc:"Staff that accelerates decay — anything it points at begins to unmake itself" },
  // Lightforging
  { id:"sunspire", name:"Sunspire", slot:"staff", discipline:"lightforging", effects:{atkBonus:0.105,hpRegen:5}, tags:[], desc:"Crystal spire that drinks sunlight and releases it as searing beams" },
  { id:"glowstaff", name:"Glowstaff", slot:"staff", discipline:"lightforging", effects:{atkBonus:0.045,hpRegen:2}, tags:[], desc:"Pale staff that steadily brightens — constructs cast through it last longer" },
  { id:"divine_focus", name:"Divine Focus", slot:"staff", discipline:"lightforging", effects:{atkBonus:0.165,hpRegen:8}, tags:[], desc:"Staff of pure condensed radiance — said to be a fragment of the sun itself" },
  // Soulbinding
  { id:"tyrants_rod", name:"Tyrant's Rod", slot:"staff", discipline:"soulbinding", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Black iron rod of ancient tyrant-kings — amplifies domination at range" },
  { id:"murmur_staff", name:"Murmur Staff", slot:"staff", discipline:"soulbinding", effects:{atkBonus:0.03,hpRegen:2}, tags:[], desc:"Staff that whispers to anyone nearby — plants doubt and obedience" },
  { id:"overlord_spire", name:"Overlord Spire", slot:"staff", discipline:"soulbinding", effects:{atkBonus:0.135,hpRegen:7}, tags:[], desc:"Obsidian spire that broadcasts the wielder's will across an entire battlefield" },
  // All
  { id:"arcanum_spire", name:"Arcanum Spire", slot:"staff", discipline:"all", effects:{atkBonus:0.06,hpRegen:3}, tags:[], desc:"Six-faceted crystal staff — resonates with all schools simultaneously" },
  { id:"wanderers_crook", name:"Wanderer's Crook", slot:"staff", discipline:"all", effects:{atkBonus:0.03,hpRegen:2}, tags:[], desc:"Ancient traveler's staff — boosts whatever magic the wielder needs most" },
  { id:"worldstaff", name:"Worldstaff", slot:"staff", discipline:"all", effects:{atkBonus:0.12,hpRegen:6}, tags:[], desc:"Staff grown from the roots of the World Tree — amplifies all magic equally" },

  // ── CHAINS / PENDANTS ────────────────────────────────
  // Flamecalling
  { id:"ember_chain", name:"Ember Chain", slot:"chain", discipline:"flamecalling", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Smoldering chain links that heat up as the wearer casts — fire feeds fire" },
  { id:"kindlethread", name:"Kindlethread", slot:"chain", discipline:"flamecalling", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Thin gold thread necklace with a flickering bead — steadies flame control" },
  { id:"pyroheart_links", name:"Pyroheart Links", slot:"chain", discipline:"flamecalling", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain of dragon-scale links — each one holds a captive ember waiting to ignite" },
  // Stormweaving
  { id:"stormlink", name:"Stormlink", slot:"chain", discipline:"stormweaving", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Chain of alternating copper and silver — acts as a lightning rod for spellwork" },
  { id:"staticweave", name:"Staticweave", slot:"chain", discipline:"stormweaving", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Braided wire pendant that crackles faintly — sharpens storm reflexes" },
  { id:"galvanic_beads", name:"Galvanic Beads", slot:"chain", discipline:"stormweaving", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Beads of charged lodestone — each one a tiny capacitor for lightning magic" },
  // Tidecalling
  { id:"tidalchain", name:"Tidal Chain", slot:"chain", discipline:"tidecalling", effects:{defBonus:0.06,hpBonus:8}, tags:[], desc:"Chain of ocean-smoothed links that drip phantom seawater" },
  { id:"frostthread", name:"Frostthread", slot:"chain", discipline:"tidecalling", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Thin ice-crystal chain that never melts — steadies water magic at range" },
  { id:"deep_current_beads", name:"Deep Current Beads", slot:"chain", discipline:"tidecalling", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Beads carved from deep-sea pressure stone — amplify tidal magic exponentially with depth" },
  // Voidweaving
  { id:"voidlink", name:"Voidlink", slot:"chain", discipline:"voidweaving", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Chain whose links are gaps in reality — each one a tiny pocket of nothing" },
  { id:"shadowthread", name:"Shadowthread", slot:"chain", discipline:"voidweaving", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Thread of woven darkness — makes the wearer's void magic harder to detect" },
  { id:"null_beads", name:"Null Beads", slot:"chain", discipline:"voidweaving", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Prayer beads of compressed absence — each one silences a different sense" },
  // Lightforging
  { id:"radiant_chain", name:"Radiant Chain", slot:"chain", discipline:"lightforging", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Golden chain that glows brighter with each spell — stores excess radiance" },
  { id:"gleamthread", name:"Gleamthread", slot:"chain", discipline:"lightforging", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Delicate luminous thread — steadies light constructs against disruption" },
  { id:"prismatic_links", name:"Prismatic Links", slot:"chain", discipline:"lightforging", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain that splits white light into six colors — each link amplifies a different wavelength" },
  // Soulbinding
  { id:"binding_chain", name:"Binding Chain", slot:"chain", discipline:"soulbinding", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Heavy iron chain worn as a collar — the wearer's will becomes inescapable" },
  { id:"whisperthread", name:"Whisperthread", slot:"chain", discipline:"soulbinding", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Nearly invisible thread pendant — plants subconscious suggestions in listeners" },
  { id:"thrall_beads", name:"Thrall Beads", slot:"chain", discipline:"soulbinding", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Beads carved from bone — each one represents a mind the wearer can touch" },
  // All
  { id:"leyline_chain", name:"Leyline Chain", slot:"chain", discipline:"all", effects:{defBonus:0.06,hpBonus:8}, tags:[], desc:"Chain aligned to natural ley lines — passively enhances all magic nearby" },
  { id:"resonance_beads", name:"Resonance Beads", slot:"chain", discipline:"all", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Prayer beads carved from six different magical stones — one for each discipline" },
  { id:"harmonic_links", name:"Harmonic Links", slot:"chain", discipline:"all", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Chain that vibrates at the resonant frequency of raw magic — boosts all casting" },

  // ── NEW WEAPONS ────────────────────────────────────────
  { id:"blazekeeper", name:"Blazekeeper", slot:"weapon", discipline:"flamecalling", effects:{atkBonus:0.1}, tags:[], desc:"Greatsword with a furnace core — heats to white-hot in the wielder's grip" },
  { id:"inferno_edge", name:"Inferno Edge", slot:"weapon", discipline:"flamecalling", effects:{atkBonus:0.04}, tags:[], desc:"Short sword that leaves cauterized wounds — flames linger where it cuts" },
  { id:"stormfury", name:"Stormfury", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.08}, tags:[], desc:"Warhammer that cracks the air on every swing — thunder follows impact" },
  { id:"arc_lance", name:"Arc Lance", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.04}, tags:[], desc:"Long lance that arcs electricity between wielder and target" },
  { id:"maelstrom_fang", name:"Maelstrom Fang", slot:"weapon", discipline:"tidecalling", effects:{atkBonus:0.08}, tags:[], desc:"Harpoon carved from kraken tooth — calls whirlpools wherever it strikes" },
  { id:"brine_edge", name:"Brine Edge", slot:"weapon", discipline:"tidecalling", effects:{atkBonus:0.06}, tags:[], desc:"Salt-crusted blade that corrodes armor and wards alike" },
  { id:"abyssal_edge", name:"Abyssal Edge", slot:"weapon", discipline:"voidweaving", effects:{atkBonus:0.06}, tags:[], desc:"Sword with a blade of compressed darkness — hums with hungry silence" },
  { id:"entropic_glaive", name:"Entropic Glaive", slot:"weapon", discipline:"voidweaving", effects:{atkBonus:0.04}, tags:[], desc:"Polearm that accelerates decay in whatever it touches" },
  { id:"novastaff", name:"Novastaff", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.08}, tags:[], desc:"Staff-blade hybrid that channels sunbursts through its cutting edge" },
  { id:"prism_blade", name:"Prism Blade", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.04}, tags:[], desc:"Translucent sword that refracts incoming spells into scattered light" },
  { id:"dominion_blade", name:"Dominion Blade", slot:"weapon", discipline:"soulbinding", effects:{atkBonus:0.06}, tags:[], desc:"Sword that cuts will, not flesh — victims become docile after a single wound" },
  { id:"subjugator", name:"Subjugator", slot:"weapon", discipline:"soulbinding", effects:{atkBonus:0.08}, tags:[], desc:"Mace wreathed in psychic pressure — nearby enemies fight sluggishly" },

  // ── NEW DAGGERS ────────────────────────────────────────
  { id:"flashburn", name:"Flashburn", slot:"dagger", discipline:"flamecalling", effects:{atkBonus:0.04,evasion:0.04}, tags:[], desc:"Dagger that detonates a flash of fire on the first stab — one devastating strike" },
  { id:"smolder_fang", name:"Smolder Fang", slot:"dagger", discipline:"flamecalling", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Small blade that leaves smoldering wounds — damage builds over time" },
  { id:"voltpick", name:"Voltpick", slot:"dagger", discipline:"stormweaving", effects:{atkBonus:0.04,evasion:0.04}, tags:[], desc:"Needle-thin dagger that delivers a paralyzing jolt on contact" },
  { id:"galeknife", name:"Galeknife", slot:"dagger", discipline:"stormweaving", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Blade so light it whistles — throws gusts of cutting wind with each slash" },
  { id:"rime_stiletto", name:"Rime Stiletto", slot:"dagger", discipline:"tidecalling", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Stiletto of permanent frost — freezes blood on contact" },
  { id:"coral_fang", name:"Coral Fang", slot:"dagger", discipline:"tidecalling", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Dagger grown from living coral — injects saltwater into wounds" },
  { id:"erasure", name:"Erasure", slot:"dagger", discipline:"voidweaving", effects:{atkBonus:0.04,evasion:0.04}, tags:[], desc:"Dagger that removes what it cuts from memory — victims forget the wound exists" },
  { id:"dim_edge", name:"Dim Edge", slot:"dagger", discipline:"voidweaving", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Blade that drinks nearby light — attacks arrive from perceived darkness" },
  { id:"solarshard", name:"Solarshard", slot:"dagger", discipline:"lightforging", effects:{atkBonus:0.04,evasion:0.04}, tags:[], desc:"Crystallized sunlight shaped into a blade — blinds and burns simultaneously" },
  { id:"glintknife", name:"Glintknife", slot:"dagger", discipline:"lightforging", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Tiny reflective blade — redirects light constructs with surgical precision" },
  { id:"coercion_fang", name:"Coercion Fang", slot:"dagger", discipline:"soulbinding", effects:{atkBonus:0.04,evasion:0.04}, tags:[], desc:"Blade laced with psychic venom — victims obey the next command they hear" },
  { id:"docility_spike", name:"Docility Spike", slot:"dagger", discipline:"soulbinding", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Iron spike that dampens aggression — stabbed targets lose the will to resist" },

  // ── NEW RINGS ──────────────────────────────────────────
  { id:"inferno_loop", name:"Inferno Loop", slot:"ring", discipline:"flamecalling", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Ring of perpetual combustion — the wearer's fire ignores magical resistance" },
  { id:"spark_band", name:"Spark Band", slot:"ring", discipline:"flamecalling", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Simple copper band that warms to the touch — steadies volatile flamecalling" },
  { id:"arc_ring", name:"Arc Ring", slot:"ring", discipline:"stormweaving", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Ring that arcs tiny bolts between fingers — speeds up lightning incantations" },
  { id:"breeze_loop", name:"Breeze Loop", slot:"ring", discipline:"stormweaving", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Silver ring that hums in wind — attunes the wearer to air pressure shifts" },
  { id:"undertow_band", name:"Undertow Band", slot:"ring", discipline:"tidecalling", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Ring that pulls moisture from the air — never far from usable water" },
  { id:"dewdrop_ring", name:"Dewdrop Ring", slot:"ring", discipline:"tidecalling", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Ring with a single perpetual dewdrop — focuses small-scale water shaping" },
  { id:"absence_ring", name:"Absence Ring", slot:"ring", discipline:"voidweaving", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring that casts no shadow — the wearer's void spells cost less effort" },
  { id:"dim_band", name:"Dim Band", slot:"ring", discipline:"voidweaving", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Dull iron band that absorbs ambient light — fuels minor void effects" },
  { id:"brilliance_ring", name:"Brilliance Ring", slot:"ring", discipline:"lightforging", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Diamond ring that stores sunlight — releases it as a protective flash" },
  { id:"glimmer_band", name:"Glimmer Band", slot:"ring", discipline:"lightforging", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Faintly glowing ring — extends the duration of light constructs" },
  { id:"authority_ring", name:"Authority Ring", slot:"ring", discipline:"soulbinding", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Iron signet ring of command — eye contact through it compels obedience" },
  { id:"calm_band", name:"Calm Band", slot:"ring", discipline:"soulbinding", effects:{evasion:0.025,defBonus:0.03}, tags:[], desc:"Smooth stone ring — radiates a calming aura that softens resistance" },

  // ── NEW AMULETS ────────────────────────────────────────
  { id:"conflagration_eye", name:"Conflagration Eye", slot:"amulet", discipline:"flamecalling", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Ruby eye pendant — lets the wearer see through their own flames" },
  { id:"warmth_pendant", name:"Warmth Pendant", slot:"amulet", discipline:"flamecalling", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Small ember pendant — keeps fire spells stable in rain and wind" },
  { id:"tempest_heart", name:"Tempest Heart", slot:"amulet", discipline:"stormweaving", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Pendant containing a trapped micro-storm — caster draws power from it mid-fight" },
  { id:"breeze_charm", name:"Breeze Charm", slot:"amulet", discipline:"stormweaving", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Feather pendant that hums in high wind — helps time storm spells precisely" },
  { id:"drowned_eye", name:"Drowned Eye", slot:"amulet", discipline:"tidecalling", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Eye-shaped pendant filled with seawater — the wearer senses all nearby water" },
  { id:"rime_pendant", name:"Rime Pendant", slot:"amulet", discipline:"tidecalling", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Frost-rimed pendant — keeps ice constructs from melting in warm climates" },
  { id:"abyss_locket", name:"Abyss Locket", slot:"amulet", discipline:"voidweaving", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Locket that contains a sliver of true void — opens to release silence and darkness" },
  { id:"shade_pendant", name:"Shade Pendant", slot:"amulet", discipline:"voidweaving", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Pendant carved from darkite — the wearer blends into shadows effortlessly" },
  { id:"solar_locket", name:"Solar Locket", slot:"amulet", discipline:"lightforging", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Locket that stores hours of sunlight — can release it all in a single blinding pulse" },
  { id:"dawn_charm", name:"Dawn Charm", slot:"amulet", discipline:"lightforging", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Small sun-shaped charm — light constructs require less concentration" },
  { id:"will_eater", name:"Will Eater", slot:"amulet", discipline:"soulbinding", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Fanged pendant that feeds on broken wills — grows stronger with each mind subjugated" },
  { id:"serenity_charm", name:"Serenity Charm", slot:"amulet", discipline:"soulbinding", effects:{hpRegen:2,hpBonus:4}, tags:[], desc:"Smooth jade pendant — radiates false peace, making targets lower their guard" },

  // ── LIGHTFORGING 100-120% ──────────────────────────────
  { id:"radiant_cleaver", name:"Radiant Cleaver", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.12}, tags:[], desc:"Greataxe of solidified light — cleaves through magical barriers like paper" },
  { id:"lumin_shard", name:"Lumin Shard", slot:"dagger", discipline:"lightforging", effects:{atkBonus:0.05,evasion:0.05}, tags:[], desc:"Dagger carved from a dying star's core — stabs leave permanent light wounds" },
  { id:"corona_ring", name:"Corona Ring", slot:"ring", discipline:"lightforging", effects:{evasion:0.075,enemyAtkPenalty:0.045}, tags:[], desc:"Ring surrounded by a miniature corona — the wearer's constructs burn on contact" },
  { id:"zenith_pendant", name:"Zenith Pendant", slot:"amulet", discipline:"lightforging", effects:{hpRegen:5,hpBonus:10}, tags:[], desc:"Pendant that channels noon sunlight regardless of time — always at peak radiance" },
  { id:"beacon_spire", name:"Beacon Spire", slot:"staff", discipline:"lightforging", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Staff that acts as a lighthouse for magic — draws radiant energy from miles away" },
  { id:"luminary_links", name:"Luminary Links", slot:"chain", discipline:"lightforging", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain of tiny suns — each link stores a day's worth of concentrated light" },

  // ── LIGHTFORGING 160-180% ──────────────────────────────
  { id:"godray", name:"Godray", slot:"weapon", discipline:"lightforging", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Blade of divine radiance — anything it cuts turns to ash and light" },
  { id:"starfall_fang", name:"Starfall Fang", slot:"dagger", discipline:"lightforging", effects:{atkBonus:0.09,evasion:0.09}, tags:[], desc:"Dagger forged from a meteorite's heart — stabs release miniature supernovae" },
  { id:"eternal_halo", name:"Eternal Halo", slot:"ring", discipline:"lightforging", effects:{evasion:0.113,enemyAtkPenalty:0.068}, tags:[], desc:"Ring that crowns the wearer's hand in a permanent halo — constructs become unbreakable" },
  { id:"suns_heart", name:"Sun's Heart", slot:"amulet", discipline:"lightforging", effects:{hpRegen:9,hpBonus:18,defBonus:0.045}, tags:[], desc:"Pendant containing a fragment of the actual sun — the wearer becomes a walking furnace of light" },
  { id:"seraph_spire", name:"Seraph Spire", slot:"staff", discipline:"lightforging", effects:{atkBonus:0.135,hpRegen:7}, tags:[], desc:"Staff of angelic crystal — spells cast through it arrive with blinding detonations" },
  { id:"dawnbreaker_chain", name:"Dawnbreaker Chain", slot:"chain", discipline:"lightforging", effects:{defBonus:0.18,hpBonus:23,hpRegen:5}, tags:[], desc:"Chain that burns with the first light of creation — voidweaving crumbles in its presence" },

  // ── VOIDWEAVING 100-120% ───────────────────────────────
  { id:"oblivion_edge", name:"Oblivion Edge", slot:"weapon", discipline:"voidweaving", effects:{atkBonus:0.1}, tags:[], desc:"Sword whose blade is a visible tear in reality — cuts leave wounds in space itself" },
  { id:"deletha", name:"Deletha", slot:"dagger", discipline:"voidweaving", effects:{atkBonus:0.06,evasion:0.06}, tags:[], desc:"Dagger that erases whatever it touches from the present moment — wounds arrive late" },
  { id:"event_horizon", name:"Event Horizon", slot:"ring", discipline:"voidweaving", effects:{evasion:0.075,enemyAtkPenalty:0.045}, tags:[], desc:"Ring that bends nearby space — projectiles curve away from the wearer" },
  { id:"hollow_eye", name:"Hollow Eye", slot:"amulet", discipline:"voidweaving", effects:{hpRegen:5,hpBonus:10}, tags:[], desc:"Empty eye socket pendant — the wearer can see through magical darkness and silence" },
  { id:"negation_rod", name:"Negation Rod", slot:"staff", discipline:"voidweaving", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Staff of antimatter crystal — touching it to a spell unravels the magic instantly" },
  { id:"unlink", name:"Unlink", slot:"chain", discipline:"voidweaving", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain with missing links that still holds — each gap devours a different type of energy" },

  // ── VOIDWEAVING 160-180% ───────────────────────────────
  { id:"worldender", name:"Worldender", slot:"weapon", discipline:"voidweaving", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Greatsword that severs the bonds between atoms — cuts cannot be healed by any means" },
  { id:"final_whisper", name:"Final Whisper", slot:"dagger", discipline:"voidweaving", effects:{atkBonus:0.09,evasion:0.09}, tags:[], desc:"Dagger that kills silently and completely — the victim simply ceases, no wound, no sound" },
  { id:"singularity_band", name:"Singularity Band", slot:"ring", discipline:"voidweaving", effects:{evasion:0.113,enemyAtkPenalty:0.068}, tags:[], desc:"Ring that collapses space around the wearer — nearby magic is crushed into nothing" },
  { id:"dead_gods_eye", name:"Dead God's Eye", slot:"amulet", discipline:"voidweaving", effects:{hpRegen:9,hpBonus:18,defBonus:0.045}, tags:[], desc:"Second eye of the god whose first became Oblivion's Eye — together they could end a world" },
  { id:"annihilation_staff", name:"Annihilation Staff", slot:"staff", discipline:"voidweaving", effects:{atkBonus:0.15,hpRegen:8}, tags:[], desc:"Staff that hums at the frequency of unmaking — sustained contact dissolves reality" },
  { id:"severance_chain", name:"Severance Chain", slot:"chain", discipline:"voidweaving", effects:{defBonus:0.18,hpBonus:23,hpRegen:5}, tags:[], desc:"Chain that cuts whatever it's draped over from existence — worn as a noose by the fearless" },

  // ── STORMWEAVING 100-120% ──────────────────────────────
  { id:"thundercleaver", name:"Thundercleaver", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.12}, tags:[], desc:"Axe that summons a thunderclap with every swing — the shockwave hits harder than the blade" },
  { id:"bolt_needle", name:"Bolt Needle", slot:"dagger", discipline:"stormweaving", effects:{atkBonus:0.05,evasion:0.05}, tags:[], desc:"Hair-thin blade of magnetized steel — stabs call down a targeted lightning bolt" },
  { id:"fulgurite_ring", name:"Fulgurite Ring", slot:"ring", discipline:"stormweaving", effects:{evasion:0.075,enemyAtkPenalty:0.045}, tags:[], desc:"Ring fused from sand struck by lightning — the wearer's reflexes match the speed of their bolts" },
  { id:"cyclone_heart", name:"Cyclone Heart", slot:"amulet", discipline:"stormweaving", effects:{hpRegen:5,hpBonus:10}, tags:[], desc:"Pendant with a trapped cyclone — the wearer can redirect wind at will within a mile" },
  { id:"stormcaller_rod", name:"Stormcaller Rod", slot:"staff", discipline:"stormweaving", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Staff topped with a caged ball of lightning — plants thunderstorms wherever it strikes the ground" },
  { id:"conduit_chain", name:"Conduit Chain", slot:"chain", discipline:"stormweaving", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain that conducts ambient electricity — the wearer charges passively during storms" },

  // ── STORMWEAVING 160-180% ──────────────────────────────
  { id:"heaven_splitter", name:"Heaven Splitter", slot:"weapon", discipline:"stormweaving", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Sword that splits the sky when drawn — permanent storm follows the wielder" },
  { id:"lightning_fang", name:"Lightning Fang", slot:"dagger", discipline:"stormweaving", effects:{atkBonus:0.09,evasion:0.09}, tags:[], desc:"Dagger that IS a frozen lightning bolt — throws become actual strikes from the sky" },
  { id:"maelstrom_ring", name:"Maelstrom Ring", slot:"ring", discipline:"stormweaving", effects:{evasion:0.113,enemyAtkPenalty:0.068}, tags:[], desc:"Ring containing a compressed hurricane — the wearer walks inside the eye of permanent destruction" },
  { id:"wrath_of_skies", name:"Wrath of Skies", slot:"amulet", discipline:"stormweaving", effects:{hpRegen:9,hpBonus:18,defBonus:0.045}, tags:[], desc:"Pendant forged from skystone — calls down consecutive lightning strikes without pause" },
  { id:"stormfather_staff", name:"Stormfather Staff", slot:"staff", discipline:"stormweaving", effects:{atkBonus:0.135,hpRegen:7}, tags:[], desc:"Staff of the original storm god — weather itself answers to the wielder's mood" },
  { id:"tempest_shackle", name:"Tempest Shackle", slot:"chain", discipline:"stormweaving", effects:{defBonus:0.18,hpBonus:23,hpRegen:5}, tags:[], desc:"Chain of living lightning links — wraps the wearer in an electrical field that destroys on touch" },

  // ── NEW CROWNS ─────────────────────────────────────────
  { id:"radiant_aureole", name:"Radiant Aureole", slot:"crown", discipline:"lightforging", effects:{atkBonus:0.24,defBonus:0.16,hpBonus:24}, tags:[], desc:"Crown of perpetual dawn — the wearer's light constructs gain physical weight and hit like siege weapons" },
  { id:"crown_of_sol", name:"Crown of Sol", slot:"crown", discipline:"lightforging", effects:{atkBonus:0.18,defBonus:0.12,hpBonus:18}, tags:[], desc:"Ancient solar crown — sunlight bends toward the wearer, leaving enemies in shadow" },
  { id:"crown_of_nothing", name:"Crown of Nothing", slot:"crown", discipline:"voidweaving", effects:{atkBonus:0.225,defBonus:0.15,hpBonus:23}, tags:[], desc:"Crown visible only as an absence — the wearer's negation magic becomes area-wide" },
  { id:"hollow_diadem", name:"Hollow Diadem", slot:"crown", discipline:"voidweaving", effects:{atkBonus:0.165,defBonus:0.11}, tags:[], desc:"Diadem that eats sound and light around the wearer — spells arrive without warning" },
  { id:"pyralis_crown", name:"Pyralis Crown", slot:"crown", discipline:"flamecalling", effects:{atkBonus:0.195,defBonus:0.13,hpBonus:20}, tags:[], desc:"Crown of white-hot iron that never cools — the wearer's flames ignore all resistance" },
  { id:"tsunamic_crown", name:"Tsunamic Crown", slot:"crown", discipline:"tidecalling", effects:{atkBonus:0.195,defBonus:0.13,hpBonus:20}, tags:[], desc:"Coral crown from the deepest trench — the wearer commands water within a mile radius" },
  { id:"tyrant_diadem", name:"Tyrant's Diadem", slot:"crown", discipline:"soulbinding", effects:{atkBonus:0.21,defBonus:0.14,hpBonus:21}, tags:[], desc:"Iron crown of the last god-emperor — entire cities bend to the wearer's spoken word" },
  { id:"hurricane_crown", name:"Hurricane Crown", slot:"crown", discipline:"stormweaving", effects:{atkBonus:0.195,defBonus:0.13,hpBonus:20}, tags:[], desc:"Crown forged from crystallized lightning — storms form overhead the moment it's worn" },

  { id:"pyreclutch", name:"Pyreclutch", slot:"dagger", discipline:"flamecalling", effects:{atkBonus:0.06,evasion:0.06}, tags:[], desc:"Dagger with a grip of living coal — the blade ignites hotter the tighter you squeeze" },
  { id:"inferno_conduit", name:"Inferno Conduit", slot:"staff", discipline:"flamecalling", effects:{atkBonus:0.105,hpRegen:5}, tags:[], desc:"Hollow iron staff that roars like a furnace when channeling — fire exits both ends" },
  { id:"blazelink", name:"Blazelink", slot:"chain", discipline:"flamecalling", effects:{defBonus:0.12,hpBonus:15,hpRegen:3}, tags:[], desc:"Chain of self-igniting links — each spell cast adds another burning ring to the necklace" },

  // ── EARTHSHAPING ──────────────────────────────────────────
  // Crowns
  { id:"lithic_crown", name:"Lithic Crown", slot:"crown", discipline:"earthshaping", effects:{atkBonus:0.195,defBonus:0.13,hpBonus:20}, tags:[], desc:"Crown of compressed granite — the wearer commands tectonic force, raising walls and sinking foundations" },
  // Weapons
  { id:"faultbreaker", name:"Faultbreaker", slot:"weapon", discipline:"earthshaping", effects:{atkBonus:0.14}, tags:[], desc:"Warhammer of meteoric iron — each strike sends shockwaves through stone and bone" },
  { id:"ironroot_blade", name:"Ironroot Blade", slot:"weapon", discipline:"earthshaping", effects:{atkBonus:0.08}, tags:[], desc:"Sword grown from living iron ore — the blade reshapes itself to find gaps in armor" },
  { id:"quake_maul", name:"Quake Maul", slot:"weapon", discipline:"earthshaping", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Maul carved from a mountain's heartstone — impacts create localized earthquakes" },
  // Daggers
  { id:"shard_fang", name:"Shard Fang", slot:"dagger", discipline:"earthshaping", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Dagger of fractured crystal — shards embed in wounds and grow" },
  { id:"granite_spike", name:"Granite Spike", slot:"dagger", discipline:"earthshaping", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Stone spike that hardens on impact — punches through plate armor" },
  { id:"tectonic_needle", name:"Tectonic Needle", slot:"dagger", discipline:"earthshaping", effects:{atkBonus:0.05,evasion:0.05}, tags:[], desc:"Thin blade that finds fault lines in any material — even magical barriers crack" },
  // Rings
  { id:"lodestone_band", name:"Lodestone Band", slot:"ring", discipline:"earthshaping", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring of magnetic iron — the wearer can pull metal weapons from enemy hands" },
  { id:"bedrock_signet", name:"Bedrock Signet", slot:"ring", discipline:"earthshaping", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Signet carved from the world's deepest stone — grants unshakeable footing" },
  { id:"crystal_loop", name:"Crystal Loop", slot:"ring", discipline:"earthshaping", effects:{evasion:0.075,enemyAtkPenalty:0.045}, tags:[], desc:"Ring of living crystal — grows armor plating over the wearer's skin when threatened" },
  // Amulets
  { id:"heartstone", name:"Heartstone", slot:"amulet", discipline:"earthshaping", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Pendant of compressed mountain core — the wearer feels vibrations through miles of earth" },
  { id:"geode_heart", name:"Geode Heart", slot:"amulet", discipline:"earthshaping", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Split geode pendant — resonates with nearby minerals, amplifying stone-shaping" },
  { id:"titan_pendant", name:"Titan Pendant", slot:"amulet", discipline:"earthshaping", effects:{hpRegen:9,hpBonus:18,defBonus:0.045}, tags:[], desc:"Pendant containing a titan's petrified eye — the wearer can reshape landscapes at will" },
  // Staves
  { id:"pillar_staff", name:"Pillar Staff", slot:"staff", discipline:"earthshaping", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Staff carved from a single pillar of basalt — plants itself and raises stone walls on command" },
  { id:"tremor_rod", name:"Tremor Rod", slot:"staff", discipline:"earthshaping", effects:{atkBonus:0.045,hpRegen:2}, tags:[], desc:"Iron-shod rod that hums with seismic energy — tapping the ground reveals buried structures" },
  { id:"worldbone_spire", name:"Worldbone Spire", slot:"staff", discipline:"earthshaping", effects:{atkBonus:0.165,hpRegen:8}, tags:[], desc:"Staff made from the spine of the earth itself — mountains answer its call" },
  // Chains
  { id:"ironvein_chain", name:"Ironvein Chain", slot:"chain", discipline:"earthshaping", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Chain of raw ore links — each one pulls iron from the surrounding earth to repair itself" },
  { id:"mineral_beads", name:"Mineral Beads", slot:"chain", discipline:"earthshaping", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Beads of eight different stones — each amplifies a different aspect of earthshaping" },
  { id:"tectonic_links", name:"Tectonic Links", slot:"chain", discipline:"earthshaping", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain whose links are compressed fault lines — breaking one triggers a localized quake" },

  // ── RUNEBINDING ───────────────────────────────────────────
  // Crowns
  { id:"glyph_crown", name:"Glyph Crown", slot:"crown", discipline:"runebinding", effects:{atkBonus:0.21,defBonus:0.14,hpBonus:21}, tags:[], desc:"Crown inscribed with every known ward — the wearer's enchantments become permanent and self-repairing" },
  // Weapons
  { id:"runescarred_blade", name:"Runescarred Blade", slot:"weapon", discipline:"runebinding", effects:{atkBonus:0.12}, tags:[], desc:"Sword covered in shifting glyphs — each strike inscribes a weakening rune on the target" },
  { id:"wardbreaker", name:"Wardbreaker", slot:"weapon", discipline:"runebinding", effects:{atkBonus:0.08}, tags:[], desc:"Mace designed to shatter enchantments — impacts dispel magical protections" },
  { id:"inscription_glaive", name:"Inscription Glaive", slot:"weapon", discipline:"runebinding", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Polearm that writes killing words in the air — the glyphs detonate on contact" },
  // Daggers
  { id:"sigil_fang", name:"Sigil Fang", slot:"dagger", discipline:"runebinding", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Dagger that carves self-activating runes into whatever it cuts" },
  { id:"glyph_needle", name:"Glyph Needle", slot:"dagger", discipline:"runebinding", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Needle-thin blade for precise rune inscription — enchantments placed by this blade last decades" },
  { id:"wardpiercer", name:"Wardpiercer", slot:"dagger", discipline:"runebinding", effects:{atkBonus:0.05,evasion:0.05}, tags:[], desc:"Dagger that phases through magical barriers — no ward can stop it" },
  // Rings
  { id:"inscription_ring", name:"Inscription Ring", slot:"ring", discipline:"runebinding", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring that leaves glowing runes wherever the wearer gestures" },
  { id:"seal_band", name:"Seal Band", slot:"ring", discipline:"runebinding", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Ring of seven seals — each one locks a different school of hostile magic" },
  { id:"keystone_ring", name:"Keystone Ring", slot:"ring", discipline:"runebinding", effects:{evasion:0.075,enemyAtkPenalty:0.045}, tags:[], desc:"Ring that serves as the keystone for all nearby wards — they strengthen in its presence" },
  // Amulets
  { id:"lexicon_pendant", name:"Lexicon Pendant", slot:"amulet", discipline:"runebinding", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Pendant containing a compressed library of glyphs — the wearer can inscribe wards by thought alone" },
  { id:"wardstone_amulet", name:"Wardstone Amulet", slot:"amulet", discipline:"runebinding", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Amulet carved from a shattered wardstone — passively deflects minor hostile magic" },
  { id:"masters_seal", name:"Master's Seal", slot:"amulet", discipline:"runebinding", effects:{hpRegen:9,hpBonus:18,defBonus:0.045}, tags:[], desc:"Seal of the first Runebinder — all enchantments within sight bend to the wearer's will" },
  // Staves
  { id:"inscription_staff", name:"Inscription Staff", slot:"staff", discipline:"runebinding", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Staff tipped with an ever-writing stylus — traces wards in the air that solidify into barriers" },
  { id:"wardkeeper", name:"Wardkeeper", slot:"staff", discipline:"runebinding", effects:{atkBonus:0.045,hpRegen:2}, tags:[], desc:"Staff that maintains and repairs all nearby enchantments — wards never decay in its presence" },
  { id:"archglyph_spire", name:"Archglyph Spire", slot:"staff", discipline:"runebinding", effects:{atkBonus:0.165,hpRegen:8}, tags:[], desc:"Staff inscribed with the fundamental glyph of binding — can seal away entire schools of magic" },
  // Chains
  { id:"runelink_chain", name:"Runelink Chain", slot:"chain", discipline:"runebinding", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Chain where each link is a different rune — together they form a mobile ward network" },
  { id:"glyph_beads", name:"Glyph Beads", slot:"chain", discipline:"runebinding", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Prayer beads inscribed with protective glyphs — each one absorbs a single hostile spell" },
  { id:"binding_links", name:"Binding Links", slot:"chain", discipline:"runebinding", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain that connects to the wearer's enchantments — pulling a link activates a distant ward" },

  // ── FROSTBRINGER ──────────────────────────────────────────
  // Crowns
  { id:"glacial_crown", name:"Glacial Crown", slot:"crown", discipline:"frostbringer", effects:{atkBonus:0.195,defBonus:0.13,hpBonus:20}, tags:[], desc:"Crown of eternal ice that radiates killing cold — the air crystallizes for a hundred paces in every direction" },
  // Weapons
  { id:"frostcleaver", name:"Frostcleaver", slot:"weapon", discipline:"frostbringer", effects:{atkBonus:0.14}, tags:[], desc:"Greataxe of black ice — each strike flash-freezes blood and bone on contact" },
  { id:"rime_edge", name:"Rime Edge", slot:"weapon", discipline:"frostbringer", effects:{atkBonus:0.08}, tags:[], desc:"Longsword sheathed in hoarfrost — wounds go numb before the victim knows they're cut" },
  { id:"avalanche_maul", name:"Avalanche Maul", slot:"weapon", discipline:"frostbringer", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Hammer of compressed glacial ice — impacts release shockwaves of freezing wind" },
  // Daggers
  { id:"icicle_fang", name:"Icicle Fang", slot:"dagger", discipline:"frostbringer", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Dagger that reforms from moisture in the air — impossible to disarm, impossible to dull" },
  { id:"permafrost_needle", name:"Permafrost Needle", slot:"dagger", discipline:"frostbringer", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Needle-thin blade of ancient frost — wounds freeze shut then shatter at the slightest touch" },
  { id:"blizzard_fang", name:"Blizzard Fang", slot:"dagger", discipline:"frostbringer", effects:{atkBonus:0.05,evasion:0.05}, tags:[], desc:"Dagger carved from the heart of an eternal blizzard — each cut releases a localized snowstorm" },
  // Rings
  { id:"frostbite_band", name:"Frostbite Band", slot:"ring", discipline:"frostbringer", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring of blue-white metal — the wearer's touch drains warmth from anything it contacts" },
  { id:"glacier_signet", name:"Glacier Signet", slot:"ring", discipline:"frostbringer", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Signet carved from compressed glacial ice — grants the wearer dominion over frozen terrain" },
  { id:"winters_loop", name:"Winter's Loop", slot:"ring", discipline:"frostbringer", effects:{evasion:0.075,enemyAtkPenalty:0.045}, tags:[], desc:"Ring containing a fragment of endless winter — the wearer radiates killing cold at will" },
  // Amulets
  { id:"hoarfrost_heart", name:"Hoarfrost Heart", slot:"amulet", discipline:"frostbringer", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Pendant of frozen breath from a dying ice titan — amplifies cold magic with primal fury" },
  { id:"cryoshard", name:"Cryoshard", slot:"amulet", discipline:"frostbringer", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Shard of ice that never melts — hums louder as the wearer's frost magic deepens" },
  { id:"absolute_zero", name:"Absolute Zero", slot:"amulet", discipline:"frostbringer", effects:{hpRegen:11,hpBonus:22,defBonus:0.055}, tags:[], desc:"Pendant containing a point of impossible cold — nearby matter stops moving at the molecular level" },
  // Staves
  { id:"winterspine", name:"Winterspine", slot:"staff", discipline:"frostbringer", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Staff of petrified ice from before the world had warmth — frost obeys it like a living thing" },
  { id:"glacial_rod", name:"Glacial Rod", slot:"staff", discipline:"frostbringer", effects:{atkBonus:0.045,hpRegen:2}, tags:[], desc:"Ice-crystal rod that steadies frost magic — the wearer's cold spells gain surgical precision" },
  { id:"fimbulwinter_spire", name:"Fimbulwinter Spire", slot:"staff", discipline:"frostbringer", effects:{atkBonus:0.165,hpRegen:8}, tags:[], desc:"Staff that remembers the first winter — plunges entire battlefields into frozen twilight" },
  // Chains
  { id:"frostlink_chain", name:"Frostlink Chain", slot:"chain", discipline:"frostbringer", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Chain of frozen links that creak and groan — each one lowers the temperature around the wearer" },
  { id:"rime_beads", name:"Rime Beads", slot:"chain", discipline:"frostbringer", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Beads of condensed frost — each one can flash-freeze a bucket of water instantly" },
  { id:"permafrost_links", name:"Permafrost Links", slot:"chain", discipline:"frostbringer", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain forged in an ice age — the links grow colder the more magic flows through them" },

  // ── FORCEBINDING ──────────────────────────────────────────
  // Crowns
  { id:"graviton_crown", name:"Graviton Crown", slot:"crown", discipline:"forcebinding", effects:{atkBonus:0.195,defBonus:0.13,hpBonus:20}, tags:[], desc:"Crown of compressed space — light bends around the wearer and nearby objects drift toward them" },
  // Weapons
  { id:"voidhammer", name:"Voidhammer", slot:"weapon", discipline:"forcebinding", effects:{atkBonus:0.14}, tags:[], desc:"Warhammer that multiplies its own weight a hundredfold at the point of impact" },
  { id:"gravity_edge", name:"Gravity Edge", slot:"weapon", discipline:"forcebinding", effects:{atkBonus:0.08}, tags:[], desc:"Blade surrounded by a field of intensified gravity — strikes land with the weight of a landslide" },
  { id:"singularity_maul", name:"Singularity Maul", slot:"weapon", discipline:"forcebinding", effects:{atkBonus:0.2,enemyDefPenalty:0.05}, tags:[], desc:"Maul containing a stabilized micro-singularity — each swing warps space and crushes armor" },
  // Daggers
  { id:"force_fang", name:"Force Fang", slot:"dagger", discipline:"forcebinding", effects:{atkBonus:0.03,evasion:0.03}, tags:[], desc:"Dagger propelled by telekinetic force — it flies from the hand and returns like a loyal hawk" },
  { id:"spatial_needle", name:"Spatial Needle", slot:"dagger", discipline:"forcebinding", effects:{atkBonus:0.02,evasion:0.02}, tags:[], desc:"Needle-thin blade that cuts through space itself — wounds appear before the strike is seen" },
  { id:"kinetic_fang", name:"Kinetic Fang", slot:"dagger", discipline:"forcebinding", effects:{atkBonus:0.05,evasion:0.05}, tags:[], desc:"Dagger that stores kinetic energy from every parry and releases it in devastating counter-thrusts" },
  // Rings
  { id:"gravity_band", name:"Gravity Band", slot:"ring", discipline:"forcebinding", effects:{evasion:0.037,defBonus:0.045}, tags:[], desc:"Ring of hyperdense metal — the wearer can shift their personal gravity at will to dodge attacks" },
  { id:"force_signet", name:"Force Signet", slot:"ring", discipline:"forcebinding", effects:{evasion:0.05,enemyAtkPenalty:0.03}, tags:[], desc:"Signet that projects a constant repulsive field — incoming strikes lose momentum before landing" },
  { id:"spatial_loop", name:"Spatial Loop", slot:"ring", discipline:"forcebinding", effects:{evasion:0.075,enemyAtkPenalty:0.045}, tags:[], desc:"Ring that folds space around the wearer — attacks pass through warped trajectories and miss" },
  // Amulets
  { id:"mass_pendant", name:"Mass Pendant", slot:"amulet", discipline:"forcebinding", effects:{hpRegen:4,hpBonus:8}, tags:[], desc:"Pendant of impossibly dense material — it weighs nothing to the wearer but anchors their life force" },
  { id:"gravity_shard", name:"Gravity Shard", slot:"amulet", discipline:"forcebinding", effects:{hpRegen:3,hpBonus:6}, tags:[], desc:"Fragment of a collapsed star — hums with restrained gravitational pull" },
  { id:"event_horizon", name:"Event Horizon", slot:"amulet", discipline:"forcebinding", effects:{hpRegen:11,hpBonus:22,defBonus:0.055}, tags:[], desc:"Pendant containing a frozen moment of spatial collapse — nothing escapes its protective embrace" },
  // Staves
  { id:"gravity_staff", name:"Gravity Staff", slot:"staff", discipline:"forcebinding", effects:{atkBonus:0.09,hpRegen:5}, tags:[], desc:"Staff that bends gravity along its length — point it at something and the world pulls toward it" },
  { id:"kinetic_rod", name:"Kinetic Rod", slot:"staff", discipline:"forcebinding", effects:{atkBonus:0.045,hpRegen:2}, tags:[], desc:"Rod of focused telekinetic resonance — amplifies the wielder's force manipulation to surgical precision" },
  { id:"worldbender_spire", name:"Worldbender Spire", slot:"staff", discipline:"forcebinding", effects:{atkBonus:0.165,hpRegen:8}, tags:[], desc:"Staff that remembers how to fold space — entire battlefields warp under its influence" },
  // Chains
  { id:"gravity_chain", name:"Gravity Chain", slot:"chain", discipline:"forcebinding", effects:{defBonus:0.08,hpBonus:10}, tags:[], desc:"Chain of links that each contain a pocket of intensified gravity — weighs down hostile magic" },
  { id:"force_beads", name:"Force Beads", slot:"chain", discipline:"forcebinding", effects:{defBonus:0.04,hpBonus:5}, tags:[], desc:"Beads of compressed force — each one can deflect a single incoming projectile on its own" },
  { id:"spatial_links", name:"Spatial Links", slot:"chain", discipline:"forcebinding", effects:{defBonus:0.1,hpBonus:13}, tags:[], desc:"Chain whose links exist partially in folded space — attacks that strike them are redirected elsewhere" },
];

function ArtifactsField({ artifacts, onChange, globalEquippedIds }) {
  const getSlot = (key) => (Array.isArray(artifacts) ? artifacts : []).find(a => a?.slot === key) || { slot: key, id: "", name: "", effects: {}, discipline: "all" };

  function selectArtifact(slotKey, artifactId) {
    if (!artifactId) {
      const updated = ARTIFACT_SLOTS.map(s => {
        if (s.key === slotKey) return { slot: slotKey, id: "", name: "", effects: {}, discipline: "all", attunement: 0 };
        return getSlot(s.key);
      });
      const filled = updated.filter(a => a.name.trim() !== "");
      onChange(filled.length > 0 ? updated : []);
      return;
    }
    const cat = ARTIFACT_CATALOG.find(a => a.id === artifactId);
    if (!cat) return;
    const current = getSlot(slotKey);
    const keepAttunement = current.id === cat.id ? (current.attunement || 0) : 0;
    const updated = ARTIFACT_SLOTS.map(s => {
      if (s.key === slotKey) return { slot: slotKey, id: cat.id, name: cat.name, effects: cat.effects || {}, discipline: cat.discipline, attunement: keepAttunement };
      return getSlot(s.key);
    });
    const filled = updated.filter(a => a.name.trim() !== "");
    onChange(filled.length > 0 ? updated : []);
  }

  const myIds = ARTIFACT_SLOTS.map(s => getSlot(s.key).id).filter(Boolean);
  const allTaken = globalEquippedIds || [];
  const activeArts = (Array.isArray(artifacts) ? artifacts : []).filter(a => a?.name && a?.effects);
  const artEffectSummary = (() => {
    const totals = { atkBonus:0, defBonus:0, hpRegen:0, hpBonus:0, evasion:0 };
    for (const a of activeArts) {
      const eff = a.effects || {};
      const am = 1 + Math.min(a.attunement || 0, 10) * 0.05;
      if (eff.atkBonus) totals.atkBonus += eff.atkBonus * am;
      if (eff.defBonus) totals.defBonus += eff.defBonus * am;
      if (eff.hpRegen) totals.hpRegen += Math.round(eff.hpRegen * am);
      if (eff.hpBonus) totals.hpBonus += Math.round(eff.hpBonus * am);
      if (eff.evasion) totals.evasion += eff.evasion * am;
    }
    const parts = [];
    if (totals.atkBonus > 0) parts.push(`+${(totals.atkBonus*100).toFixed(0)}% atk`);
    if (totals.defBonus > 0) parts.push(`+${(totals.defBonus*100).toFixed(0)}% def`);
    if (totals.hpRegen > 0) parts.push(`+${totals.hpRegen} regen`);
    if (totals.hpBonus > 0) parts.push(`+${totals.hpBonus} HP`);
    if (totals.evasion > 0) parts.push(`+${(totals.evasion*100).toFixed(0)}% eva`);
    return parts.join(" · ");
  })();

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {ARTIFACT_SLOTS.map(({ key, label }) => {
        const slot = getSlot(key);
        const filled = slot.name && slot.name.trim() !== "";
        const catEntry = filled ? ARTIFACT_CATALOG.find(a => a.id === slot.id) : null;
        const discInfo = slot.discipline && slot.discipline !== "all"
          ? MAGIC_DISCIPLINES.find(d => d.key === slot.discipline) : null;
        const available = ARTIFACT_CATALOG.filter(a => a.slot === key && (a.id === slot.id || (!myIds.includes(a.id) && !allTaken.includes(a.id))));

        return (
          <div key={key} style={{
            border:`1px solid ${filled ? (discInfo ? discInfo.color + "88" : "#f59e0b") : "#e5e7eb"}`,
            borderRadius:5, padding:"7px 8px",
            background: filled ? (discInfo ? discInfo.color + "12" : "#fffbeb") : "#f9fafb",
          }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:5 }}>
              <span style={{ fontSize:10, fontWeight:600, color: filled ? "#92400e" : "#9ca3af", letterSpacing:0.5, textTransform:"uppercase" }}>
                {key === "crown" ? "\u{1F451}" : "\u{1F3FA}"} {label}
              </span>
              {filled && (
                <span style={{ fontSize:8, fontWeight:700, color:"#16a34a", background:"#dcfce7", padding:"1px 5px", borderRadius:3, border:"1px solid #16a34a44" }}>ACTIVE</span>
              )}
            </div>
            <select
              value={slot.id || ""}
              onChange={e => selectArtifact(key, e.target.value)}
              style={{ ...selectStyle, fontSize:12 }}
            >
              <option value="">— Empty —</option>
              {available.map(a => {
                const dInfo = a.discipline !== "all" ? MAGIC_DISCIPLINES.find(d => d.key === a.discipline) : null;
                const eff = a.effects || {};
                const tags = [];
                if (eff.atkBonus) tags.push(`+${(eff.atkBonus*100).toFixed(0)}% atk`);
                if (eff.defBonus) tags.push(`+${(eff.defBonus*100).toFixed(0)}% def`);
                if (eff.hpRegen) tags.push(`+${eff.hpRegen} regen`);
                if (eff.hpBonus) tags.push(`+${eff.hpBonus} HP`);
                if (eff.evasion) tags.push(`+${(eff.evasion*100).toFixed(0)}% eva`);
                if (eff.enemyAtkPenalty) tags.push(`-${(eff.enemyAtkPenalty*100).toFixed(0)}% e.atk`);
                if (eff.enemyDefPenalty) tags.push(`-${(eff.enemyDefPenalty*100).toFixed(0)}% e.def`);
                return (
                  <option key={a.id} value={a.id}>
                    {dInfo ? dInfo.icon : "⊕"} {a.name} ({tags.join(", ")})
                  </option>
                );
              })}
            </select>
            {filled && catEntry && (
              <div style={{ marginTop:4 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  <span style={{ fontSize:11, fontWeight:600, color: discInfo ? discInfo.color : "#d97706" }}>
                    {discInfo ? `${discInfo.icon} ${discInfo.label}` : "⊕ All Disciplines"}
                  </span>
                  {(() => {
                    const eff = slot.effects || catEntry.effects || {};
                    const attMult = 1 + Math.min(slot.attunement || 0, 10) * 0.05;
                    const tags = [];
                    if (eff.atkBonus) tags.push({ label:`+${(eff.atkBonus*attMult*100).toFixed(0)}% atk`, color:"#ef4444" });
                    if (eff.defBonus) tags.push({ label:`+${(eff.defBonus*attMult*100).toFixed(0)}% def`, color:"#3b82f6" });
                    if (eff.hpRegen) tags.push({ label:`+${Math.round(eff.hpRegen*attMult)} regen`, color:"#22c55e" });
                    if (eff.hpBonus) tags.push({ label:`+${Math.round(eff.hpBonus*attMult)} HP`, color:"#f59e0b" });
                    if (eff.evasion) tags.push({ label:`+${(eff.evasion*attMult*100).toFixed(0)}% evasion`, color:"#a855f7" });
                    if (eff.enemyAtkPenalty) tags.push({ label:`-${(eff.enemyAtkPenalty*attMult*100).toFixed(0)}% enemy atk`, color:"#f97316" });
                    if (eff.enemyDefPenalty) tags.push({ label:`-${(eff.enemyDefPenalty*attMult*100).toFixed(0)}% enemy def`, color:"#f97316" });
                    return tags.map((t,i) => (
                      <span key={i} style={{ fontSize:9, fontWeight:600, color:t.color, background:t.color+"14", padding:"1px 5px", borderRadius:3, border:`1px solid ${t.color}33` }}>{t.label}</span>
                    ));
                  })()}
                  {(slot.attunement || 0) > 0 && (
                    <span style={{ fontSize:9, fontWeight:600, color:"#7c3aed", background:"#7c3aed15", padding:"1px 5px", borderRadius:3, border:"1px solid #7c3aed33" }}
                      title={`Attunement ${slot.attunement}/10 — all effects scaled by +${((slot.attunement||0)*5)}%`}>
                      🔮 {slot.attunement}/10 (+{(slot.attunement||0)*5}%)
                    </span>
                  )}
                </div>
                {(slot.attunement || 0) > 0 && (
                  <div style={{ display:"flex", gap:1, marginTop:3, marginBottom:2 }}>
                    {Array.from({length:10}).map((_, i) => (
                      <div key={i} style={{
                        flex:1, height:3, borderRadius:1,
                        background: i < (slot.attunement || 0) ? "#7c3aed" : "#e5e7eb",
                      }} />
                    ))}
                  </div>
                )}
                <div style={{ fontSize:10, color:"#9ca3af", marginTop:2, lineHeight:1.4, fontStyle:"italic" }}>{catEntry.desc}</div>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ display:"flex", justifyContent:"space-between", padding:"2px 0" }}>
        <span style={{ fontSize:10, color:"#9ca3af" }}>{activeArts.length} artifact{activeArts.length !== 1 ? "s" : ""} active</span>
        {artEffectSummary && (
          <span style={{ fontSize:10, color:"#d97706", fontWeight:600 }}>
            {artEffectSummary}
          </span>
        )}
      </div>
    </div>
  );
}


// Sum of raw discipline levels (for display)
function calcDisciplineLevels(disciplines) {
  if (!disciplines || typeof disciplines !== "object") return 0;
  return MAGIC_DISCIPLINES.reduce((sum, d) => sum + (disciplines[d.key] || 0), 0);
}

// Actual power rating from disciplines for a given entity type
// Artifacts no longer affect power rating — they provide flat effects in combat via getPassiveEffects
// fatigue: 0-1 (0 = fresh, 1 = fully exhausted). Only reduces power above 50%.
// 0-50% fatigue = full power (tired but functional).
// 50-100% fatigue = linear dropoff from 100% to 0% power.
function calcDisciplinePowerForType(disciplines, type, artifacts, fatigue) {
  if (!disciplines || typeof disciplines !== "object") return 0;
  const weights = type === "dragon" ? DRAGON_WEIGHTS : MAGE_WEIGHTS;
  const base    = type === "dragon" ? DRAGON_BASE   : MAGE_BASE;
  const fat = Math.max(0, Math.min(1, fatigue || 0));
  const fatigueMultiplier = fat <= 0.5 ? 1.0 : 1.0 - ((fat - 0.5) / 0.5); // 50%→1.0, 100%→0.0

  // Find the peak discipline (highest level)
  let peakKey = null, peakLvl = 0;
  for (const d of MAGIC_DISCIPLINES) {
    const lvl = disciplines[d.key] || 0;
    if (lvl > peakLvl) { peakKey = d.key; peakLvl = lvl; }
  }

  let total = 0;
  for (const d of MAGIC_DISCIPLINES) {
    const lvl = disciplines[d.key] || 0;
    if (lvl > 0) {
      let val = (weights[d.key] || 0) * (Math.pow(base, lvl) - 1);
      if (d.key === peakKey) val *= PEAK_MULT;
      val *= fatigueMultiplier;
      total += val;
    }
  }

  // Synergy bonuses — additive flat bonus for each active synergy pair
  const synergyTotal = calcSynergyBonus(disciplines, type);
  total += synergyTotal * fatigueMultiplier;

  // Runebinding passive — multiplicative bonus to total power when runebinding >= 7
  const runePassive = calcRunebindingPassive(disciplines);
  if (runePassive > 0) {
    total *= (1 + runePassive);
  }

  return total;
}

// Calculate synergy bonus from discipline pairs
function calcSynergyBonus(disciplines, type) {
  if (!disciplines || typeof disciplines !== "object") return 0;
  const weights = type === "dragon" ? DRAGON_WEIGHTS : MAGE_WEIGHTS;
  const base    = type === "dragon" ? DRAGON_BASE   : MAGE_BASE;
  let bonus = 0;
  for (const syn of DISCIPLINE_SYNERGIES) {
    const [k1, k2] = syn.pair;
    const lvl1 = disciplines[k1] || 0;
    const lvl2 = disciplines[k2] || 0;
    if (lvl1 >= SYNERGY_THRESHOLD && lvl2 >= SYNERGY_THRESHOLD) {
      const minLvl = Math.min(lvl1, lvl2);
      // Synergy scales with the lower level: base bonus × (minLvl / 12)
      const scale = minLvl / 12;
      // Use average weight of both disciplines as the base
      const avgWeight = ((weights[k1] || 0) + (weights[k2] || 0)) / 2;
      bonus += syn.bonus * scale * (Math.pow(base, minLvl) - 1) * avgWeight * 10;
    }
  }
  return bonus;
}

// Get active synergies for display
function getActiveSynergies(disciplines) {
  if (!disciplines || typeof disciplines !== "object") return [];
  return DISCIPLINE_SYNERGIES.filter(syn => {
    const [k1, k2] = syn.pair;
    return (disciplines[k1] || 0) >= SYNERGY_THRESHOLD && (disciplines[k2] || 0) >= SYNERGY_THRESHOLD;
  });
}

// Get counter matchup result between two entities' peak disciplines (used by auto-resolve)
// Manual duels use per-ability counters instead — see resolveRound
function getCounterMatchup(discA, discB) {
  const peakA = peakDiscipline(discA);
  const peakB = peakDiscipline(discB);
  let attackerBonus = 0, defenderBonus = 0;
  let attackerCounters = false, defenderCounters = false;
  if (peakA.key && peakB.key) {
    const aCountersB = disciplineCounters(peakA.key, peakB.key);
    const bCountersA = disciplineCounters(peakB.key, peakA.key);
    if (aCountersB) { attackerBonus = COUNTER_ATK_BONUS; attackerCounters = true; }
    if (bCountersA) { defenderBonus = COUNTER_ATK_BONUS; defenderCounters = true; }
    if (bCountersA) { attackerBonus -= COUNTERED_PENALTY; }
    if (aCountersB) { defenderBonus -= COUNTERED_PENALTY; }
  }
  return { attackerBonus, defenderBonus, attackerCounters, defenderCounters, peakA, peakB };
}

// Discipline depth bonus — total discipline investment gives a combat multiplier.
// This makes secondary/tertiary disciplines matter in manual duels.
// Returns a multiplier (0.0+) that applies to both attack and defense.
function calcDisciplineDepthBonus(disciplines) {
  if (!disciplines || typeof disciplines !== "object") return 0;
  let totalLevels = 0;
  let discCount = 0;
  for (const d of MAGIC_DISCIPLINES) {
    const lvl = disciplines[d.key] || 0;
    if (lvl > 0) {
      // Superlinear: high levels contribute much more than low levels
      // lvl 7 → 13.1, lvl 10 → 22.4, lvl 12 → 29.3
      totalLevels += Math.pow(lvl, 1.5);
      discCount++;
    }
  }
  if (discCount <= 1) return totalLevels * 0.002; // single disc gets modest bonus
  // Multi-disc: breadth multiplier
  const breadthMult = 1 + (discCount - 1) * 0.05;
  return totalLevels * 0.002 * breadthMult;
}

// Highest discipline level
function peakDiscipline(disciplines, primaryDiscipline) {
  if (!disciplines || typeof disciplines !== "object") return { key: null, level: 0 };
  let best = { key: null, level: 0 };
  for (const d of MAGIC_DISCIPLINES) {
    const lvl = disciplines[d.key] || 0;
    if (lvl > best.level) best = { key: d.key, level: lvl };
    else if (lvl === best.level && lvl > 0 && d.key === primaryDiscipline) best = { key: d.key, level: lvl };
  }
  // If primary is set and tied with best, prefer primary
  if (primaryDiscipline && (disciplines[primaryDiscipline] || 0) === best.level && best.level > 0) {
    best = { key: primaryDiscipline, level: best.level };
  }
  return best;
}

// ── Abilities Editor ─────────────────────────────────────────────────────────
// Allows learning and viewing abilities for a specific entity
function AbilitiesEditor({ entity, onUpdate }) {
  const disc = entity.disciplines || {};
  const learned = entity.learnedAbilities || {};
  const mastery = entity.abilityMastery || {};
  const [expandedDisc, setExpandedDisc] = useState(null);

  function learnAbility(abilityKey) {
    const updated = { ...entity, learnedAbilities: { ...learned, [abilityKey]: true } };
    onUpdate(updated);
  }

  function unlearnAbility(abilityKey) {
    const newLearned = { ...learned };
    delete newLearned[abilityKey];
    onUpdate({ ...entity, learnedAbilities: newLearned });
  }

  const activeDisciplines = MAGIC_DISCIPLINES.filter(d => (disc[d.key] || 0) > 0);

  if (activeDisciplines.length === 0) {
    return <div style={{ fontSize:11, color:"#6b7280", fontStyle:"italic" }}>No active disciplines — level up to unlock abilities.</div>;
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {activeDisciplines.map(d => {
        const discLevel = disc[d.key] || 0;
        const abilities = ABILITIES[d.key] || [];
        const isPrimary = entity.primaryDiscipline ? (entity.primaryDiscipline === d.key) : undefined;
        const hasPrimary = !!entity.primaryDiscipline;
        const totalPts = calcAvailableSkillPoints(discLevel, isPrimary);
        const spent = calcSpentSkillPoints(learned, d.key);
        const remaining = totalPts - spent;
        const learnedCount = abilities.filter(a => learned[a.key]).length;
        const isExpanded = expandedDisc === d.key;

        return (
          <div key={d.key} style={{
            borderRadius:6, border:`1px solid ${isPrimary ? d.color + "88" : d.color + "44"}`,
            background: isExpanded ? d.color + "0a" : "transparent",
            overflow:"hidden",
          }}>
            <div onClick={() => setExpandedDisc(isExpanded ? null : d.key)} style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"6px 10px", cursor:"pointer",
            }}>
              <span style={{ fontSize:12, fontWeight:600, color:d.color }}>
                {d.icon} {d.label} {isPrimary && <span style={{ fontSize:8, fontWeight:700, color:"#f59e0b", background:"#f59e0b18", padding:"1px 4px", borderRadius:3, marginLeft:3 }}>★ PRIMARY</span>}
                <span style={{ fontSize:10, fontWeight:400, color:"#9ca3af" }}> ({learnedCount}/{abilities.length})</span>
              </span>
              <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:10, color: remaining > 0 ? "#22c55e" : "#6b7280", fontWeight:600 }}>
                  {remaining} SP {hasPrimary && isPrimary === true && <span style={{ fontSize:8, color:"#f59e0b" }}>(1.5×)</span>}{hasPrimary && isPrimary === false && <span style={{ fontSize:8, color:"#6b7280" }}>(½)</span>}
                </span>
                <span style={{ fontSize:10, color:"#9ca3af" }}>{isExpanded ? "▲" : "▼"}</span>
              </span>
            </div>

            {isExpanded && (
              <div style={{ padding:"4px 10px 10px", display:"flex", flexDirection:"column", gap:4 }}>
                {["basic","advanced","ultimate","passive_master"].map(tierKey => {
                  const tierAbilities = abilities.filter(a => a.tier === tierKey);
                  const tierInfo = ABILITY_TIERS[tierKey];
                  if (tierAbilities.length === 0) return null;
                  const isPassiveTier = tierKey === "passive_master";
                  // Only show passive tier under primary discipline
                  if (isPassiveTier && entity.primaryDiscipline && entity.primaryDiscipline !== d.key) return null;
                  // Check if entity already has any passive learned (globally)
                  const hasAnyPassive = isPassiveTier && Object.keys(ABILITIES).some(dk =>
                    ABILITIES[dk].some(a => a.type === "passive" && learned[a.key])
                  );
                  return (
                    <div key={tierKey}>
                      <div style={{ fontSize:9, fontWeight:700, color: tierInfo.color, letterSpacing:0.5, textTransform:"uppercase", marginBottom:3, marginTop:4 }}>
                        {isPassiveTier ? "Passive (Choose 1 · Primary Only · Req: Lv.11)" : `${tierInfo.label} (Cost: ${tierInfo.cost} SP · Req: Lv.${tierInfo.reqLevel})`}
                      </div>
                      {tierAbilities.map(ab => {
                        const isLearned = !!learned[ab.key];
                        const canLearn = !isLearned && canLearnAbility(entity, ab.key);
                        const meetsLevel = discLevel >= tierInfo.reqLevel;
                        const masteryXP = mastery[ab.key] || 0;
                        const mp = getMasteryProgress(masteryXP);
                        const typeInfo = ABILITY_TYPE_INFO[ab.type];
                        // Determine why passive can't be learned
                        const passiveBlockReason = isPassiveTier && !isLearned && !canLearn ? (
                          !meetsLevel ? `Lv.${tierInfo.reqLevel}` :
                          !isPrimary ? "Not Primary" :
                          hasAnyPassive ? "Passive Taken" : "—"
                        ) : null;

                        return (
                          <div key={ab.key} style={{
                            display:"flex", alignItems:"center", gap:6, padding:"5px 8px",
                            borderRadius:5, marginBottom:2,
                            background: isLearned ? d.color + "14" : "#ffffff06",
                            border: `1px solid ${isLearned ? d.color + "44" : "#ffffff12"}`,
                            opacity: meetsLevel ? 1 : 0.4,
                          }}>
                            <span style={{ fontSize:14, flexShrink:0 }}>{ab.icon}</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                                <span style={{ fontSize:11, fontWeight:600, color: isLearned ? d.color : "#9ca3af" }}>{ab.name}</span>
                                <span style={{ fontSize:8, fontWeight:600, color: typeInfo.color, background: typeInfo.color + "18", padding:"1px 4px", borderRadius:2 }}>
                                  {typeInfo.icon} {typeInfo.label}
                                </span>
                                {ab.cooldown > 0 && <span style={{ fontSize:8, color:"#6b7280" }}>CD:{ab.cooldown}</span>}
                              </div>
                              <div style={{ fontSize:9, color:"#6b7280", marginTop:1 }}>{ab.desc}</div>
                              {isLearned && ab.type !== "passive" && (
                                <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:3 }}>
                                  <span style={{ fontSize:8, fontWeight:600, color: mp.level >= MASTERY_LEVELS ? "#f59e0b" : d.color }}>
                                    {MASTERY_LABELS[mp.level]}
                                  </span>
                                  {mp.level < MASTERY_LEVELS && (
                                    <div style={{ flex:1, maxWidth:60, height:3, borderRadius:2, background:"#ffffff14", overflow:"hidden" }}>
                                      <div style={{ height:"100%", width:`${mp.needed > 0 ? (mp.current / mp.needed) * 100 : 100}%`, borderRadius:2, background:d.color }} />
                                    </div>
                                  )}
                                  <span style={{ fontSize:8, color:"#6b7280" }}>
                                    Pwr: {ab.basePower.toFixed(1)} × {(1 + mp.level * MASTERY_BONUS_PER_LEVEL).toFixed(1)}
                                  </span>
                                </div>
                              )}
                            </div>
                            {isLearned ? (
                              <button onClick={() => unlearnAbility(ab.key)} style={{
                                fontSize:9, padding:"2px 6px", borderRadius:3, cursor:"pointer",
                                background:"transparent", border:"1px solid #ef444444", color:"#ef4444", fontWeight:600, flexShrink:0,
                              }}>✕</button>
                            ) : canLearn ? (
                              <button onClick={() => learnAbility(ab.key)} style={{
                                fontSize:9, padding:"2px 6px", borderRadius:3, cursor:"pointer",
                                background: d.color + "22", border:`1px solid ${d.color}66`, color:d.color, fontWeight:600, flexShrink:0,
                              }}>Learn</button>
                            ) : (
                              <span style={{ fontSize:9, color:"#4b5563", flexShrink:0 }}>
                                {passiveBlockReason || (!meetsLevel ? `Lv.${tierInfo.reqLevel}` : "No SP")}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Disciplines editor — 8 sliders with labels, icons, colors, counters, synergies
function DisciplinesEditor({ disciplines, onChange, entityType, artifacts, disciplineXP }) {
  const disc = disciplines || {};
  const xp = disciplineXP || {};
  const totalLvl = calcDisciplineLevels(disc);
  const basePwr = calcDisciplinePowerForType(disc, entityType || "mage");
  const totalPwr = basePwr; // Artifacts no longer affect power rating
  const hasArtBoost = false;
  const weights = entityType === "dragon" ? DRAGON_WEIGHTS : MAGE_WEIGHTS;
  // Sort disciplines by weight for this entity type (strongest first)
  const sorted = [...MAGIC_DISCIPLINES].sort((a, b) => (weights[b.key] || 0) - (weights[a.key] || 0));
  // Tier labels — now 8 disciplines: top 2 = S, next 3 = A, bottom 3 = B
  const tierOf = (key) => {
    const keys = Object.keys(weights).sort((a,b) => weights[b] - weights[a]);
    const idx = keys.indexOf(key);
    return idx <= 1 ? "S" : idx <= 4 ? "A" : "B";
  };
  // Check which disciplines have targeted artifacts (for display)
  const arts = Array.isArray(artifacts) ? artifacts.filter(a => a?.name && a?.effects) : [];
  const artTargets = {};
  for (const a of arts) {
    if (a.discipline && a.discipline !== "all" && a.name && a.effects) {
      artTargets[a.discipline] = true;
    }
  }
  const tierColors = { S:"#eab308", A:"#a855f7", B:"#6b7280" };

  // Active synergies
  const activeSynergies = getActiveSynergies(disc);
  const synergyBonus = calcSynergyBonus(disc, entityType || "mage");

  // Peak discipline + its counters
  const peak = peakDiscipline(disc);
  const peakCounters = peak.key ? (DISCIPLINE_COUNTERS[peak.key] || []) : [];
  const counteredBy = peak.key ? MAGIC_DISCIPLINES.filter(d => (DISCIPLINE_COUNTERS[d.key] || []).includes(peak.key)).map(d => d.key) : [];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
      {sorted.map(({ key, label, icon, color }) => {
        const val = disc[key] || 0;
        const tier = tierOf(key);
        const artBoost = artTargets[key] || false;
        const isCountered = peakCounters.includes(key);
        const countersUs = counteredBy.includes(key);
        return (
          <div key={key} style={{
            padding:"5px 7px", borderRadius:5,
            border:`1px solid ${val > 0 ? color + "44" : "#e5e7eb"}`,
            background: val > 0 ? color + "08" : "#f9fafb",
          }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:3 }}>
              <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:9, fontWeight:700, color: tierColors[tier], background: tierColors[tier] + "18", padding:"1px 4px", borderRadius:3, lineHeight:1.2 }}>{tier}</span>
                <span style={{ fontSize:11, fontWeight:600, color: val > 0 ? color : "#9ca3af" }}>
                  {icon} {label}
                </span>
                {artBoost && <span style={{ fontSize:9, color:"#d97706", fontWeight:600 }}>🏺</span>}
              </span>
              <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                {peak.key && val > 0 && key !== peak.key && isCountered && <span title="Your peak counters this discipline" style={{ fontSize:8, color:"#22c55e", fontWeight:700 }}>▼WEAK</span>}
                {peak.key && val > 0 && key !== peak.key && countersUs && <span title="This discipline counters your peak" style={{ fontSize:8, color:"#ef4444", fontWeight:700 }}>▲STRONG</span>}
                <span style={{ fontSize:10, color: val > 0 ? color : "#d1d5db", fontWeight:600 }}>
                  {val > 0 ? `${val} — ${DISCIPLINE_LABELS[val]}` : "—"}
                </span>
              </span>
            </div>
            <input type="range" min={0} max={12} value={val}
              onChange={e => onChange({ ...disc, [key]: parseInt(e.target.value) })}
              style={{ width:"100%", accentColor: color, height:14 }} />
            {/* XP progress bar */}
            {val > 0 && val < 12 && (xp[key] || 0) > 0 && (() => {
              const threshold = xpThresholdForLevel(val);
              const pct = Math.min(1, (xp[key] || 0) / threshold);
              return (
                <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:2 }}>
                  <div style={{ flex:1, height:3, borderRadius:2, background:"#e5e7eb", overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct * 100}%`, borderRadius:2, background: color, opacity:0.6 }} />
                  </div>
                  <span style={{ fontSize:8, color: color, fontWeight:600 }}>{Math.round(pct * 100)}% XP</span>
                  {threshold > 1.5 && <span style={{ fontSize:7, color:"#9ca3af" }}>({threshold.toFixed(1)}x)</span>}
                </div>
              );
            })()}
          </div>
        );
      })}

      {/* Counter info for peak discipline */}
      {peak.key && (
        <div style={{ padding:"5px 7px", borderRadius:5, background:"#fef2f2", border:"1px solid #fee2e2" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#991b1b", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3 }}>
            ⚔ Matchup — {MAGIC_DISCIPLINES.find(d=>d.key===peak.key)?.icon} {MAGIC_DISCIPLINES.find(d=>d.key===peak.key)?.label}
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <span style={{ fontSize:9, color:"#16a34a" }}>
              Strong vs: {peakCounters.map(k => MAGIC_DISCIPLINES.find(d=>d.key===k)).filter(Boolean).map(d => `${d.icon}${d.label}`).join(", ") || "—"}
            </span>
            <span style={{ fontSize:9, color:"#dc2626" }}>
              Weak vs: {counteredBy.map(k => MAGIC_DISCIPLINES.find(d=>d.key===k)).filter(Boolean).map(d => `${d.icon}${d.label}`).join(", ") || "—"}
            </span>
          </div>
        </div>
      )}

      {/* Active synergies */}
      {activeSynergies.length > 0 && (
        <div style={{ padding:"5px 7px", borderRadius:5, background:"#f0fdf4", border:"1px solid #bbf7d0" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#166534", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3 }}>
            ✦ Active Synergies ({activeSynergies.length})
          </div>
          {activeSynergies.map((syn, i) => {
            const [k1, k2] = syn.pair;
            const d1 = MAGIC_DISCIPLINES.find(d => d.key === k1);
            const d2 = MAGIC_DISCIPLINES.find(d => d.key === k2);
            const minLvl = Math.min(disc[k1] || 0, disc[k2] || 0);
            return (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:4, marginBottom:2 }}>
                <span style={{ fontSize:10 }}>{syn.icon}</span>
                <span style={{ fontSize:10, fontWeight:600, color:"#166534" }}>{syn.label}</span>
                <span style={{ fontSize:9, color:"#6b7280" }}>({d1?.icon}{disc[k1]||0} + {d2?.icon}{disc[k2]||0})</span>
                <span style={{ fontSize:9, color:"#15803d", fontWeight:600, marginLeft:"auto" }}>Lv.{minLvl}</span>
              </div>
            );
          })}
          <div style={{ fontSize:9, color:"#15803d", fontWeight:600, marginTop:2 }}>
            Synergy bonus: +{(synergyBonus * 100).toFixed(0)}%
          </div>
        </div>
      )}

      {/* Runebinding passive */}
      {calcRunebindingPassive(disc) > 0 && (
        <div style={{ padding:"5px 7px", borderRadius:5, background:"#eff6ff", border:"1px solid #bfdbfe" }}>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:10 }}>🔷</span>
            <span style={{ fontSize:10, fontWeight:700, color:"#1d4ed8" }}>Runic Amplification</span>
            <span style={{ fontSize:9, color:"#6b7280" }}>(Runebinding {disc.runebinding || 0})</span>
            <span style={{ fontSize:10, color:"#1d4ed8", fontWeight:700, marginLeft:"auto" }}>+{(calcRunebindingPassive(disc) * 100).toFixed(0)}% total</span>
          </div>
          <div style={{ fontSize:9, color:"#6b7280", marginTop:2 }}>Wards amplify all magic — multiplies total power</div>
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"space-between", padding:"3px 2px" }}>
        <span style={{ fontSize:10, color:"#9ca3af" }}>Levels: {totalLvl}/96</span>
        <span style={{ fontSize:11, fontWeight:700, color: totalPwr > 0 ? "#7c3aed" : "#d1d5db" }}>
          Power: +{(totalPwr * 100).toFixed(0)}%
          {hasArtBoost && <span style={{ fontSize:9, color:"#d97706", marginLeft:4 }}>(🏺 boosted)</span>}
        </span>
      </div>
    </div>
  );
}

// Draggable entity row inside city panel
function CityEntityRow({ item, type, onDragOut }) {
  const shapeStyle = type === "army" ? { borderRadius: 2, transform: "rotate(45deg)" }
    : type === "dragon" ? { borderRadius: 2, clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }
    : { borderRadius: "50%" };
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 6px", background:"#f7f5f2", borderRadius:5, border:"1px solid #e2ddd6", marginBottom:3 }}>
      <span style={{
        width:10, height:10, background:item.color, flexShrink:0, display:"inline-block",
        ...shapeStyle,
      }} />
      <span style={{ fontSize:12, color:"#3d3529", flex:1, fontFamily:FONT_BODY }}>{item.name}{item.role ? ` · ${item.role}` : ""}{item.size ? ` (${item.size.toLocaleString()})` : ""}</span>
      <button
        title="Move out to map"
        onClick={() => onDragOut(item.primary_key, type)}
        style={{ background:"none", border:"1px solid #d9d2c9", borderRadius:4, cursor:"pointer", fontSize:10, color:"#8a7e72", padding:"2px 5px" }}>
        ↗ Map
      </button>
    </div>
  );
}

// ── Name Modal ────────────────────────────────────────────────────────────────
function NameModal({ type, onConfirm, onCancel }) {
  const [value, setValue] = useState("");
  const [size, setSize] = useState("");
  const [role, setRole] = useState("");
  const [school, setSchool] = useState("");
  const [power, setPower] = useState(5);
  const [primaryDiscipline, setPrimaryDiscipline] = useState("");
  const [element, setElement] = useState("");
  const [dragonSize, setDragonSize] = useState("Adult");
  const isArmy = type === "army";
  const isChar = type === "character";
  const isMage = type === "mage";
  const isDragon = type === "dragon";
  

  function handleConfirm() {
    if (!value.trim()) return;
    // Build initial disciplines object if a primary was picked
    let initDisciplines = undefined;
    if ((isMage || isDragon) && primaryDiscipline) {
      initDisciplines = {};
      MAGIC_DISCIPLINES.forEach(d => { initDisciplines[d.key] = 0; });
      initDisciplines[primaryDiscipline] = isMage ? 3 : 2; // mages start a bit higher in their primary
    }
    onConfirm({
      name: value.trim(),
      size: isArmy ? (parseInt(size) || 0) : undefined,
      role: isChar ? role.trim() : undefined,
      school: isMage ? school.trim() : undefined,
      power: isMage ? power : undefined,
      element: isDragon ? element.trim() : undefined,
      dragonSize: isDragon ? dragonSize : undefined,
      disciplines: initDisciplines,
    });
  }

  const titles = { kingdom:"Name this Kingdom", city:"Name this City", character:"New Character", army:"New Army", mage:"New Mage", dragon:"New Dragon" };

  return (
    <div style={{
      position:"fixed", inset:0, background:"#00000044", zIndex:100,
      display:"flex", alignItems:"center", justifyContent:"center",
      backdropFilter:"blur(4px)", WebkitBackdropFilter:"blur(4px)",
    }}>
      <div style={{ background:"#faf9f7", borderRadius:12, padding:24, width:"min(300px, calc(100vw - 32px))", boxShadow:"0 8px 32px #0003", maxHeight:"85vh", overflowY:"auto", border:"1px solid #e2ddd6" }}>
        <div style={{ fontWeight:700, fontSize:16, color:"#1a1814", marginBottom:14, fontFamily:FONT_DISPLAY, letterSpacing:0.5 }}>{titles[type]}</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <Field label="Name">
            <FInput autoFocus value={value} onChange={e => setValue(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleConfirm(); if (e.key === "Escape") onCancel(); }}
              placeholder={type === "kingdom" ? "e.g. Kingdom of Valdor" : type === "city" ? "e.g. Ironhold" : type === "character" ? "e.g. Ser Aldric" : type === "mage" ? "e.g. Archmage Veylan" : type === "dragon" ? "e.g. Scorathax" : "e.g. First Legion"} />
          </Field>
          {isChar && (
            <Field label="Role">
              <FInput value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. noble, warrior, merchant..." />
            </Field>
          )}
          {isArmy && (
            <Field label="Size">
              <FInput value={size} onChange={e => setSize(e.target.value)} type="number" placeholder="e.g. 5000" />
            </Field>
          )}
          {isMage && (
            <>
              <Field label="Primary Discipline">
                <select value={primaryDiscipline} onChange={e => setPrimaryDiscipline(e.target.value)} style={selectStyle}>
                  <option value="">— None (set later) —</option>
                  {MAGIC_DISCIPLINES.map(d => <option key={d.key} value={d.key}>{d.icon} {d.label}</option>)}
                </select>
                <span style={{ fontSize:10, color:"#9a8e82" }}>Sets starting level — fine-tune in edit panel</span>
              </Field>
            </>
          )}
          {isDragon && (
            <>
              <Field label="Element / Type">
                <FInput value={element} onChange={e => setElement(e.target.value)} placeholder="e.g. fire, ice, shadow..." />
              </Field>
              <Field label="Size Category">
                <select value={dragonSize} onChange={e => setDragonSize(e.target.value)} style={selectStyle}>
                  {DRAGON_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Primary Discipline">
                <select value={primaryDiscipline} onChange={e => setPrimaryDiscipline(e.target.value)} style={selectStyle}>
                  <option value="">— None (set later) —</option>
                  {MAGIC_DISCIPLINES.map(d => <option key={d.key} value={d.key}>{d.icon} {d.label}</option>)}
                </select>
                <span style={{ fontSize:10, color:"#9a8e82" }}>Dragon+rider magical affinity — fine-tune in panel</span>
              </Field>
            </>
          )}
        </div>
        <div style={{ display:"flex", gap:8, marginTop:16 }}>
          <button onClick={handleConfirm} style={mkBtn("#3b82f6")}>Place</button>
          <button onClick={onCancel} style={mkBtn("#9a8e82")}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── City Panel ────────────────────────────────────────────────────────────────
function CityPanel({ city, characters, armies, mages, dragons, kingdoms, onUpdate, onClose, onDelete, onMoveOut }) {
  const [name, setName]           = useState(city.name);
  const [allegiance, setAllegiance] = useState(city.allegiance || "");
  const [status, setStatus]       = useState(city.status || "normal");
  const [notes, setNotes]         = useState(city.notes || "");

  // Computed from linked entities
  const cityChars   = characters.filter(c => c.city === city.primary_key);
  const cityArmies  = armies.filter(a => a.city === city.primary_key);
  const cityMages   = mages.filter(m => m.city === city.primary_key);
  const cityDragons = dragons.filter(d => d.city === city.primary_key);
  const garrison    = cityArmies.reduce((sum, a) => sum + (a.size || 0), 0);

  return (
    <Panel title={`● ${city.name}`} onClose={onClose} accentColor={STATUS_COLORS[status] || "#22c55e"}>
      <Field label="Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label="Allegiance">
        <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
          {CITY_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </Field>

      {/* Computed */}
      <Field label="Garrison (computed)">
        <Badge label="Total troops" value={garrison.toLocaleString()} color="#2563eb" />
      </Field>

      <Field label={`Armies here (${cityArmies.length})`}>
        {cityArmies.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : cityArmies.map(a => <CityEntityRow key={a.primary_key} item={a} type="army" onDragOut={onMoveOut} />)
        }
      </Field>

      <Field label={`Characters here (${cityChars.length})`}>
        {cityChars.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : cityChars.sort((a, b) => b.skill - a.skill).map(c => <CityEntityRow key={c.primary_key} item={c} type="character" onDragOut={onMoveOut} />)
        }
      </Field>

      <Field label={`Mages here (${cityMages.length})`}>
        {cityMages.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : cityMages.sort((a, b) => calcDisciplinePowerForType(b.disciplines,"mage") - calcDisciplinePowerForType(a.disciplines,"mage")).map(m => <CityEntityRow key={m.primary_key} item={{...m, role: m.school}} type="mage" onDragOut={onMoveOut} />)
        }
      </Field>

      <Field label={`Dragons here (${cityDragons.length})`}>
        {cityDragons.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : cityDragons.sort((a, b) => calcDisciplinePowerForType(b.disciplines,"dragon") - calcDisciplinePowerForType(a.disciplines,"dragon")).map(d => <CityEntityRow key={d.primary_key} item={{...d, role: d.element}} type="dragon" onDragOut={onMoveOut} />)
        }
      </Field>

      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="Lore, description..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...city, name, allegiance, status, notes })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
    </Panel>
  );
}

// ── Kingdom Panel ─────────────────────────────────────────────────────────────
function KingdomPanel({ kingdom, characters, armies, cities, mages, dragons, onUpdate, onClose, onDelete, onEditBorders }) {
  const [name, setName]   = useState(kingdom.name);
  const [notes, setNotes] = useState(kingdom.notes || "");

  // Computed
  const kCities  = cities.filter(c => c.allegiance === kingdom.primary_key);
  const kArmies  = armies.filter(a => a.allegiance === kingdom.primary_key);
  const totalTroops = kArmies.reduce((sum, a) => sum + (a.size || 0), 0);
  const kChars   = characters.filter(c => c.allegiance === kingdom.primary_key);
  const kMages   = mages.filter(m => m.allegiance === kingdom.primary_key);
  const kDragons = dragons.filter(d => d.allegiance === kingdom.primary_key);

  const totalMagePower = kMages.reduce((sum, m) => sum + calcDisciplinePowerForType(m.disciplines, "mage"), 0);
  const totalDragonPower = kDragons.reduce((sum, d) => sum + calcDisciplinePowerForType(d.disciplines, "dragon"), 0);
  const totalMageDisc = kMages.reduce((sum, m) => sum + calcDisciplineLevels(m.disciplines), 0);
  const totalDragonDisc = kDragons.reduce((sum, d) => sum + calcDisciplineLevels(d.disciplines), 0);

  return (
    <Panel title={`▨ ${kingdom.name}`} onClose={onClose} accentColor={kingdom.color}>
      <Field label="Kingdom Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>

      <Field label="Summary">
        <Badge label="Cities" value={kCities.length} />
        <div style={{ height:4 }} />
        <Badge label="Armies" value={kArmies.length} color="#dc2626" />
        <div style={{ height:4 }} />
        <Badge label="Total Troops" value={totalTroops.toLocaleString()} color="#dc2626" />
        <div style={{ height:4 }} />
        <Badge label="Characters" value={kChars.length} color="#7c3aed" />
        <div style={{ height:4 }} />
        <Badge label="Mages" value={kMages.length} color="#6d28d9" />
        <div style={{ height:4 }} />
        <Badge label="Mage Power" value={`+${(totalMagePower * 100).toFixed(0)}%`} color="#6d28d9" />
        <div style={{ height:4 }} />
        <Badge label="Mage Discipline Levels" value={`🔮 ${totalMageDisc}`} color="#7c3aed" />
        <div style={{ height:4 }} />
        <Badge label="Dragons" value={kDragons.length} color="#b91c1c" />
        <div style={{ height:4 }} />
        <Badge label="Dragon Power" value={`+${(totalDragonPower * 100).toFixed(0)}%`} color="#b91c1c" />
        <div style={{ height:4 }} />
        <Badge label="Dragon Discipline Levels" value={`🔮 ${totalDragonDisc}`} color="#ea580c" />
      </Field>

      <Field label={`Cities (${kCities.length})`}>
        {kCities.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kCities.map(c => (
            <div key={c.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#3d3529", marginBottom:3 }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background: STATUS_COLORS[c.status] || "#9ca3af", display:"inline-block" }} />
              {c.name} <span style={{ fontSize:11, color:"#9ca3af" }}>({STATUS_LABELS[c.status]})</span>
            </div>
          ))
        }
      </Field>

      <Field label={`Armies (${kArmies.length})`}>
        {kArmies.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kArmies.map(a => (
            <div key={a.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#3d3529", marginBottom:3 }}>
              <span style={{ width:10, height:10, background:a.color, display:"inline-block", borderRadius:2, transform:"rotate(45deg)", flexShrink:0 }} />
              {a.name}{a.size ? ` — ${a.size.toLocaleString()}` : ""}
            </div>
          ))
        }
      </Field>

      <Field label={`Characters (${kChars.length})`}>
        {kChars.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kChars.sort((a, b) => b.skill - a.skill).map(c => (
            <div key={c.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#3d3529", marginBottom:3 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:c.color, display:"inline-block", flexShrink:0 }} />
              {c.name}{c.role ? ` · ${c.role}` : ""}
            </div>
          ))
        }
      </Field>

      <Field label={`Mages (${kMages.length})`}>
        {kMages.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kMages.sort((a, b) => b.skill - a.skill).map(m => (
            <div key={m.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#3d3529", marginBottom:3 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:m.color, display:"inline-block", flexShrink:0 }} />
              {m.name}{m.school ? ` · ${m.school}` : ""}
            </div>
          ))
        }
      </Field>

      <Field label={`Dragons (${kDragons.length})`}>
        {kDragons.sort((a, b) => b.skill - a.skill).length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kDragons.map(d => (
            <div key={d.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#3d3529", marginBottom:3 }}>
              <span style={{ width:0, height:0, borderLeft:"5px solid transparent", borderRight:"5px solid transparent", borderBottom:`10px solid ${d.color}`, display:"inline-block", flexShrink:0 }} />
              {d.name}{d.element ? ` · ${d.element}` : ""}
            </div>
          ))
        }
      </Field>

      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="History, lore..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...kingdom, name, notes })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
      <button onClick={() => onEditBorders(kingdom)}
        style={{ background:"#7c3aed", color:"#fff", border:"none", borderRadius:5, padding:"6px 12px", cursor:"pointer", fontSize:13, fontWeight:500, width:"100%", marginTop:4 }}>
        ✏ Edit Borders
      </button>
    </Panel>
  );
}

// ── Character Panel ───────────────────────────────────────────────────────────
function CharacterPanel({ character, cities, kingdoms, armies, onUpdate, onClose, onDelete }) {
  const [name, setName]           = useState(character.name);
  const [role, setRole]           = useState(character.role || "");
  const [allegiance, setAllegiance] = useState(character.allegiance || "");
  const [army, setArmy]           = useState(character.army || "");
  const [skill, setSkill]         = useState(character.skill || 5);
  const [notes, setNotes]         = useState(character.notes || "");
  const [color, setColor]         = useState(character.color || CHAR_COLORS[0]);
  const [cityVal, setCityVal]     = useState(character.city || "");

  // location display
  const locationDesc = character.city
    ? `In ${cities.find(c => c.primary_key === character.city)?.name || character.city}`
    : character.coordinates ? `Map (${character.coordinates})` : "Unknown";

  return (
    <Panel title={`👤 ${character.name}`} onClose={onClose} accentColor={character.color}>
      <Field label="Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label="Role"><FInput value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. noble, warrior..." /></Field>
      <Field label="Allegiance">
        <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
        </select>
      </Field>
      <Field label="Army (if general)">
        <select value={army} onChange={e => setArmy(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {armies.map(a => <option key={a.primary_key} value={a.primary_key}>{a.name}</option>)}
        </select>
      </Field>
      <Field label="City">
        <select value={cityVal} onChange={e => setCityVal(e.target.value)} style={selectStyle}>
          <option value="">— On map —</option>
          {cities.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Current Location">
        <Badge label="Location" value={locationDesc} />
      </Field>
      <Field label={`Skill (${skill} — ${SKILL_LABELS[skill]})`}>
        <input type="range" min={1} max={12} value={skill} onChange={e => setSkill(parseInt(e.target.value))} style={{ width:"100%" }} />
      </Field>
      <Field label="Color">
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          {CHAR_COLORS.map(c => (
            <div key={c} onClick={() => setColor(c)} style={{
              width:20, height:20, borderRadius:"50%", background:c, cursor:"pointer",
              border: color === c ? "2px solid #111" : "2px solid transparent",
            }} />
          ))}
        </div>
      </Field>
      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="Background, goals..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...character, name, role, allegiance, army, skill, notes, color, city: cityVal })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
    </Panel>
  );
}

// ── Army Panel ────────────────────────────────────────────────────────────────
function ArmyPanel({ army, characters, cities, kingdoms, mages, dragons, onUpdate, onClose, onDelete, onMoveOut }) {
  const [name, setName]           = useState(army.name);
  const [allegiance, setAllegiance] = useState(army.allegiance || "");
  const [general, setGeneral]     = useState(army.general || "");
  const [size, setSize]           = useState(army.size || 0);
  const [bonus, setBonus]         = useState(army.bonus || 0);
  const [notes, setNotes]         = useState(army.notes || "");
  const [color, setColor]         = useState(army.color || ARMY_COLORS[0]);
  const [cityVal, setCityVal]     = useState(army.city || "");

  const locationDesc = army.city
    ? `In ${cities.find(c => c.primary_key === army.city)?.name || army.city}`
    : army.coordinates ? `Map (${army.coordinates})` : "Unknown";

  const armyChars = characters.filter(c => c.army === army.primary_key);
  const armyMages = mages.filter(m => m.army === army.primary_key);
  const armyDragons = dragons.filter(d => d.army === army.primary_key);

  return (
    <Panel title={`⚔ ${army.name}`} onClose={onClose} accentColor={army.color}>
      <Field label="Army Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label="Size">
        <FInput value={size} onChange={e => setSize(parseInt(e.target.value) || 0)} type="number" />
      </Field>
      <Field label="Army Bonus (%)">
        <FInput
          value={Math.round((bonus || 0) * 100)}
          onChange={e => setBonus((parseFloat(e.target.value) || 0) / 100)}
          type="number" min="0" placeholder="e.g. 20 for +20%" />
      </Field>
      <Field label="Allegiance">
        <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
        </select>
      </Field>
      <Field label="General">
        <select value={general} onChange={e => setGeneral(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {characters.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="City">
        <select value={cityVal} onChange={e => setCityVal(e.target.value)} style={selectStyle}>
          <option value="">— On map —</option>
          {cities.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Current Location">
        <Badge label="Location" value={locationDesc} />
      </Field>
      <Field label="Token Color">
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          {ARMY_COLORS.map(c => (
            <div key={c} onClick={() => setColor(c)} style={{
              width:20, height:20, borderRadius:3, background:c, cursor:"pointer",
              border: color === c ? "2px solid #111" : "2px solid transparent",
            }} />
          ))}
        </div>
      </Field>
      <Field label={`Characters (${armyChars.length})`}>
        {armyChars.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None enlisted</span>
          : armyChars.sort((a, b) => b.skill - a.skill).map(c => <CityEntityRow key={c.primary_key} item={c} type="character" onDragOut={(id) => onMoveOut(id, "fromArmy")} />)
        }
      </Field>
      <Field label={`Mages (${armyMages.length})`}>
        {armyMages.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : armyMages.sort((a, b) => calcDisciplinePowerForType(b.disciplines,"mage") - calcDisciplinePowerForType(a.disciplines,"mage")).map(m => <CityEntityRow key={m.primary_key} item={{...m, role: m.school}} type="mage" onDragOut={(id) => onMoveOut(id, "fromArmyMage")} />)
        }
      </Field>
      <Field label={`Dragons (${armyDragons.length})`}>
        {armyDragons.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : armyDragons.sort((a, b) => calcDisciplinePowerForType(b.disciplines,"dragon") - calcDisciplinePowerForType(a.disciplines,"dragon")).map(d => <CityEntityRow key={d.primary_key} item={{...d, role: d.element}} type="dragon" onDragOut={(id) => onMoveOut(id, "fromArmyDragon")} />)
        }
      </Field>
      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="Composition, notes..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...army, name, allegiance, general, size, notes, color, city: cityVal, bonus: bonus || undefined })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
    </Panel>
  );
}

// ── Shared modal backdrop + card shell for Mage/Dragon ───────────────────────
// Check if viewport is mobile-sized (used by modal components)
function isMobileView() { return typeof window !== "undefined" && window.innerWidth <= 768; }

function EntityModal({ onClose, tier, children }) {
  const mobile = isMobileView();
  const glowAnim = tier.animBorder ? {
    "--tier-glow": tier.color + "88",
    "--tier-glow-in": tier.color + "18",
    animation: tier.key === "celestial"
      ? "borderGlow 2.2s ease-in-out infinite"
      : "borderGlow 3s ease-in-out infinite",
  } : {};

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0,
      background: tier.key === "celestial"
        ? "radial-gradient(ellipse at center, #1a140044 0%, #00000088 100%)"
        : tier.key === "legendary"
        ? "radial-gradient(ellipse at center, #1c090022 0%, #00000088 100%)"
        : "#00000077",
      zIndex:100, display:"flex", alignItems: mobile ? "stretch" : "center", justifyContent:"center",
      backdropFilter:"blur(2px)",
      overflowY: mobile ? "auto" : "hidden",
    }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: mobile ? "100%" : "min(960px, 96vw)",
          maxHeight: mobile ? "none" : "90vh",
          minHeight: mobile ? "100vh" : "auto",
          background: tier.gradient,
          borderRadius: mobile ? 0 : 16,
          border: mobile ? "none" : `${tier.borderWidth}px solid ${tier.border}88`,
          boxShadow: tier.glowSize > 0
            ? `0 0 ${tier.glowSize * 2}px ${tier.color}66, 0 0 ${tier.glowSize * 4}px ${tier.color}22, 0 32px 80px #000c`
            : "0 24px 80px #000a",
          display:"flex", flexDirection:"column",
          overflow: mobile ? "visible" : "hidden",
          ...glowAnim,
        }}
      >
        {/* Animated top-edge sweep for celestial/legendary */}
        {tier.animBorder && (
          <div style={{
            height:2,
            background: tier.barStyle,
            backgroundSize:"300% 100%",
            animation:"borderSweep 2s linear infinite",
            flexShrink:0,
          }} />
        )}
        {children}
      </div>
    </div>
  );
}

// ── Mage Modal ──────────────────────────────────────────────────────────────
function MagePanel({ mage, cities, kingdoms, armies, allMages, allDragons, onUpdate, onClose, onDelete }) {
  const [name, setName]               = useState(mage.name);
  const [allegiance, setAllegiance]   = useState(mage.allegiance || "");
  const [army, setArmy]               = useState(mage.army || "");
  const [cityVal, setCityVal]         = useState(mage.city || "");
  const [notes, setNotes]             = useState(mage.notes || "");
  const [color, setColor]             = useState(mage.color || MAGE_COLORS[0]);
  const [artifacts, setArtifacts]     = useState(mage.artifacts || []);
  const [disciplines, setDisciplines] = useState(mage.disciplines || {});
  const [fatigue, setFatigue]         = useState(mage.fatigue || 0);
  const [learnedAbilities, setLearnedAbilities] = useState(mage.learnedAbilities || {});
  const [abilityMastery, setAbilityMastery] = useState(mage.abilityMastery || {});
  const [primaryDiscipline, setPrimaryDiscipline] = useState(mage.primaryDiscipline || "");

  // Build a "live" entity object that AbilitiesEditor can use
  const liveEntity = { ...mage, disciplines, artifacts, learnedAbilities, abilityMastery, primaryDiscipline };

  const totalPower = calcDisciplinePowerForType(disciplines, "mage", artifacts, fatigue);
  const basePower  = calcDisciplinePowerForType(disciplines, "mage");
  const peak       = peakDiscipline(disciplines, primaryDiscipline);
  const tier       = getPowerTier(basePower);
  const baseNorm   = normalizePower(basePower);
  const actualNorm = normalizePower(totalPower);
  const actualDiff = actualNorm - baseNorm;
  const fatigueColor = fatigue <= 0 ? "#22c55e" : fatigue <= 0.3 ? "#86efac" : fatigue <= 0.6 ? "#f59e0b" : "#ef4444";

  const globalEquippedIds = [
    ...(allMages || []).filter(m => m.primary_key !== mage.primary_key),
    ...(allDragons || []),
  ].flatMap(e => (Array.isArray(e.artifacts) ? e.artifacts : []).map(a => a?.id).filter(Boolean));

  const locationDesc = mage.city
    ? `In ${cities.find(c => c.primary_key === mage.city)?.name || mage.city}`
    : mage.army ? `In ${armies.find(a => a.primary_key === mage.army)?.name || mage.army}`
    : mage.coordinates ? `On map` : "Unknown";

  const darkInput = { ...inputStyle, background:"#1f2937", border:"1px solid #374151", color:"#f9fafb" };
  const darkSelect = { ...darkInput, cursor:"pointer" };
  const darkLabel = { fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3, display:"block" };

  return (
    <EntityModal onClose={onClose} tier={tier}>
      {/* ── Header bar ── */}
      <div style={{ background:tier.headerGrad, padding: isMobileView() ? "14px 16px" : "18px 24px", display:"flex", alignItems:"center", gap: isMobileView() ? 10 : 16, flexShrink:0, borderBottom:`1px solid ${tier.color}33`, flexWrap: isMobileView() ? "wrap" : "nowrap" }}>
        {/* Avatar */}
        <div style={{ position:"relative", flexShrink:0 }}>
          <div style={{
            width: isMobileView() ? 40 : 52, height: isMobileView() ? 40 : 52, borderRadius:"50%", background:color,
            border:`3px solid ${tier.color}66`,
            display:"flex", alignItems:"center", justifyContent:"center", fontSize: isMobileView() ? 18 : 24,
            boxShadow: tier.glowSize > 0 ? `0 0 ${tier.glowSize}px ${tier.color}88` : "none",
          }}>✦</div>
          <div style={{
            position:"absolute", bottom:-3, right:-3, fontSize:13,
            filter: tier.glowSize > 0 ? `drop-shadow(0 0 4px ${tier.color})` : "none",
          }}>{tier.ornament}</div>
        </div>
        {/* Name + subtitle */}
        <div style={{ flex:1, minWidth:0 }}>
          <input value={name} onChange={e => setName(e.target.value)}
            style={{
              background:"transparent", border:"none", outline:"none",
              fontSize: isMobileView() ? 16 : 22, fontWeight:800, color:tier.textColor, width:"100%", padding:0,
              letterSpacing: tier.key === "celestial" ? 1 : 0,
              textShadow: tier.glowSize > 0 ? `0 0 20px ${tier.color}66` : "none",
            }} />
          <div style={{ fontSize: isMobileView() ? 10 : 11, color:tier.subColor, marginTop:3, letterSpacing:tier.letterSpacing * 0.5 }}>
            {locationDesc} · {tier.label.toUpperCase()} MAGE
          </div>
        </div>
        {/* Power readout */}
        <div style={{ textAlign:"right", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"baseline", gap:4, justifyContent:"flex-end" }}>
            <span style={{
              fontSize: isMobileView() ? 28 : 44, fontWeight:900, color:tier.color, lineHeight:1,
              textShadow: tier.glowSize > 0 ? `0 0 ${tier.glowSize * 2}px ${tier.color}` : "none",
            }}>{baseNorm}</span>
            <span style={{ fontSize: isMobileView() ? 10 : 13, color:tier.subColor, fontWeight:600 }}>PWR</span>
          </div>
          {actualDiff !== 0 && (
            <div style={{ fontSize: isMobileView() ? 10 : 12, color: actualDiff > 0 ? "#4ade80" : "#f87171", fontWeight:700 }}>
              {actualDiff > 0 ? "+" : ""}{actualDiff} → {actualNorm} actual
            </div>
          )}
          <div style={{ fontSize:10, color:tier.subColor, marginTop:1 }}>+{(basePower*100).toFixed(0)}% base</div>
        </div>
        <button onClick={onClose} style={{
          background:"none", border:"none", cursor:"pointer", fontSize:20,
          color:tier.textColor + "66", flexShrink:0, padding:"0 0 0 8px",
          transition:"color 0.15s",
        }}>✕</button>
      </div>

      {/* ── Body: two columns (stacks on mobile) ── */}
      <div style={{ display:"flex", flex: isMobileView() ? "none" : 1, overflow: isMobileView() ? "visible" : "hidden", flexDirection: isMobileView() ? "column" : "row" }}>

        {/* LEFT: disciplines + fatigue */}
        <div style={{ width: isMobileView() ? "100%" : 340, borderRight: isMobileView() ? "none" : "1px solid #1f2937", borderBottom: isMobileView() ? "1px solid #1f2937" : "none", padding: isMobileView() ? "16px" : "20px 20px", overflowY: isMobileView() ? "visible" : "auto", flexShrink:0, display:"flex", flexDirection:"column", gap:16 }}>
          {/* Fatigue */}
          <div>
            <label style={darkLabel}>Fatigue</label>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <input type="range" min={0} max={100} value={Math.round(fatigue*100)}
                onChange={e => setFatigue(parseInt(e.target.value)/100)}
                style={{ flex:1, accentColor: fatigueColor }} />
              <span style={{ fontSize:13, fontWeight:700, color:fatigueColor, minWidth:36, textAlign:"right" }}>{Math.round(fatigue*100)}%</span>
            </div>
            <div style={{ fontSize:10, color:"#6b7280" }}>
              {fatigue <= 0 ? "Fresh — full power" : fatigue <= 0.5 ? `Tired (${Math.round(fatigue*100)}%) — full power` : fatigue >= 1 ? "Exhausted — no power" : `Power at ${Math.round((1 - (fatigue - 0.5) / 0.5) * 100)}%`}
            </div>
          </div>

          {/* Primary Discipline */}
          <div>
            <label style={darkLabel}>Primary Discipline <span style={{ fontSize:8, color:"#f59e0b" }}>★ Full SP — others get ½</span></label>
            <select value={primaryDiscipline} onChange={e => setPrimaryDiscipline(e.target.value)}
              style={{ ...inputStyle, background:"#1f2937", border:"1px solid #374151", color:"#f9fafb", cursor:"pointer" }}>
              <option value="">— Auto (highest level) —</option>
              {MAGIC_DISCIPLINES.filter(d => (disciplines[d.key] || 0) > 0).map(d => (
                <option key={d.key} value={d.key}>{d.icon} {d.label} (Lv.{disciplines[d.key] || 0})</option>
              ))}
            </select>
          </div>

          {/* Disciplines */}
          <div>
            <label style={darkLabel}>Magic Disciplines</label>
            <DisciplinesEditor disciplines={disciplines} onChange={setDisciplines} entityType="mage" artifacts={artifacts} disciplineXP={mage.disciplineXP} />
          </div>

          {/* Abilities */}
          <div>
            <label style={darkLabel}>Abilities</label>
            <AbilitiesEditor entity={liveEntity} onUpdate={(updated) => {
              setLearnedAbilities(updated.learnedAbilities || {});
              setAbilityMastery(updated.abilityMastery || {});
            }} />
          </div>
        </div>

        {/* RIGHT: artifacts + metadata */}
        <div style={{ flex:1, padding: isMobileView() ? "16px" : "20px 20px", overflowY: isMobileView() ? "visible" : "auto", display:"flex", flexDirection:"column", gap:14 }}>
          {/* Artifacts */}
          <div>
            <label style={darkLabel}>Artifacts</label>
            <ArtifactsField artifacts={artifacts} onChange={setArtifacts} globalEquippedIds={globalEquippedIds} />
          </div>

          {/* Meta fields */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <label style={darkLabel}>Allegiance</label>
              <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={darkSelect}>
                <option value="">— None —</option>
                {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
              </select>
            </div>
            <div>
              <label style={darkLabel}>Army</label>
              <select value={army} onChange={e => setArmy(e.target.value)} style={darkSelect}>
                <option value="">— None —</option>
                {armies.map(a => <option key={a.primary_key} value={a.primary_key}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label style={darkLabel}>City</label>
              <select value={cityVal} onChange={e => setCityVal(e.target.value)} style={darkSelect}>
                <option value="">— On map —</option>
                {cities.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={darkLabel}>Token Color</label>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", paddingTop:4 }}>
                {MAGE_COLORS.map(c => (
                  <div key={c} onClick={() => setColor(c)} style={{
                    width:20, height:20, borderRadius:"50%", background:c, cursor:"pointer",
                    border: color === c ? "2px solid #f9fafb" : "2px solid transparent",
                  }} />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label style={darkLabel}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              style={{ ...darkInput, resize:"vertical" }} placeholder="Spells, background..." />
          </div>

          {/* Action buttons */}
          <div style={{ display:"flex", gap:8, marginTop:"auto", paddingTop:8 }}>
            <button onClick={() => onUpdate({ ...mage, name, allegiance, army, notes, color, city: cityVal, artifacts: artifacts?.length > 0 ? artifacts : undefined, disciplines, fatigue, learnedAbilities: Object.keys(learnedAbilities).length > 0 ? learnedAbilities : undefined, abilityMastery: Object.keys(abilityMastery).length > 0 ? abilityMastery : undefined, primaryDiscipline: primaryDiscipline || undefined })}
              style={{ flex:1, background:"#3b82f6", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              Save
            </button>
            <button onClick={onDelete}
              style={{ background:"#1f2937", color:"#ef4444", border:"1px solid #ef444444", borderRadius:8, padding:"10px 18px", fontSize:14, cursor:"pointer" }}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </EntityModal>
  );
}

// ── Dragon Modal ───────────────────────────────────────────────────────────
function DragonPanel({ dragon, cities, kingdoms, armies, characters, mages, allDragons, onUpdate, onClose, onDelete }) {
  const [name, setName]               = useState(dragon.name);
  const [element, setElement]         = useState(dragon.element || "");
  const [dragonSize, setDragonSize]   = useState(dragon.dragonSize || "Adult");
  const [allegiance, setAllegiance]   = useState(dragon.allegiance || "");
  const [rider, setRider]             = useState(dragon.rider || "");
  const [army, setArmy]               = useState(dragon.army || "");
  const [cityVal, setCityVal]         = useState(dragon.city || "");
  const [notes, setNotes]             = useState(dragon.notes || "");
  const [color, setColor]             = useState(dragon.color || DRAGON_COLORS[0]);
  const [artifacts, setArtifacts]     = useState(dragon.artifacts || []);
  const [disciplines, setDisciplines] = useState(dragon.disciplines || {});
  const [fatigue, setFatigue]         = useState(dragon.fatigue || 0);
  const [learnedAbilities, setLearnedAbilities] = useState(dragon.learnedAbilities || {});
  const [abilityMastery, setAbilityMastery] = useState(dragon.abilityMastery || {});
  const [primaryDiscipline, setPrimaryDiscipline] = useState(dragon.primaryDiscipline || "");

  const liveEntity = { ...dragon, disciplines, artifacts, learnedAbilities, abilityMastery, primaryDiscipline };

  const totalPower = calcDisciplinePowerForType(disciplines, "dragon", artifacts, fatigue);
  const basePower  = calcDisciplinePowerForType(disciplines, "dragon");
  const peak       = peakDiscipline(disciplines, primaryDiscipline);
  const tier       = getPowerTier(basePower);
  const baseNorm   = normalizePower(basePower);
  const actualNorm = normalizePower(totalPower);
  const actualDiff = actualNorm - baseNorm;
  const fatigueColor = fatigue <= 0 ? "#22c55e" : fatigue <= 0.3 ? "#86efac" : fatigue <= 0.6 ? "#f59e0b" : "#ef4444";

  const riderOptions = [...characters, ...mages];
  const globalEquippedIds = [
    ...(mages || []),
    ...(allDragons || []).filter(d => d.primary_key !== dragon.primary_key),
  ].flatMap(e => (Array.isArray(e.artifacts) ? e.artifacts : []).map(a => a?.id).filter(Boolean));

  const locationDesc = dragon.city
    ? `In ${cities.find(c => c.primary_key === dragon.city)?.name || dragon.city}`
    : dragon.army ? `In ${armies.find(a => a.primary_key === dragon.army)?.name || dragon.army}`
    : dragon.coordinates ? `On map` : "Unknown";

  const darkInput  = { ...inputStyle, background:"#1f2937", border:"1px solid #374151", color:"#f9fafb" };
  const darkSelect = { ...darkInput, cursor:"pointer" };
  const darkLabel  = { fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3, display:"block" };

  return (
    <EntityModal onClose={onClose} tier={tier}>
      {/* ── Header bar ── */}
      <div style={{ background:tier.headerGrad, padding:"18px 24px", display:"flex", alignItems:"center", gap:16, flexShrink:0, borderBottom:`1px solid ${tier.color}33` }}>
        <div style={{ position:"relative", flexShrink:0 }}>
          <div style={{
            width:52, height:52, borderRadius:8, background:color,
            border:`3px solid ${tier.color}66`,
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:24,
            transform:"rotate(45deg)",
            boxShadow: tier.glowSize > 0 ? `0 0 ${tier.glowSize}px ${tier.color}88` : "none",
          }}>
            <span style={{ transform:"rotate(-45deg)" }}>🐉</span>
          </div>
          <div style={{
            position:"absolute", bottom:-3, right:-3, fontSize:13,
            filter: tier.glowSize > 0 ? `drop-shadow(0 0 4px ${tier.color})` : "none",
          }}>{tier.ornament}</div>
        </div>
        <div style={{ flex:1 }}>
          <input value={name} onChange={e => setName(e.target.value)}
            style={{
              background:"transparent", border:"none", outline:"none",
              fontSize:22, fontWeight:800, color:tier.textColor, width:"100%", padding:0,
              letterSpacing: tier.key === "celestial" ? 1 : 0,
              textShadow: tier.glowSize > 0 ? `0 0 20px ${tier.color}66` : "none",
            }} />
          <div style={{ fontSize:11, color:tier.subColor, marginTop:3, letterSpacing:tier.letterSpacing * 0.5 }}>
            {locationDesc} · {dragonSize} {tier.label.toUpperCase()} DRAGON{element ? ` · ${element}` : ""}
          </div>
        </div>
        <div style={{ textAlign:"right", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"baseline", gap:4, justifyContent:"flex-end" }}>
            <span style={{
              fontSize:44, fontWeight:900, color:tier.color, lineHeight:1,
              textShadow: tier.glowSize > 0 ? `0 0 ${tier.glowSize * 2}px ${tier.color}` : "none",
            }}>{baseNorm}</span>
            <span style={{ fontSize:13, color:tier.subColor, fontWeight:600 }}>PWR</span>
          </div>
          {actualDiff !== 0 && (
            <div style={{ fontSize:12, color: actualDiff > 0 ? "#4ade80" : "#f87171", fontWeight:700 }}>
              {actualDiff > 0 ? "+" : ""}{actualDiff} → {actualNorm} actual
            </div>
          )}
          <div style={{ fontSize:10, color:tier.subColor, marginTop:1 }}>+{(basePower*100).toFixed(0)}% base</div>
        </div>
        <button onClick={onClose} style={{
          background:"none", border:"none", cursor:"pointer", fontSize:20,
          color:tier.textColor + "66", flexShrink:0, padding:"0 0 0 8px",
        }}>✕</button>
      </div>

      {/* ── Body: two columns (stacks on mobile) ── */}
      <div style={{ display:"flex", flex: isMobileView() ? "none" : 1, overflow: isMobileView() ? "visible" : "hidden", flexDirection: isMobileView() ? "column" : "row" }}>

        {/* LEFT: disciplines + fatigue */}
        <div style={{ width: isMobileView() ? "100%" : 340, borderRight: isMobileView() ? "none" : "1px solid #1f2937", borderBottom: isMobileView() ? "1px solid #1f2937" : "none", padding: isMobileView() ? "16px" : "20px 20px", overflowY: isMobileView() ? "visible" : "auto", flexShrink:0, display:"flex", flexDirection:"column", gap:16 }}>
          <div>
            <label style={darkLabel}>Fatigue</label>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <input type="range" min={0} max={100} value={Math.round(fatigue*100)}
                onChange={e => setFatigue(parseInt(e.target.value)/100)}
                style={{ flex:1, accentColor: fatigueColor }} />
              <span style={{ fontSize:13, fontWeight:700, color:fatigueColor, minWidth:36, textAlign:"right" }}>{Math.round(fatigue*100)}%</span>
            </div>
            <div style={{ fontSize:10, color:"#6b7280" }}>
              {fatigue <= 0 ? "Fresh — full power" : fatigue <= 0.5 ? `Tired (${Math.round(fatigue*100)}%) — full power` : fatigue >= 1 ? "Exhausted — no power" : `Power at ${Math.round((1 - (fatigue - 0.5) / 0.5) * 100)}%`}
            </div>
          </div>

          <div>
            <label style={darkLabel}>Primary Discipline <span style={{ fontSize:8, color:"#f59e0b" }}>★ Full SP — others get ½</span></label>
            <select value={primaryDiscipline} onChange={e => setPrimaryDiscipline(e.target.value)}
              style={{ ...inputStyle, background:"#1f2937", border:"1px solid #374151", color:"#f9fafb", cursor:"pointer" }}>
              <option value="">— Auto (highest level) —</option>
              {MAGIC_DISCIPLINES.filter(d => (disciplines[d.key] || 0) > 0).map(d => (
                <option key={d.key} value={d.key}>{d.icon} {d.label} (Lv.{disciplines[d.key] || 0})</option>
              ))}
            </select>
          </div>

          <div>
            <label style={darkLabel}>Magic Disciplines</label>
            <DisciplinesEditor disciplines={disciplines} onChange={setDisciplines} entityType="dragon" artifacts={artifacts} disciplineXP={dragon.disciplineXP} />
          </div>

          {/* Abilities */}
          <div>
            <label style={darkLabel}>Abilities</label>
            <AbilitiesEditor entity={liveEntity} onUpdate={(updated) => {
              setLearnedAbilities(updated.learnedAbilities || {});
              setAbilityMastery(updated.abilityMastery || {});
            }} />
          </div>
        </div>

        {/* RIGHT: artifacts + metadata */}
        <div style={{ flex:1, padding: isMobileView() ? "16px" : "20px 20px", overflowY: isMobileView() ? "visible" : "auto", display:"flex", flexDirection:"column", gap:14 }}>
          <div>
            <label style={darkLabel}>Artifacts</label>
            <ArtifactsField artifacts={artifacts} onChange={setArtifacts} globalEquippedIds={globalEquippedIds} />
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <label style={darkLabel}>Element / Type</label>
              <input value={element} onChange={e => setElement(e.target.value)}
                placeholder="fire, ice, shadow..." style={darkInput} />
            </div>
            <div>
              <label style={darkLabel}>Size</label>
              <select value={dragonSize} onChange={e => setDragonSize(e.target.value)} style={darkSelect}>
                {DRAGON_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={darkLabel}>Allegiance</label>
              <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={darkSelect}>
                <option value="">— Wild —</option>
                {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
              </select>
            </div>
            <div>
              <label style={darkLabel}>Rider</label>
              <select value={rider} onChange={e => setRider(e.target.value)} style={darkSelect}>
                <option value="">— None —</option>
                {riderOptions.map(r => <option key={r.primary_key} value={r.primary_key}>{r.name}{r.sort_key==="Mage"?" (Mage)":""}</option>)}
              </select>
            </div>
            <div>
              <label style={darkLabel}>Army</label>
              <select value={army} onChange={e => setArmy(e.target.value)} style={darkSelect}>
                <option value="">— None —</option>
                {armies.map(a => <option key={a.primary_key} value={a.primary_key}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label style={darkLabel}>City</label>
              <select value={cityVal} onChange={e => setCityVal(e.target.value)} style={darkSelect}>
                <option value="">— On map —</option>
                {cities.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={darkLabel}>Token Color</label>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", paddingTop:4 }}>
                {DRAGON_COLORS.map(c => (
                  <div key={c} onClick={() => setColor(c)} style={{
                    width:20, height:20, borderRadius:3, background:c, cursor:"pointer",
                    border: color === c ? "2px solid #f9fafb" : "2px solid transparent",
                  }} />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label style={darkLabel}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              style={{ ...darkInput, resize:"vertical" }} placeholder="Lore, abilities..." />
          </div>

          <div style={{ display:"flex", gap:8, marginTop:"auto", paddingTop:8 }}>
            <button onClick={() => onUpdate({ ...dragon, name, element, dragonSize, allegiance, rider, army, notes, color, city: cityVal, artifacts: artifacts?.length > 0 ? artifacts : undefined, disciplines, fatigue, learnedAbilities: Object.keys(learnedAbilities).length > 0 ? learnedAbilities : undefined, abilityMastery: Object.keys(abilityMastery).length > 0 ? abilityMastery : undefined, primaryDiscipline: primaryDiscipline || undefined })}
              style={{ flex:1, background:"#3b82f6", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              Save
            </button>
            <button onClick={onDelete}
              style={{ background:"#1f2937", color:"#ef4444", border:"1px solid #ef444444", borderRadius:8, padding:"10px 18px", fontSize:14, cursor:"pointer" }}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </EntityModal>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function T3() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 600 });
  const [tool, setTool]           = useState("select");
  const [selectedColor, setSelectedColor] = useState(KINGDOM_COLORS[2]);

  // Schema-aligned state
  // Kingdom:   { primary_key, sort_key:"Kingdom", name, color, points:[[x,y],...], notes }
  // City:      { primary_key, sort_key:"City", name, allegiance, status, coordinates:"x,y", notes, color(ui) }
  // Character: { primary_key, sort_key:"Character", name, role, allegiance, army, city, coordinates, notes, color }
  // Army:      { primary_key, sort_key:"army#generalName", name, allegiance, city, coordinates, size, general, notes, color }
  const [kingdoms,   setKingdoms]   = useState([]);
  const [cities,     setCities]     = useState([]);
  const [characters, setCharacters] = useState([]);
  const [armies,     setArmies]     = useState([]);
  const [mages,      setMages]      = useState([]);
  const [dragons,    setDragons]    = useState([]);

  const [drawingPoints, setDrawingPoints] = useState([]);
  const [panel, setPanel]           = useState(null); // { type, id }

  // API state
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showBattle, setShowBattle] = useState(false);
  const [showDuel, setShowDuel] = useState(false);
  const [showPowerRankings, setShowPowerRankings] = useState(false);
  const [showFatigueManager, setShowFatigueManager] = useState(false);
  const [showGearModal, setShowGearModal] = useState(false);
  const [showReadiness, setShowReadiness] = useState(false);
  const [showWars, setShowWars] = useState(false);
  const [wars, setWars] = useState([]);  // [{ id, kingdomA, kingdomB, scoreA, scoreB, duels:[], createdAt }]
  const [showLeagues, setShowLeagues] = useState(false);
  const [leagues, setLeagues] = useState([]);  // [{ primary_key, sort_key:"League", name, icon, members:[{pk,points,wins,losses,streak,history,exams}], duels:[], alliances:[{name,icon,members:[pk]}], createdAt }]
  const [sidebarSearch, setSidebarSearch] = useState("");

  // ── Mobile responsiveness ──────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const touchState = useRef({ lastDist: null, lastCenter: null, isPanning: false, startPan: null, startTouch: null, didMove: false, longPressTimer: null });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ── Initial data load ─────────────────────────────────────────────────────
  useEffect(() => {
    apiGet()
      .then(data => {
        setKingdoms(data.kingdoms   || []);
        setCities(data.cities       || []);
        setCharacters(data.characters || []);
        setArmies(data.armies       || []);
        setMages(data.mages         || []);
        setDragons(data.dragons     || []);
        setWars(data.wars           || []);
        setLeagues(data.leagues     || []);
      })
      .catch(err => setApiError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Modal state: pending placement
  const [modal, setModal] = useState(null); // { type, x, y } — position queued, waiting for name

  // dragging tokens
  const [dragging, setDragging]     = useState(null); // { type:"character"|"army"|"mage"|"dragon", id }
  const [dragPos,  setDragPos]      = useState(null);

  // kingdom border editing
  const [editingKingdom, setEditingKingdom] = useState(null); // { id, points: [[x,y],...] }
  const [draggingVertex, setDraggingVertex] = useState(null); // index into editingKingdom.points

  // context menu
  const [contextMenu, setContextMenu] = useState(null); // { x, y, wx, wy, entity? }

  const [pan,  setPan]  = useState({ x:0, y:0 });
  const [zoom, setZoom] = useState(1);
  const isPanning  = useRef(false);
  const panStart   = useRef(null);
  const panOrigin  = useRef(null);
  const didPan     = useRef(false);

  const getSVGPoint = useCallback((e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return [(e.clientX - rect.left - pan.x) / zoom, (e.clientY - rect.top - pan.y) / zoom];
  }, [pan, zoom]);

  // Which army token is at map coords (wx,wy)?
  const armyAt = useCallback((wx, wy) => {
    for (const army of armies)
      if (army.coordinates) {
        const { x, y } = parseCoord(army.coordinates);
        if (Math.abs(wx - x) < 16 && Math.abs(wy - y) < 16) return army;
      }
    return null;
  }, [armies]);

  // Which mage is at map coords?
  const mageAt = useCallback((wx, wy) => {
    for (const m of mages)
      if (m.coordinates) {
        const { x, y } = parseCoord(m.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) return m;
      }
    return null;
  }, [mages]);

  // Which dragon is at map coords?
  const dragonAt = useCallback((wx, wy) => {
    for (const d of dragons)
      if (d.coordinates) {
        const { x, y } = parseCoord(d.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) return d;
      }
    return null;
  }, [dragons]);

  // Which city is at map coords (wx,wy)?
  const cityAt = useCallback((wx, wy) => {
    for (const city of cities)
      if (city.coordinates) {
        const { x, y } = parseCoord(city.coordinates);
        if (Math.abs(wx - x) < 20 && Math.abs(wy - y) < 20) return city;
      }
    return null;
  }, [cities]);

  // Which kingdom polygon contains (wx,wy)?
  const kingdomAt = useCallback((wx, wy) => {
    for (let i = kingdoms.length - 1; i >= 0; i--)
      if (kingdoms[i].points && pointInPolygon(wx, wy, kingdoms[i].points)) return kingdoms[i];
    return null;
  }, [kingdoms]);

  // ── Pan / zoom ────────────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    setContextMenu(null); // close context menu on any click
    setShowMobileMenu(false); // close mobile menu
    if (e.button === 1 || (e.button === 0 && tool === "pan")) {
      isPanning.current = true; didPan.current = false;
      panStart.current  = { x:e.clientX, y:e.clientY };
      panOrigin.current = { ...pan };
      e.preventDefault();
    }
  }, [tool, pan]);

  const handleMouseMove = useCallback((e) => {
    if (isPanning.current) {
      const dx = e.clientX - panStart.current.x, dy = e.clientY - panStart.current.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didPan.current = true;
      setPan({ x:panOrigin.current.x + dx, y:panOrigin.current.y + dy });
    }
    if (draggingVertex !== null && editingKingdom) {
      const [wx, wy] = getSVGPoint(e);
      const [gx, gy] = snapToGrid(wx, wy);
      setEditingKingdom(ek => {
        const pts = [...ek.points];
        pts[draggingVertex] = [gx, gy];
        return { ...ek, points: pts };
      });
    }
    if (dragging) setDragPos(getSVGPoint(e));
  }, [dragging, draggingVertex, editingKingdom, getSVGPoint]);

  const handleMouseUp = useCallback(async (e) => {
    // Always reset panning state first
    const wasPanning = isPanning.current;
    isPanning.current = false;

    if (draggingVertex !== null) { setDraggingVertex(null); }

    if (dragging) {
      const [wx, wy] = getSVGPoint(e);
      const [gx, gy] = snapToGrid(wx, wy);
      const nearCity  = cityAt(wx, wy);
      let updated = null;

      const nearArmy = (dragging.type === "character" || dragging.type === "mage" || dragging.type === "dragon") ? armyAt(wx, wy) : null;

      if (dragging.type === "character") {
        setCharacters(cs => cs.map(c => {
          if (c.primary_key !== dragging.id) return c;
          if (nearArmy && nearArmy.primary_key !== dragging.id) {
            updated = { ...c, army: nearArmy.primary_key, coordinates: null };
          } else if (nearCity) {
            updated = { ...c, city: nearCity.primary_key, coordinates: null };
          } else {
            updated = { ...c, city: null, army: null, coordinates: coordStr(gx, gy) };
          }
          return updated;
        }));
      } else if (dragging.type === "mage") {
        setMages(ms => ms.map(m => {
          if (m.primary_key !== dragging.id) return m;
          if (nearArmy) {
            updated = { ...m, army: nearArmy.primary_key, city: null, coordinates: null };
          } else if (nearCity) {
            updated = { ...m, city: nearCity.primary_key, army: null, coordinates: null };
          } else {
            updated = { ...m, city: null, army: null, coordinates: coordStr(gx, gy) };
          }
          return updated;
        }));
      } else if (dragging.type === "dragon") {
        setDragons(ds => ds.map(d => {
          if (d.primary_key !== dragging.id) return d;
          if (nearArmy) {
            updated = { ...d, army: nearArmy.primary_key, city: null, coordinates: null };
          } else if (nearCity) {
            updated = { ...d, city: nearCity.primary_key, army: null, coordinates: null };
          } else {
            updated = { ...d, city: null, army: null, coordinates: coordStr(gx, gy) };
          }
          return updated;
        }));
      } else {
        setArmies(as => as.map(a => {
          if (a.primary_key !== dragging.id) return a;
          updated = nearCity
            ? { ...a, city: nearCity.primary_key, coordinates: null }
            : { ...a, city: null, coordinates: coordStr(gx, gy) };
          return updated;
        }));
      }
      setDragging(null); setDragPos(null);
      if (updated) {
        setPanel(null); // Close panel to prevent stale data after drag
        try { await apiPut(updated); } catch (e) { console.error("Failed to save position:", e); }
      }
    }
  }, [dragging, draggingVertex, getSVGPoint, cityAt, armyAt]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    const nz = Math.min(4, Math.max(0.3, zoom * factor));
    setZoom(nz);
    setPan({ x:cx - (cx - pan.x) * (nz / zoom), y:cy - (cy - pan.y) * (nz / zoom) });
  }, [zoom, pan]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive:false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // ── Touch handlers for mobile ──────────────────────────────────────────────
  const getTouchSVGPoint = useCallback((touch) => {
    const rect = svgRef.current.getBoundingClientRect();
    return [(touch.clientX - rect.left - pan.x) / zoom, (touch.clientY - rect.top - pan.y) / zoom];
  }, [pan, zoom]);

  const handleTouchStart = useCallback((e) => {
    setContextMenu(null);
    const ts = touchState.current;
    if (ts.longPressTimer) { clearTimeout(ts.longPressTimer); ts.longPressTimer = null; }

    if (e.touches.length === 2) {
      // Pinch zoom start
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      ts.lastDist = Math.hypot(dx, dy);
      ts.lastCenter = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
      ts.isPanning = false;
      e.preventDefault();
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      ts.isPanning = true;
      ts.didMove = false;
      ts.startPan = { ...pan };
      ts.startTouch = { x: touch.clientX, y: touch.clientY };

      // Long press for context menu (500ms)
      const [wx, wy] = getTouchSVGPoint(touch);
      ts.longPressTimer = setTimeout(() => {
        const [gx, gy] = snapToGrid(wx, wy);
        let entity = null;
        for (const d of dragons) { if (d.coordinates) { const p = parseCoord(d.coordinates); if (Math.abs(wx - p.x) < 20 && Math.abs(wy - p.y) < 20) { entity = { type:"dragon", id:d.primary_key }; break; } } }
        if (!entity) for (const m of mages) { if (m.coordinates) { const p = parseCoord(m.coordinates); if (Math.abs(wx - p.x) < 20 && Math.abs(wy - p.y) < 20) { entity = { type:"mage", id:m.primary_key }; break; } } }
        if (!entity) for (const a of armies) { if (a.coordinates) { const p = parseCoord(a.coordinates); if (Math.abs(wx - p.x) < 20 && Math.abs(wy - p.y) < 20) { entity = { type:"army", id:a.primary_key }; break; } } }
        if (!entity) for (const ch of characters) { if (ch.coordinates) { const p = parseCoord(ch.coordinates); if (Math.abs(wx - p.x) < 20 && Math.abs(wy - p.y) < 20) { entity = { type:"character", id:ch.primary_key }; break; } } }
        if (!entity) for (const c of cities) { if (c.coordinates) { const p = parseCoord(c.coordinates); if (Math.abs(wx - p.x) < 20 && Math.abs(wy - p.y) < 20) { entity = { type:"city", id:c.primary_key }; break; } } }
        const rect = svgRef.current.getBoundingClientRect();
        setContextMenu({ screenX: touch.clientX - rect.left, screenY: touch.clientY - rect.top, wx: gx, wy: gy, entity });
        ts.isPanning = false;
      }, 500);

      // If select tool — check for entity drag start with bigger hit area for touch
      if (tool === "select") {
        for (const d of dragons) {
          if (!d.coordinates) continue;
          const { x, y } = parseCoord(d.coordinates);
          if (Math.abs(wx - x) < 24 && Math.abs(wy - y) < 24) {
            setDragging({ type:"dragon", id:d.primary_key });
            e.preventDefault();
            return;
          }
        }
        for (const m of mages) {
          if (!m.coordinates) continue;
          const { x, y } = parseCoord(m.coordinates);
          if (Math.abs(wx - x) < 24 && Math.abs(wy - y) < 24) {
            setDragging({ type:"mage", id:m.primary_key });
            e.preventDefault();
            return;
          }
        }
        for (const a of armies) {
          if (!a.coordinates) continue;
          const { x, y } = parseCoord(a.coordinates);
          if (Math.abs(wx - x) < 24 && Math.abs(wy - y) < 24) {
            setDragging({ type:"army", id:a.primary_key });
            e.preventDefault();
            return;
          }
        }
        for (const ch of characters) {
          if (!ch.coordinates) continue;
          const { x, y } = parseCoord(ch.coordinates);
          if (Math.abs(wx - x) < 24 && Math.abs(wy - y) < 24) {
            setDragging({ type:"character", id:ch.primary_key });
            e.preventDefault();
            return;
          }
        }
      }
    }
  }, [pan, zoom, tool, dragons, mages, armies, characters, cities, getTouchSVGPoint]);

  const handleTouchMove = useCallback((e) => {
    const ts = touchState.current;
    if (ts.longPressTimer) { clearTimeout(ts.longPressTimer); ts.longPressTimer = null; }

    if (e.touches.length === 2 && ts.lastDist !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const center = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };

      // Pinch zoom
      const factor = dist / ts.lastDist;
      const nz = Math.min(4, Math.max(0.3, zoom * factor));
      const rect = svgRef.current.getBoundingClientRect();
      const cx = center.x - rect.left, cy = center.y - rect.top;
      setZoom(nz);
      setPan({ x: cx - (cx - pan.x) * (nz / zoom), y: cy - (cy - pan.y) * (nz / zoom) });

      ts.lastDist = dist;
      ts.lastCenter = center;
    } else if (e.touches.length === 1 && ts.isPanning && !dragging) {
      const touch = e.touches[0];
      const dx = touch.clientX - ts.startTouch.x;
      const dy = touch.clientY - ts.startTouch.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) ts.didMove = true;
      setPan({ x: ts.startPan.x + dx, y: ts.startPan.y + dy });
    } else if (e.touches.length === 1 && dragging) {
      e.preventDefault();
      const touch = e.touches[0];
      ts.didMove = true;
      setDragPos(getTouchSVGPoint(touch));
    }
  }, [zoom, pan, dragging, getTouchSVGPoint]);

  const handleTouchEnd = useCallback(async (e) => {
    const ts = touchState.current;
    if (ts.longPressTimer) { clearTimeout(ts.longPressTimer); ts.longPressTimer = null; }
    ts.lastDist = null;
    ts.lastCenter = null;
    ts.isPanning = false;

    if (dragging && dragPos) {
      const [gx, gy] = snapToGrid(dragPos[0], dragPos[1]);
      const nearCity = cityAt(dragPos[0], dragPos[1]);
      const nearArmy = (dragging.type === "character" || dragging.type === "mage" || dragging.type === "dragon") ? armyAt(dragPos[0], dragPos[1]) : null;
      let updated = null;

      if (dragging.type === "character") {
        setCharacters(cs => cs.map(c => {
          if (c.primary_key !== dragging.id) return c;
          if (nearArmy) { updated = { ...c, army: nearArmy.primary_key, coordinates: null }; }
          else if (nearCity) { updated = { ...c, city: nearCity.primary_key, coordinates: null }; }
          else { updated = { ...c, city: null, army: null, coordinates: coordStr(gx, gy) }; }
          return updated;
        }));
      } else if (dragging.type === "mage") {
        setMages(ms => ms.map(m => {
          if (m.primary_key !== dragging.id) return m;
          if (nearArmy) { updated = { ...m, army: nearArmy.primary_key, city: null, coordinates: null }; }
          else if (nearCity) { updated = { ...m, city: nearCity.primary_key, army: null, coordinates: null }; }
          else { updated = { ...m, city: null, army: null, coordinates: coordStr(gx, gy) }; }
          return updated;
        }));
      } else if (dragging.type === "dragon") {
        setDragons(ds => ds.map(d => {
          if (d.primary_key !== dragging.id) return d;
          if (nearArmy) { updated = { ...d, army: nearArmy.primary_key, city: null, coordinates: null }; }
          else if (nearCity) { updated = { ...d, city: nearCity.primary_key, army: null, coordinates: null }; }
          else { updated = { ...d, city: null, army: null, coordinates: coordStr(gx, gy) }; }
          return updated;
        }));
      } else {
        setArmies(as => as.map(a => {
          if (a.primary_key !== dragging.id) return a;
          updated = nearCity ? { ...a, city: nearCity.primary_key, coordinates: null } : { ...a, city: null, coordinates: coordStr(gx, gy) };
          return updated;
        }));
      }
      setDragging(null); setDragPos(null);
      if (updated) {
        setPanel(null);
        try { await apiPut(updated); } catch (err) { console.error("Failed to save position:", err); }
      }
    }
  }, [dragging, dragPos, cityAt, armyAt]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Track container size for scrollbars
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Release vertex drag on window mouseup so it fires even if mouse leaves SVG
  useEffect(() => {
    const onUp = () => { if (draggingVertex !== null) setDraggingVertex(null); };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [draggingVertex]);

  // ── Click → open modal or draw ────────────────────────────────────────────
  const handleSVGClick = useCallback((e) => {
    if (tool === "pan" || modal) return;
    if (didPan.current) { didPan.current = false; return; }
    setContextMenu(null);
    const [wx, wy] = getSVGPoint(e);
    const [gx, gy] = snapToGrid(wx, wy);

    // In edit mode — check for click near an edge to insert a new point
    // if (editingKingdom) {
    //   const pts = editingKingdom.points;
    //   let minDist = Infinity, minIdx = -1;
    //   for (let i = 0; i < pts.length; i++) {
    //     const a = pts[i];
    //     const b = pts[(i + 1) % pts.length];
    //     const dist = distToSegment(wx, wy, a, b);
    //     if (dist < minDist) { minDist = dist; minIdx = i; }
    //   }
    //   if (minDist < 20 / zoom) {
    //     const newPts = [...pts];
    //     newPts.splice(minIdx + 1, 0, [gx, gy]);
    //     setEditingKingdom(ek => ({ ...ek, points: newPts }));
    //   }
    //   return;
    // }

    if (tool === "draw") {
      if (e.detail === 2 && drawingPoints.length >= 3) {
        setModal({ type:"kingdom", points: drawingPoints.slice(0, -1), color: selectedColor });
        setDrawingPoints([]);
      } else if (e.detail === 1) {
        setDrawingPoints(p => [...p, [gx, gy]]);
      }
      return;
    }
    if (["city","character","army","mage","dragon"].includes(tool)) {
      setModal({ type:tool, x:gx, y:gy });
      return;
    }
    if (tool === "select") {
      // hit-test: dragons > mages > armies > characters > cities > kingdoms
      for (const d of dragons) {
        if (!d.coordinates) continue;
        const { x, y } = parseCoord(d.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"dragon", id:d.primary_key }); return; }
      }
      for (const m of mages) {
        if (!m.coordinates) continue;
        const { x, y } = parseCoord(m.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"mage", id:m.primary_key }); return; }
      }
      for (const a of armies) {
        if (!a.coordinates) continue;
        const { x, y } = parseCoord(a.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"army", id:a.primary_key }); return; }
      }
      for (const ch of characters) {
        if (!ch.coordinates) continue;
        const { x, y } = parseCoord(ch.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"character", id:ch.primary_key }); return; }
      }
      for (const city of cities) {
        if (!city.coordinates) continue;
        const { x, y } = parseCoord(city.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"city", id:city.primary_key }); return; }
      }
      const k = kingdomAt(wx, wy);
      if (k) { setPanel({ type:"kingdom", id:k.primary_key }); return; }
      setPanel(null);
    }
  }, [tool, getSVGPoint, drawingPoints, selectedColor, armies, characters, cities, mages, dragons, kingdomAt, modal, editingKingdom, zoom]);

  // ── Modal confirm ─────────────────────────────────────────────────────────
  const handleModalConfirm = useCallback(async ({ name, size, role, school, power, element, dragonSize, disciplines }) => {
    const primary_key = `${modal.type}_${Date.now()}`;
    let newItem = null;

    if (modal.type === "kingdom") {
      newItem = { primary_key, sort_key:"Kingdom", name, color: modal.color, points: modal.points, notes:"" };
      setKingdoms(ks => [...ks, newItem]);
    } else if (modal.type === "city") {
      newItem = { primary_key, sort_key:"City", name, allegiance:"", status:"normal", coordinates: coordStr(modal.x, modal.y), notes:"" };
      setCities(cs => [...cs, newItem]);
    } else if (modal.type === "character") {
      const colorIdx = characters.length % CHAR_COLORS.length;
      newItem = { primary_key, sort_key:"Character", name, role: role || "", allegiance:"", army:"", coordinates: coordStr(modal.x, modal.y), notes:"", color: CHAR_COLORS[colorIdx], skill: 5 };
      setCharacters(cs => [...cs, newItem]);
    } else if (modal.type === "army") {
      const colorIdx = armies.length % ARMY_COLORS.length;
      newItem = { primary_key, sort_key:`army#`, name, allegiance:"", general:"", coordinates: coordStr(modal.x, modal.y), size: size || 0, notes:"", color: ARMY_COLORS[colorIdx] };
      setArmies(as => [...as, newItem]);
    } else if (modal.type === "mage") {
      const colorIdx = mages.length % MAGE_COLORS.length;
      const initDisc = disciplines || {};
      newItem = { primary_key, sort_key:"Mage", name, school: school || "", power: power || 5, skill: 5, allegiance:"", army:"", city:"", coordinates: coordStr(modal.x, modal.y), notes:"", color: MAGE_COLORS[colorIdx], disciplines: initDisc };
      setMages(ms => [...ms, newItem]);
    } else if (modal.type === "dragon") {
      const colorIdx = dragons.length % DRAGON_COLORS.length;
      const initDisc = disciplines || {};
      newItem = { primary_key, sort_key:"Dragon", name, element: element || "", dragonSize: dragonSize || "Adult", skill: 5, allegiance:"", rider:"", army:"", city:"", coordinates: coordStr(modal.x, modal.y), notes:"", color: DRAGON_COLORS[colorIdx], disciplines: initDisc };
      setDragons(ds => [...ds, newItem]);
    }

    setModal(null);

    if (newItem) {
      try { await apiPut(newItem); }
      catch (err) { console.error("Failed to save new item:", err); }
    }
  }, [modal, characters.length, armies.length, mages.length, dragons.length]);

  // ── Token drag start ──────────────────────────────────────────────────────
  const handleTokenMouseDown = useCallback((e, type, id) => {
    if (tool !== "select") return;
    e.stopPropagation();
    setDragging({ type, id });
    setDragPos(getSVGPoint(e));
  }, [tool, getSVGPoint]);

  // ── Move entity out of city back to map ───────────────────────────────────
  const handleMoveOut = useCallback(async (id, type) => {
    let updated = null;
    if (type === "character") {
      setCharacters(cs => cs.map(c => {
        if (c.primary_key !== id) return c;
        const city = cities.find(ci => ci.primary_key === c.city);
        const base = city?.coordinates ? parseCoord(city.coordinates) : { x:400, y:400 };
        updated = { ...c, city: null, coordinates: coordStr(base.x + 30, base.y + 30) };
        return updated;
      }));
    } else if (type === "mage") {
      setMages(ms => ms.map(m => {
        if (m.primary_key !== id) return m;
        const city = cities.find(ci => ci.primary_key === m.city);
        const army = armies.find(a => a.primary_key === m.army);
        const base = city?.coordinates ? parseCoord(city.coordinates) : army?.coordinates ? parseCoord(army.coordinates) : { x:400, y:400 };
        updated = { ...m, city: null, army: null, coordinates: coordStr(base.x + 30, base.y - 30) };
        return updated;
      }));
    } else if (type === "dragon") {
      setDragons(ds => ds.map(d => {
        if (d.primary_key !== id) return d;
        const city = cities.find(ci => ci.primary_key === d.city);
        const army = armies.find(a => a.primary_key === d.army);
        const base = city?.coordinates ? parseCoord(city.coordinates) : army?.coordinates ? parseCoord(army.coordinates) : { x:400, y:400 };
        updated = { ...d, city: null, army: null, coordinates: coordStr(base.x - 30, base.y - 30) };
        return updated;
      }));
    } else if (type === "fromArmy") {
      // Moving a character out of an army back to map
      setCharacters(cs => cs.map(c => {
        if (c.primary_key !== id) return c;
        const army = armies.find(a => a.primary_key === c.army);
        const base = army?.coordinates ? parseCoord(army.coordinates) : { x:400, y:400 };
        updated = { ...c, army: null, coordinates: coordStr(base.x + 30, base.y + 30) };
        return updated;
      }));
    } else if (type === "fromArmyMage") {
      setMages(ms => ms.map(m => {
        if (m.primary_key !== id) return m;
        const army = armies.find(a => a.primary_key === m.army);
        const base = army?.coordinates ? parseCoord(army.coordinates) : { x:400, y:400 };
        updated = { ...m, army: null, coordinates: coordStr(base.x + 30, base.y - 30) };
        return updated;
      }));
    } else if (type === "fromArmyDragon") {
      setDragons(ds => ds.map(d => {
        if (d.primary_key !== id) return d;
        const army = armies.find(a => a.primary_key === d.army);
        const base = army?.coordinates ? parseCoord(army.coordinates) : { x:400, y:400 };
        updated = { ...d, army: null, coordinates: coordStr(base.x - 30, base.y - 30) };
        return updated;
      }));
    } else {
      // Moving an army out of a city
      setArmies(as => as.map(a => {
        if (a.primary_key !== id) return a;
        const city = cities.find(ci => ci.primary_key === a.city);
        const base = city?.coordinates ? parseCoord(city.coordinates) : { x:400, y:400 };
        updated = { ...a, city: null, coordinates: coordStr(base.x - 30, base.y + 30) };
        return updated;
      }));
    }
    if (updated) {
      try { await apiPut(updated); } catch (e) { console.error("Failed to move out:", e); }
    }
  }, [cities, armies]);

  // ── Finish kingdom from sidebar button ────────────────────────────────────
  const finishKingdom = useCallback(() => {
    if (drawingPoints.length >= 3)
      setModal({ type:"kingdom", points: drawingPoints, color: selectedColor });
      setDrawingPoints([]);
  }, [drawingPoints, selectedColor]);

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const updateKingdom = useCallback(async (u) => {
    setKingdoms(ks => ks.map(k => k.primary_key === u.primary_key ? u : k));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const updateCity = useCallback(async (u) => {
    setCities(cs => cs.map(c => c.primary_key === u.primary_key ? u : c));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const updateCharacter = useCallback(async (u) => {
    setCharacters(cs => cs.map(c => c.primary_key === u.primary_key ? u : c));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const updateArmy = useCallback(async (u) => {
    setArmies(as => as.map(a => a.primary_key === u.primary_key ? u : a));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const deleteKingdom = useCallback(async (id) => {
    setKingdoms(ks => ks.filter(k => k.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "Kingdom"); } catch (e) { console.error(e); }
  }, []);
  const deleteCity = useCallback(async (id) => {
    setCities(cs => cs.filter(c => c.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "City"); } catch (e) { console.error(e); }
  }, []);
  const deleteCharacter = useCallback(async (id) => {
    setCharacters(cs => cs.filter(c => c.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "Character"); } catch (e) { console.error(e); }
  }, []);
  const deleteArmy = useCallback(async (id, sort_key) => {
    setArmies(as => as.filter(a => a.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, sort_key || "army#"); } catch (e) { console.error(e); }
  }, []);
  const updateMage = useCallback(async (u) => {
    setMages(ms => ms.map(m => m.primary_key === u.primary_key ? u : m));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const deleteMage = useCallback(async (id) => {
    setMages(ms => ms.filter(m => m.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "Mage"); } catch (e) { console.error(e); }
  }, []);
  const updateDragon = useCallback(async (u) => {
    setDragons(ds => ds.map(d => d.primary_key === u.primary_key ? u : d));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const deleteDragon = useCallback(async (id) => {
    setDragons(ds => ds.filter(d => d.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "Dragon"); } catch (e) { console.error(e); }
  }, []);

  // ── Kingdom border edit ──────────────────────────────────────────────────────
  const startEditingKingdom = useCallback((kingdom) => {
    setEditingKingdom({ id: kingdom.primary_key, points: kingdom.points.map(p => [...p]) });
    setPanel(null); // close panel while editing
  }, []);

  const confirmKingdomEdit = useCallback(async () => {
    if (!editingKingdom) return;
    setKingdoms(ks => ks.map(k =>
      k.primary_key === editingKingdom.id ? { ...k, points: editingKingdom.points } : k
    ));
    const updated = kingdoms.find(k => k.primary_key === editingKingdom.id);
    const saved = { ...updated, points: editingKingdom.points };
    setEditingKingdom(null);
    try { setSaving(true); await apiPut(saved); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, [editingKingdom, kingdoms]);

  const cancelKingdomEdit = useCallback(() => {
    setEditingKingdom(null);
    setDraggingVertex(null);
  }, []);

  // ── Grid ──────────────────────────────────────────────────────────────────
  const gridLines = [];
  for (let x = 0; x <= CANVAS_W; x += GRID_SIZE)
    gridLines.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={CANVAS_H} stroke="#d0d4d8" strokeWidth="0.7" />);
  for (let y = 0; y <= CANVAS_H; y += GRID_SIZE)
    gridLines.push(<line key={`h${y}`} x1={0} y1={y} x2={CANVAS_W} y2={y} stroke="#d0d4d8" strokeWidth="0.7" />);

  const cursorStyle = editingKingdom ? (draggingVertex !== null ? "grabbing" : "crosshair")
    : tool === "pan" ? "grab" : tool === "draw" ? "crosshair"
    : ["city","character","army","mage","dragon"].includes(tool) ? "cell"
    : dragging ? "grabbing" : "default";

  const panelKingdom = panel?.type === "kingdom"   ? kingdoms.find(k   => k.primary_key === panel.id) : null;
  const panelCity    = panel?.type === "city"      ? cities.find(c     => c.primary_key === panel.id) : null;
  const panelChar    = panel?.type === "character" ? characters.find(c => c.primary_key === panel.id) : null;
  const panelArmy    = panel?.type === "army"      ? armies.find(a     => a.primary_key === panel.id) : null;
  const panelMage    = panel?.type === "mage"      ? mages.find(m      => m.primary_key === panel.id) : null;
  const panelDragon  = panel?.type === "dragon"    ? dragons.find(d    => d.primary_key === panel.id) : null;

  // ── Scrollbar calculations ───────────────────────────────────────────────────
  const SCROLL_W = 12; // scrollbar track width
  const scaledW = CANVAS_W * zoom;
  const scaledH = CANVAS_H * zoom;
  // Thumb size as fraction of visible area vs total canvas
  const hThumbRatio = Math.min(1, containerSize.w / scaledW);
  const vThumbRatio = Math.min(1, containerSize.h / scaledH);
  // Clamp pan so thumb doesn't go out of track
  const maxPanX = 0;
  const minPanX = containerSize.w - scaledW;
  const maxPanY = 0;
  const minPanY = containerSize.h - scaledH;
  // Thumb position: how far into the scrollable range are we
  const hScrollRange = scaledW - containerSize.w;
  const vScrollRange = scaledH - containerSize.h;
  const hThumbX = hScrollRange > 0 ? ((-pan.x) / hScrollRange) * (containerSize.w - SCROLL_W) * (1 - hThumbRatio) : 0;
  const vThumbY = vScrollRange > 0 ? ((-pan.y) / vScrollRange) * (containerSize.h - SCROLL_W) * (1 - vThumbRatio) : 0;
  const hThumbW = (containerSize.w - SCROLL_W) * hThumbRatio;
  const vThumbH = (containerSize.h - SCROLL_W) * vThumbRatio;
  const showHScroll = hThumbRatio < 1;
  const showVScroll = vThumbRatio < 1;

  const scrollDrag = useRef(null); // { axis, startMouse, startPan }

  const handleScrollMouseDown = useCallback((e, axis) => {
    e.preventDefault();
    e.stopPropagation();
    scrollDrag.current = { axis, startMouse: axis === "h" ? e.clientX : e.clientY, startPan: { ...pan } };
    const onMove = (me) => {
      if (!scrollDrag.current) return;
      const { axis, startMouse, startPan } = scrollDrag.current;
      if (axis === "h") {
        const trackW = containerSize.w - SCROLL_W;
        const delta = (me.clientX - startMouse) / (trackW * (1 - hThumbRatio));
        const newPanX = Math.min(0, Math.max(minPanX, startPan.x - delta * hScrollRange));
        setPan(p => ({ ...p, x: newPanX }));
      } else {
        const trackH = containerSize.h - SCROLL_W;
        const delta = (me.clientY - startMouse) / (trackH * (1 - vThumbRatio));
        const newPanY = Math.min(0, Math.max(minPanY, startPan.y - delta * vScrollRange));
        setPan(p => ({ ...p, y: newPanY }));
      }
    };
    const onUp = () => { scrollDrag.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [pan, hThumbRatio, vThumbRatio, hScrollRange, vScrollRange, minPanX, minPanY, containerSize]);

  // Only show tokens for entities NOT in a city or army
  const mapCharacters = characters.filter(c => !c.city && !c.army && c.coordinates);
  const mapArmies     = armies.filter(a => !a.city && a.coordinates);
  const mapMages      = mages.filter(m => !m.city && !m.army && m.coordinates);
  const mapDragons    = dragons.filter(d => !d.city && !d.army && d.coordinates);

  function tokenXY(entity) {
    if (dragging?.id === entity.primary_key && dragPos) return { x:dragPos[0], y:dragPos[1] };
    const { x, y } = parseCoord(entity.coordinates);
    return { x, y };
  }

  // ── Fit to content ─────────────────────────────────────────────────────────
  const fitToContent = useCallback(() => {
    const allCoords = [];
    cities.forEach(c => { if (c.coordinates) { const p = parseCoord(c.coordinates); allCoords.push(p); } });
    mapCharacters.forEach(c => { const p = parseCoord(c.coordinates); allCoords.push(p); });
    mapArmies.forEach(a => { const p = parseCoord(a.coordinates); allCoords.push(p); });
    mapMages.forEach(m => { const p = parseCoord(m.coordinates); allCoords.push(p); });
    mapDragons.forEach(d => { const p = parseCoord(d.coordinates); allCoords.push(p); });
    kingdoms.forEach(k => { if (k.points) k.points.forEach(([x, y]) => allCoords.push({ x, y })); });
    if (allCoords.length === 0) { setPan({ x: 0, y: 0 }); setZoom(1); return; }
    const minX = Math.min(...allCoords.map(p => p.x));
    const maxX = Math.max(...allCoords.map(p => p.x));
    const minY = Math.min(...allCoords.map(p => p.y));
    const maxY = Math.max(...allCoords.map(p => p.y));
    const contentW = maxX - minX || 200;
    const contentH = maxY - minY || 200;
    const pad = 60;
    const scaleX = containerSize.w / (contentW + pad * 2);
    const scaleY = containerSize.h / (contentH + pad * 2);
    const newZoom = Math.min(4, Math.max(0.3, Math.min(scaleX, scaleY)));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    setPan({ x: containerSize.w / 2 - cx * newZoom, y: containerSize.h / 2 - cy * newZoom });
    setZoom(newZoom);
  }, [cities, mapCharacters, mapArmies, mapMages, mapDragons, kingdoms, containerSize]);

  // ── Context menu ──────────────────────────────────────────────────────────
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    const [wx, wy] = getSVGPoint(e);
    const [gx, gy] = snapToGrid(wx, wy);
    // Check if right-clicking on an entity
    let entity = null;
    for (const d of dragons) { if (d.coordinates) { const p = parseCoord(d.coordinates); if (Math.abs(wx - p.x) < 14 && Math.abs(wy - p.y) < 14) { entity = { type:"dragon", id:d.primary_key }; break; } } }
    if (!entity) for (const m of mages) { if (m.coordinates) { const p = parseCoord(m.coordinates); if (Math.abs(wx - p.x) < 14 && Math.abs(wy - p.y) < 14) { entity = { type:"mage", id:m.primary_key }; break; } } }
    if (!entity) for (const a of armies) { if (a.coordinates) { const p = parseCoord(a.coordinates); if (Math.abs(wx - p.x) < 14 && Math.abs(wy - p.y) < 14) { entity = { type:"army", id:a.primary_key }; break; } } }
    if (!entity) for (const ch of characters) { if (ch.coordinates) { const p = parseCoord(ch.coordinates); if (Math.abs(wx - p.x) < 14 && Math.abs(wy - p.y) < 14) { entity = { type:"character", id:ch.primary_key }; break; } } }
    if (!entity) for (const c of cities) { if (c.coordinates) { const p = parseCoord(c.coordinates); if (Math.abs(wx - p.x) < 14 && Math.abs(wy - p.y) < 14) { entity = { type:"city", id:c.primary_key }; break; } } }
    const rect = svgRef.current.getBoundingClientRect();
    setContextMenu({ screenX: e.clientX - rect.left, screenY: e.clientY - rect.top, wx: gx, wy: gy, entity });
  }, [getSVGPoint, dragons, mages, armies, characters, cities]);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontFamily:FONT_BODY, flexDirection:"column", gap:12 }}>
      <div style={{ fontSize:28 }}>🗺</div>
      <div style={{ fontSize:14, color:"#8a7e72" }}>Loading map data...</div>
    </div>
  );

  if (apiError) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontFamily:FONT_BODY, flexDirection:"column", gap:12 }}>
      <div style={{ fontSize:28 }}>⚠️</div>
      <div style={{ fontSize:14, color:"#ef4444", fontWeight:600 }}>Failed to load map data</div>
      <div style={{ fontSize:12, color:"#8a7e72" }}>{apiError}</div>
      <button onClick={() => window.location.reload()} style={{ marginTop:8, padding:"8px 16px", background:"#3b82f6", color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontSize:13, fontFamily:FONT_BODY }}>Retry</button>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", width:"100vw", fontFamily:FONT_BODY, overflow:"hidden" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap: isMobile ? 6 : 12, padding: isMobile ? "8px 10px" : "10px 18px", background:"#faf9f7", borderBottom:"1px solid #e2ddd6", boxShadow:"0 1px 6px #0000000a", zIndex:30, flexShrink:0, position:"relative" }}>
        {isMobile && (
          <button onClick={() => setShowSidebar(!showSidebar)} style={{
            background:"none", border:"1px solid #d9d2c9", borderRadius:6, padding:"6px 8px",
            cursor:"pointer", fontSize:16, color:"#3d3529", flexShrink:0,
          }}>☰</button>
        )}
        <span style={{ fontSize: isMobile ? 13 : 15, fontWeight:700, color:"#3d3529", fontFamily:FONT_DISPLAY, letterSpacing:1.5 }}>🗺 {isMobile ? "" : "Map Editor"}</span>
        <div style={{ flex:1 }} />
        {saving && <span style={{ fontSize:11, color:"#3b82f6", fontFamily:FONT_BODY }}>💾</span>}
        {isMobile ? (
          <div style={{ position:"relative" }}>
            <button onClick={() => setShowMobileMenu(!showMobileMenu)} style={{
              background: showMobileMenu ? "#1f2937" : "#f7f5f2",
              color: showMobileMenu ? "#fff" : "#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"6px 10px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>⋯ Actions</button>
            {showMobileMenu && (
              <>
                <div onClick={() => setShowMobileMenu(false)} style={{ position:"fixed", inset:0, zIndex:99 }} />
                <div style={{
                  position:"fixed", top:48, right:10,
                  background:"#faf9f7", border:"1px solid #e2ddd6", borderRadius:10,
                  boxShadow:"0 8px 32px #0000001a", zIndex:100, minWidth:170,
                  animation:"mobileMenuFadeIn 0.15s ease-out",
                }}>
                  {[
                    { label:"⚔ Battle", fn:() => setShowBattle(true) },
                    { label:"⚡ Duel", fn:() => setShowDuel(true) },
                    { label:"✦ Rankings", fn:() => setShowPowerRankings(true) },
                    { label:"😓 Fatigue", fn:() => setShowFatigueManager(true) },
                    { label:"🏺 Gear", fn:() => setShowGearModal(true) },
                    { label:"🛡 Readiness", fn:() => setShowReadiness(true) },
                    { label:"⚔️ Wars", fn:() => setShowWars(true) },
                    { label:"🏆 Leagues", fn:() => setShowLeagues(true) },
                  ].map(item => (
                    <div key={item.label} onClick={() => { item.fn(); setShowMobileMenu(false); }}
                      style={{ padding:"12px 18px", fontSize:14, color:"#3d3529", cursor:"pointer", borderBottom:"1px solid #ece7e0", fontFamily:FONT_BODY, fontWeight:500 }}>
                      {item.label}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <button onClick={() => setShowBattle(true)} style={{
              background: showBattle ? "#1f2937" : "#f7f5f2",
              color: showBattle ? "#fff" : "#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>⚔ Battle</button>
            <button onClick={() => setShowDuel(true)} style={{
              background:"#f7f5f2", color:"#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>⚡ Duel</button>
            <button onClick={() => setShowPowerRankings(true)} style={{
              background:"#f7f5f2", color:"#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>✦ Power Rankings</button>
            <button onClick={() => setShowFatigueManager(true)} style={{
              background:"#f7f5f2", color:"#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>😓 Fatigue</button>
            <button onClick={() => setShowGearModal(true)} style={{
              background:"#f7f5f2", color:"#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>🏺 Gear</button>
            <button onClick={() => setShowReadiness(true)} style={{
              background:"#f7f5f2", color:"#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>🛡 Readiness</button>
            <button onClick={() => setShowWars(true)} style={{
              background:"#f7f5f2", color:"#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>⚔️ Wars</button>
            <button onClick={() => setShowLeagues(true)} style={{
              background:"#f7f5f2", color:"#3d3529",
              border:"1px solid #d9d2c9", borderRadius:6,
              padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT_BODY,
            }}>🏆 Leagues</button>
            <span style={{ fontSize:11, color:"#9a8e82", fontFamily:FONT_BODY }}>Scroll to zoom · Middle-click to pan · Right-click for quick menu · Draw: click then <b>Finish</b> · Drag tokens to move</span>
          </>
        )}
      </div>
 
      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
        {/* Sidebar backdrop on mobile */}
        {isMobile && showSidebar && (
          <div onClick={() => setShowSidebar(false)} style={{
            position:"fixed", inset:0, background:"#00000044", zIndex:39,
          }} />
        )}
        {/* Sidebar */}
        {(!isMobile || showSidebar) && (
        <div style={{
          width: isMobile ? "min(300px, 85vw)" : 280,
          background:"#faf9f7",
          borderRight:"1px solid #e2ddd6",
          display:"flex", flexDirection:"column", padding:12, gap:14, overflowY:"auto", flexShrink:0,
          ...(isMobile ? {
            position:"fixed", top:0, left:0, bottom:0, zIndex:40,
            boxShadow:"4px 0 20px #0000001a",
            animation:"drawerSlideIn 0.2s ease-out",
          } : {}),
        }}>
          {isMobile && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <span style={{ fontSize:14, fontWeight:700, color:"#3d3529", fontFamily:FONT_DISPLAY }}>Tools & Entities</span>
              <button onClick={() => setShowSidebar(false)} className="panel-close-btn">✕</button>
            </div>
          )}
          <ToolSection label="TOOLS">
            {[
              { id:"select",    icon:"↖", label:"Select / Move" },
              { id:"draw",      icon:"✏", label:"Draw Kingdom" },
              { id:"city",      icon:"●", label:"Place City" },
              { id:"character", icon:"👤", label:"Add Character" },
              { id:"army",      icon:"⚔", label:"Add Army" },
              { id:"mage",      icon:"✦", label:"Add Mage" },
              { id:"dragon",    icon:"🐉", label:"Add Dragon" },
              { id:"pan",       icon:"✋", label:"Pan" },
            ].map(t => (
              <ToolBtn key={t.id} active={tool === t.id} onClick={() => { setTool(t.id); setDrawingPoints([]); }}>
                <span style={{ width:16, textAlign:"center" }}>{t.icon}</span> {t.label}
              </ToolBtn>
            ))}
          </ToolSection>
 
          {tool === "draw" && (
            <ToolSection label="KINGDOM COLOR">
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {KINGDOM_COLORS.map(c => (
                  <div key={c} onClick={() => setSelectedColor(c)} style={{
                    width:22, height:22, borderRadius:4, background:c, cursor:"pointer",
                    border: selectedColor === c ? "2px solid #3d3529" : "2px solid transparent",
                  }} />
                ))}
              </div>
              <div style={{ fontSize:11, color:"#8a7e72", marginTop:4 }}>{drawingPoints.length} point{drawingPoints.length !== 1 ? "s" : ""} placed</div>
              {drawingPoints.length >= 3 && <button onClick={finishKingdom} style={{ ...mkBtn("#22c55e"), marginTop:4 }}>✓ Finish Kingdom</button>}
              {drawingPoints.length > 0  && <button onClick={() => setDrawingPoints([])} style={{ ...mkBtn("#9a8e82"), marginTop:4 }}>✕ Cancel</button>}
            </ToolSection>
          )}
 
          <ToolSection label="MAP">
            <ToolBtn onClick={() => { setPan({ x:0, y:0 }); setZoom(1); }}>⊞ Reset View</ToolBtn>
            <ToolBtn onClick={fitToContent}>⊡ Fit to Content</ToolBtn>
            <div style={{ fontSize:11, color:"#9a8e82", marginTop:2, lineHeight:1.8, fontFamily:FONT_BODY }}>
              {kingdoms.length} kingdoms · {cities.length} cities<br />{characters.length} chars · {armies.length} armies<br />{mages.length} mages · {dragons.length} dragons
            </div>
          </ToolSection>
 
          <div style={{ position:"relative" }}>
            <input
              value={sidebarSearch}
              onChange={e => setSidebarSearch(e.target.value)}
              placeholder="Search entities..."
              style={{ ...inputStyle, fontSize:12, paddingLeft:26, borderRadius:8, background:"#f2efe9", border:"1px solid #e2ddd6" }}
            />
            <span style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", fontSize:12, color:"#9a8e82", pointerEvents:"none" }}>🔍</span>
            {sidebarSearch && (
              <button onClick={() => setSidebarSearch("")} style={{ position:"absolute", right:4, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:11, color:"#9a8e82", padding:2 }}>✕</button>
            )}
          </div>
 
          {(() => {
            const q = sidebarSearch.toLowerCase().trim();
            const matchName = (item) => !q || item.name?.toLowerCase().includes(q);
            const filteredChars = characters.filter(matchName);
            const filteredArmies = armies.filter(matchName);
            const filteredMages = mages.filter(matchName);
            const filteredDragons = dragons.filter(matchName);
 
            return (
              <>
                {filteredChars.length > 0 && (
                  <ToolSection label={`CHARACTERS${q ? ` (${filteredChars.length})` : ""}`}>
                    {filteredChars.sort((a, b) => b.skill - a.skill).map(c => (
                      <SidebarItem key={c.primary_key} color={c.color} label={c.name} shape="circle"
                        sub={c.city ? `In ${cities.find(ci => ci.primary_key === c.city)?.name || "city"}` : c.army ? `In ${armies.find(a => a.primary_key === c.army)?.name || "army"}` : null}
                        active={panel?.id === c.primary_key}
                        onClick={() => { setPanel({ type:"character", id:c.primary_key }); if (isMobile) setShowSidebar(false); }} />
                    ))}
                  </ToolSection>
                )}
 
                {filteredArmies.length > 0 && (
                  <ToolSection label={`ARMIES${q ? ` (${filteredArmies.length})` : ""}`}>
                    {filteredArmies.map(a => (
                      <SidebarItem key={a.primary_key} color={a.color} label={a.name} shape="diamond"
                        sub={a.city ? `In ${cities.find(ci => ci.primary_key === a.city)?.name || "city"}` : a.size ? `${a.size.toLocaleString()} troops` : null}
                        active={panel?.id === a.primary_key}
                        onClick={() => { setPanel({ type:"army", id:a.primary_key }); if (isMobile) setShowSidebar(false); }} />
                    ))}
                  </ToolSection>
                )}
 
                {filteredMages.length > 0 && (
                  <ToolSection label={`MAGES${q ? ` (${filteredMages.length})` : ""}`}>
                    {filteredMages.sort((a, b) => calcDisciplinePowerForType(b.disciplines,"mage") - calcDisciplinePowerForType(a.disciplines,"mage")).map(m => (
                      <MageDragonCard key={m.primary_key} entity={m} type="mage"
                        cities={cities} armies={armies}
                        active={panel?.id === m.primary_key}
                        onClick={() => { setPanel({ type:"mage", id:m.primary_key }); if (isMobile) setShowSidebar(false); }} />
                    ))}
                  </ToolSection>
                )}

                {filteredDragons.length > 0 && (
                  <ToolSection label={`DRAGONS${q ? ` (${filteredDragons.length})` : ""}`}>
                    {filteredDragons.sort((a, b) => calcDisciplinePowerForType(b.disciplines,"dragon") - calcDisciplinePowerForType(a.disciplines,"dragon")).map(d => (
                      <MageDragonCard key={d.primary_key} entity={d} type="dragon"
                        cities={cities} armies={armies}
                        active={panel?.id === d.primary_key}
                        onClick={() => { setPanel({ type:"dragon", id:d.primary_key }); if (isMobile) setShowSidebar(false); }} />
                    ))}
                  </ToolSection>
                )}
 
                {q && filteredChars.length === 0 && filteredArmies.length === 0 && filteredMages.length === 0 && filteredDragons.length === 0 && (
                  <div style={{ fontSize:12, color:"#9a8e82", textAlign:"center", padding:12, fontFamily:FONT_BODY }}>No results for "{sidebarSearch}"</div>
                )}
              </>
            );
          })()}
        </div>
        )}
 
        {/* Canvas */}
        <div ref={containerRef} style={{ flex:1, overflow:"hidden", position:"relative", touchAction:"none" }}>
          <svg ref={svgRef} width="100%" height="100%"
            style={{ cursor:cursorStyle, display:"block", background:"#f0f2f5", touchAction:"none" }}
            onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp} onClick={handleSVGClick} onContextMenu={handleContextMenu}>
            <defs>
              <filter id="powerGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="powerGlowStrong" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="7" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              <rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="#f8f9fb" />
              {gridLines}
              <rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="none" stroke="#c9cdd4" strokeWidth="1.5" />
 
              {/* Kingdoms */}
              {kingdoms.map(k => {
                if (!k.points?.length) return null;
                const isEditing = editingKingdom?.id === k.primary_key;
                const displayPoints = isEditing ? editingKingdom.points : k.points;
                const pts = displayPoints.map(p => p.join(",")).join(" ");
                const isSel = panel?.type === "kingdom" && panel?.id === k.primary_key;
                const cx = displayPoints.reduce((a, p) => a + p[0], 0) / displayPoints.length;
                const cy = displayPoints.reduce((a, p) => a + p[1], 0) / displayPoints.length;
                return (
                  <g key={k.primary_key} style={{ cursor: isEditing ? "default" : "pointer" }}>
                    <polygon points={pts}
                      fill={k.color} fillOpacity={isEditing ? 0.15 : isSel ? 0.38 : 0.2}
                      stroke={k.color} strokeWidth={isEditing ? 2 : isSel ? 2.5 : 1.5}
                      strokeOpacity={isEditing ? 1 : isSel ? 1 : 0.6}
                      strokeDasharray={isEditing ? "6 3" : "none"}
                      style={{ filter: isSel && !isEditing ? `drop-shadow(0 0 5px ${k.color}88)` : "none" }} />
                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
                      fill="#1f2937" fontSize={14} fontWeight={600} style={{ pointerEvents:"none" }}
                      paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{k.name}</text>
                    {/* Vertex handles when editing */}
                    {isEditing && displayPoints.map((p, i) => (
                      <circle key={i} cx={p[0]} cy={p[1]} r={7}
                        fill={draggingVertex === i ? k.color : "#fff"}
                        stroke={k.color} strokeWidth={2}
                        style={{ cursor:"grab" }}
                        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setDraggingVertex(i); }} />
                    ))}
                  </g>
                );
              })}
 
              {/* In-progress drawing */}
              {drawingPoints.length > 0 && (
                <g>
                  {drawingPoints.length >= 2 && (
                    <polygon points={drawingPoints.map(p => p.join(",")).join(" ")}
                      fill={selectedColor} fillOpacity={0.1} stroke={selectedColor} strokeWidth={1.5} strokeDasharray="5 4" strokeOpacity={0.8} />
                  )}
                  {drawingPoints.map((p, i) => (
                    <circle key={i} cx={p[0]} cy={p[1]} r={4}
                      fill={i === 0 ? "#fff" : selectedColor} stroke={selectedColor} strokeWidth={1.5} />
                  ))}
                </g>
              )}
 
              {/* Cities */}
              {cities.map(c => {
                if (!c.coordinates) return null;
                const { x, y } = parseCoord(c.coordinates);
                const isSel = panel?.type === "city" && panel?.id === c.primary_key;
                const statusColor = STATUS_COLORS[c.status] || "#22c55e";
                const kColor = kingdoms.find(k => k.primary_key === c.allegiance)?.color;
                // Count chars/armies inside
                const inCount = characters.filter(ch => ch.city === c.primary_key).length
                              + armies.filter(a => a.city === c.primary_key).length
                              + mages.filter(m => m.city === c.primary_key).length
                              + dragons.filter(d => d.city === c.primary_key).length;
                // Contained magical power
                const cp = calcContainedPower(mages, dragons, c.primary_key, "city");
                const hasPower = cp.totalNorm > 0;
                // Scale city radius: base 7, up to 14 at high power
                const baseR = 7;
                const powerR = hasPower ? Math.min(14, baseR + Math.sqrt(cp.totalNorm) * 0.9) : baseR;
                // Aura rings: concentric pulses scaled to power
                const auraR1 = powerR + 6 + Math.min(12, cp.totalNorm * 0.15);
                const auraR2 = powerR + 3 + Math.min(6, cp.totalNorm * 0.08);
                const auraColor = cp.peakColor || "#8b5cf6";
                const auraOpacity = hasPower ? Math.min(0.5, 0.12 + cp.totalNorm * 0.005) : 0;
                const tierKey = cp.peakTier?.key;
                const isHigh = tierKey === "celestial" || tierKey === "legendary";
                return (
                  <g key={c.primary_key} style={{ cursor:"pointer" }}>
                    {/* Power aura — outer pulse ring */}
                    {hasPower && (
                      <>
                        <circle cx={x} cy={y} r={auraR1} fill="none"
                          stroke={auraColor} strokeWidth={isHigh ? 2 : 1.5} opacity={auraOpacity}
                          strokeDasharray={isHigh ? "none" : "4 3"}
                          filter={isHigh ? "url(#powerGlowStrong)" : "url(#powerGlow)"}>
                          <animate attributeName="r" values={`${auraR1-2};${auraR1+3};${auraR1-2}`} dur={isHigh ? "2s" : "3s"} repeatCount="indefinite" />
                          <animate attributeName="opacity" values={`${auraOpacity};${auraOpacity*1.6};${auraOpacity}`} dur={isHigh ? "2s" : "3s"} repeatCount="indefinite" />
                        </circle>
                        {/* Inner aura fill */}
                        <circle cx={x} cy={y} r={auraR2} fill={auraColor} opacity={auraOpacity * 0.3} />
                      </>
                    )}
                    {/* Selection ring */}
                    {isSel && <circle cx={x} cy={y} r={powerR + 7} fill="none" stroke="#3d3529" strokeWidth={1.5} strokeDasharray="3 2" opacity={0.6} />}
                    {/* Main city dot — scaled to power */}
                    <circle cx={x} cy={y} r={powerR} fill={isSel ? "#1a1814" : "#3d3529"} stroke={kColor || "#f8f9fb"} strokeWidth={2}
                      filter={isHigh ? "url(#powerGlow)" : "none"} />
                    {/* Status indicator */}
                    <circle cx={x + powerR * 0.85} cy={y - powerR * 0.85} r={3.5} fill={statusColor} stroke="#fff" strokeWidth={1} />
                    {/* Entity count badge */}
                    {inCount > 0 && (
                      <text x={x - powerR - 2} y={y - powerR + 1} textAnchor="middle" fill="#3d3529" fontSize={9} fontWeight={700} style={{ pointerEvents:"none" }}>{inCount}</text>
                    )}
                    {/* City name */}
                    <text x={x} y={y + powerR + 12} textAnchor="middle" fill="#1f2937" fontSize={11} fontWeight={500}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{c.name}</text>
                    {/* Power readout — shown if power > 0 */}
                    {hasPower && (
                      <text x={x} y={y + powerR + 23} textAnchor="middle" fill={auraColor} fontSize={9} fontWeight={700}
                        style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>
                        ✦{cp.totalNorm} PWR
                      </text>
                    )}
                  </g>
                );
              })}
 
              {/* Army tokens (map only — not in city) */}
              {mapArmies.map(a => {
                const isSel = panel?.type === "army" && panel?.id === a.primary_key;
                const isDrag = dragging?.id === a.primary_key && dragging?.type === "army";
                const { x, y } = tokenXY(a);
                // Contained magical power
                const cp = calcContainedPower(mages, dragons, a.primary_key, "army");
                const hasPower = cp.totalNorm > 0;
                // Scale diamond: base 12, up to 20 at high power
                const baseD = 12;
                const d = hasPower ? Math.min(20, baseD + Math.sqrt(cp.totalNorm) * 0.8) : baseD;
                const pts = `${x},${y-d} ${x+d},${y} ${x},${y+d} ${x-d},${y}`;
                // Characters attached to this army
                const armyMages = mages.filter(m => m.army === a.primary_key);
                const armyDragons = dragons.filter(dr => dr.army === a.primary_key);
                const armyChars = characters.filter(c => c.army === a.primary_key);
                const magicCount = armyMages.length + armyDragons.length;
                const entityCount = magicCount + armyChars.length;
                // Aura derived from contained power
                const auraColor = cp.peakColor || "#8b5cf6";
                const auraOpacity = hasPower ? Math.min(0.5, 0.12 + cp.totalNorm * 0.005) : 0;
                const tierKey = cp.peakTier?.key;
                const isHigh = tierKey === "celestial" || tierKey === "legendary";
                const auraD = d + 6 + Math.min(12, cp.totalNorm * 0.15);
                const auraD2 = d + 3 + Math.min(6, cp.totalNorm * 0.08);
                return (
                  <g key={a.primary_key} onMouseDown={e => handleTokenMouseDown(e, "army", a.primary_key)}
                    style={{ cursor: tool === "select" ? "grab" : "default" }}>
                    {/* Power aura — outer pulse ring */}
                    {hasPower && (
                      <>
                        <polygon points={`${x},${y-auraD} ${x+auraD},${y} ${x},${y+auraD} ${x-auraD},${y}`}
                          fill="none" stroke={auraColor} strokeWidth={isHigh ? 2 : 1.5}
                          opacity={auraOpacity} strokeDasharray={isHigh ? "none" : "4 3"}
                          filter={isHigh ? "url(#powerGlowStrong)" : "url(#powerGlow)"}>
                          <animate attributeName="opacity" values={`${auraOpacity};${auraOpacity*1.6};${auraOpacity}`} dur={isHigh ? "2s" : "3s"} repeatCount="indefinite" />
                        </polygon>
                        {/* Inner aura fill */}
                        <polygon points={`${x},${y-auraD2} ${x+auraD2},${y} ${x},${y+auraD2} ${x-auraD2},${y}`}
                          fill={auraColor} opacity={auraOpacity * 0.25} stroke="none" />
                      </>
                    )}
                    {/* Legacy magic support glow (if has magic but calcContainedPower returned 0 for some reason) */}
                    {!hasPower && magicCount > 0 && (
                      <polygon points={`${x},${y-d-6} ${x+d+6},${y} ${x},${y+d+6} ${x-d-6},${y}`}
                        fill="none" stroke="#8b5cf6" strokeWidth={2} opacity={0.5} strokeDasharray="4 2">
                        <animate attributeName="opacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" />
                      </polygon>
                    )}
                    {/* Selection outline */}
                    {isSel && <polygon points={`${x},${y-d-4} ${x+d+4},${y} ${x},${y+d+4} ${x-d-4},${y}`}
                      fill="none" stroke={a.color} strokeWidth={1.5} strokeDasharray="3 2" />}
                    {/* Main diamond — scaled to power */}
                    <polygon points={pts} fill={a.color} stroke="#fff" strokeWidth={2} opacity={isDrag ? 0.7 : 1}
                      filter={isHigh ? "url(#powerGlow)" : "none"} />
                    <text x={x} y={y+1} textAnchor="middle" dominantBaseline="middle"
                      fill="#fff" fontSize={d > 14 ? 11 : 9} fontWeight={700} style={{ pointerEvents:"none" }}>⚔</text>
                    {/* Name + size */}
                    <text x={x} y={y+d+10} textAnchor="middle" fill="#1f2937" fontSize={10} fontWeight={600}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{a.name}</text>
                    {a.size > 0 && <text x={x} y={y+d+20} textAnchor="middle" fill="#6b7280" fontSize={9}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>{a.size.toLocaleString()}</text>}
                    {/* Power readout */}
                    {hasPower && (
                      <text x={x} y={y+d + (a.size > 0 ? 30 : 20)} textAnchor="middle" fill={auraColor} fontSize={9} fontWeight={700}
                        style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>
                        ✦{cp.totalNorm} PWR
                      </text>
                    )}
                    {/* Entity count badge */}
                    {entityCount > 0 && (
                      <g>
                        <circle cx={x+d+3} cy={y-d-3} r={7} fill={hasPower ? (cp.peakColor || "#7c3aed") : magicCount > 0 ? "#7c3aed" : "#6b7280"} stroke="#fff" strokeWidth={1.5} />
                        <text x={x+d+3} y={y-d-2} textAnchor="middle" dominantBaseline="middle"
                          fill="#fff" fontSize={7} fontWeight={700} style={{ pointerEvents:"none" }}>{entityCount}</text>
                      </g>
                    )}
                  </g>
                );
              })}
 
              {/* Character tokens (map only) */}
              {mapCharacters.map(c => {
                const isSel = panel?.type === "character" && panel?.id === c.primary_key;
                const isDrag = dragging?.id === c.primary_key && dragging?.type === "character";
                const { x, y } = tokenXY(c);
                return (
                  <g key={c.primary_key} onMouseDown={e => handleTokenMouseDown(e, "character", c.primary_key)}
                    style={{ cursor: tool === "select" ? "grab" : "default" }}>
                    {isSel && <circle cx={x} cy={y} r={14} fill="none" stroke={c.color} strokeWidth={1.5} strokeDasharray="3 2" />}
                    <circle cx={x} cy={y} r={9} fill={c.color} stroke="#fff" strokeWidth={2} opacity={isDrag ? 0.7 : 1} />
                    <text x={x} y={y+1} textAnchor="middle" dominantBaseline="middle"
                      fill="#fff" fontSize={10} fontWeight={700} style={{ pointerEvents:"none" }}>
                      {c.name.charAt(0).toUpperCase()}
                    </text>
                    <text x={x} y={y+18} textAnchor="middle" fill="#1f2937" fontSize={10} fontWeight={500}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{c.name}</text>
                  </g>
                );
              })}
 
              {/* Mage tokens (map only) */}
              {mapMages.map(m => {
                const isSel = panel?.type === "mage" && panel?.id === m.primary_key;
                const isDrag = dragging?.id === m.primary_key && dragging?.type === "mage";
                const { x, y } = tokenXY(m);
                const peak = peakDiscipline(m.disciplines, m.primaryDiscipline);
                const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(dd => dd.key === peak.key) : null;
                const peakColor = peakInfo?.color || m.color;
                const fat = m.fatigue || 0;
                const equippedCount = (Array.isArray(m.artifacts) ? m.artifacts : []).filter(a => a?.name && a?.effects).length;
                const power = calcDisciplinePowerForType(m.disciplines, "mage", m.artifacts, fat);
                return (
                  <g key={m.primary_key} onMouseDown={e => handleTokenMouseDown(e, "mage", m.primary_key)}
                    style={{ cursor: tool === "select" ? "grab" : "default" }}>
                    {/* Outer discipline ring */}
                    <circle cx={x} cy={y} r={13} fill="none" stroke={peakColor} strokeWidth={2.5} opacity={isDrag ? 0.5 : 0.8} />
                    {isSel && <circle cx={x} cy={y} r={16} fill="none" stroke={peakColor} strokeWidth={1.5} strokeDasharray="3 2" />}
                    {/* Main circle */}
                    <circle cx={x} cy={y} r={10} fill={m.color} stroke="#fff" strokeWidth={2} opacity={isDrag ? 0.7 : 1} />
                    <text x={x} y={y+1} textAnchor="middle" dominantBaseline="middle"
                      fill="#fff" fontSize={11} fontWeight={700} style={{ pointerEvents:"none" }}>✦</text>
                    {/* Name */}
                    <text x={x} y={y+22} textAnchor="middle" fill="#1f2937" fontSize={10} fontWeight={600}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{m.name}</text>
                    {/* Peak discipline icon */}
                    {peakInfo && (
                      <g>
                        <circle cx={x+11} cy={y-11} r={6} fill={peakColor} stroke="#fff" strokeWidth={1} />
                        <text x={x+11} y={y-10} textAnchor="middle" dominantBaseline="middle"
                          fill="#fff" fontSize={7} style={{ pointerEvents:"none" }}>{peakInfo.icon}</text>
                      </g>
                    )}
                    {/* Artifact count badge */}
                    {equippedCount > 0 && (
                      <g>
                        <circle cx={x-11} cy={y-11} r={5} fill="#d97706" stroke="#fff" strokeWidth={1} />
                        <text x={x-11} y={y-10} textAnchor="middle" dominantBaseline="middle"
                          fill="#fff" fontSize={6} fontWeight={700} style={{ pointerEvents:"none" }}>{equippedCount}</text>
                      </g>
                    )}
                    {/* Fatigue indicator */}
                    {fat > 0.3 && (
                      <text x={x} y={y+31} textAnchor="middle" fill={fat > 0.6 ? "#ef4444" : "#f59e0b"} fontSize={8}
                        style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>
                        {fat > 0.6 ? "😓" : "💤"} {Math.round(fat*100)}%
                      </text>
                    )}
                  </g>
                );
              })}
 
              {/* Dragon tokens (map only) */}
              {mapDragons.map(d => {
                const isSel = panel?.type === "dragon" && panel?.id === d.primary_key;
                const isDrag = dragging?.id === d.primary_key && dragging?.type === "dragon";
                const { x, y } = tokenXY(d);
                const s = 12;
                const triPts = `${x},${y-s} ${x+s},${y+s} ${x-s},${y+s}`;
                const peak = peakDiscipline(d.disciplines, d.primaryDiscipline);
                const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(dd => dd.key === peak.key) : null;
                const peakColor = peakInfo?.color || d.color;
                const fat = d.fatigue || 0;
                const equippedCount = (Array.isArray(d.artifacts) ? d.artifacts : []).filter(a => a?.name && a?.effects).length;
                // Size-based scaling
                const sizeScale = d.dragonSize === "Ancient" ? 1.3 : d.dragonSize === "Adult" ? 1.1 : d.dragonSize === "Young" ? 0.95 : 0.8;
                const ss = Math.round(s * sizeScale);
                const scaledPts = `${x},${y-ss} ${x+ss},${y+ss} ${x-ss},${y+ss}`;
                return (
                  <g key={d.primary_key} onMouseDown={e => handleTokenMouseDown(e, "dragon", d.primary_key)}
                    style={{ cursor: tool === "select" ? "grab" : "default" }}>
                    {/* Outer discipline glow */}
                    <polygon points={`${x},${y-ss-5} ${x+ss+5},${y+ss+5} ${x-ss-5},${y+ss+5}`}
                      fill="none" stroke={peakColor} strokeWidth={2.5} opacity={isDrag ? 0.4 : 0.7} />
                    {isSel && <polygon points={`${x},${y-ss-8} ${x+ss+8},${y+ss+8} ${x-ss-8},${y+ss+8}`}
                      fill="none" stroke={peakColor} strokeWidth={1.5} strokeDasharray="3 2" />}
                    {/* Main triangle */}
                    <polygon points={scaledPts} fill={d.color} stroke="#fff" strokeWidth={2} opacity={isDrag ? 0.7 : 1} />
                    <text x={x} y={y+Math.round(ss*0.4)} textAnchor="middle" dominantBaseline="middle"
                      fill="#fff" fontSize={Math.round(9*sizeScale)} fontWeight={700} style={{ pointerEvents:"none" }}>🐉</text>
                    {/* Name + element */}
                    <text x={x} y={y+ss+14} textAnchor="middle" fill="#1f2937" fontSize={10} fontWeight={600}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{d.name}</text>
                    {d.element && <text x={x} y={y+ss+24} textAnchor="middle" fill="#6b7280" fontSize={9}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>{d.element}</text>}
                    {/* Peak discipline badge */}
                    {peakInfo && (
                      <g>
                        <circle cx={x+ss+2} cy={y-ss+2} r={6} fill={peakColor} stroke="#fff" strokeWidth={1} />
                        <text x={x+ss+2} y={y-ss+3} textAnchor="middle" dominantBaseline="middle"
                          fill="#fff" fontSize={7} style={{ pointerEvents:"none" }}>{peakInfo.icon}</text>
                      </g>
                    )}
                    {/* Artifact badge */}
                    {equippedCount > 0 && (
                      <g>
                        <circle cx={x-ss-2} cy={y-ss+2} r={5} fill="#d97706" stroke="#fff" strokeWidth={1} />
                        <text x={x-ss-2} y={y-ss+3} textAnchor="middle" dominantBaseline="middle"
                          fill="#fff" fontSize={6} fontWeight={700} style={{ pointerEvents:"none" }}>{equippedCount}</text>
                      </g>
                    )}
                    {/* Fatigue */}
                    {fat > 0.3 && (
                      <text x={x} y={y+ss+(d.element ? 33 : 24)} textAnchor="middle" fill={fat > 0.6 ? "#ef4444" : "#f59e0b"} fontSize={8}
                        style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>
                        {fat > 0.6 ? "😓" : "💤"} {Math.round(fat*100)}%
                      </text>
                    )}
                    {/* Size label for Ancient/Wyrmling */}
                    {(d.dragonSize === "Ancient" || d.dragonSize === "Wyrmling") && (
                      <text x={x} y={y-ss-5} textAnchor="middle" fill={d.dragonSize === "Ancient" ? "#7c3aed" : "#9ca3af"} fontSize={7} fontWeight={600}
                        style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>{d.dragonSize}</text>
                    )}
                  </g>
                );
              })}
 
              {/* Drag trail */}
              {dragging && dragPos && (() => {
                const src = dragging.type === "character"
                  ? characters.find(c => c.primary_key === dragging.id)
                  : dragging.type === "mage"
                  ? mages.find(m => m.primary_key === dragging.id)
                  : dragging.type === "dragon"
                  ? dragons.find(d => d.primary_key === dragging.id)
                  : armies.find(a => a.primary_key === dragging.id);
                if (!src?.coordinates) return null;
                const { x, y } = parseCoord(src.coordinates);
                return <line x1={x} y1={y} x2={dragPos[0]} y2={dragPos[1]}
                  stroke={src.color} strokeWidth={1} strokeDasharray="4 3" opacity={0.4} />;
              })()}
            </g>
          </svg>
 
          {/* Kingdom border edit overlay */}
          {editingKingdom && (
            <div style={{
              position:"absolute", top:12, left:"50%", transform:"translateX(-50%)",
              background:"#1e1b4b", borderRadius:10, padding:"10px 18px",
              display:"flex", alignItems:"center", gap:12, boxShadow:"0 4px 20px #0004", zIndex:25,
            }}>
              <span style={{ color:"#a5b4fc", fontSize:13 }}>✏ Drag vertices to move · Click edge to add point</span>
              <button onClick={confirmKingdomEdit}
                style={{ background:"#22c55e", color:"#fff", border:"none", borderRadius:6, padding:"5px 14px", cursor:"pointer", fontSize:13, fontWeight:600 }}>
                ✓ Confirm
              </button>
              <button onClick={cancelKingdomEdit}
                style={{ background:"#6b7280", color:"#fff", border:"none", borderRadius:6, padding:"5px 14px", cursor:"pointer", fontSize:13 }}>
                ✕ Cancel
              </button>
            </div>
          )}
 
          {/* Horizontal scrollbar */}
          {showHScroll && (
            <div style={{
              position:"absolute", bottom:0, left:0,
              width: showVScroll ? `calc(100% - ${SCROLL_W}px)` : "100%",
              height:SCROLL_W, background:"#e5e7eb", zIndex:15,
            }}>
              <div
                onMouseDown={e => handleScrollMouseDown(e, "h")}
                style={{
                  position:"absolute", top:2, height:SCROLL_W - 4, borderRadius:4,
                  left: hThumbX, width: hThumbW,
                  background:"#9ca3af", cursor:"pointer",
                  transition: scrollDrag.current ? "none" : "background 0.1s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#6b7280"}
                onMouseLeave={e => e.currentTarget.style.background = "#9ca3af"}
              />
            </div>
          )}
 
          {/* Vertical scrollbar */}
          {showVScroll && (
            <div style={{
              position:"absolute", top:0, right:0,
              height: showHScroll ? `calc(100% - ${SCROLL_W}px)` : "100%",
              width:SCROLL_W, background:"#e5e7eb", zIndex:15,
            }}>
              <div
                onMouseDown={e => handleScrollMouseDown(e, "v")}
                style={{
                  position:"absolute", left:2, width:SCROLL_W - 4, borderRadius:4,
                  top: vThumbY, height: vThumbH,
                  background:"#9ca3af", cursor:"pointer",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#6b7280"}
                onMouseLeave={e => e.currentTarget.style.background = "#9ca3af"}
              />
            </div>
          )}
 
          {/* Corner fill when both scrollbars visible */}
          {showHScroll && showVScroll && (
            <div style={{ position:"absolute", bottom:0, right:0, width:SCROLL_W, height:SCROLL_W, background:"#e5e7eb", zIndex:16 }} />
          )}
 
          {/* Zoom indicator + mobile zoom controls */}
          <div style={{ position:"absolute", bottom: showHScroll ? SCROLL_W + 8 : 12, right: panel ? 316 + (showVScroll ? SCROLL_W : 0) : showVScroll ? SCROLL_W + 8 : 12, display:"flex", alignItems:"center", gap:4, zIndex:16 }}>
            {isMobile && (
              <>
                <button onClick={() => { const nz = Math.max(0.3, zoom * 0.8); setZoom(nz); setPan(p => ({ x: containerSize.w/2 - (containerSize.w/2 - p.x) * (nz/zoom), y: containerSize.h/2 - (containerSize.h/2 - p.y) * (nz/zoom) })); }}
                  style={{ width:32, height:32, borderRadius:8, border:"1px solid #d9d2c9", background:"#ffffffdd", color:"#3d3529", fontSize:18, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>−</button>
                <button onClick={() => { const nz = Math.min(4, zoom * 1.25); setZoom(nz); setPan(p => ({ x: containerSize.w/2 - (containerSize.w/2 - p.x) * (nz/zoom), y: containerSize.h/2 - (containerSize.h/2 - p.y) * (nz/zoom) })); }}
                  style={{ width:32, height:32, borderRadius:8, border:"1px solid #d9d2c9", background:"#ffffffdd", color:"#3d3529", fontSize:18, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>+</button>
              </>
            )}
            <div style={{ background:"#ffffffcc", border:"1px solid #e5e7eb", borderRadius:6, padding:"3px 10px", fontSize:12, color:"#6b7280" }}>
              {Math.round(zoom * 100)}%
            </div>
          </div>

          {/* Mobile: Fit to content + reset buttons */}
          {isMobile && (
            <div style={{ position:"absolute", bottom: showHScroll ? SCROLL_W + 8 : 12, left:12, display:"flex", gap:4, zIndex:16 }}>
              <button onClick={fitToContent} style={{ height:32, borderRadius:8, border:"1px solid #d9d2c9", background:"#ffffffdd", color:"#3d3529", fontSize:11, fontWeight:600, cursor:"pointer", padding:"0 10px" }}>⊡ Fit</button>
              <button onClick={() => { setPan({ x:0, y:0 }); setZoom(1); }} style={{ height:32, borderRadius:8, border:"1px solid #d9d2c9", background:"#ffffffdd", color:"#3d3529", fontSize:11, fontWeight:600, cursor:"pointer", padding:"0 10px" }}>⊞ Reset</button>
            </div>
          )}
 
          {/* Context menu */}
          {contextMenu && (() => {
            const ent = contextMenu.entity;
            // Find the actual entity object for quick-assign
            const entityObj = ent ? (
              ent.type === "character" ? characters.find(c => c.primary_key === ent.id) :
              ent.type === "mage" ? mages.find(m => m.primary_key === ent.id) :
              ent.type === "dragon" ? dragons.find(d => d.primary_key === ent.id) :
              ent.type === "army" ? armies.find(a => a.primary_key === ent.id) :
              ent.type === "city" ? cities.find(c => c.primary_key === ent.id) : null
            ) : null;
            const canAssign = ent && ["character","mage","dragon"].includes(ent.type);
            const canAssignArmy = ent && ent.type === "army";

            async function quickAssignToArmy(armyKey) {
              if (!entityObj) return;
              const updated = { ...entityObj, army: armyKey || null, city: null, coordinates: armyKey ? null : entityObj.coordinates };
              if (!armyKey) { const base = entityObj.coordinates ? parseCoord(entityObj.coordinates) : { x:400, y:400 }; updated.coordinates = coordStr(base.x, base.y); updated.army = null; }
              if (ent.type === "character") { setCharacters(cs => cs.map(c => c.primary_key === ent.id ? updated : c)); }
              else if (ent.type === "mage") { setMages(ms => ms.map(m => m.primary_key === ent.id ? updated : m)); }
              else if (ent.type === "dragon") { setDragons(ds => ds.map(d => d.primary_key === ent.id ? updated : d)); }
              setContextMenu(null); setPanel(null);
              try { await apiPut(updated); } catch(e) { console.error(e); }
            }

            async function quickAssignToCity(cityKey) {
              if (!entityObj) return;
              const updated = { ...entityObj, city: cityKey || null, army: null, coordinates: cityKey ? null : entityObj.coordinates };
              if (!cityKey) { updated.coordinates = coordStr(contextMenu.wx, contextMenu.wy); updated.city = null; }
              if (ent.type === "character") { setCharacters(cs => cs.map(c => c.primary_key === ent.id ? updated : c)); }
              else if (ent.type === "mage") { setMages(ms => ms.map(m => m.primary_key === ent.id ? updated : m)); }
              else if (ent.type === "dragon") { setDragons(ds => ds.map(d => d.primary_key === ent.id ? updated : d)); }
              else if (ent.type === "army") { setArmies(as => as.map(a => a.primary_key === ent.id ? updated : a)); }
              setContextMenu(null); setPanel(null);
              try { await apiPut(updated); } catch(e) { console.error(e); }
            }

            const menuItemStyle = { padding:"7px 14px", fontSize:12, color:"#3d3529", cursor:"pointer", display:"flex", alignItems:"center", gap:8, fontFamily:FONT_BODY };
            const hoverIn = e => e.currentTarget.style.background="#f0ebe4";
            const hoverOut = e => e.currentTarget.style.background="transparent";

            return (
              <div style={{
                position:"absolute",
                left: Math.min(contextMenu.screenX, (containerRef.current?.clientWidth || 300) - 200),
                top: Math.min(contextMenu.screenY, (containerRef.current?.clientHeight || 400) - 200),
                background:"#faf9f7", border:"1px solid #e2ddd6", borderRadius:8,
                boxShadow:"0 4px 20px #0000001a", zIndex:50, minWidth:180, maxHeight:400, overflow:"auto",
              }}>
                {ent ? (
                  <>
                    {/* Edit */}
                    <div onClick={() => { setPanel(ent); setContextMenu(null); }}
                      style={menuItemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                      ✏️ Edit {ent.type.charAt(0).toUpperCase() + ent.type.slice(1)}
                    </div>

                    {/* Quick assign to army */}
                    {canAssign && (
                      <>
                        <div style={{ padding:"4px 14px", fontSize:9, color:"#9ca3af", fontWeight:600, letterSpacing:1, textTransform:"uppercase", borderTop:"1px solid #ece7e0" }}>Assign to Army</div>
                        {entityObj?.army && (
                          <div onClick={() => quickAssignToArmy(null)} style={menuItemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                            <span style={{ width:16, textAlign:"center", fontSize:10 }}>↗</span> Remove from Army
                          </div>
                        )}
                        {armies.filter(a => a.primary_key !== entityObj?.army).slice(0, 8).map(a => (
                          <div key={a.primary_key} onClick={() => quickAssignToArmy(a.primary_key)}
                            style={menuItemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                            <span style={{ width:10, height:10, background:a.color, borderRadius:2, transform:"rotate(45deg)", flexShrink:0 }} />
                            <span style={{ fontSize:11 }}>{a.name}</span>
                            {a.size > 0 && <span style={{ fontSize:9, color:"#9ca3af", marginLeft:"auto" }}>{a.size.toLocaleString()}</span>}
                          </div>
                        ))}
                      </>
                    )}

                    {/* Quick assign to city */}
                    {(canAssign || canAssignArmy) && (
                      <>
                        <div style={{ padding:"4px 14px", fontSize:9, color:"#9ca3af", fontWeight:600, letterSpacing:1, textTransform:"uppercase", borderTop:"1px solid #ece7e0" }}>Assign to City</div>
                        {entityObj?.city && (
                          <div onClick={() => quickAssignToCity(null)} style={menuItemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                            <span style={{ width:16, textAlign:"center", fontSize:10 }}>↗</span> Remove from City
                          </div>
                        )}
                        {cities.filter(c => c.primary_key !== entityObj?.city).slice(0, 8).map(c => (
                          <div key={c.primary_key} onClick={() => quickAssignToCity(c.primary_key)}
                            style={menuItemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background: STATUS_COLORS[c.status] || "#22c55e", flexShrink:0 }} />
                            <span style={{ fontSize:11 }}>{c.name}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ padding:"5px 14px", fontSize:10, color:"#9ca3af", fontWeight:600, letterSpacing:1, textTransform:"uppercase" }}>Place at this location</div>
                    {[
                      { type:"city",      icon:"●",  label:"City" },
                      { type:"character", icon:"👤", label:"Character" },
                      { type:"army",      icon:"⚔",  label:"Army" },
                      { type:"mage",      icon:"✦",  label:"Mage" },
                      { type:"dragon",    icon:"🐉", label:"Dragon" },
                    ].map(item => (
                      <div key={item.type}
                        onClick={() => { setModal({ type:item.type, x:contextMenu.wx, y:contextMenu.wy }); setContextMenu(null); }}
                        style={menuItemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                        <span style={{ width:16, textAlign:"center" }}>{item.icon}</span> {item.label}
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })()}
 
          {/* Detail panels */}
          {panelKingdom && <KingdomPanel key={panelKingdom.primary_key} kingdom={panelKingdom} characters={characters} armies={armies} cities={cities} mages={mages} dragons={dragons}
            onUpdate={updateKingdom} onClose={() => setPanel(null)} onDelete={() => deleteKingdom(panelKingdom.primary_key)} onEditBorders={startEditingKingdom} />}
          {panelCity && <CityPanel key={panelCity.primary_key} city={panelCity} characters={characters} armies={armies} mages={mages} dragons={dragons} kingdoms={kingdoms}
            onUpdate={updateCity} onClose={() => setPanel(null)} onDelete={() => deleteCity(panelCity.primary_key)} onMoveOut={handleMoveOut} />}
          {panelChar && <CharacterPanel key={panelChar.primary_key} character={panelChar} cities={cities} kingdoms={kingdoms} armies={armies}
            onUpdate={updateCharacter} onClose={() => setPanel(null)} onDelete={() => deleteCharacter(panelChar.primary_key)} />}
          {panelArmy && <ArmyPanel key={panelArmy.primary_key} army={panelArmy} characters={characters} cities={cities} kingdoms={kingdoms} mages={mages} dragons={dragons}
            onUpdate={updateArmy} onClose={() => setPanel(null)} onDelete={() => deleteArmy(panelArmy.primary_key)} onMoveOut={handleMoveOut} />}
        </div>
      </div>

      {/* Name modal */}
      {modal && <NameModal type={modal.type} onConfirm={handleModalConfirm} onCancel={() => { setModal(null); setDrawingPoints([]); }} />}

      {/* Mage / Dragon modals — centered overlays */}
      {panelMage && <MagePanel key={panelMage.primary_key} mage={panelMage} cities={cities} kingdoms={kingdoms} armies={armies} allMages={mages} allDragons={dragons}
        onUpdate={updateMage} onClose={() => setPanel(null)} onDelete={() => deleteMage(panelMage.primary_key)} />}
      {panelDragon && <DragonPanel key={panelDragon.primary_key} dragon={panelDragon} cities={cities} kingdoms={kingdoms} armies={armies} characters={characters} mages={mages} allDragons={dragons}
        onUpdate={updateDragon} onClose={() => setPanel(null)} onDelete={() => deleteDragon(panelDragon.primary_key)} />}

      {/* Power Rankings modal */}
      {showPowerRankings && <PowerRankingsModal mages={mages} dragons={dragons} kingdoms={kingdoms} onClose={() => setShowPowerRankings(false)} />}

      {/* Fatigue Manager modal */}
      {showFatigueManager && <FatigueManagerModal mages={mages} dragons={dragons} kingdoms={kingdoms} onUpdateMage={updateMage} onUpdateDragon={updateDragon} onClose={() => setShowFatigueManager(false)} />}

      {/* Gear modal */}
      {showGearModal && <GearModal mages={mages} dragons={dragons} kingdoms={kingdoms} onClose={() => setShowGearModal(false)} />}

      {/* Battle Readiness modal */}
      {showReadiness && <ReadinessModal armies={armies} characters={characters} mages={mages} dragons={dragons} kingdoms={kingdoms} cities={cities} onClose={() => setShowReadiness(false)} />}
 
      {/* Battle modal */}
      {showBattle && <BattleModal
        armies={armies} characters={characters} mages={mages} dragons={dragons}
        onUpdateMage={updateMage} onUpdateDragon={updateDragon}
        onClose={() => setShowBattle(false)}
        onConfirmBattle={async (idA, survA, idB, survB, entsA, entsB) => {
          const updA = { ...armies.find(a => a.primary_key === idA), size: survA };
          const updB = { ...armies.find(a => a.primary_key === idB), size: survB };
          setArmies(as => as.map(a => a.primary_key === idA ? updA : a.primary_key === idB ? updB : a));
          setSaving(true);
          try {
            await apiPut(updA); await apiPut(updB);
            const allEnts = [...(entsA || []), ...(entsB || [])];
            for (const ent of allEnts) {
              if (ent.type !== "mage" && ent.type !== "dragon") continue;
              const fatigueIncrease = 0.03 + Math.random() * 0.03;
              const newFatigue = Math.min(1, (ent.fatigue || 0) + fatigueIncrease);
              const newArts = Array.isArray(ent.artifacts) ? ent.artifacts.map(a => {
                if (a?.name && a?.effects) return { ...a, energy: a.energy - 1, attunement: Math.min(10, (a.attunement || 0) + 1) };
                return a;
              }) : [];
              const updated = { ...ent, fatigue: newFatigue, artifacts: newArts.length > 0 ? newArts : undefined };
              const { type: _, ...saveData } = updated;
              if (ent.type === "mage") { setMages(ms => ms.map(m => m.primary_key === ent.primary_key ? saveData : m)); }
              else { setDragons(ds => ds.map(d => d.primary_key === ent.primary_key ? saveData : d)); }
              await apiPut(saveData);
            }
          } catch(e) { console.error(e); } finally { setSaving(false); }
        }}
      />}

      {/* Duel modal — mages/dragons only, no armies */}
      {showDuel && <DuelModal
        mages={mages} dragons={dragons} kingdoms={kingdoms}
        onUpdateMage={updateMage} onUpdateDragon={updateDragon}
        leagues={leagues} setLeagues={setLeagues}
        onClose={() => setShowDuel(false)}
        onConfirmDuel={async (finalEnts) => {
          setSaving(true);
          try {
            // ── War scoring: check if this duel involves warring kingdoms ──
            const alive = finalEnts.filter(e => (e._currentHP ?? e.currentHP ?? 1) > 0);
            const dead = finalEnts.filter(e => (e._currentHP ?? e.currentHP ?? 1) <= 0);
            if (dead.length > 0 && alive.length > 0 && wars.length > 0) {
              const updatedWars = [...wars];
              for (const loser of dead) {
                const loserAllegiance = loser.allegiance;
                if (!loserAllegiance) continue;
                const loserPwr = normalizePower(calcDisciplinePowerForType(loser.disciplines, loser.type));
                // Find a winner who killed this loser (any alive entity from opposite allegiance)
                for (const winner of alive) {
                  if (!winner.allegiance || winner.allegiance === loserAllegiance) continue;
                  // Check if these two kingdoms are at war
                  for (const war of updatedWars) {
                    const wIsA = (winner.allegiance === war.kingdomA && loserAllegiance === war.kingdomB);
                    const wIsB = (winner.allegiance === war.kingdomB && loserAllegiance === war.kingdomA);
                    if (wIsA || wIsB) {
                      const points = Math.max(1, loserPwr);
                      if (wIsA) war.scoreA += points;
                      else war.scoreB += points;
                      war.duels.push({
                        winner: winner.name, winnerKingdom: winner.allegiance,
                        loser: loser.name, loserKingdom: loserAllegiance,
                        points, timestamp: Date.now(),
                      });
                      break; // Only score once per war per loser
                    }
                  }
                  break; // Only credit one winner per loser
                }
              }
              setWars(updatedWars);
              // Persist updated wars to DynamoDB
              for (const war of updatedWars) {
                try { await apiPut(war); } catch (e) { console.error("Failed to save war score:", e); }
              }
            }

            for (const ent of finalEnts) {
              const fatigueIncrease = 0.03 + Math.random() * 0.03;
              const newFatigue = Math.min(1, (ent.fatigue || 0) + fatigueIncrease);
              const newArts = Array.isArray(ent.artifacts) ? ent.artifacts.map(a => {
                if (a?.name && a?.effects) {
                  // Drain 1 energy + gain 1 attunement (capped at 10)
                  return { ...a, energy: a.energy - 1, attunement: Math.min(10, (a.attunement || 0) + 1) };
                }
                return a;
              }) : [];
              // Apply discipline XP gains
              const xpResult = applyDisciplineXP(ent, ent._xpGains || {});
              const updated = {
                ...ent,
                fatigue: newFatigue,
                artifacts: newArts.length > 0 ? newArts : undefined,
                disciplines: xpResult.disciplines,
                disciplineXP: xpResult.disciplineXP,
                abilityMastery: ent.abilityMastery || ent._abilityUses || undefined,
                currentHP: ent.currentHP,
              };
              const { type: _, _battleFatigue: __, _xpGains: ___, _abilityUses: ____, _currentHP: _____, _maxHP: ______, ...saveData } = updated;
              if (ent.type === "mage") { setMages(ms => ms.map(m => m.primary_key === ent.primary_key ? saveData : m)); }
              else { setDragons(ds => ds.map(d => d.primary_key === ent.primary_key ? saveData : d)); }
              await apiPut(saveData);
            }
          } catch(e) { console.error(e); } finally { setSaving(false); }
        }}
      />}

      {/* Wars modal */}
      {showWars && <WarsModal
        kingdoms={kingdoms} wars={wars} setWars={setWars}
        mages={mages} dragons={dragons}
        onClose={() => setShowWars(false)}
      />}

      {/* Leagues modal — full page ranking system */}
      {showLeagues && <LeaguesModal
        leagues={leagues} setLeagues={setLeagues}
        mages={mages} dragons={dragons} kingdoms={kingdoms}
        onClose={() => setShowLeagues(false)}
      />}
    </div>
  );
}
 
 
// ── Discipline XP System ─────────────────────────────────────────────────────
// Each entity can have a `disciplineXP` object: { flamecalling: 0.4, ... }
// XP ranges 0.0 to xpThreshold per discipline. At threshold, the entity levels up and XP resets.
// Threshold scales with level: higher levels need dramatically more XP.
//
// XP sources per round:
// 1. OBSERVATION XP: gained for every discipline where you OR the opponent has level > 0
//    - Higher opponent = more XP (learning from the better)
//    - Own disciplines gain XP too (practice through combat)
//    - Small XP even at level 11 vs equal/lower (no hard gate)
// 2. PRACTICE XP: bonus XP for disciplines of abilities you actually USE this round
//    - Attack slots and defense slot all grant practice XP
//    - Rewards actively using your disciplines in combat
// 3. UNDERDOG BONUS: slight XP bonus when losing against a stronger opponent
//
// Formula: XP = base * exposure * difficultyScale * outcomeBonus
// + practiceBonus for used disciplines

// XP threshold to level up — exponential scaling makes higher levels much harder
// Lv0-2: 1.0 (quick), Lv3-5: ~1.3-2.2, Lv6-8: ~2.9-4.8, Lv9-11: ~6.3-10.6
function xpThresholdForLevel(currentLevel) {
  if (currentLevel <= 0) return 1.0;
  if (currentLevel <= 2) return 1.0;
  // Exponential curve: 1.0 * 1.3^(lvl - 2)
  return Math.round(Math.pow(1.3, currentLevel - 2) * 100) / 100;
}

function calcDuelXPGains(myDisc, oppDisc, isWinner, usedDiscs, myPower, oppPower, primaryDisc) {
  const myD = myDisc || {};
  const oppD = oppDisc || {};
  const gains = {};
  const used = usedDiscs || [];

  // Collect all disciplines that are relevant (mine OR opponent's)
  for (const d of MAGIC_DISCIPLINES) {
    const myLvl = myD[d.key] || 0;
    const oppLvl = oppD[d.key] || 0;
    if (myLvl >= 12) continue;
    if (myLvl <= 0 && oppLvl <= 0) continue; // neither side has this discipline

    // Exposure: how much there is to learn
    let exposure;
    if (oppLvl > myLvl) {
      const gap = Math.min(oppLvl - myLvl, 6);
      exposure = 0.5 + (gap / 6) * 0.5; // 0.5 to 1.0
    } else if (oppLvl === myLvl && oppLvl > 0) {
      exposure = 0.25;
    } else if (oppLvl > 0 && oppLvl < myLvl) {
      exposure = 0.10 + (oppLvl / 12) * 0.15;
    } else if (myLvl > 0 && oppLvl <= 0) {
      // Own discipline, opponent doesn't have it — still gain from practice (lower rate)
      exposure = 0.12;
    } else {
      exposure = 0.05; // minimal — opponent has it but you don't, and you're not using it
    }

    const base = 0.08;
    // Difficulty scale: exponential dropoff at higher levels
    // Lv0: 1.0, Lv3: 0.55, Lv6: 0.30, Lv9: 0.17, Lv11: 0.11
    const diffScale = Math.max(0.10, Math.pow(0.82, myLvl));
    // Outcome bonus: winners get +35%, losers vs stronger opponents get +15%
    let outcomeBonus = 1.0;
    if (isWinner) {
      outcomeBonus = 1.35;
    } else if (oppPower && myPower && oppPower > myPower * 1.2) {
      // Underdog: losing against a significantly stronger opponent teaches you
      outcomeBonus = 1.15;
    }

    let xp = base * exposure * diffScale * outcomeBonus;

    // Practice bonus: +50% XP for disciplines you actively used this round
    if (used.includes(d.key) && myLvl > 0) {
      xp += base * 0.5 * diffScale;
    }

    // Primary discipline bonus: +40% XP if this is the entity's primary discipline
    // Stacks with practice bonus — primary disciplines you USE get both bonuses
    if (primaryDisc && d.key === primaryDisc && myLvl > 0) {
      xp *= 1.4;
    }

    if (xp > 0.001) gains[d.key] = Math.round(xp * 1000) / 1000;
  }

  return gains;
}

// Apply accumulated XP gains to an entity, auto-leveling when XP >= threshold
function applyDisciplineXP(entity, totalXPGains) {
  const disc = { ...(entity.disciplines || {}) };
  const xp = { ...(entity.disciplineXP || {}) };
  const levelUps = {};

  for (const [key, gain] of Object.entries(totalXPGains)) {
    let currentLvl = disc[key] || 0;
    if (currentLvl >= 12) continue;
    xp[key] = (xp[key] || 0) + gain;

    // Check level-ups against threshold (may gain multiple levels in rare cases)
    let threshold = xpThresholdForLevel(currentLvl);
    while (xp[key] >= threshold && currentLvl < 12) {
      xp[key] -= threshold;
      const fromLvl = currentLvl;
      currentLvl = Math.min(12, currentLvl + 1);
      if (!levelUps[key]) levelUps[key] = { from: fromLvl, to: currentLvl };
      else levelUps[key].to = currentLvl;
      threshold = xpThresholdForLevel(currentLvl);
    }
    disc[key] = currentLvl;
    if (currentLvl >= 12) { disc[key] = 12; xp[key] = 0; }
  }

  return { disciplines: disc, disciplineXP: xp, levelUps };
}

// ── Wars Modal — kingdom vs kingdom competition tracker ──────────────────────
function WarsModal({ kingdoms, wars, setWars, mages, dragons, onClose }) {
  const [newWarA, setNewWarA] = useState("");
  const [newWarB, setNewWarB] = useState("");
  const [saving, setSaving] = useState(false);

  async function createWar() {
    if (!newWarA || !newWarB || newWarA === newWarB || saving) return;
    const exists = wars.some(w =>
      (w.kingdomA === newWarA && w.kingdomB === newWarB) ||
      (w.kingdomA === newWarB && w.kingdomB === newWarA)
    );
    if (exists) return;
    const primary_key = "war_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const newWar = {
      primary_key, sort_key: "War",
      kingdomA: newWarA, kingdomB: newWarB,
      scoreA: 0, scoreB: 0,
      duels: [], createdAt: Date.now(),
    };
    setSaving(true);
    try {
      await apiPut(newWar);
      setWars(ws => [...ws, newWar]);
      setNewWarA(""); setNewWarB("");
    } catch (e) { console.error("Failed to create war:", e); }
    finally { setSaving(false); }
  }

  async function resetWar(war) {
    if (saving) return;
    const updated = { ...war, scoreA: 0, scoreB: 0, duels: [] };
    setSaving(true);
    try {
      await apiPut(updated);
      setWars(ws => ws.map(w => w.primary_key === war.primary_key ? updated : w));
    } catch (e) { console.error("Failed to reset war:", e); }
    finally { setSaving(false); }
  }

  async function deleteWar(war) {
    if (saving) return;
    setSaving(true);
    try {
      await apiDelete(war.primary_key, "War");
      setWars(ws => ws.filter(w => w.primary_key !== war.primary_key));
    } catch (e) { console.error("Failed to delete war:", e); }
    finally { setSaving(false); }
  }

  const getKingdom = (pk) => kingdoms.find(k => k.primary_key === pk);
  const getKingdomEntities = (pk) => {
    const m = mages.filter(e => e.allegiance === pk);
    const d = dragons.filter(e => e.allegiance === pk);
    return { mages: m, dragons: d, total: m.length + d.length };
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000066", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:"#0f172a", borderRadius:12, width:"min(700px, 95vw)", maxHeight:"90vh", boxShadow:"0 20px 60px #0008", display:"flex", flexDirection:"column", overflow:"hidden", color:"#f1f5f9" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", background:"linear-gradient(135deg,#1e293b,#0f172a)", borderBottom:"1px solid #334155", flexShrink:0 }}>
          <span style={{ fontSize:16, fontWeight:700, fontFamily:FONT_DISPLAY, letterSpacing:2 }}>⚔️ Kingdom Wars</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#94a3b8" }}>✕</button>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:16, display:"flex", flexDirection:"column", gap:16 }}>

          {/* Create new war */}
          <div style={{ padding:12, borderRadius:8, background:"#1e293b", border:"1px solid #334155" }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>Declare War</div>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <select value={newWarA} onChange={e => setNewWarA(e.target.value)}
                style={{ flex:1, minWidth:120, fontSize:11, padding:"6px 8px", borderRadius:5, background:"#0f172a", border:"1px solid #475569", color:"#f1f5f9", cursor:"pointer" }}>
                <option value="">— Kingdom A —</option>
                {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
              </select>
              <span style={{ fontSize:14, fontWeight:900, color:"#ef4444" }}>VS</span>
              <select value={newWarB} onChange={e => setNewWarB(e.target.value)}
                style={{ flex:1, minWidth:120, fontSize:11, padding:"6px 8px", borderRadius:5, background:"#0f172a", border:"1px solid #475569", color:"#f1f5f9", cursor:"pointer" }}>
                <option value="">— Kingdom B —</option>
                {kingdoms.filter(k => k.primary_key !== newWarA).map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
              </select>
              <button onClick={createWar} disabled={!newWarA || !newWarB || newWarA === newWarB} style={{
                fontSize:11, fontWeight:700, padding:"6px 14px", borderRadius:5, cursor:"pointer",
                background: newWarA && newWarB && newWarA !== newWarB ? "linear-gradient(135deg, #dc262644, #ef444444)" : "#1e293b",
                border: "1px solid #ef444444", color: newWarA && newWarB ? "#ef4444" : "#475569",
              }}>⚔️ Declare</button>
            </div>
          </div>

          {/* Active wars */}
          {wars.length === 0 && (
            <div style={{ textAlign:"center", padding:20, color:"#475569", fontSize:12 }}>No active wars. Declare one above.</div>
          )}

          {wars.map(war => {
            const kA = getKingdom(war.kingdomA);
            const kB = getKingdom(war.kingdomB);
            if (!kA || !kB) return null;
            const entsA = getKingdomEntities(war.kingdomA);
            const entsB = getKingdomEntities(war.kingdomB);
            const totalScore = war.scoreA + war.scoreB;
            const pctA = totalScore > 0 ? (war.scoreA / totalScore) * 100 : 50;
            const leading = war.scoreA > war.scoreB ? "A" : war.scoreB > war.scoreA ? "B" : null;

            return (
              <div key={war.id} style={{ padding:14, borderRadius:8, background:"#1e293b", border:"1px solid #334155" }}>
                {/* Kingdom names + scores */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flex:1 }}>
                    <div style={{ width:10, height:10, borderRadius:"50%", background: kA.color || "#64748b" }} />
                    <span style={{ fontSize:13, fontWeight:700, color: leading === "A" ? "#22c55e" : "#f1f5f9", fontFamily:FONT_DISPLAY }}>
                      {kA.name}
                    </span>
                    <span style={{ fontSize:9, color:"#64748b" }}>({entsA.total} fighters)</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:20, fontWeight:900, color: leading === "A" ? "#22c55e" : "#94a3b8", fontFamily:FONT_DISPLAY }}>{war.scoreA}</span>
                    <span style={{ fontSize:10, color:"#475569", fontWeight:700 }}>—</span>
                    <span style={{ fontSize:20, fontWeight:900, color: leading === "B" ? "#22c55e" : "#94a3b8", fontFamily:FONT_DISPLAY }}>{war.scoreB}</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flex:1, justifyContent:"flex-end" }}>
                    <span style={{ fontSize:9, color:"#64748b" }}>({entsB.total} fighters)</span>
                    <span style={{ fontSize:13, fontWeight:700, color: leading === "B" ? "#22c55e" : "#f1f5f9", fontFamily:FONT_DISPLAY }}>
                      {kB.name}
                    </span>
                    <div style={{ width:10, height:10, borderRadius:"50%", background: kB.color || "#64748b" }} />
                  </div>
                </div>

                {/* Score bar */}
                <div style={{ height:8, borderRadius:4, background:"#0f172a", overflow:"hidden", marginBottom:8, display:"flex" }}>
                  <div style={{ width:`${pctA}%`, height:"100%", background: kA.color || "#3b82f6", transition:"width 0.3s", borderRadius: pctA >= 100 ? 4 : "4px 0 0 4px" }} />
                  <div style={{ flex:1, height:"100%", background: kB.color || "#ef4444", borderRadius: pctA <= 0 ? 4 : "0 4px 4px 0" }} />
                </div>

                {/* Recent duels log */}
                {war.duels.length > 0 && (
                  <div style={{ marginBottom:8, maxHeight:120, overflowY:"auto" }}>
                    <div style={{ fontSize:8, fontWeight:700, color:"#64748b", letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>Recent Duels ({war.duels.length})</div>
                    {[...war.duels].reverse().slice(0, 10).map((d, i) => {
                      const winK = getKingdom(d.winnerKingdom);
                      return (
                        <div key={i} style={{ fontSize:9, color:"#94a3b8", padding:"2px 0", display:"flex", gap:4, alignItems:"center" }}>
                          <span style={{ color: winK?.color || "#22c55e", fontWeight:600 }}>{d.winner}</span>
                          <span style={{ color:"#475569" }}>defeated</span>
                          <span style={{ color:"#ef4444" }}>{d.loser}</span>
                          <span style={{ color:"#a855f7", fontWeight:700 }}>+{d.points} pts</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={() => resetWar(war)} disabled={saving} style={{
                    fontSize:9, fontWeight:600, padding:"4px 10px", borderRadius:4, cursor: saving ? "wait" : "pointer",
                    background:"#0f172a", border:"1px solid #475569", color:"#94a3b8",
                  }}>↻ Reset Scores</button>
                  <button onClick={() => deleteWar(war)} disabled={saving} style={{
                    fontSize:9, fontWeight:600, padding:"4px 10px", borderRadius:4, cursor: saving ? "wait" : "pointer",
                    background:"#0f172a", border:"1px solid #ef444444", color:"#ef4444",
                  }}>✕ End War</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Duel Modal — mages/dragons only, no armies ───────────────────────────────
function DuelModal({ mages, dragons, kingdoms, onUpdateMage, onUpdateDragon, onConfirmDuel, onClose, leagues, setLeagues }) {
  const [sideA, setSideA] = useState([]);
  const [sideB, setSideB] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [severity, setSeverity] = useState(5);
  const [confirming, setConfirming] = useState(false);
  const [battleEnts, setBattleEnts] = useState(null);

  // Ability selections per entity per round: { [pk]: { attack1, attack2, defense, target } }
  const [selections, setSelections] = useState({});
  // Cooldown tracking: { [pk]: { [abilityKey]: roundsRemaining } }
  const [cooldowns, setCooldowns] = useState({});
  // Active buffs: { [pk]: [{ key, type, rounds, power, desc }] }
  const [activeBuffs, setActiveBuffs] = useState({});
  // Whether current round selections are locked (ready to resolve)
  const [roundPhase, setRoundPhase] = useState("select"); // "select" | "resolve"
  const [duelFilterName, setDuelFilterName] = useState("");
  const [duelFilterAllegiance, setDuelFilterAllegiance] = useState("");
  const [selectedLeague, setSelectedLeague] = useState(""); // league primary_key for point award
  const [duelFilterAlliance, setDuelFilterAlliance] = useState(""); // alliance name filter
  const [duelStakes, setDuelStakes] = useState(0); // challenge stakes (0 = normal duel, >0 = challenge)
  const [randPerSide, setRandPerSide] = useState(1); // entities per side for randomizer
  const [randResult, setRandResult] = useState(null); // { sideA: [{name, pk}], sideB: [{name, pk}] }

  const allEntities = [
    ...mages.map(m => ({ ...m, type: "mage" })),
    ...dragons.map(d => ({ ...d, type: "dragon" })),
  ];

  const entsA = sideA.map(pk => battleEnts ? battleEnts.a.find(e => e.primary_key === pk) : allEntities.find(e => e.primary_key === pk)).filter(Boolean);
  const entsB = sideB.map(pk => battleEnts ? battleEnts.b.find(e => e.primary_key === pk) : allEntities.find(e => e.primary_key === pk)).filter(Boolean);

  const activeA = entsA.filter(e => (e._currentHP ?? getCurrentHP(e)) > 0);
  const activeB = entsB.filter(e => (e._currentHP ?? getCurrentHP(e)) > 0);

  const canSim = sideA.length > 0 && sideB.length > 0 && !sideA.some(pk => sideB.includes(pk));
  const battleStarted = battleEnts !== null;
  const battleOver = battleStarted && (activeA.length === 0 || activeB.length === 0);
  const winner = battleOver ? (activeA.length > activeB.length ? "A" : activeB.length > activeA.length ? "B" : "A") : null;

  function addToSide(pk, side) {
    if (sideA.includes(pk) || sideB.includes(pk)) return;
    if (side === "A") setSideA(s => [...s, pk]);
    else setSideB(s => [...s, pk]);
    resetBattle();
  }
  function removeFromSide(pk, side) {
    if (side === "A") setSideA(s => s.filter(p => p !== pk));
    else setSideB(s => s.filter(p => p !== pk));
    resetBattle();
  }

  function resetBattle() { setRounds([]); setBattleEnts(null); setSelections({}); setCooldowns({}); setActiveBuffs({}); setRoundPhase("select"); }

  // ── Randomizer: pick matchups prioritizing entities with fewest duels ──
  function randomizeMatchup() {
    // Get pool: filter by league if selected, then by alliance if selected
    let pool = [...available];
    if (duelFilterAlliance && selectedLeagueObj) {
      const alliance = (selectedLeagueObj.alliances || []).find(a => a.name === duelFilterAlliance);
      if (alliance) pool = pool.filter(e => (alliance.members || []).includes(e.primary_key));
    }
    // Only include entities with abilities
    pool = pool.filter(e => e.learnedAbilities && Object.keys(e.learnedAbilities).length > 0);

    const needed = randPerSide * 2;
    if (pool.length < needed) {
      setRandResult({ error: `Need at least ${needed} eligible entities, only ${pool.length} available` });
      return;
    }

    // Count duels per entity from member wins+losses (authoritative source)
    const duelCounts = {};
    if (selectedLeagueObj) {
      for (const m of (selectedLeagueObj.members || [])) {
        duelCounts[m.pk] = (m.wins || 0) + (m.losses || 0);
      }
    }

    // Sort pool by fewest duels, then shuffle within same count
    const sorted = pool.map(e => ({ ...e, _duelCount: duelCounts[e.primary_key] || 0 }))
      .sort((a, b) => a._duelCount - b._duelCount || Math.random() - 0.5);

    // Pick randPerSide for each side
    const picked = sorted.slice(0, needed);
    // Shuffle picked and split
    for (let i = picked.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [picked[i], picked[j]] = [picked[j], picked[i]];
    }
    const sA = picked.slice(0, randPerSide).map(e => ({ name: e.name, pk: e.primary_key, type: e.type, duelCount: e._duelCount }));
    const sB = picked.slice(randPerSide).map(e => ({ name: e.name, pk: e.primary_key, type: e.type, duelCount: e._duelCount }));

    setRandResult({ sideA: sA, sideB: sB });
  }

  function applyRandomResult() {
    if (!randResult || randResult.error) return;
    setSideA(randResult.sideA.map(e => e.pk));
    setSideB(randResult.sideB.map(e => e.pk));
    resetBattle();
    setRandResult(null);
  }

  function startDuel() {
    const a = sideA.map(pk => allEntities.find(e => e.primary_key === pk)).filter(Boolean).map(e => {
      const maxHP = calcMaxHP(e);
      const curHP = getCurrentHP(e);
      return { ...e, _battleFatigue: e.fatigue || 0, _xpGains: {}, _abilityUses: { ...(e.abilityMastery || {}) }, _maxHP: maxHP, _currentHP: curHP, _shield: 0 };
    });
    const b = sideB.map(pk => allEntities.find(e => e.primary_key === pk)).filter(Boolean).map(e => {
      const maxHP = calcMaxHP(e);
      const curHP = getCurrentHP(e);
      return { ...e, _battleFatigue: e.fatigue || 0, _xpGains: {}, _abilityUses: { ...(e.abilityMastery || {}) }, _maxHP: maxHP, _currentHP: curHP, _shield: 0 };
    });
    setBattleEnts({ a, b });
    setRounds([]); setSelections({}); setCooldowns({}); setActiveBuffs({}); setRoundPhase("select");
  }

  // Get available abilities for an entity (learned + not on cooldown)
  function getAvailableAbilities(entity, type) {
    const learned = entity.learnedAbilities || {};
    const cd = cooldowns[entity.primary_key] || {};
    const all = [];
    for (const disc of Object.keys(ABILITIES)) {
      for (const ab of ABILITIES[disc]) {
        if (!learned[ab.key]) continue;
        if (type && ab.type !== type) continue;
        if ((cd[ab.key] || 0) > 0) continue;
        all.push({ ...ab, discipline: disc });
      }
    }
    return all;
  }

  // Get ALL learned abilities including those on cooldown (for showing grayed-out CD abilities)
  function getAllLearnedAbilities(entity, type) {
    const learned = entity.learnedAbilities || {};
    const cd = cooldowns[entity.primary_key] || {};
    const all = [];
    for (const disc of Object.keys(ABILITIES)) {
      for (const ab of ABILITIES[disc]) {
        if (!learned[ab.key]) continue;
        if (type && ab.type !== type) continue;
        const cdRemaining = cd[ab.key] || 0;
        all.push({ ...ab, discipline: disc, onCooldown: cdRemaining > 0, cdRemaining });
      }
    }
    return all;
  }

  // Get the target entity for counter indicator purposes
  function getTargetForEntity(entity, side) {
    const sel = selections[entity.primary_key] || {};
    const opponents = side === "A" ? activeB : activeA;
    if (!opponents.length) return null;
    if (sel.target) {
      const t = opponents.find(e => e.primary_key === sel.target && (e._currentHP ?? getCurrentHP(e)) > 0);
      if (t) return t;
    }
    const allies = side === "A" ? activeA : activeB;
    const sortedAllies = [...allies].sort((a, b) => getEntityPower(b) - getEntityPower(a));
    const sortedOpponents = [...opponents].sort((a, b) => getEntityPower(b) - getEntityPower(a));
    const myRank = sortedAllies.findIndex(e => e.primary_key === entity.primary_key);
    return myRank < sortedOpponents.length ? sortedOpponents[myRank] : sortedOpponents[0];
  }

  // Get counter matchup info for an attack ability vs target's defense
  function getCounterInfo(atkDisc, targetEntity) {
    if (!atkDisc || !targetEntity) return null;
    const tSel = selections[targetEntity.primary_key] || {};
    const tDefAb = tSel.defense ? getAbility(tSel.defense) : null;
    const tDefDisc = tDefAb?.discipline;
    if (!tDefDisc) return null;
    if (disciplineCounters(atkDisc, tDefDisc)) return { type: "strong", label: "⚔▶🛡 Counters defense!", color: "#22c55e" };
    if (disciplineCounters(tDefDisc, atkDisc)) return { type: "weak", label: "⚔◀🛡 Countered by defense", color: "#ef4444" };
    return null;
  }

  // Compute HP delta from last round for a given entity
  function getHPDelta(entityPk) {
    if (rounds.length === 0) return null;
    const lastRound = rounds[rounds.length - 1];
    // Find this entity in the last round's results (could be nameA or nameB side)
    // We need to match by primary_key — but round results store names. 
    // Better: look at entity._currentHP vs what it was before last resolve.
    // Since rounds store hpA/hpB and we know entity names, search for it.
    // Actually: the current entity._currentHP is AFTER last round. 
    // If rounds.length >= 2, previous HP = second-to-last round's HP for this entity.
    // If rounds.length === 1, previous HP = maxHP (start of battle).
    const allBattle = [...(battleEnts?.a || []), ...(battleEnts?.b || [])];
    const ent = allBattle.find(e => e.primary_key === entityPk);
    if (!ent) return null;
    const curHP = ent._currentHP ?? 0;
    
    if (rounds.length === 1) {
      // Before first round, HP was maxHP
      const maxHP = ent._maxHP ?? calcMaxHP(ent);
      const delta = curHP - maxHP;
      return delta !== 0 ? delta : null;
    }
    
    // Find HP from 2nd-to-last round
    const prevRound = rounds[rounds.length - 2];
    for (const r of prevRound.results) {
      if (r.nameA === ent.name) return curHP - (r.hpA ?? curHP) !== 0 ? curHP - r.hpA : null;
      if (r.nameB === ent.name) return curHP - (r.hpB ?? curHP) !== 0 ? curHP - r.hpB : null;
    }
    // If only 1 round, compare to last round's own result (the delta IS dmg taken)
    const lastResults = lastRound.results;
    for (const r of lastResults) {
      if (r.nameA === ent.name) {
        const prevHP = curHP + (r.dmgToA || 0) - (ent._regenApplied ? 0 : 0); // approximate
        return -(r.dmgToA || 0) || null;
      }
      if (r.nameB === ent.name) {
        return -(r.dmgToB || 0) || null;
      }
    }
    return null;
  }

  function setEntitySelection(pk, field, value) {
    setSelections(s => ({ ...s, [pk]: { ...(s[pk] || {}), [field]: value } }));
  }

  // Check all active entities have selections
  function allSelectionsReady() {
    const allActive = [...activeA, ...activeB];
    return allActive.every(e => {
      const sel = selections[e.primary_key];
      return sel && sel.attack1;
      // attack2 and defense are optional
    });
  }

  // Auto-assign targets for entities that haven't picked one
  // Equal sides: each entity targets a separate opponent (matched by power rank)
  // Larger side: first N match 1:1 by rank, extras default to strongest
  function getTarget(entity, side) {
    const sel = selections[entity.primary_key] || {};
    const allies = side === "A" ? activeA : activeB;
    const opponents = side === "A" ? activeB : activeA;
    if (!opponents.length) return null;
    // If user selected a target, use it (if still alive)
    if (sel.target) {
      const t = opponents.find(e => e.primary_key === sel.target && (e._currentHP ?? getCurrentHP(e)) > 0);
      if (t) return t;
    }
    // Sort both sides by power descending
    const sortedAllies = [...allies].sort((a, b) => getEntityPower(b) - getEntityPower(a));
    const sortedOpponents = [...opponents].sort((a, b) => getEntityPower(b) - getEntityPower(a));
    const myRank = sortedAllies.findIndex(e => e.primary_key === entity.primary_key);

    if (myRank < sortedOpponents.length) {
      // 1:1 match by power rank: strongest ally → strongest opponent, etc.
      return sortedOpponents[myRank];
    }
    // Surplus entity (larger side) — target the strongest remaining opponent
    return sortedOpponents[0];
  }

  // Quick Pick: auto-select best available abilities for an entity
  function quickPickEntity(entity, side) {
    const pk = entity.primary_key;
    const cd = cooldowns[pk] || {};
    const learned = entity.learnedAbilities || {};

    // Gather all available abilities (learned + off cooldown)
    const avail = [];
    for (const disc of Object.keys(ABILITIES)) {
      for (const ab of ABILITIES[disc]) {
        if (!learned[ab.key]) continue;
        if (ab.type === "passive") continue;
        if ((cd[ab.key] || 0) > 0) continue;
        avail.push({ ...ab, discipline: disc, power: calcAbilityPower(ab.key, entity) });
      }
    }

    // Best damage/buff abilities sorted by power
    const attacks = avail.filter(a => a.type === "damage" || a.type === "buff").sort((a, b) => b.power - a.power);
    // Best defense abilities sorted by power
    const defenses = avail.filter(a => a.type === "defense").sort((a, b) => b.power - a.power);

    const pick = { ...(selections[pk] || {}) };

    // Attack 1: highest power damage ability (prefer damage over buff)
    const dmgAttacks = attacks.filter(a => a.type === "damage");
    const buffAttacks = attacks.filter(a => a.type === "buff");
    pick.attack1 = dmgAttacks[0]?.key || attacks[0]?.key || "";

    // Exclude attack1
    const remaining = attacks.filter(a => a.key !== pick.attack1);

    // Prefer buff from remaining, otherwise next strongest
    const remainingBuffs = remaining.filter(a => a.type === "buff");

    pick.attack2 =
    remainingBuffs[0]?.key ||
    remaining[0]?.key ||
    "";
    // Defense: highest power
    pick.defense = defenses[0]?.key || "";

    // Target: keep existing or auto-assign by rank
    if (!pick.target) {
      const allies = side === "A" ? activeA : activeB;
      const opponents = side === "A" ? activeB : activeA;
      if (opponents.length > 1) {
        const sortedAllies = [...allies].sort((a, b) => getEntityPower(b) - getEntityPower(a));
        const sortedOpponents = [...opponents].sort((a, b) => getEntityPower(b) - getEntityPower(a));
        const myRank = sortedAllies.findIndex(e => e.primary_key === pk);
        if (myRank < sortedOpponents.length) {
          // 1:1 rank match
          pick.target = sortedOpponents[myRank]?.primary_key || "";
        } else {
          // Surplus — target strongest
          pick.target = sortedOpponents[0]?.primary_key || "";
        }
      }
    }

    setSelections(s => ({ ...s, [pk]: pick }));
  }

  // Quick Pick All: auto-select for every active entity
  function quickPickAll() {
    for (const ent of activeA) quickPickEntity(ent, "A");
    for (const ent of activeB) quickPickEntity(ent, "B");
  }

  // Compute one entity's attack/defense stats for a round (called once per entity)
  function calcEntityCombatStats(entity, targetEntity, currentRound, newBuffs) {
    const sel = selections[entity.primary_key] || {};
    const attack1Ab = sel.attack1 ? getAbility(sel.attack1) : null;
    const attack2Ab = sel.attack2 ? getAbility(sel.attack2) : null;
    const defenseAb = sel.defense ? getAbility(sel.defense) : null;
    const pow = getEntityPower(entity);

    let raw1 = (attack1Ab && attack1Ab.type !== "buff") ? calcAbilityPower(sel.attack1, entity) : (!attack1Ab ? pow * 0.3 : 0);
    let raw2 = (attack2Ab && attack2Ab.type !== "buff") ? calcAbilityPower(sel.attack2, entity) : 0;

    const atk1Disc = attack1Ab?.discipline;
    const atk2Disc = attack2Ab?.discipline;
    const defDisc  = defenseAb?.discipline;

    const crossAtk = (atk1Disc && atk2Disc && atk1Disc !== atk2Disc) ? 1.15 : 1.0;
    const crossDef = (defDisc && ((atk1Disc && defDisc !== atk1Disc) || (atk2Disc && defDisc !== atk2Disc))) ? 1.10 : 1.0;

    // Counter system vs target's defense
    const totalRaw = (raw1 + raw2) || 1;
    const w1 = raw1 / totalRaw, w2 = raw2 / totalRaw;
    let counterAtkMult = 1.0, counterDefMult = 1.0;
    const counters = [];

    const tSel = targetEntity ? (selections[targetEntity.primary_key] || {}) : {};
    const tDefAb = tSel.defense ? getAbility(tSel.defense) : null;
    const tDefDisc = tDefAb?.discipline;
    const tAtk1Ab = tSel.attack1 ? getAbility(tSel.attack1) : null;
    const tAtk2Ab = tSel.attack2 ? getAbility(tSel.attack2) : null;
    const tAtk1Disc = tAtk1Ab?.discipline;
    const tAtk2Disc = tAtk2Ab?.discipline;

    // My attacks vs target's defense
    if (raw1 > 0 && tDefDisc) {
      if (disciplineCounters(atk1Disc, tDefDisc)) { counterAtkMult += COUNTER_ATK_BONUS * w1; counters.push("⚔1▶🛡"); }
      if (disciplineCounters(tDefDisc, atk1Disc)) { counterAtkMult -= COUNTERED_PENALTY * w1; counters.push("⚔1◀🛡"); }
    }
    if (raw2 > 0 && tDefDisc) {
      if (disciplineCounters(atk2Disc, tDefDisc)) { counterAtkMult += COUNTER_ATK_BONUS * w2; counters.push("⚔2▶🛡"); }
      if (disciplineCounters(tDefDisc, atk2Disc)) { counterAtkMult -= COUNTERED_PENALTY * w2; counters.push("⚔2◀🛡"); }
    }
    // My defense vs target's attacks
    if (defDisc) {
      const tRaw1 = (tAtk1Ab && tAtk1Ab.type !== "buff") ? 1 : 0;
      const tRaw2 = (tAtk2Ab && tAtk2Ab.type !== "buff") ? 1 : 0;
      if (tRaw1 > 0 && disciplineCounters(defDisc, tAtk1Disc)) { counterDefMult += COUNTER_DEF_BONUS * 0.5; counters.push("🛡▶⚔1"); }
      if (tRaw2 > 0 && disciplineCounters(defDisc, tAtk2Disc)) { counterDefMult += COUNTER_DEF_BONUS * 0.5; counters.push("🛡▶⚔2"); }
    }

    const atkPow = (raw1 + raw2) * crossAtk * counterAtkMult;
    const defPow = (defenseAb ? calcAbilityPower(sel.defense, entity) : 0) * crossDef * counterDefMult;

    const passives = getPassiveEffects(entity, currentRound);
    let atkMult = 1 + passives.atkBonus;
    let defMult = 1 + passives.defBonus;
    // Passive penalties from target
    if (targetEntity) {
      const tPassives = getPassiveEffects(targetEntity, currentRound);
      atkMult -= tPassives.enemyAtkPenalty || 0;
      defMult -= tPassives.enemyDefPenalty || 0;
    }

    for (const buff of (newBuffs[entity.primary_key] || [])) {
      if (buff.effect === "atkUp") atkMult += buff.power;
      if (buff.effect === "defUp") defMult += buff.power;
      if (buff.effect === "atkDown") atkMult -= buff.power;
      if (buff.effect === "defDown") defMult -= buff.power;
    }

    const depth = 1 + calcDisciplineDepthBonus(entity.disciplines);
    const effAtk = atkPow * Math.max(0.1, atkMult) * depth;
    const effDef = defPow * Math.max(0.1, defMult) * depth;

    return {
      effAtk, effDef, counters, passives, atkPow, defPow,
      attack1Ab, attack2Ab, defenseAb,
      atk1Disc, atk2Disc, defDisc,
      sel,
    };
  }

  function resolveRound() {
    if (!canSim || battleOver) return;
    const sevScale = severity / 10;
    const roundResults = [];
    const newCooldowns = { ...cooldowns };
    const newBuffs = { ...activeBuffs };
    const currentRound = rounds.length + 1;

    // Decrement existing cooldowns
    for (const pk of Object.keys(newCooldowns)) {
      const cd = { ...newCooldowns[pk] };
      for (const key of Object.keys(cd)) {
        cd[key] = Math.max(0, cd[key] - 1);
        if (cd[key] <= 0) delete cd[key];
      }
      newCooldowns[pk] = cd;
    }

    // Decrement buff durations
    for (const pk of Object.keys(newBuffs)) {
      newBuffs[pk] = (newBuffs[pk] || []).map(b => ({ ...b, rounds: b.rounds - 1 })).filter(b => b.rounds > 0);
    }

    // ── Build attack graph: each entity attacks exactly ONE target ──
    // attackMap: pk → { attacker entity, target entity, side }
    const attackMap = {};
    // incomingMap: pk → [{ attacker entity, stats }] (all entities attacking this target)
    const incomingMap = {};

    // Each entity on side A picks a target on side B
    for (const entA of activeA) {
      const target = getTarget(entA, "A");
      if (target) {
        attackMap[entA.primary_key] = { attacker: entA, target, side: "A" };
        if (!incomingMap[target.primary_key]) incomingMap[target.primary_key] = [];
        incomingMap[target.primary_key].push({ attacker: entA, side: "A" });
      }
    }
    for (const entB of activeB) {
      const target = getTarget(entB, "B");
      if (target) {
        attackMap[entB.primary_key] = { attacker: entB, target, side: "B" };
        if (!incomingMap[target.primary_key]) incomingMap[target.primary_key] = [];
        incomingMap[target.primary_key].push({ attacker: entB, side: "B" });
      }
    }

    // ── Compute combat stats once per entity ──
    const statsCache = {};
    for (const ent of [...activeA, ...activeB]) {
      const mapping = attackMap[ent.primary_key];
      const target = mapping ? mapping.target : null;
      statsCache[ent.primary_key] = calcEntityCombatStats(ent, target, currentRound, newBuffs);
    }

    // ── Resolve damage: each entity's attack hits their target, defense protects against ALL incoming ──
    // Accumulate total damage taken by each entity
    const dmgTaken = {}; // pk → total HP damage
    const dmgDealt = {}; // pk → { [targetPk]: hpDmg }
    const dodgeResults = {}; // pk → boolean (did this entity dodge?)
    const regenApplied = {}; // pk → boolean

    for (const ent of [...activeA, ...activeB]) {
      dmgTaken[ent.primary_key] = 0;
      dmgDealt[ent.primary_key] = {};
      // Evasion check — one roll per entity per round
      const passives = statsCache[ent.primary_key].passives;
      dodgeResults[ent.primary_key] = (passives.evasion || 0) > 0 && Math.random() < passives.evasion;
    }

    const dmgScalar = 5 + sevScale * 15;

    // For each attacker, compute damage to their target
    for (const ent of [...activeA, ...activeB]) {
      const mapping = attackMap[ent.primary_key];
      if (!mapping) continue;
      const stats = statsCache[ent.primary_key];
      const targetPk = mapping.target.primary_key;
      const targetStats = statsCache[targetPk];
      if (!targetStats) continue;

      // Incoming attackers to the target — split defense across them
      const incoming = incomingMap[targetPk] || [];
      const numAttackers = incoming.length;
      // Defense is split: each attacker faces defense / numAttackers
      const defShare = numAttackers > 1 ? targetStats.effDef / numAttackers : targetStats.effDef;

      const rawDmg = Math.max(0, stats.effAtk - defShare * 0.7) * (0.8 + Math.random() * 0.4);
      const dodged = dodgeResults[targetPk];
      const hpDmg = dodged ? 0 : Math.round(rawDmg * dmgScalar);

      dmgTaken[targetPk] += hpDmg;
      dmgDealt[ent.primary_key][targetPk] = hpDmg;
    }

    // ── Apply DoT ticks from previous rounds ──
    const dotDmg = {}; // pk → total DoT damage this round
    for (const ent of [...activeA, ...activeB]) {
      dotDmg[ent.primary_key] = 0;
      const buffs = newBuffs[ent.primary_key] || [];
      for (const b of buffs) {
        if (b.effect === "dot" && b.rounds > 0) {
          dotDmg[ent.primary_key] += b.power;
        }
      }
    }

    // ── Apply HoT ticks from previous rounds ──
    const hotHeal = {}; // pk → total HoT healing this round
    for (const ent of [...activeA, ...activeB]) {
      hotHeal[ent.primary_key] = 0;
      const buffs = newBuffs[ent.primary_key] || [];
      for (const b of buffs) {
        if (b.effect === "hot" && b.rounds > 0) {
          hotHeal[ent.primary_key] += b.power;
        }
      }
    }

    // ── Lifesteal tracking ──
    const lifestealHeal = {}; // pk → HP healed from lifesteal

    // ── Apply HP changes, fatigue, mastery, cooldowns, buffs, XP — ONCE per entity ──
    const fatGain = 0.02 + sevScale * 0.03;
    const processedEntities = new Set();

    // Build per-matchup results for the combat log
    // Group by: each entity on the smaller side vs all entities attacking it
    // For equal sides or 1v1, this is standard matchup display
    const allEnts = [...activeA, ...activeB];

    for (const ent of allEnts) {
      if (processedEntities.has(ent.primary_key)) continue;
      processedEntities.add(ent.primary_key);
      const stats = statsCache[ent.primary_key];
      const sel = stats.sel;

      // Apply HP: shield absorbs damage first, then HP. Add regen + HoT. Subtract DoT.
      const regen = stats.passives.hpRegen || 0;
      const totalDirectDmg = dmgTaken[ent.primary_key] || 0;
      const totalDoT = dotDmg[ent.primary_key] || 0;
      const totalHoT = hotHeal[ent.primary_key] || 0;
      const totalDmg = totalDirectDmg + totalDoT;
      const totalHeal = regen + totalHoT;

      // Shield absorbs damage first
      let remainingDmg = totalDmg;
      if ((ent._shield || 0) > 0 && remainingDmg > 0) {
        const absorbed = Math.min(ent._shield, remainingDmg);
        ent._shield -= absorbed;
        remainingDmg -= absorbed;
      }

      ent._currentHP = Math.max(0, Math.min(ent._maxHP, ent._currentHP - remainingDmg + totalHeal));

      // Fatigue — once per entity per round
      ent._battleFatigue = Math.min(1, (ent._battleFatigue || 0) + fatGain * (0.8 + Math.random() * 0.4));

      // Track ability mastery — once per entity
      if (sel.attack1) ent._abilityUses[sel.attack1] = (ent._abilityUses[sel.attack1] || 0) + 1;
      if (sel.attack2) ent._abilityUses[sel.attack2] = (ent._abilityUses[sel.attack2] || 0) + 1;
      if (sel.defense) ent._abilityUses[sel.defense] = (ent._abilityUses[sel.defense] || 0) + 1;

      // Cooldowns — once per entity
      if (stats.attack1Ab && stats.attack1Ab.cooldown > 0) { if (!newCooldowns[ent.primary_key]) newCooldowns[ent.primary_key] = {}; newCooldowns[ent.primary_key][sel.attack1] = stats.attack1Ab.cooldown; }
      if (stats.attack2Ab && stats.attack2Ab.cooldown > 0) { if (!newCooldowns[ent.primary_key]) newCooldowns[ent.primary_key] = {}; newCooldowns[ent.primary_key][sel.attack2] = stats.attack2Ab.cooldown; }
      if (stats.defenseAb && stats.defenseAb.cooldown > 0) { if (!newCooldowns[ent.primary_key]) newCooldowns[ent.primary_key] = {}; newCooldowns[ent.primary_key][sel.defense] = stats.defenseAb.cooldown; }

      // Process abilities — buffs, heals, shields, DoTs, HoTs, lifesteal — once per entity
      const mapping = attackMap[ent.primary_key];
      const targetPk = mapping ? mapping.target.primary_key : null;
      const targetDodged = targetPk ? dodgeResults[targetPk] : false;
      let lifestealTotal = 0;
      for (const [atkAb, atkKey] of [
        [stats.attack1Ab, sel.attack1],
        [stats.attack2Ab, sel.attack2],
      ]) {
        if (!atkAb) continue;
        const eMult = discEffectMult(ent, atkAb.discipline);

        // Standard buff effects — always apply (buffs can't be dodged)
        if (atkAb.type === "buff" && atkAb.effects) {
          const eff = atkAb.effects;
          const pow = atkAb.basePower;
          if (!newBuffs[ent.primary_key]) newBuffs[ent.primary_key] = [];
          if (eff.selfAtkUp) newBuffs[ent.primary_key].push({ key: atkKey, effect: "atkUp", power: pow * 0.3, rounds: 2, name: atkAb.name });
          if (eff.selfDefUp) newBuffs[ent.primary_key].push({ key: atkKey, effect: "defUp", power: pow * 0.3, rounds: 2, name: atkAb.name });
          if (targetPk) {
            if (!newBuffs[targetPk]) newBuffs[targetPk] = [];
            // Debuffs from buff-type abilities always land (can't be dodged)
            if (eff.enemyAtkDown) newBuffs[targetPk].push({ key: atkKey+"_debuff", effect: "atkDown", power: pow * 0.2, rounds: 2, name: atkAb.name+" (debuff)" });
            if (eff.enemyDefDown) newBuffs[targetPk].push({ key: atkKey+"_debuff", effect: "defDown", power: pow * 0.2, rounds: 2, name: atkAb.name+" (shred)" });
          }
        }

        // Instant heal — scaled by discipline level
        if (atkAb.heal) {
          const healAmt = Math.round(atkAb.heal * eMult);
          ent._currentHP = Math.min(ent._maxHP, ent._currentHP + healAmt);
        }

        // Shield — scaled by discipline level, stacks additively, capped at 50% max HP
        if (atkAb.shield) {
          const shieldAmt = Math.round(atkAb.shield * eMult);
          ent._shield = Math.min(Math.round(ent._maxHP * 0.5), (ent._shield || 0) + shieldAmt);
        }

        // Heal over Time — scaled by discipline level, enters buff system
        if (atkAb.hot) {
          if (!newBuffs[ent.primary_key]) newBuffs[ent.primary_key] = [];
          const hotAmt = Math.round(atkAb.hot.heal * eMult);
          newBuffs[ent.primary_key].push({ key: atkKey+"_hot", effect: "hot", power: hotAmt, rounds: atkAb.hot.rounds, name: atkAb.name+" (HoT)" });
        }

        // Damage over Time — scaled by discipline level, applied to target
        // DoT from damage abilities is blocked by dodge; DoT from buff abilities always lands
        if (atkAb.dot && targetPk) {
          const dotBlocked = (atkAb.type === "damage") && targetDodged;
          if (!dotBlocked) {
            if (!newBuffs[targetPk]) newBuffs[targetPk] = [];
            const dotAmt = Math.round(atkAb.dot.damage * eMult);
            newBuffs[targetPk].push({ key: atkKey+"_dot", effect: "dot", power: dotAmt, rounds: atkAb.dot.rounds, name: atkAb.name+" (DoT)" });
          }
        }

        // Lifesteal — heal based on damage dealt to target (blocked if dodged, since dealt=0)
        if (atkAb.lifesteal && targetPk && dmgDealt[ent.primary_key]) {
          const dealt = dmgDealt[ent.primary_key][targetPk] || 0;
          lifestealTotal += Math.round(dealt * atkAb.lifesteal);
        }
      }

      // Apply lifesteal healing
      if (lifestealTotal > 0) {
        ent._currentHP = Math.min(ent._maxHP, ent._currentHP + lifestealTotal);
        lifestealHeal[ent.primary_key] = lifestealTotal;
      }

      // Buff stealing — stealBuffs takes 1 positive buff, stealAllBuffs takes all + shield
      if (targetPk && !targetDodged) {
        let doStealAll = false;
        let doStealOne = false;
        for (const [atkAb] of [[stats.attack1Ab, sel.attack1], [stats.attack2Ab, sel.attack2]]) {
          if (atkAb && atkAb.stealAllBuffs) doStealAll = true;
          else if (atkAb && atkAb.stealBuffs) doStealOne = true;
        }

        if (doStealAll || doStealOne) {
          const targetBuffs = newBuffs[targetPk] || [];
          const positiveEffects = ["atkUp", "defUp", "hot"];
          const stolen = [];
          const remaining = [];

          for (const b of targetBuffs) {
            if (positiveEffects.includes(b.effect)) {
              if (doStealAll || (doStealOne && stolen.length === 0)) {
                // Transfer to caster — flip the buff to self
                stolen.push({ ...b, name: b.name + " (stolen)" });
              } else {
                remaining.push(b);
              }
            } else {
              remaining.push(b); // negative buffs (debuffs, DoTs) stay on target
            }
          }

          // Apply stolen buffs to caster
          if (stolen.length > 0) {
            if (!newBuffs[ent.primary_key]) newBuffs[ent.primary_key] = [];
            newBuffs[ent.primary_key].push(...stolen);
          }
          newBuffs[targetPk] = remaining;

          // Steal shield HP (stealAllBuffs only)
          if (doStealAll) {
            const targetEnt = [...(battleEnts?.a || []), ...(battleEnts?.b || [])].find(e => e.primary_key === targetPk);
            if (targetEnt && (targetEnt._shield || 0) > 0) {
              const stolenShield = targetEnt._shield;
              targetEnt._shield = 0;
              ent._shield = Math.min(Math.round(ent._maxHP * 0.5), (ent._shield || 0) + stolenShield);
            }
          }
        }
      }

      // XP — once per entity, calculated against the entity's chosen target
      if (mapping) {
        const target = mapping.target;
        const usedDiscs = [stats.atk1Disc, stats.atk2Disc, stats.defDisc].filter(Boolean);
        const myPow = getEntityPower(ent);
        const oppPow = getEntityPower(target);
        const dealt = dmgDealt[ent.primary_key]?.[target.primary_key] || 0;
        const received = dmgDealt[target.primary_key]?.[ent.primary_key] || 0;
        const isWinner = dealt > received;
        const xp = calcDuelXPGains(ent.disciplines, target.disciplines, isWinner, usedDiscs, myPow, oppPow, ent.primaryDiscipline);
        if (!ent._xpGains) ent._xpGains = {};
        for (const [k, v] of Object.entries(xp)) { ent._xpGains[k] = (ent._xpGains[k] || 0) + v; }
      }
    }

    // ── Build round results for combat log ──
    // Show each attacker→target matchup as a row
    for (const ent of allEnts) {
      const mapping = attackMap[ent.primary_key];
      if (!mapping) continue;
      // Only show from side A perspective to avoid double-listing 1v1s
      // For asymmetric fights, show all attacks
      const sideAEnts = activeA.map(e => e.primary_key);
      const isSideA = sideAEnts.includes(ent.primary_key);
      // In equal fights, only list from side A to avoid duplicates
      if (!isSideA && activeA.length === activeB.length) continue;
      // In asymmetric fights from side B, check if their target is already shown as the A-side attacker
      if (!isSideA) {
        const targetMapping = attackMap[mapping.target.primary_key];
        if (targetMapping && targetMapping.target.primary_key === ent.primary_key) continue; // already covered
      }

      const target = mapping.target;
      const stats = statsCache[ent.primary_key];
      const tStats = statsCache[target.primary_key];
      const dealt = dmgDealt[ent.primary_key]?.[target.primary_key] || 0;
      const received = dmgDealt[target.primary_key]?.[ent.primary_key] || 0;
      const entKo = ent._currentHP <= 0;
      const targetKo = target._currentHP <= 0;
      const roundWinner = dealt > received ? (isSideA ? "A" : "B") : received > dealt ? (isSideA ? "B" : "A") : (Math.random() < 0.5 ? "A" : "B");

      const tierEnt = getPowerTier(calcDisciplinePowerForType(ent.disciplines, ent.type));
      const tierTarget = getPowerTier(calcDisciplinePowerForType(target.disciplines, target.type));

      // Incoming count display
      const incoming = incomingMap[target.primary_key] || [];
      const gangUp = incoming.length > 1;

      if (isSideA) {
        roundResults.push({
          nameA: ent.name, nameB: target.name,
          tierColorA: tierEnt.color, tierColorB: tierTarget.color,
          tierOrnA: tierEnt.ornament, tierOrnB: tierTarget.ornament,
          attack1A: stats.attack1Ab?.name || "Basic Attack", attack2A: stats.attack2Ab?.name || "—",
          attack1B: tStats.attack1Ab?.name || "Basic Attack", attack2B: tStats.attack2Ab?.name || "—",
          defenseA: stats.defenseAb?.name || "No Defense", defenseB: tStats.defenseAb?.name || "No Defense",
          atkPowA: stats.effAtk, atkPowB: tStats.effAtk,
          defPowA: stats.effDef, defPowB: tStats.effDef,
          dmgToA: received, dmgToB: dealt,
          dodgeA: dodgeResults[ent.primary_key], dodgeB: dodgeResults[target.primary_key],
          countersA: stats.counters, countersB: tStats.counters,
          xpA: {}, xpB: {},
          hpA: ent._currentHP, hpB: target._currentHP,
          maxHpA: ent._maxHP, maxHpB: target._maxHP,
          fatA: ent._battleFatigue, fatB: target._battleFatigue,
          koA: entKo, koB: targetKo, roundWinner,
          gangUpA: (incomingMap[ent.primary_key]||[]).length > 1 ? (incomingMap[ent.primary_key]||[]).length : 0,
          gangUpB: gangUp ? incoming.length : 0,
        });
      } else {
        roundResults.push({
          nameA: target.name, nameB: ent.name,
          tierColorA: tierTarget.color, tierColorB: tierEnt.color,
          tierOrnA: tierTarget.ornament, tierOrnB: tierEnt.ornament,
          attack1A: tStats.attack1Ab?.name || "Basic Attack", attack2A: tStats.attack2Ab?.name || "—",
          attack1B: stats.attack1Ab?.name || "Basic Attack", attack2B: stats.attack2Ab?.name || "—",
          defenseA: tStats.defenseAb?.name || "No Defense", defenseB: stats.defenseAb?.name || "No Defense",
          atkPowA: tStats.effAtk, atkPowB: stats.effAtk,
          defPowA: tStats.effDef, defPowB: stats.effDef,
          dmgToA: dmgDealt[ent.primary_key]?.[target.primary_key] || 0, dmgToB: dmgDealt[target.primary_key]?.[ent.primary_key] || 0,
          dodgeA: dodgeResults[target.primary_key], dodgeB: dodgeResults[ent.primary_key],
          countersA: tStats.counters, countersB: stats.counters,
          xpA: {}, xpB: {},
          hpA: target._currentHP, hpB: ent._currentHP,
          maxHpA: target._maxHP, maxHpB: ent._maxHP,
          fatA: target._battleFatigue, fatB: ent._battleFatigue,
          koA: target._currentHP <= 0, koB: ent._currentHP <= 0, roundWinner,
          gangUpA: (incomingMap[target.primary_key]||[]).length > 1 ? (incomingMap[target.primary_key]||[]).length : 0,
          gangUpB: (incomingMap[ent.primary_key]||[]).length > 1 ? (incomingMap[ent.primary_key]||[]).length : 0,
        });
      }
    }

    setCooldowns(newCooldowns);
    setActiveBuffs(newBuffs);
    setBattleEnts({ a: [...battleEnts.a], b: [...battleEnts.b] });
    setRounds(r => [...r, { results: roundResults, round: r.length + 1 }]);
    setSelections({}); // Clear selections for next round
    setRoundPhase("select");
  }

  async function confirmResult() {
    if (!battleStarted) return;
    setConfirming(true);
    try {
      const all = [...battleEnts.a, ...battleEnts.b].map(e => ({
        ...e,
        fatigue: e._battleFatigue ?? e.fatigue ?? 0,
        abilityMastery: e._abilityUses || e.abilityMastery,
        currentHP: e._currentHP ?? getCurrentHP(e),
      }));

      // ── League scoring: award +1 point to each surviving winner, handle challenge stakes ──
      if (selectedLeague && leagues && setLeagues) {
        const leagueObj = leagues.find(l => l.primary_key === selectedLeague);
        if (leagueObj) {
          const alive = all.filter(e => (e._currentHP ?? e.currentHP ?? 1) > 0);
          const dead = all.filter(e => (e._currentHP ?? e.currentHP ?? 1) <= 0);
          const aliveSet = new Set(alive.map(e => e.primary_key));
          const deadSet = new Set(dead.map(e => e.primary_key));
          const updatedLeague = { ...leagueObj, members: leagueObj.members.map(m => ({ ...m })), duels: [...(leagueObj.duels || [])] };

          const stakes = duelStakes || 0;

          // Award points: +1 for normal wins, or steal stakes from losers
          for (const member of updatedLeague.members) {
            if (aliveSet.has(member.pk)) {
              const ptsGained = stakes > 0 ? stakes : 2;
              member.points = (member.points || 0) + ptsGained;
              member.wins = (member.wins || 0) + 1;
              member.streak = Math.max(1, (member.streak || 0) + 1);
              member.history = [...(member.history || []), { result: "win", pts: ptsGained, ts: Date.now() }];
            } else if (deadSet.has(member.pk)) {
              const ptsLost = stakes > 0 ? stakes : 0;
              member.points = Math.max(0, (member.points || 0) - ptsLost);
              member.losses = (member.losses || 0) + 1;
              member.streak = 0;
              member.history = [...(member.history || []), { result: "loss", pts: -ptsLost, ts: Date.now() }];
            }
          }

          // Mark any matching pending challenges as resolved
          if (stakes > 0) {
            updatedLeague.duels = updatedLeague.duels.map(d => {
              if (d.type === "intrigue" && d.action === "challenge" && d.success && !d.resolved) {
                const involvedPks = new Set([...aliveSet, ...deadSet]);
                if (involvedPks.has(d.actorPk) && involvedPks.has(d.targetPk)) {
                  return { ...d, resolved: true, resolvedAt: Date.now() };
                }
              }
              return d;
            });
          }

          // Record duel log
          const winnerNames = alive.filter(e => updatedLeague.members.some(m => m.pk === e.primary_key)).map(e => e.name);
          const loserNames = dead.filter(e => updatedLeague.members.some(m => m.pk === e.primary_key)).map(e => e.name);
          if (winnerNames.length > 0 || loserNames.length > 0) {
            updatedLeague.duels.push({
              id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
              winners: winnerNames, losers: loserNames,
              stakes: stakes > 0 ? stakes : undefined,
              timestamp: Date.now(),
            });
          }

          setLeagues(ls => ls.map(l => l.primary_key === selectedLeague ? updatedLeague : l));
          try {
            // Clean and cap for DynamoDB
            const cappedLeague = {
              ...updatedLeague,
              members: (updatedLeague.members || []).map(m => ({
                ...m,
                history: (m.history || []).slice(-50),
                exams: (m.exams || []).slice(-30),
              })),
              duels: (updatedLeague.duels || []).slice(-200),
            };
            // Deep clean nulls/undefined/empty strings
            const cleanObj = (o) => {
              if (o === null || o === undefined || o === "") return undefined;
              if (Array.isArray(o)) return o.map(cleanObj).filter(v => v !== undefined);
              if (typeof o === "object") {
                const c = {};
                for (const [k, v] of Object.entries(o)) { const cv = cleanObj(v); if (cv !== undefined) c[k] = cv; }
                return Object.keys(c).length > 0 ? c : undefined;
              }
              return o;
            };
            const cleaned = cleanObj(cappedLeague) || {};
            cleaned.primary_key = updatedLeague.primary_key;
            cleaned.sort_key = "League";
            await apiPut(cleaned);
          } catch (e) { console.error("Failed to save league scores:", e); }
        }
      }

      await onConfirmDuel(all);
      resetBattle();
      setSideA([]); setSideB([]);
    } catch(e) { console.error(e); } finally { setConfirming(false); }
  }

  // Compact entity card with ability selector
  function DuelCard({ entity, side }) {
    const peak = peakDiscipline(entity.disciplines, entity.primaryDiscipline);
    const pi = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
    const pow = getEntityPower(entity);
    const fat = entity._battleFatigue ?? entity.fatigue ?? 0;
    const maxHP = entity._maxHP ?? calcMaxHP(entity);
    const curHP = entity._currentHP ?? getCurrentHP(entity);
    const ko = curHP <= 0;
    const hpPct = maxHP > 0 ? curHP / maxHP : 0;
    const icon = entity.type === "mage" ? "✦" : "🐉";
    const tier = getPowerTier(calcDisciplinePowerForType(entity.disciplines, entity.type));
    const baseNorm = normalizePower(calcDisciplinePowerForType(entity.disciplines, entity.type));
    const sel = selections[entity.primary_key] || {};
    const entityBuffs = activeBuffs[entity.primary_key] || [];
    const passives = getPassiveEffects(entity);
    const learnedPassives = Object.keys(ABILITIES).flatMap(d => ABILITIES[d].filter(a => a.type === "passive" && (entity.learnedAbilities || {})[a.key]));
    const hpDelta = battleStarted ? getHPDelta(entity.primary_key) : null;
    const currentTarget = battleStarted ? getTargetForEntity(entity, side) : null;

    const availAttacks = getAvailableAbilities(entity, "damage").concat(getAvailableAbilities(entity, "buff"));
    const availDefense = getAvailableAbilities(entity, "defense");
    const allAttacks = getAllLearnedAbilities(entity, "damage").concat(getAllLearnedAbilities(entity, "buff"));
    const allDefense = getAllLearnedAbilities(entity, "defense");

    // Tier-specific ambient background
    const ambientBg = ko ? "none" : tier.key === "celestial"
      ? `radial-gradient(ellipse at 20% 30%, ${tier.color}12 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, ${tier.color}0a 0%, transparent 50%)`
      : tier.key === "legendary"
      ? `radial-gradient(circle at 30% 80%, #f9731612 0%, transparent 40%), radial-gradient(circle at 70% 20%, #dc262608 0%, transparent 40%)`
      : tier.key === "epic"
      ? `radial-gradient(ellipse at 50% 0%, #a855f70d 0%, transparent 60%)`
      : "none";
    const ambientAnim = ko ? "none" : tier.key === "celestial" ? "celestialDrift 8s ease-in-out infinite"
      : tier.key === "legendary" ? "emberFloat 6s linear infinite"
      : tier.key === "epic" ? "epicSwirl 10s linear infinite" : "none";

    return (
      <div style={{
        padding:10, borderRadius:8,
        border: ko ? "2px solid #37415188" : `${tier.borderWidth || 1}px solid ${tier.color}66`,
        background: ko ? "#111827" : tier.cardBg,
        position:"relative", overflow:"hidden",
        boxShadow: ko ? "none" : tier.glowSize > 0 ? `0 0 ${tier.glowSize}px ${tier.color}33, inset 0 0 ${tier.glowSize/2}px ${tier.color}11` : "none",
        "--tier-glow": tier.color + "55",
        "--tier-glow-in": tier.color + "15",
        animation: ko ? "none" : tier.animBorder ? "borderGlow 3s ease-in-out infinite" : "none",
        filter: ko ? "saturate(0.3) brightness(0.6)" : "none",
        transition: "filter 0.5s, opacity 0.5s",
      }}>
        {/* Ambient tier particle layer */}
        {!ko && ambientBg !== "none" && (
          <div style={{
            position:"absolute", inset:0, borderRadius:8, pointerEvents:"none", zIndex:0,
            background: ambientBg, backgroundSize: "200% 200%",
            animation: ambientAnim, opacity: 0.8,
          }} />
        )}

        {/* Content layer */}
        <div style={{ position:"relative", zIndex:1 }}>
          <button onClick={() => removeFromSide(entity.primary_key, side)} style={{
            position:"absolute", top:-6, right:-6, background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#6b7280",
          }}>✕</button>

          {/* Tier badge */}
          <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4 }}>
            <span style={{
              fontSize:7, fontWeight:800, letterSpacing: tier.letterSpacing || 0.5,
              padding:"1px 6px", borderRadius:3, textTransform:"uppercase",
              background: tier.badge + "22", color: tier.badge || tier.color,
              border: `1px solid ${tier.color}44`,
              fontFamily: FONT_DISPLAY,
            }}>{tier.ornament} {tier.label}</span>
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
            {/* Icon with halo for high tiers */}
            <span style={{
              fontSize:14, width:22, height:22, display:"flex", alignItems:"center", justifyContent:"center",
              borderRadius:"50%",
              "--tier-glow": tier.color + "66",
              boxShadow: tier.glowSize > 6 ? `0 0 ${tier.glowSize/2}px ${tier.color}44` : "none",
              animation: tier.glowSize > 6 ? "iconHalo 2.5s ease-in-out infinite" : "none",
            }}>{icon}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{
                fontSize:11, fontWeight:700,
                color: tier.textColor || "#f1f5f9",
                textShadow: tier.glowSize > 4 ? `0 0 ${tier.glowSize/2}px ${tier.color}66` : "none",
                fontFamily: FONT_DISPLAY,
              }}>{entity.name}</div>
              {pi && <span style={{ fontSize:8, color:pi.color }}>{pi.icon} {pi.label} {peak.level}</span>}
            </div>
            <div style={{ textAlign:"right" }}>
              <span style={{
                fontSize:16, fontWeight:900, color: tier.color,
                textShadow: tier.glowSize > 4 ? `0 0 ${tier.glowSize}px ${tier.color}88` : "none",
              }}>{baseNorm}</span>
              <span style={{ fontSize:8, color:"#6b7280" }}> PWR</span>
            </div>
          </div>

          {/* HP Bar — taller for high tiers, pulse when full, flicker when critical */}
          <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:3 }}>
            <span style={{ fontSize:8, fontWeight:700, color:"#ef4444", minWidth:14 }}>HP</span>
            <div style={{
              flex:1, height: tier.glowSize > 10 ? 8 : 6, borderRadius:3,
              background:"#1e293b", overflow:"hidden",
              border: tier.glowSize > 4 ? `1px solid ${tier.color}22` : "none",
            }}>
              <div style={{
                height:"100%", width:`${hpPct*100}%`, borderRadius:3,
                background: hpPct > 0.6 ? (tier.barStyle || "#22c55e") : hpPct > 0.3 ? "#f59e0b" : "#ef4444",
                backgroundSize: tier.animBorder ? "200% 100%" : "100%",
                animation: hpPct > 0.9 && tier.animBorder ? "borderSweep 3s linear infinite"
                  : hpPct < 0.2 && hpPct > 0 ? "hpCritFlicker 0.8s ease-in-out infinite"
                  : "none",
                transition:"width 0.3s",
              }} />
            </div>
            <span style={{ fontSize:9, fontWeight:700, color: hpPct > 0.5 ? "#22c55e" : hpPct > 0.2 ? "#f59e0b" : "#ef4444", minWidth:44, textAlign:"right" }}>
              {curHP}/{maxHP}
            </span>
            {hpDelta !== null && hpDelta !== 0 && (
              <span style={{
                fontSize:8, fontWeight:800, minWidth:28, textAlign:"right",
                color: hpDelta > 0 ? "#22c55e" : "#ef4444",
                animation: "hpDeltaFlash 1.5s ease-out",
              }}>
                {hpDelta > 0 ? "+" : ""}{hpDelta}
              </span>
            )}
          </div>

          {/* Fatigue bar */}
          <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4 }}>
            <span style={{ fontSize:7, color:"#64748b", minWidth:14 }}>FAT</span>
            <div style={{ flex:1, height:3, borderRadius:2, background:"#1e293b", overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${fat*100}%`, borderRadius:2, background: fat > 0.6 ? "#f59e0b" : "#64748b" }} />
            </div>
            <span style={{ fontSize:7, color:"#64748b", minWidth:24, textAlign:"right" }}>{Math.round(fat*100)}%</span>
          </div>

          {/* Targeting indicator — shows who this entity is focused on */}
          {battleStarted && !ko && !battleOver && (() => {
            const allies = side === "A" ? activeA : activeB;
            const opponents = side === "A" ? activeB : activeA;
            if (opponents.length <= 1) return null;
            const targetPk = sel.target;
            let target;
            let autoLabel = "auto";
            if (targetPk) {
              target = opponents.find(o => o.primary_key === targetPk);
            } else {
              const sortedAllies = [...allies].sort((a, b) => getEntityPower(b) - getEntityPower(a));
              const sortedOpponents = [...opponents].sort((a, b) => getEntityPower(b) - getEntityPower(a));
              const myRank = sortedAllies.findIndex(e => e.primary_key === entity.primary_key);
              if (myRank < sortedOpponents.length) {
                target = sortedOpponents[myRank];
                autoLabel = `rank #${myRank+1}`;
              } else {
                target = sortedOpponents[0];
                autoLabel = "surplus → strongest";
              }
            }
            if (!target) return null;
            const tIcon = target.type === "mage" ? "✦" : "🐉";
            return (
              <div style={{ fontSize:7, color:"#a855f7", marginBottom:3, display:"flex", alignItems:"center", gap:3 }}>
                <span>🎯</span>
                <span style={{ fontWeight:600 }}>Targeting: {tIcon} {target.name}</span>
                {!targetPk && <span style={{ color:"#64748b" }}>({autoLabel})</span>}
              </div>
            );
          })()}

          {/* Passive abilities + aggregate passive stats */}
          {learnedPassives.length > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:2, marginBottom:4 }}>
              <div style={{ display:"flex", gap:2, flexWrap:"wrap" }}>
                {learnedPassives.map(p => (
                  <span key={p.key} title={p.desc} style={{ fontSize:7, padding:"1px 4px", borderRadius:3, background: tier.color + "14", color: tier.color, border:`1px solid ${tier.color}33`, fontWeight:600 }}>
                    ◈ {p.name}
                  </span>
                ))}
              </div>
              {/* Aggregate passive stat line */}
              <div style={{ display:"flex", gap:4, flexWrap:"wrap", fontSize:7, color:"#94a3b8" }}>
                {passives.atkBonus > 0 && <span style={{ color:"#22c55e", fontWeight:600 }}>⚔+{Math.round(passives.atkBonus*100)}%</span>}
                {passives.defBonus > 0 && <span style={{ color:"#60a5fa", fontWeight:600 }}>🛡+{Math.round(passives.defBonus*100)}%</span>}
                {passives.hpBonus > 0 && <span style={{ color:"#f472b6", fontWeight:600 }}>♥+{passives.hpBonus}</span>}
                {passives.hpRegen > 0 && <span style={{ color:"#34d399", fontWeight:600 }}>♥↻{passives.hpRegen}/r</span>}
                {passives.evasion > 0 && <span style={{ color:"#a78bfa", fontWeight:600 }}>☆{Math.round(passives.evasion*100)}%eva</span>}
                {passives.enemyAtkPenalty > 0 && <span style={{ color:"#f97316", fontWeight:600 }}>⚔↓{Math.round(passives.enemyAtkPenalty*100)}%</span>}
                {passives.enemyDefPenalty > 0 && <span style={{ color:"#fb923c", fontWeight:600 }}>🛡↓{Math.round(passives.enemyDefPenalty*100)}%</span>}
              </div>
            </div>
          )}

          {/* Active buffs — with percentage values */}
          {entityBuffs.length > 0 && (
            <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginBottom:6 }}>
              {entityBuffs.map((b, i) => {
                const isPositive = b.effect === "atkUp" || b.effect === "defUp" || b.effect === "hot";
                const isNegative = b.effect === "atkDown" || b.effect === "defDown" || b.effect === "dot";
                const color = b.effect === "atkUp" ? "#22c55e" : b.effect === "defUp" ? "#3b82f6" : b.effect === "defDown" ? "#f97316" : b.effect === "atkDown" ? "#ef4444" : b.effect === "dot" ? "#a855f7" : b.effect === "hot" ? "#2dd4bf" : "#94a3b8";
                const effectLabel = b.effect === "atkUp" ? `+${Math.round(b.power*100)}% ATK`
                  : b.effect === "defUp" ? `+${Math.round(b.power*100)}% DEF`
                  : b.effect === "atkDown" ? `-${Math.round(b.power*100)}% ATK`
                  : b.effect === "defDown" ? `-${Math.round(b.power*100)}% DEF`
                  : b.effect === "dot" ? `☠ ${b.power} dmg/r`
                  : b.effect === "hot" ? `♥ ${b.power} heal/r`
                  : b.effect;
                const arrow = isPositive ? "↑" : isNegative ? "↓" : "•";
                return (
                  <span key={i} title={b.name} style={{
                    fontSize:7, padding:"1px 5px", borderRadius:3, fontWeight:700,
                    background: color + "18", color: color,
                    border: `1px solid ${color}44`,
                    display:"flex", alignItems:"center", gap:2,
                  }}>
                    {arrow} {effectLabel}
                    <span style={{ opacity:0.6, fontSize:6 }}>{b.rounds}r</span>
                  </span>
                );
              })}
            </div>
          )}

          {/* Shield bar — shown below HP when shield > 0 */}
          {(entity._shield || 0) > 0 && (
            <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:3 }}>
              <span style={{ fontSize:7, fontWeight:700, color:"#fbbf24", minWidth:14 }}>🛡</span>
              <div style={{ flex:1, height:4, borderRadius:2, background:"#1e293b", overflow:"hidden", border:"1px solid #fbbf2422" }}>
                <div style={{
                  height:"100%", width:`${Math.min(100, (entity._shield / (maxHP * 0.5)) * 100)}%`, borderRadius:2,
                  background:"linear-gradient(90deg, #fbbf24, #f59e0b)",
                  transition:"width 0.3s",
                }} />
              </div>
              <span style={{ fontSize:8, fontWeight:700, color:"#fbbf24", minWidth:24, textAlign:"right" }}>{entity._shield}</span>
            </div>
          )}

          {/* Ability selectors — tier-colored borders */}
          {battleStarted && !ko && !battleOver && (
            <div style={{ display:"flex", flexDirection:"column", gap:3, marginTop:4 }}>
              {/* Quick Pick button */}
              <button onClick={() => quickPickEntity(entity, side)} style={{
                fontSize:9, fontWeight:700, padding:"4px 8px", borderRadius:4, cursor:"pointer",
                background: "linear-gradient(135deg, #7c3aed22, #6d28d922)", border: "1px solid #a855f744",
                color: "#a855f7", letterSpacing: 0.5, display:"flex", alignItems:"center", justifyContent:"center", gap:4,
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "linear-gradient(135deg, #7c3aed44, #6d28d944)"; e.currentTarget.style.color = "#c4b5fd"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "linear-gradient(135deg, #7c3aed22, #6d28d922)"; e.currentTarget.style.color = "#a855f7"; }}
              >⚡ Quick Pick</button>
              {/* Target selector — show when opponents > 1 */}
              {(() => {
                const allies = side === "A" ? activeA : activeB;
                const opponents = side === "A" ? activeB : activeA;
                if (opponents.length > 1) {
                  const sortedAllies = [...allies].sort((a, b) => getEntityPower(b) - getEntityPower(a));
                  const sortedOpponents = [...opponents].sort((a, b) => getEntityPower(b) - getEntityPower(a));
                  const myRank = sortedAllies.findIndex(e => e.primary_key === entity.primary_key);
                  const isSurplus = myRank >= sortedOpponents.length;
                  const autoTarget = isSurplus ? sortedOpponents[0] : sortedOpponents[myRank];
                  const autoLabel = isSurplus ? `Auto (strongest — ${autoTarget?.name || "?"})` : `Auto (rank #${myRank+1} — ${autoTarget?.name || "?"})`;
                  return (
                    <div>
                      <div style={{ fontSize:8, fontWeight:700, color:"#a855f7", letterSpacing:0.5, textTransform:"uppercase", marginBottom:2 }}>🎯 Target</div>
                      <select value={sel.target || ""} onChange={e => setEntitySelection(entity.primary_key, "target", e.target.value)}
                        style={{ width:"100%", fontSize:10, padding:"4px 6px", borderRadius:4, background:"#1e293b", border:"1px solid #a855f733", color:"#f1f5f9", cursor:"pointer" }}>
                        <option value="">{autoLabel}</option>
                        {opponents.filter(opp => (opp._currentHP ?? getCurrentHP(opp)) > 0).map(opp => {
                          const oppTier = getPowerTier(calcDisciplinePowerForType(opp.disciplines, opp.type));
                          const oppPow = normalizePower(calcDisciplinePowerForType(opp.disciplines, opp.type));
                          const oppIcon = opp.type === "mage" ? "✦" : "🐉";
                          const isAutoTarget = autoTarget && opp.primary_key === autoTarget.primary_key;
                          return <option key={opp.primary_key} value={opp.primary_key}>{oppIcon} {opp.name} — PWR:{oppPow} {oppTier.ornament}{isAutoTarget ? " ◄" : ""}</option>;
                        })}
                      </select>
                    </div>
                  );
                }
                return null;
              })()}
              <div>
                <div style={{ fontSize:8, fontWeight:700, color:"#ef4444", letterSpacing:0.5, textTransform:"uppercase", marginBottom:2 }}>⚔ Attack 1</div>
                <select value={sel.attack1 || ""} onChange={e => setEntitySelection(entity.primary_key, "attack1", e.target.value)}
                  style={{ width:"100%", fontSize:10, padding:"4px 6px", borderRadius:4, background:"#1e293b", border:`1px solid ${tier.color}33`, color:"#f1f5f9", cursor:"pointer" }}>
                  <option value="">— None —</option>
                  {allAttacks.filter(ab => ab.key !== sel.attack2).map(ab => {
                    const typeInfo = ABILITY_TYPE_INFO[ab.type];
                    const pwr = calcAbilityPower(ab.key, entity);
                    const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                    const effectHint = ab.effects ? (ab.effects.selfAtkUp ? " ↑ATK" : ab.effects.selfDefUp ? " ↑DEF" : ab.effects.enemyAtkDown ? " ↓eATK" : ab.effects.enemyDefDown ? " ↓eDEF" : "") : "";
                    const cdLabel = ab.onCooldown ? ` ⏳ CD:${ab.cdRemaining}` : "";
                    return <option key={ab.key} value={ab.key} disabled={ab.onCooldown} style={ab.onCooldown ? {color:"#475569"} : {}}>{discInfo?.icon||ab.icon} {ab.name} ({typeInfo.label}{effectHint}) — Pwr:{pwr.toFixed(2)}{ab.cooldown > 0 && !ab.onCooldown ? ` CD:${ab.cooldown}` : ""}{cdLabel}</option>;
                  })}
                </select>
                {/* Cooldown tracker */}
                {allAttacks.some(ab => ab.onCooldown && ab.key !== sel.attack2) && (
                  <div style={{ display:"flex", gap:2, flexWrap:"wrap", marginTop:2 }}>
                    {allAttacks.filter(ab => ab.onCooldown && ab.key !== sel.attack2).map(ab => {
                      const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                      return (
                        <span key={ab.key} style={{ fontSize:6, padding:"1px 4px", borderRadius:2, background:"#1e293b", color:"#475569", border:"1px solid #334155", fontWeight:600, display:"flex", alignItems:"center", gap:2 }}>
                          <span style={{ color: discInfo?.color || "#64748b" }}>{discInfo?.icon||ab.icon}</span> {ab.name} <span style={{ color:"#f59e0b" }}>⏳{ab.cdRemaining}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* Selected ability preview with counter indicator */}
                {sel.attack1 && (() => {
                  const ab = getAbility(sel.attack1);
                  if (!ab) return null;
                  const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                  const tags = getAbilityEffectTags(ab, entity);
                  const counter = getCounterInfo(ab.discipline, currentTarget);
                  return (
                    <div style={{ marginTop:2, padding:"3px 6px", borderRadius:4, background: (discInfo?.color || "#64748b") + "10", border: `1px solid ${(discInfo?.color || "#64748b")}22`, display:"flex", flexDirection:"column", gap:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:3, flexWrap:"wrap" }}>
                        {tags.map((t, i) => (
                          <span key={i} style={{ fontSize:6, fontWeight:700, padding:"0px 3px", borderRadius:2, background: t.color + "22", color: t.color, border: `1px solid ${t.color}33` }}>{t.icon} {t.label}</span>
                        ))}
                        {ab.cooldown > 0 && <span style={{ fontSize:6, color:"#94a3b8", fontWeight:600 }}>CD:{ab.cooldown}</span>}
                        {counter && (
                          <span style={{ fontSize:6, fontWeight:800, padding:"0px 4px", borderRadius:2, background: counter.color + "22", color: counter.color, border: `1px solid ${counter.color}44` }}>{counter.label}</span>
                        )}
                      </div>
                      <div style={{ fontSize:7, color:"#94a3b8", lineHeight:1.3 }}>{ab.desc}</div>
                    </div>
                  );
                })()}
              </div>
              <div>
                <div style={{ fontSize:8, fontWeight:700, color:"#f97316", letterSpacing:0.5, textTransform:"uppercase", marginBottom:2 }}>⚔ Attack 2</div>
                <select value={sel.attack2 || ""} onChange={e => setEntitySelection(entity.primary_key, "attack2", e.target.value)}
                  style={{ width:"100%", fontSize:10, padding:"4px 6px", borderRadius:4, background:"#1e293b", border:`1px solid ${tier.color}33`, color:"#f1f5f9", cursor:"pointer" }}>
                  <option value="">— None —</option>
                  {allAttacks.filter(ab => ab.key !== sel.attack1).map(ab => {
                    const typeInfo = ABILITY_TYPE_INFO[ab.type];
                    const pwr = calcAbilityPower(ab.key, entity);
                    const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                    const effectHint = ab.effects ? (ab.effects.selfAtkUp ? " ↑ATK" : ab.effects.selfDefUp ? " ↑DEF" : ab.effects.enemyAtkDown ? " ↓eATK" : ab.effects.enemyDefDown ? " ↓eDEF" : "") : "";
                    const cdLabel = ab.onCooldown ? ` ⏳ CD:${ab.cdRemaining}` : "";
                    return <option key={ab.key} value={ab.key} disabled={ab.onCooldown}>{discInfo?.icon||ab.icon} {ab.name} ({typeInfo.label}{effectHint}) — Pwr:{pwr.toFixed(2)}{ab.cooldown > 0 && !ab.onCooldown ? ` CD:${ab.cooldown}` : ""}{cdLabel}</option>;
                  })}
                </select>
                {sel.attack2 && (() => {
                  const ab = getAbility(sel.attack2);
                  if (!ab) return null;
                  const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                  const tags = getAbilityEffectTags(ab, entity);
                  const counter = getCounterInfo(ab.discipline, currentTarget);
                  return (
                    <div style={{ marginTop:2, padding:"3px 6px", borderRadius:4, background: (discInfo?.color || "#64748b") + "10", border: `1px solid ${(discInfo?.color || "#64748b")}22`, display:"flex", flexDirection:"column", gap:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:3, flexWrap:"wrap" }}>
                        {tags.map((t, i) => (
                          <span key={i} style={{ fontSize:6, fontWeight:700, padding:"0px 3px", borderRadius:2, background: t.color + "22", color: t.color, border: `1px solid ${t.color}33` }}>{t.icon} {t.label}</span>
                        ))}
                        {ab.cooldown > 0 && <span style={{ fontSize:6, color:"#94a3b8", fontWeight:600 }}>CD:{ab.cooldown}</span>}
                        {counter && (
                          <span style={{ fontSize:6, fontWeight:800, padding:"0px 4px", borderRadius:2, background: counter.color + "22", color: counter.color, border: `1px solid ${counter.color}44` }}>{counter.label}</span>
                        )}
                      </div>
                      <div style={{ fontSize:7, color:"#94a3b8", lineHeight:1.3 }}>{ab.desc}</div>
                    </div>
                  );
                })()}
              </div>
              <div>
                <div style={{ fontSize:8, fontWeight:700, color:"#3b82f6", letterSpacing:0.5, textTransform:"uppercase", marginBottom:2 }}>🛡 Defense</div>
                <select value={sel.defense || ""} onChange={e => setEntitySelection(entity.primary_key, "defense", e.target.value)}
                  style={{ width:"100%", fontSize:10, padding:"4px 6px", borderRadius:4, background:"#1e293b", border:`1px solid ${tier.color}33`, color:"#f1f5f9", cursor:"pointer" }}>
                  <option value="">— No Defense —</option>
                  {allDefense.map(ab => {
                    const pwr = calcAbilityPower(ab.key, entity);
                    const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                    const cdLabel = ab.onCooldown ? ` ⏳ CD:${ab.cdRemaining}` : "";
                    return <option key={ab.key} value={ab.key} disabled={ab.onCooldown}>{discInfo?.icon||"🛡"} {ab.name} — Pwr:{pwr.toFixed(2)}{ab.cooldown > 0 && !ab.onCooldown ? ` CD:${ab.cooldown}` : ""}{cdLabel}</option>;
                  })}
                </select>
                {/* Defense cooldown tracker */}
                {allDefense.some(ab => ab.onCooldown) && (
                  <div style={{ display:"flex", gap:2, flexWrap:"wrap", marginTop:2 }}>
                    {allDefense.filter(ab => ab.onCooldown).map(ab => {
                      const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                      return (
                        <span key={ab.key} style={{ fontSize:6, padding:"1px 4px", borderRadius:2, background:"#1e293b", color:"#475569", border:"1px solid #334155", fontWeight:600, display:"flex", alignItems:"center", gap:2 }}>
                          <span style={{ color: discInfo?.color || "#3b82f6" }}>🛡</span> {ab.name} <span style={{ color:"#f59e0b" }}>⏳{ab.cdRemaining}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
                {sel.defense && (() => {
                  const ab = getAbility(sel.defense);
                  if (!ab) return null;
                  const discInfo = MAGIC_DISCIPLINES.find(d => d.key === ab.discipline);
                  return (
                    <div style={{ marginTop:2, padding:"3px 6px", borderRadius:4, background: (discInfo?.color || "#3b82f6") + "10", border: `1px solid ${(discInfo?.color || "#3b82f6")}22`, display:"flex", flexDirection:"column", gap:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                        <span style={{ fontSize:6, fontWeight:700, padding:"0px 3px", borderRadius:2, background:"#3b82f622", color:"#3b82f6", border:"1px solid #3b82f633" }}>🛡 DEF</span>
                        {ab.cooldown > 0 && <span style={{ fontSize:6, color:"#94a3b8", fontWeight:600 }}>CD:{ab.cooldown}</span>}
                      </div>
                      <div style={{ fontSize:7, color:"#94a3b8", lineHeight:1.3 }}>{ab.desc}</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {/* KO overlay — tier-aware fallen visual */}
        {ko && (
          <div style={{
            position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
            pointerEvents:"none", zIndex:2, borderRadius:8,
            background:"radial-gradient(ellipse at center, #00000088 0%, transparent 70%)",
          }}>
            <div style={{
              fontSize:11, fontWeight:900, letterSpacing:3, textTransform:"uppercase",
              color: tier.color, fontFamily:FONT_DISPLAY,
              textShadow:`0 0 12px ${tier.color}88`,
              animation:"fallenText 0.4s ease-out",
            }}>FALLEN</div>
          </div>
        )}
      </div>
    );
  }

  const sevLabels = ["Sparring","Light Duel","Serious Duel","Fierce Duel","Blood Duel","Death Match","No Mercy","Annihilation","Cataclysm","Extinction"];
  const assigned = new Set([...sideA, ...sideB]);
  const selectedLeagueObj = selectedLeague ? (leagues || []).find(l => l.primary_key === selectedLeague) : null;
  const leagueMembers = selectedLeagueObj ? new Set((selectedLeagueObj.members || []).map(m => m.pk)) : null;
  const leagueAlliances = selectedLeagueObj?.alliances || [];
  const pendingChallenges = selectedLeagueObj ? (selectedLeagueObj.duels || []).filter(d => d.type === "intrigue" && d.action === "challenge" && d.success && !d.resolved) : [];
  const available = allEntities.filter(e => {
    if (assigned.has(e.primary_key)) return false;
    if (leagueMembers && !leagueMembers.has(e.primary_key)) return false;
    return true;
  });

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000066", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:"#0f172a", borderRadius:12, width:"min(1060px, 97vw)", maxHeight:"94vh", boxShadow:"0 20px 60px #0008", display:"flex", flexDirection:"column", overflow:"hidden", color:"#f1f5f9" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px", background:"linear-gradient(135deg,#1e293b,#0f172a,#1e293b)", flexShrink:0, borderBottom:"1px solid #334155", flexWrap:"wrap", gap:8 }}>
          <span style={{ fontSize:16, fontWeight:700, fontFamily:FONT_DISPLAY, letterSpacing:2 }}>⚡ Ability Duel</span>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {rounds.length > 0 && <span style={{ fontSize:11, color:"#a855f7", fontWeight:700, fontFamily:FONT_DISPLAY }}>Round {rounds.length}</span>}
            {winner && <span style={{ fontSize:12, color:"#22c55e", fontWeight:700, fontFamily:FONT_DISPLAY, letterSpacing:1, textShadow:"0 0 12px #22c55e66" }}>🏆 Side {winner} Wins!</span>}
            <span style={{ fontSize:10, color:"#64748b" }}>Pick attack + defense each round</span>
            <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#94a3b8" }}>✕</button>
          </div>
        </div>

        {/* Severity */}
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 20px", borderBottom:"1px solid #1e293b", flexShrink:0, flexWrap:"wrap" }}>
          <span style={{ fontSize:9, fontWeight:600, color:"#94a3b8", letterSpacing:0.5, textTransform:"uppercase" }}>Intensity</span>
          <input type="range" min={1} max={10} value={severity} onChange={e => setSeverity(parseInt(e.target.value))}
            style={{ flex:1, maxWidth:200, accentColor:"#a855f7" }} />
          <span style={{ fontSize:11, fontWeight:700, color: severity <= 3 ? "#22c55e" : severity <= 6 ? "#f59e0b" : "#ef4444" }}>
            {sevLabels[severity-1]} ({severity}/10)
          </span>
          {/* League award selector */}
          {leagues && leagues.length > 0 && (
            <>
              <div style={{ width:1, height:16, background:"#334155", margin:"0 4px" }} />
              <span style={{ fontSize:9, fontWeight:600, color:"#94a3b8", letterSpacing:0.5, textTransform:"uppercase" }}>League</span>
              <select value={selectedLeague} onChange={e => { setSelectedLeague(e.target.value); setDuelFilterAlliance(""); setDuelStakes(0); }}
                style={{
                  fontSize:10, padding:"4px 8px", borderRadius:5,
                  background: selectedLeague ? "#1e293b" : "#0f172a",
                  border: `1px solid ${selectedLeague ? "#e0c97f55" : "#334155"}`,
                  color: selectedLeague ? "#e0c97f" : "#64748b",
                  cursor:"pointer", fontFamily:FONT_BODY, outline:"none",
                }}>
                <option value="">None — all entities</option>
                {leagues.map(l => <option key={l.primary_key} value={l.primary_key}>{l.icon || "🏆"} {l.name}</option>)}
              </select>
              {selectedLeague && leagueAlliances.length > 0 && (
                <>
                  <span style={{ fontSize:9, fontWeight:600, color:"#94a3b8", letterSpacing:0.5, textTransform:"uppercase" }}>Alliance</span>
                  <select value={duelFilterAlliance} onChange={e => setDuelFilterAlliance(e.target.value)}
                    style={{ fontSize:10, padding:"4px 8px", borderRadius:5, background:"#1e293b", border:"1px solid #334155", color: duelFilterAlliance ? "#8b5cf6" : "#64748b", cursor:"pointer", fontFamily:FONT_BODY, outline:"none" }}>
                    <option value="">All members</option>
                    {leagueAlliances.map(a => <option key={a.name} value={a.name}>{a.icon || "🤝"} {a.name} ({(a.members || []).length})</option>)}
                  </select>
                </>
              )}
              {selectedLeague && (
                <>
                  <div style={{ width:1, height:16, background:"#334155", margin:"0 4px" }} />
                  <span style={{ fontSize:9, fontWeight:600, color:"#94a3b8", letterSpacing:0.5, textTransform:"uppercase" }}>Stakes</span>
                  <div style={{ display:"flex", gap:2 }}>
                    {[0,1,2,3,5].map(v => (
                      <button key={v} onClick={() => setDuelStakes(v)} style={{
                        fontSize:9, padding:"2px 6px", borderRadius:3, cursor:"pointer",
                        background: duelStakes === v ? (v > 0 ? "#f59e0b22" : "#1e293b") : "#0f172a",
                        border: `1px solid ${duelStakes === v ? (v > 0 ? "#f59e0b" : "#334155") : "#1e293b"}`,
                        color: duelStakes === v ? (v > 0 ? "#f59e0b" : "#94a3b8") : "#475569",
                        fontWeight: 700, fontFamily: FONT_BODY,
                      }}>{v === 0 ? "+1" : `±${v}`}</button>
                    ))}
                  </div>
                  <span style={{ fontSize:9, color: duelStakes > 0 ? "#f59e0b" : "#e0c97f", fontWeight:600 }}>
                    {duelStakes > 0 ? `Winner steals ${duelStakes} pts` : "+1 pt to winners"}
                  </span>
                </>
              )}
            </>
          )}
        </div>

        {/* Pending challenges banner */}
        {selectedLeague && pendingChallenges.length > 0 && !battleStarted && (
          <div style={{ padding:"6px 20px", borderBottom:"1px solid #1e293b", flexShrink:0, background:"#f59e0b08" }}>
            <div style={{ fontSize:9, fontWeight:700, color:"#f59e0b", letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>⚔ Pending Challenges</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {pendingChallenges.map((ch, i) => (
                <button key={i} onClick={() => {
                  // Auto-load the challenge combatants
                  setSideA([ch.actorPk]); setSideB([ch.targetPk]);
                  setDuelStakes(ch.stakes || ch.amount || 1);
                  resetBattle();
                }} style={{
                  fontSize:10, padding:"4px 10px", borderRadius:5, cursor:"pointer",
                  background:"#1e293b", border:"1px solid #f59e0b44", color:"#f59e0b",
                  fontWeight:600, fontFamily:FONT_BODY,
                }}>
                  {ch.actor} ⚔ {ch.target} — {ch.stakes || ch.amount || 1} pt{(ch.stakes || ch.amount || 1) !== 1 ? "s" : ""}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Entity picker */}
        {!battleStarted && (
          <div style={{ padding:"10px 20px", borderBottom:"1px solid #1e293b", flexShrink:0, maxHeight:200, overflowY:"auto" }}>
            {/* Filters */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
              <span style={{ fontSize:9, fontWeight:600, color:"#64748b", letterSpacing:0.5, textTransform:"uppercase" }}>
                Filter
              </span>
              <input
                type="text" placeholder="Search name..." value={duelFilterName}
                onChange={e => setDuelFilterName(e.target.value)}
                style={{ fontSize:10, padding:"3px 8px", borderRadius:4, background:"#1e293b", border:"1px solid #334155", color:"#f1f5f9", width:140, outline:"none" }}
              />
              <select value={duelFilterAllegiance} onChange={e => setDuelFilterAllegiance(e.target.value)}
                style={{ fontSize:10, padding:"3px 6px", borderRadius:4, background:"#1e293b", border:"1px solid #334155", color:"#f1f5f9", cursor:"pointer" }}>
                <option value="">All Allegiances</option>
                <option value="__none__">Unaligned</option>
                {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
              </select>
              <span style={{ fontSize:9, color:"#64748b" }}>
                {(() => {
                  const filtered = available.filter(e => {
                    if (duelFilterName && !e.name.toLowerCase().includes(duelFilterName.toLowerCase())) return false;
                    if (duelFilterAllegiance === "__none__" && e.allegiance) return false;
                    if (duelFilterAllegiance && duelFilterAllegiance !== "__none__" && e.allegiance !== duelFilterAllegiance) return false;
                    if (duelFilterAlliance && selectedLeagueObj) {
                      const alliance = (selectedLeagueObj.alliances || []).find(a => a.name === duelFilterAlliance);
                      if (alliance && !(alliance.members || []).includes(e.primary_key)) return false;
                    }
                    return true;
                  });
                  return `${filtered.length} of ${available.length}`;
                })()}
              </span>
              {(duelFilterName || duelFilterAllegiance) && (
                <button onClick={() => { setDuelFilterName(""); setDuelFilterAllegiance(""); }}
                  style={{ fontSize:9, padding:"2px 6px", borderRadius:3, background:"none", border:"1px solid #475569", color:"#94a3b8", cursor:"pointer" }}>
                  Clear
                </button>
              )}
            </div>

            {/* Randomizer */}
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6, padding:"6px 8px", background:"#1e293b44", borderRadius:6 }}>
              <span style={{ fontSize:9, fontWeight:600, color:"#94a3b8", letterSpacing:0.5, textTransform:"uppercase" }}>Random</span>
              <div style={{ display:"flex", gap:2 }}>
                {[1,2,3].map(n => (
                  <button key={n} onClick={() => setRandPerSide(n)} style={{
                    fontSize:10, padding:"2px 7px", borderRadius:3, cursor:"pointer",
                    background: randPerSide === n ? "#334155" : "transparent",
                    border: `1px solid ${randPerSide === n ? "#64748b" : "transparent"}`,
                    color: randPerSide === n ? "#f1f5f9" : "#64748b", fontWeight:700,
                  }}>{n}v{n}</button>
                ))}
              </div>
              <button onClick={randomizeMatchup} style={{
                fontSize:10, padding:"3px 10px", borderRadius:4, cursor:"pointer",
                background:"#334155", border:"1px solid #475569", color:"#e2e8f0",
                fontWeight:600, fontFamily:FONT_BODY,
              }}>🎲 Roll</button>
              {randResult && !randResult.error && (
                <div style={{ display:"flex", alignItems:"center", gap:4, flex:1 }}>
                  <span style={{ fontSize:10, color:"#93c5fd", fontWeight:600 }}>
                    {randResult.sideA.map(e => e.name).join(", ")}
                  </span>
                  <span style={{ fontSize:9, color:"#475569", fontWeight:800 }}>vs</span>
                  <span style={{ fontSize:10, color:"#fca5a5", fontWeight:600 }}>
                    {randResult.sideB.map(e => e.name).join(", ")}
                  </span>
                  <span style={{ fontSize:8, color:"#57534e" }}>
                    ({randResult.sideA.map(e => e.duelCount).join("+")} / {randResult.sideB.map(e => e.duelCount).join("+")} duels)
                  </span>
                  <button onClick={applyRandomResult} style={{
                    fontSize:9, padding:"2px 8px", borderRadius:3, cursor:"pointer",
                    background:"#22c55e22", border:"1px solid #22c55e44", color:"#22c55e",
                    fontWeight:700, fontFamily:FONT_BODY,
                  }}>✓ Use</button>
                  <button onClick={randomizeMatchup} style={{
                    fontSize:9, padding:"2px 6px", borderRadius:3, cursor:"pointer",
                    background:"transparent", border:"1px solid #334155", color:"#64748b",
                    fontWeight:600,
                  }}>↺</button>
                </div>
              )}
              {randResult && randResult.error && (
                <span style={{ fontSize:10, color:"#f59e0b" }}>{randResult.error}</span>
              )}
            </div>

            <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
              {available.filter(e => {
                if (duelFilterName && !e.name.toLowerCase().includes(duelFilterName.toLowerCase())) return false;
                if (duelFilterAllegiance === "__none__" && e.allegiance) return false;
                if (duelFilterAllegiance && duelFilterAllegiance !== "__none__" && e.allegiance !== duelFilterAllegiance) return false;
                if (duelFilterAlliance && selectedLeagueObj) {
                  const alliance = (selectedLeagueObj.alliances || []).find(a => a.name === duelFilterAlliance);
                  if (alliance && !(alliance.members || []).includes(e.primary_key)) return false;
                }
                return true;
              }).map(e => {
                const peak = peakDiscipline(e.disciplines, e.primaryDiscipline);
                const pi = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
                const icon = e.type === "mage" ? "✦" : "🐉";
                const hasAbilities = e.learnedAbilities && Object.keys(e.learnedAbilities).length > 0;
                const eTier = getPowerTier(calcDisciplinePowerForType(e.disciplines, e.type));
                const eKingdom = e.allegiance ? kingdoms.find(k => k.primary_key === e.allegiance) : null;
                return (
                  <div key={e.primary_key} style={{ display:"flex", gap:2 }}>
                    <button onClick={() => addToSide(e.primary_key, "A")} style={{
                      fontSize:10, padding:"4px 8px", borderRadius:"6px 0 0 6px", cursor:"pointer",
                      border:`1px solid ${eTier.color}44`, background:"#1e293b", color:"#93c5fd",
                      fontWeight:600, display:"flex", alignItems:"center", gap:3,
                      opacity: hasAbilities ? 1 : 0.5,
                    }} title={hasAbilities ? `${eTier.label}${eKingdom ? ' · ' + eKingdom.name : ''}` : "No abilities learned!"}>
                      ← {icon} {e.name} {pi ? pi.icon : ""} <span style={{fontSize:7,color:eTier.color,fontWeight:800}}>{eTier.ornament}</span>{eKingdom && <span style={{fontSize:7,color:eKingdom.color||"#6b7280",opacity:0.8}}>{eKingdom.name.substring(0,3)}</span>} {!hasAbilities && "⚠"}
                    </button>
                    <button onClick={() => addToSide(e.primary_key, "B")} style={{
                      fontSize:10, padding:"4px 8px", borderRadius:"0 6px 6px 0", cursor:"pointer",
                      border:`1px solid ${eTier.color}44`, background:"#1e293b", color:"#fca5a5", fontWeight:600,
                    }}>→</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Arena */}
        <div style={{ display:"flex", flex:1, overflow:"hidden", flexDirection: isMobileView() ? "column" : "row" }}>
          {/* Side A */}
          <div style={{ flex:1, padding:12, overflowY:"auto", borderRight: isMobileView() ? "none" : "1px solid #1e293b", borderBottom: isMobileView() ? "1px solid #1e293b" : "none" }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#93c5fd", letterSpacing:1, textTransform:"uppercase", marginBottom:6, fontFamily:FONT_DISPLAY }}>
              ⚔ Side A ({entsA.length})
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {entsA.map(e => <DuelCard key={e.primary_key} entity={e} side="A" />)}
              {entsA.length === 0 && <span style={{ fontSize:11, color:"#475569" }}>Assign entities above</span>}
            </div>
          </div>

          {/* Center controls */}
          <div style={{ width: isMobileView() ? "100%" : 130, display:"flex", flexDirection: isMobileView() ? "row" : "column", alignItems:"center", justifyContent:"center", gap:6, padding:10, flexShrink:0, flexWrap:"wrap" }}>
            {canSim && !battleStarted && (
              <button onClick={startDuel} style={{ background:"linear-gradient(135deg,#7c3aed,#6d28d9)", color:"#fff", border:"1px solid #a855f744", borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT_DISPLAY, letterSpacing:2, boxShadow:"0 0 20px #7c3aed44" }}>
                ⚡ Begin
              </button>
            )}
            {battleStarted && !battleOver && (
              <button onClick={resolveRound} disabled={!allSelectionsReady()} style={{
                background: allSelectionsReady() ? "linear-gradient(135deg,#7c3aed,#6d28d9)" : "#374151", color:"#fff",
                border: allSelectionsReady() ? "1px solid #a855f744" : "none", borderRadius:8,
                padding:"8px 14px", fontSize:12, fontWeight:700, cursor: allSelectionsReady() ? "pointer" : "not-allowed",
                opacity: allSelectionsReady() ? 1 : 0.5,
                fontFamily:FONT_DISPLAY, letterSpacing:1.5,
                boxShadow: allSelectionsReady() ? "0 0 16px #7c3aed44" : "none",
              }}>
                ⚡ Resolve Round
              </button>
            )}
            {battleStarted && !battleOver && (
              <button onClick={quickPickAll} style={{
                background:"none", border:"1px solid #a855f744", borderRadius:6, padding:"5px 10px",
                fontSize:9, color:"#a855f7", cursor:"pointer", fontWeight:600, letterSpacing:0.5,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#a855f718"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
              >
                ⚡ Quick Pick All
              </button>
            )}
            {battleStarted && (
              <button onClick={resetBattle} style={{ background:"none", border:"1px solid #475569", borderRadius:6, padding:"5px 10px", fontSize:10, color:"#94a3b8", cursor:"pointer" }}>
                ↺ Reset
              </button>
            )}
            {battleStarted && (
              <button onClick={confirmResult} disabled={confirming} style={{
                background: battleOver ? "#dc2626" : "#2563eb", color:"#fff", border:"none", borderRadius:8,
                padding:"8px 14px", fontSize:11, fontWeight:700, cursor: confirming ? "wait" : "pointer",
              }}>
                {confirming ? "..." : battleOver ? "✓ Confirm" : "💾 Save"}
              </button>
            )}
            <div style={{ fontSize:24, fontWeight:900, color:"#334155", fontFamily:FONT_DISPLAY, letterSpacing:4, textShadow:"0 0 20px #33415544" }}>VS</div>
            {winner && (() => {
              const winEnts = winner === "A" ? entsA : entsB;
              const bestTier = winEnts.reduce((best, e) => {
                const t = getPowerTier(calcDisciplinePowerForType(e.disciplines, e.type));
                return (!best || t.glowSize > best.glowSize) ? t : best;
              }, null) || { color:"#22c55e", ornament:"·" };
              return (
                <div style={{
                  padding:"8px 12px", borderRadius:8, textAlign:"center",
                  background: bestTier.color + "18",
                  border: `1px solid ${bestTier.color}66`,
                  boxShadow: `0 0 16px ${bestTier.color}33`,
                }}>
                  <div style={{ fontSize:12, fontWeight:800, color: bestTier.color, fontFamily:FONT_DISPLAY, letterSpacing:2, textShadow:`0 0 8px ${bestTier.color}66` }}>
                    🏆 {bestTier.ornament} Side {winner} {bestTier.ornament}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Side B */}
          <div style={{ flex:1, padding:12, overflowY:"auto" }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#fca5a5", letterSpacing:1, textTransform:"uppercase", marginBottom:6, fontFamily:FONT_DISPLAY }}>
              ⚔ Side B ({entsB.length})
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {entsB.map(e => <DuelCard key={e.primary_key} entity={e} side="B" />)}
              {entsB.length === 0 && <span style={{ fontSize:11, color:"#475569" }}>Assign entities above</span>}
            </div>
          </div>
        </div>

        {/* Round log */}
        {rounds.length > 0 && (
          <div style={{ borderTop:"1px solid #1e293b", padding:"8px 16px", maxHeight:180, overflowY:"auto", flexShrink:0 }}>
            <div style={{ fontSize:9, fontWeight:700, color:"#64748b", letterSpacing:1.5, textTransform:"uppercase", marginBottom:4, fontFamily:FONT_DISPLAY }}>Combat Log</div>
            {[...rounds].reverse().map((rd, ri) => (
              <div key={ri} style={{ marginBottom:6, padding:"6px 8px", background:"#1e293b", borderRadius:6 }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#94a3b8", marginBottom:3 }}>Round {rd.round}</div>
                {rd.results.map((r, i) => (
                  <div key={i} style={{ fontSize:9, padding:"3px 0", borderBottom: i < rd.results.length - 1 ? "1px solid #334155" : "none", display:"flex", flexDirection:"column", gap:2 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
                      <span style={{ color: r.tierColorA || (r.roundWinner === "A" ? "#93c5fd" : "#94a3b8"), fontWeight: r.roundWinner === "A" ? 700 : 500, fontFamily:FONT_DISPLAY, fontSize:9 }}>
                        {r.tierOrnA || ""} {r.nameA} {r.gangUpA > 0 ? <span style={{color:"#f59e0b",fontSize:7}}>({r.gangUpA}v1)</span> : ""} {r.koA ? "☠️" : ""} {r.roundWinner === "A" ? "◀" : ""}
                      </span>
                      <span style={{ color:"#475569", fontSize:8 }}>vs</span>
                      <span style={{ color: r.tierColorB || (r.roundWinner === "B" ? "#fca5a5" : "#94a3b8"), fontWeight: r.roundWinner === "B" ? 700 : 500, fontFamily:FONT_DISPLAY, fontSize:9 }}>
                        {r.roundWinner === "B" ? "▶" : ""} {r.nameB} {r.gangUpB > 0 ? <span style={{color:"#f59e0b",fontSize:7}}>({r.gangUpB}v1)</span> : ""} {r.koB ? "☠️" : ""} {r.tierOrnB || ""}
                      </span>
                    </div>
                    <div style={{ display:"flex", gap:8, fontSize:8, color:"#64748b", flexWrap:"wrap" }}>
                      <span>⚔ {r.attack1A}{r.attack2A !== "—" ? ` + ${r.attack2A}` : ""} ({r.atkPowA.toFixed(1)}) → {r.dodgeB ? <span style={{color:"#a78bfa",fontWeight:700}}>DODGED</span> : `${r.dmgToB} dmg`}</span>
                      <span>⚔ {r.attack1B}{r.attack2B !== "—" ? ` + ${r.attack2B}` : ""} ({r.atkPowB.toFixed(1)}) → {r.dodgeA ? <span style={{color:"#a78bfa",fontWeight:700}}>DODGED</span> : `${r.dmgToA} dmg`}</span>
                      <span>🛡 {r.defenseA} / {r.defenseB}</span>
                      {(r.countersA?.length > 0 || r.countersB?.length > 0) && (
                        <span style={{color:"#f59e0b",fontWeight:600}}>
                          {r.countersA?.length > 0 && <span>A: {r.countersA.join(" ")}</span>}
                          {r.countersA?.length > 0 && r.countersB?.length > 0 && " | "}
                          {r.countersB?.length > 0 && <span>B: {r.countersB.join(" ")}</span>}
                        </span>
                      )}
                    </div>
                    {/* XP gains */}
                    {(r.xpA || r.xpB) && (
                      <div style={{ display:"flex", gap:6, fontSize:7, color:"#7c3aed", flexWrap:"wrap" }}>
                        {r.xpA && Object.keys(r.xpA).length > 0 && (
                          <span>📖 {r.nameA}: {Object.entries(r.xpA).map(([k,v]) => {
                            const di = MAGIC_DISCIPLINES.find(d => d.key === k);
                            return `${di?.icon || ""}+${(v*100).toFixed(0)}%`;
                          }).join(" ")}</span>
                        )}
                        {r.xpB && Object.keys(r.xpB).length > 0 && (
                          <span>📖 {r.nameB}: {Object.entries(r.xpB).map(([k,v]) => {
                            const di = MAGIC_DISCIPLINES.find(d => d.key === k);
                            return `${di?.icon || ""}+${(v*100).toFixed(0)}%`;
                          }).join(" ")}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Power Rankings ────────────────────────────────────────────────────────────
function calcEntityPower(e) {
  const basePower = calcDisciplinePowerForType(e.disciplines, e.type);
  const total = calcDisciplinePowerForType(e.disciplines, e.type, e.artifacts, e.fatigue || 0);
  const artifactBonus = total - basePower;
  return { basePower, artifactBonus, total, fatigue: e.fatigue || 0 };
}

function PowerRankingsModal({ mages, dragons, kingdoms, onClose }) {
  const [filterDisc, setFilterDisc] = useState("");
  const [filterAllegiance, setFilterAllegiance] = useState("");

  const allMages = [...mages]
    .map(m => ({ ...m, type: "mage", ...calcEntityPower({ ...m, type: "mage" }) }));
  const allDragons = [...dragons]
    .map(d => ({ ...d, type: "dragon", ...calcEntityPower({ ...d, type: "dragon" }) }));

  // Filter by discipline (peak discipline matches)
  const discFilter = (entity) => {
    if (!filterDisc) return true;
    const disc = entity.disciplines || {};
    return (disc[filterDisc] || 0) > 0;
  };

  // Filter by allegiance
  const allegianceFilter = (entity) => {
    if (!filterAllegiance) return true;
    if (filterAllegiance === "__none__") return !entity.allegiance;
    return entity.allegiance === filterAllegiance;
  };

  const rankedMages = allMages.filter(discFilter).filter(allegianceFilter).sort((a, b) => b.basePower - a.basePower);
  const rankedDragons = allDragons.filter(discFilter).filter(allegianceFilter).sort((a, b) => b.basePower - a.basePower);

  const maxMage   = rankedMages[0]?.basePower || 1;
  const maxDragon = rankedDragons[0]?.basePower || 1;

  // Sort options for discipline: by highest level in that discipline
  const sortByDisc = filterDisc
    ? (a, b) => ((b.disciplines || {})[filterDisc] || 0) - ((a.disciplines || {})[filterDisc] || 0) || b.basePower - a.basePower
    : (a, b) => b.basePower - a.basePower;

  const sortedMages = rankedMages.sort(sortByDisc);
  const sortedDragons = rankedDragons.sort(sortByDisc);

  function RankRow({ entity, rank, max }) {
    const icon = entity.type === "mage" ? "✦" : "🐉";
    const filledArtifacts = Array.isArray(entity.artifacts) ? entity.artifacts.filter(a => a?.name) : [];
    const barW = Math.max(4, (entity.basePower / max) * 100);
    const disc = entity.disciplines || {};
    const totalDisc = calcDisciplineLevels(disc);
    const peak = peakDiscipline(disc, entity.primaryDiscipline);
    const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
    const fat = entity.fatigue || 0;
    const kingdom = kingdoms.find(k => k.primary_key === entity.allegiance);
    const tier = getPowerTier(entity.basePower);
    const baseNorm = normalizePower(entity.basePower);
    const actualNorm = normalizePower(entity.total);
    const actualDiff = actualNorm - baseNorm;
    return (
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 0", borderBottom:"1px solid #f3f4f6" }}>
        <span style={{
          fontSize:11, fontWeight:700, width:18, textAlign:"right", flexShrink:0,
          color: rank <= 3 ? tier.color : "#9ca3af",
          textShadow: rank === 1 && tier.glowSize > 4 ? `0 0 6px ${tier.color}66` : "none",
        }}>#{rank}</span>
        {/* Tier color swatch + ornament */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0, gap:1 }}>
          <div style={{ width:4, height:28, borderRadius:2, background:tier.gradient, boxShadow: tier.glowSize > 6 ? `0 0 4px ${tier.color}44` : "none" }} />
          <span style={{ fontSize:6, color:tier.color }}>{tier.ornament}</span>
        </div>
        <span style={{ fontSize:13 }}>{icon}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3, flexWrap:"wrap" }}>
            <span style={{
              fontSize:13, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
              color: tier.key === "iron" || tier.key === "rare" ? "#111827" : tier.color,
            }}>{entity.name}</span>
            {kingdom && (
              <span style={{ fontSize:9, color: kingdom.color || "#6b7280", background: (kingdom.color || "#6b7280") + "18", padding:"1px 5px", borderRadius:3, flexShrink:0 }}>{kingdom.name}</span>
            )}
            {totalDisc > 0 && (
              <span style={{ fontSize:10, color:"#7c3aed", flexShrink:0 }}>🔮{totalDisc}</span>
            )}
            {peakInfo && (
              <span style={{ fontSize:9, color: peakInfo.color, flexShrink:0, background: peakInfo.color + "15", padding:"1px 4px", borderRadius:3 }}>{peakInfo.icon}{peak.level}</span>
            )}
            {filterDisc && (disc[filterDisc] || 0) > 0 && (
              <span style={{ fontSize:9, color: MAGIC_DISCIPLINES.find(d=>d.key===filterDisc)?.color || "#7c3aed", flexShrink:0 }}>
                {MAGIC_DISCIPLINES.find(d=>d.key===filterDisc)?.icon}{disc[filterDisc]}
              </span>
            )}
            {filledArtifacts.length > 0 && (
              <span style={{ fontSize:10, color:"#d97706", flexShrink:0 }}>🏺×{filledArtifacts.length}</span>
            )}
            {fat > 0.1 && (
              <span style={{ fontSize:9, color: fat > 0.6 ? "#ef4444" : "#f59e0b", flexShrink:0 }}>😓{Math.round(fat*100)}%</span>
            )}
          </div>
          {/* Discipline mini-bars */}
          {totalDisc > 0 && (
            <div style={{ display:"flex", gap:2, marginBottom:3 }}>
              {MAGIC_DISCIPLINES.map(d => {
                const lvl = disc[d.key] || 0;
                const isHighlighted = filterDisc === d.key;
                return (
                  <div key={d.key} title={`${d.label}: ${lvl}`} style={{
                    flex:1, height: isHighlighted ? 5 : 3, borderRadius:2, background:"#f3f4f6", overflow:"hidden",
                    border: isHighlighted ? `1px solid ${d.color}44` : "none",
                  }}>
                    <div style={{ height:"100%", width:`${(lvl/12)*100}%`, background: d.color, borderRadius:2 }} />
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ height:5, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
            <div style={{
              height:"100%", borderRadius:3,
              width:`${barW}%`,
              background: tier.barStyle || tier.gradient,
              backgroundSize: tier.animBorder ? "200% 100%" : "100%",
              animation: tier.animBorder ? "borderSweep 3s linear infinite" : "none",
            }} />
          </div>
        </div>
        <div style={{ textAlign:"right", flexShrink:0 }}>
          <div style={{ fontSize:16, fontWeight:800, color: tier.color }}>{baseNorm}</div>
          <div style={{ fontSize:8, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}>{tier.label}</div>
          {(entity.artifactBonus !== 0 || fat > 0) && (
            <div style={{ fontSize:10, color: actualDiff >= 0 ? "#22c55e" : "#ef4444", fontWeight:600 }}>
              {actualDiff >= 0 ? "+" : ""}{actualDiff} → {actualNorm}
            </div>
          )}
        </div>
      </div>
    );
  }

  function Column({ title, icon, entities, max, emptyMsg }) {
    return (
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
          <span>{icon}</span>{title}
          <span style={{ fontSize:11, fontWeight:400, color:"#9ca3af", marginLeft:"auto" }}>{entities.length} total</span>
        </div>
        {entities.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>{emptyMsg}</span>
          : entities.map((e, i) => <RankRow key={e.primary_key} entity={e} rank={i + 1} max={max} />)
        }
      </div>
    );
  }

  const activeDiscInfo = filterDisc ? MAGIC_DISCIPLINES.find(d => d.key === filterDisc) : null;
  const activeKingdom = filterAllegiance ? kingdoms.find(k => k.primary_key === filterAllegiance) : null;

  return (
    <div style={{
      position:"fixed", inset:0, background:"#00000066", zIndex:100,
      display:"flex", alignItems:"center", justifyContent:"center",
    }} onClick={onClose}>
      <div style={{
        background:"#fff", borderRadius:12, width:"min(900px, 95vw)", maxHeight:"85vh",
        boxShadow:"0 20px 60px #0004", display:"flex", flexDirection:"column", overflow:"hidden",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid #e5e7eb", flexShrink:0 }}>
          <span style={{ fontSize:15, fontWeight:700, color:"#111827" }}>✦ Power Rankings</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#9ca3af" }}>✕</button>
        </div>

        {/* Filters */}
        <div style={{ display:"flex", gap:12, padding:"10px 20px", borderBottom:"1px solid #f3f4f6", flexShrink:0, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ fontSize:11, fontWeight:600, color:"#9ca3af", letterSpacing:0.5, textTransform:"uppercase" }}>Filters</span>

          {/* Discipline filter — pill buttons */}
          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
            <button onClick={() => setFilterDisc("")} style={{
              fontSize:10, fontWeight:600, padding:"3px 8px", borderRadius:10, cursor:"pointer",
              border: `1px solid ${!filterDisc ? "#374151" : "#e5e7eb"}`,
              background: !filterDisc ? "#374151" : "#f9fafb",
              color: !filterDisc ? "#fff" : "#6b7280",
            }}>All</button>
            {MAGIC_DISCIPLINES.map(d => (
              <button key={d.key} onClick={() => setFilterDisc(filterDisc === d.key ? "" : d.key)} style={{
                fontSize:10, fontWeight:600, padding:"3px 8px", borderRadius:10, cursor:"pointer",
                border: `1px solid ${filterDisc === d.key ? d.color : "#e5e7eb"}`,
                background: filterDisc === d.key ? d.color + "18" : "#f9fafb",
                color: filterDisc === d.key ? d.color : "#6b7280",
              }}>{d.icon} {d.label}</button>
            ))}
          </div>

          <div style={{ width:1, height:20, background:"#e5e7eb" }} />

          {/* Allegiance filter */}
          <select value={filterAllegiance} onChange={e => setFilterAllegiance(e.target.value)}
            style={{ fontSize:11, padding:"3px 8px", borderRadius:5, border:"1px solid #e5e7eb", background: filterAllegiance ? "#f0fdf4" : "#f9fafb", color:"#374151", cursor:"pointer" }}>
            <option value="">All Allegiances</option>
            {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
            <option value="__none__">Unaligned</option>
          </select>

          {(filterDisc || filterAllegiance) && (
            <button onClick={() => { setFilterDisc(""); setFilterAllegiance(""); }}
              style={{ fontSize:10, color:"#9ca3af", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>
              Clear all
            </button>
          )}
        </div>

        {/* Active filter summary */}
        {(filterDisc || filterAllegiance) && (
          <div style={{ padding:"6px 20px", background:"#f9fafb", borderBottom:"1px solid #f3f4f6", fontSize:11, color:"#6b7280", display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
            <span>Showing:</span>
            {activeDiscInfo && <span style={{ color: activeDiscInfo.color, fontWeight:600 }}>{activeDiscInfo.icon} {activeDiscInfo.label} practitioners</span>}
            {activeDiscInfo && activeKingdom && <span>in</span>}
            {activeKingdom && <span style={{ color: activeKingdom.color || "#374151", fontWeight:600 }}>▨ {activeKingdom.name}</span>}
            {filterAllegiance === "__none__" && <span style={{ fontWeight:600 }}>Unaligned</span>}
            <span style={{ color:"#9ca3af" }}>— {sortedMages.length} mages, {sortedDragons.length} dragons</span>
          </div>
        )}

        {/* Body */}
        <div style={{ display:"flex", gap:0, overflow:"auto", flex:1 }}>
          <div style={{ flex:1, padding:"16px 20px", borderRight:"1px solid #f3f4f6", overflow:"auto" }}>
            <Column title="Mages" icon="✦" entities={sortedMages} max={maxMage} emptyMsg={filterDisc || filterAllegiance ? "No mages match filters" : "No mages yet"} />
          </div>
          <div style={{ flex:1, padding:"16px 20px", overflow:"auto" }}>
            <Column title="Dragons" icon="🐉" entities={sortedDragons} max={maxDragon} emptyMsg={filterDisc || filterAllegiance ? "No dragons match filters" : "No dragons yet"} />
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Battle Readiness Modal ────────────────────────────────────────────────────
function ReadinessModal({ armies, characters, mages, dragons, kingdoms, cities, onClose }) {
  const [search, setSearch] = useState("");
  const [filterKingdom, setFilterKingdom] = useState("");
  const [expandedArmy, setExpandedArmy] = useState(null);
  const [sortBy, setSortBy] = useState("power"); // power, troops, name, readiness

  const q = search.toLowerCase().trim();

  // Compute army data
  const armyData = armies.map(a => {
    const armyChars = characters.filter(c => c.army === a.primary_key);
    const armyMages = mages.filter(m => m.army === a.primary_key);
    const armyDragons = dragons.filter(d => d.army === a.primary_key);
    const kingdom = kingdoms.find(k => k.primary_key === a.allegiance);
    const location = a.city ? cities.find(c => c.primary_key === a.city)?.name || "In city" : a.coordinates ? "On map" : "Unknown";

    // Compute total entity power
    let entityPower = 0;
    for (const c of armyChars) { const sk = c.skill || 5; entityPower += 0.04 * (Math.pow(1.37, sk) - 1); }
    for (const m of armyMages) entityPower += calcDisciplinePowerForType(m.disciplines, "mage", m.artifacts, m.fatigue || 0);
    for (const d of armyDragons) entityPower += calcDisciplinePowerForType(d.disciplines, "dragon", d.artifacts, d.fatigue || 0);

    // Readiness: average inverse fatigue of magical entities, weighted by whether artifacts are equipped
    const magicEnts = [...armyMages, ...armyDragons];
    let readiness = 1;
    if (magicEnts.length > 0) {
      const avgFatigue = magicEnts.reduce((s, e) => s + (e.fatigue || 0), 0) / magicEnts.length;
      const totalArtSlots = magicEnts.reduce((s, e) => s + (Array.isArray(e.artifacts) ? e.artifacts : []).filter(aa => aa?.name).length, 0);
      const equippedArts = magicEnts.reduce((s, e) => s + (Array.isArray(e.artifacts) ? e.artifacts : []).filter(aa => aa?.name && aa?.effects).length, 0);
      const artRatio = totalArtSlots > 0 ? equippedArts / totalArtSlots : 1;
      readiness = ((1 - avgFatigue) * 0.6 + artRatio * 0.4);
    }

    const totalPower = Math.pow(a.size || 0, 0.7) * (1 + (a.bonus || 0) + entityPower);

    return {
      ...a, kingdom, location, armyChars, armyMages, armyDragons,
      entityPower, totalPower, readiness,
      magicCount: armyMages.length + armyDragons.length,
      entityCount: armyChars.length + armyMages.length + armyDragons.length,
    };
  }).filter(a => {
    if (q && !a.name.toLowerCase().includes(q)) return false;
    if (filterKingdom === "__none__" && a.allegiance) return false;
    if (filterKingdom && filterKingdom !== "__none__" && a.allegiance !== filterKingdom) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === "power") return b.totalPower - a.totalPower;
    if (sortBy === "troops") return (b.size || 0) - (a.size || 0);
    if (sortBy === "readiness") return b.readiness - a.readiness;
    return a.name.localeCompare(b.name);
  });

  const totalTroops = armyData.reduce((s, a) => s + (a.size || 0), 0);
  const totalMagic = armyData.reduce((s, a) => s + a.magicCount, 0);

  function readinessColor(r) { return r >= 0.8 ? "#22c55e" : r >= 0.5 ? "#f59e0b" : "#ef4444"; }
  function readinessLabel(r) { return r >= 0.8 ? "Ready" : r >= 0.5 ? "Tired" : "Exhausted"; }

  function EntityMini({ entity, type }) {
    const fat = entity.fatigue || 0;
    const arts = (Array.isArray(entity.artifacts) ? entity.artifacts : []).filter(a => a?.name);
    const equipped = arts.filter(a => a?.name && a?.effects).length;
    const totalEnergy = arts.reduce((s, a) => s + (a?.energy || 0), 0);
    const maxEnergy = arts.length * 3;
    const peak = peakDiscipline(entity.disciplines, entity.primaryDiscipline);
    const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
    const power = type === "character"
      ? 0.04 * (Math.pow(1.37, entity.skill || 5) - 1)
      : calcDisciplinePowerForType(entity.disciplines, type, entity.artifacts, fat);

    return (
      <div style={{
        display:"flex", alignItems:"center", gap:6, padding:"6px 8px", borderRadius:5,
        background: fat > 0.6 ? "#fef2f2" : fat > 0.3 ? "#fffbeb" : "#f0fdf4",
        border: `1px solid ${fat > 0.6 ? "#fecaca" : fat > 0.3 ? "#fde68a" : "#bbf7d0"}`,
        marginBottom:3,
      }}>
        <span style={{ fontSize:12 }}>{type === "mage" ? "✦" : type === "dragon" ? "🐉" : "👤"}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, fontWeight:600, color:"#111827" }}>{entity.name}</span>
            {peakInfo && <span style={{ fontSize:8, color: peakInfo.color, background: peakInfo.color + "15", padding:"0px 4px", borderRadius:3 }}>{peakInfo.icon}{peak.level}</span>}
          </div>
        </div>
        {/* Fatigue bar */}
        {type !== "character" && (
          <div style={{ width:40, display:"flex", flexDirection:"column", alignItems:"center", gap:1 }}>
            <div style={{ width:"100%", height:4, borderRadius:2, background:"#e5e7eb", overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${(1-fat)*100}%`, borderRadius:2, background: fat <= 0.3 ? "#22c55e" : fat <= 0.6 ? "#f59e0b" : "#ef4444" }} />
            </div>
            <span style={{ fontSize:7, color:"#9ca3af" }}>{Math.round((1-fat)*100)}%</span>
          </div>
        )}
        {/* Artifacts */}
        {arts.length > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:3 }}>
            <span style={{ fontSize:9, color: equipped > 0 ? "#d97706" : "#d1d5db" }}>🏺{equipped}/{arts.length}</span>
            {maxEnergy > 0 && (
              <div style={{ display:"flex", gap:1 }}>
                {[...Array(Math.min(arts.length * 3, 9))].map((_, i) => (
                  <div key={i} style={{ width:3, height:6, borderRadius:1, background: i < totalEnergy ? "#22c55e" : "#e5e7eb" }} />
                ))}
              </div>
            )}
          </div>
        )}
        <span style={{ fontSize:9, color:"#6b7280", fontWeight:600, minWidth:40, textAlign:"right" }}>+{(power * 100).toFixed(0)}%</span>
      </div>
    );
  }

  function ArmyRow({ army }) {
    const isExpanded = expandedArmy === army.primary_key;
    const rColor = readinessColor(army.readiness);

    return (
      <div style={{ borderBottom:"1px solid #e5e7eb" }}>
        {/* Main row */}
        <div onClick={() => setExpandedArmy(isExpanded ? null : army.primary_key)}
          style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", cursor:"pointer", background: isExpanded ? "#f8fafc" : "#fff" }}>
          {/* Color + name */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, minWidth:0 }}>
            <div style={{ width:12, height:12, borderRadius:2, background: army.color, transform:"rotate(45deg)", flexShrink:0 }} />
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{army.name}</div>
              <div style={{ display:"flex", gap:4, alignItems:"center", marginTop:2 }}>
                {army.kingdom && <span style={{ fontSize:9, color: army.kingdom.color || "#6b7280", background: (army.kingdom.color || "#6b7280") + "18", padding:"1px 4px", borderRadius:3 }}>{army.kingdom.name}</span>}
                <span style={{ fontSize:9, color:"#9ca3af" }}>{army.location}</span>
              </div>
            </div>
          </div>

          {/* Troops */}
          <div style={{ textAlign:"center", minWidth:70 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{(army.size || 0).toLocaleString()}</div>
            <div style={{ fontSize:8, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.5 }}>Troops</div>
          </div>

          {/* Entities */}
          <div style={{ textAlign:"center", minWidth:50 }}>
            <div style={{ display:"flex", justifyContent:"center", gap:3, alignItems:"center" }}>
              {army.armyMages.length > 0 && <span style={{ fontSize:10, color:"#7c3aed" }}>✦{army.armyMages.length}</span>}
              {army.armyDragons.length > 0 && <span style={{ fontSize:10, color:"#ea580c" }}>🐉{army.armyDragons.length}</span>}
              {army.armyChars.length > 0 && <span style={{ fontSize:10, color:"#6b7280" }}>👤{army.armyChars.length}</span>}
              {army.entityCount === 0 && <span style={{ fontSize:10, color:"#d1d5db" }}>—</span>}
            </div>
            <div style={{ fontSize:8, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.5 }}>Entities</div>
          </div>

          {/* Entity power */}
          <div style={{ textAlign:"center", minWidth:60 }}>
            <div style={{ fontSize:12, fontWeight:700, color: army.entityPower > 0 ? "#7c3aed" : "#d1d5db" }}>+{(army.entityPower * 100).toFixed(0)}%</div>
            <div style={{ fontSize:8, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.5 }}>Magic</div>
          </div>

          {/* Total power */}
          <div style={{ textAlign:"center", minWidth:70 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#111827" }}>{army.totalPower.toFixed(0)}</div>
            <div style={{ fontSize:8, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.5 }}>Power</div>
          </div>

          {/* Readiness */}
          <div style={{ textAlign:"center", minWidth:65 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>
              <div style={{ width:32, height:6, borderRadius:3, background:"#e5e7eb", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${army.readiness*100}%`, borderRadius:3, background: rColor }} />
              </div>
              <span style={{ fontSize:10, fontWeight:700, color: rColor }}>{Math.round(army.readiness*100)}%</span>
            </div>
            <div style={{ fontSize:8, color: rColor, fontWeight:600 }}>{readinessLabel(army.readiness)}</div>
          </div>

          <span style={{ fontSize:10, color:"#9ca3af" }}>{isExpanded ? "▲" : "▼"}</span>
        </div>

        {/* Expanded detail */}
        {isExpanded && (
          <div style={{ padding:"8px 16px 12px 40px", background:"#f8fafc", borderTop:"1px solid #ece7e0" }}>
            {army.entityCount === 0 ? (
              <div style={{ fontSize:11, color:"#9ca3af", padding:8 }}>No entities attached to this army</div>
            ) : (
              <>
                {army.armyMages.length > 0 && (
                  <div style={{ marginBottom:6 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:"#7c3aed", letterSpacing:0.5, textTransform:"uppercase", marginBottom:4 }}>Mages ({army.armyMages.length})</div>
                    {army.armyMages.map(m => <EntityMini key={m.primary_key} entity={m} type="mage" />)}
                  </div>
                )}
                {army.armyDragons.length > 0 && (
                  <div style={{ marginBottom:6 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:"#ea580c", letterSpacing:0.5, textTransform:"uppercase", marginBottom:4 }}>Dragons ({army.armyDragons.length})</div>
                    {army.armyDragons.map(d => <EntityMini key={d.primary_key} entity={d} type="dragon" />)}
                  </div>
                )}
                {army.armyChars.length > 0 && (
                  <div>
                    <div style={{ fontSize:9, fontWeight:700, color:"#6b7280", letterSpacing:0.5, textTransform:"uppercase", marginBottom:4 }}>Characters ({army.armyChars.length})</div>
                    {army.armyChars.map(c => <EntityMini key={c.primary_key} entity={c} type="character" />)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      position:"fixed", inset:0, background:"#00000066", zIndex:100,
      display:"flex", alignItems:"center", justifyContent:"center",
    }} onClick={onClose}>
      <div style={{
        background:"#fff", borderRadius:12, width:"min(950px, 96vw)", maxHeight:"88vh",
        boxShadow:"0 20px 60px #0004", display:"flex", flexDirection:"column", overflow:"hidden",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid #e5e7eb", flexShrink:0 }}>
          <span style={{ fontSize:15, fontWeight:700, color:"#111827" }}>🛡 Battle Readiness</span>
          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
            <span style={{ fontSize:11, color:"#6b7280" }}>{armyData.length} armies</span>
            <span style={{ fontSize:11, color:"#374151", fontWeight:600 }}>{totalTroops.toLocaleString()} troops</span>
            <span style={{ fontSize:11, color:"#7c3aed" }}>{totalMagic} magical entities</span>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#9ca3af" }}>✕</button>
        </div>

        {/* Filters */}
        <div style={{ display:"flex", gap:8, padding:"10px 20px", borderBottom:"1px solid #f3f4f6", flexShrink:0, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, minWidth:140 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search armies..."
              style={{ ...inputStyle, fontSize:11, paddingLeft:24, width:"100%" }} />
            <span style={{ position:"absolute", left:7, top:"50%", transform:"translateY(-50%)", fontSize:11, color:"#9ca3af", pointerEvents:"none" }}>🔍</span>
            {search && <button onClick={() => setSearch("")} style={{ position:"absolute", right:4, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:10, color:"#9ca3af" }}>✕</button>}
          </div>
          <select value={filterKingdom} onChange={e => setFilterKingdom(e.target.value)}
            style={{ fontSize:10, padding:"4px 8px", borderRadius:5, border:"1px solid #e5e7eb", background:"#f9fafb", cursor:"pointer" }}>
            <option value="">All Kingdoms</option>
            {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
            <option value="__none__">Unaligned</option>
          </select>
          <div style={{ display:"flex", gap:2 }}>
            {[
              { key:"power", label:"Power" },
              { key:"troops", label:"Troops" },
              { key:"readiness", label:"Readiness" },
              { key:"name", label:"Name" },
            ].map(s => (
              <button key={s.key} onClick={() => setSortBy(s.key)} style={{
                fontSize:9, fontWeight:600, padding:"3px 8px", borderRadius:4, cursor:"pointer",
                border: `1px solid ${sortBy === s.key ? "#374151" : "#e5e7eb"}`,
                background: sortBy === s.key ? "#374151" : "#f9fafb",
                color: sortBy === s.key ? "#fff" : "#6b7280",
              }}>{s.label}</button>
            ))}
          </div>
        </div>

        {/* Column headers */}
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 16px", background:"#f9fafb", borderBottom:"1px solid #e5e7eb", flexShrink:0 }}>
          <span style={{ flex:1, fontSize:9, color:"#9ca3af", fontWeight:600 }}>ARMY</span>
          <span style={{ fontSize:9, color:"#9ca3af", fontWeight:600, minWidth:70, textAlign:"center" }}>TROOPS</span>
          <span style={{ fontSize:9, color:"#9ca3af", fontWeight:600, minWidth:50, textAlign:"center" }}>ENTITIES</span>
          <span style={{ fontSize:9, color:"#9ca3af", fontWeight:600, minWidth:60, textAlign:"center" }}>MAGIC</span>
          <span style={{ fontSize:9, color:"#9ca3af", fontWeight:600, minWidth:70, textAlign:"center" }}>POWER</span>
          <span style={{ fontSize:9, color:"#9ca3af", fontWeight:600, minWidth:65, textAlign:"center" }}>READINESS</span>
          <span style={{ width:14 }}></span>
        </div>

        {/* Army list */}
        <div style={{ overflow:"auto", flex:1 }}>
          {armyData.length === 0 ? (
            <div style={{ padding:32, textAlign:"center", color:"#9ca3af", fontSize:13 }}>
              {search || filterKingdom ? "No armies match your filters" : "No armies yet"}
            </div>
          ) : (
            armyData.map(a => <ArmyRow key={a.primary_key} army={a} />)
          )}
        </div>
      </div>
    </div>
  );
}

// ── Gear Modal ────────────────────────────────────────────────────────────────
function GearModal({ mages, dragons, kingdoms, onClose }) {
  const [search, setSearch] = useState("");
  const [filterSlot, setFilterSlot] = useState("");
  const [filterDisc, setFilterDisc] = useState("");
  const [filterPossessor, setFilterPossessor] = useState(""); // "unowned", entity primary_key, or ""
  const [filterKingdom, setFilterKingdom] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [editingTagId, setEditingTagId] = useState(null);

  // Build possessor lookup: artifact id -> entity
  const allEntities = [
    ...mages.map(m => ({ ...m, _type: "mage" })),
    ...dragons.map(d => ({ ...d, _type: "dragon" })),
  ];
  const possessorMap = {};
  for (const ent of allEntities) {
    const arts = Array.isArray(ent.artifacts) ? ent.artifacts : [];
    for (const a of arts) {
      if (a?.id) possessorMap[a.id] = ent;
    }
  }

  // Collect all tags used across the catalog for the filter dropdown
  const allTags = [...new Set(ARTIFACT_CATALOG.flatMap(a => a.tags || []))].sort();

  const q = search.toLowerCase().trim();

  const filtered = ARTIFACT_CATALOG.filter(a => {
    if (q && !a.name.toLowerCase().includes(q) && !a.desc.toLowerCase().includes(q)) return false;
    if (filterSlot && a.slot !== filterSlot) return false;
    if (filterDisc && a.discipline !== filterDisc) return false;
    if (filterTag && !(a.tags || []).includes(filterTag)) return false;
    const owner = possessorMap[a.id];
    if (filterPossessor === "__unowned__" && owner) return false;
    if (filterPossessor === "__owned__" && !owner) return false;
    if (filterPossessor && filterPossessor !== "__unowned__" && filterPossessor !== "__owned__" && owner?.primary_key !== filterPossessor) return false;
    if (filterKingdom === "__none__" && owner?.allegiance) return false;
    if (filterKingdom && filterKingdom !== "__none__" && owner?.allegiance !== filterKingdom) return false;
    return true;
  }).sort((a, b) => {
    // Sort by total effect weight (rough power estimate)
    const effVal = (eff) => (eff?.atkBonus||0)*100 + (eff?.defBonus||0)*80 + (eff?.hpRegen||0)*3 + (eff?.hpBonus||0)*0.5 + (eff?.evasion||0)*150 + (eff?.enemyAtkPenalty||0)*80 + (eff?.enemyDefPenalty||0)*80;
    return effVal(b.effects) - effVal(a.effects);
  });

  function addTag(artifactId, tag) {
    const trimmed = tag.trim().toLowerCase();
    if (!trimmed) return;
    const cat = ARTIFACT_CATALOG.find(a => a.id === artifactId);
    if (!cat) return;
    if (!cat.tags) cat.tags = [];
    if (!cat.tags.includes(trimmed)) cat.tags.push(trimmed);
    setTagInput("");
    setEditingTagId(null);
  }

  function removeTag(artifactId, tag) {
    const cat = ARTIFACT_CATALOG.find(a => a.id === artifactId);
    if (!cat || !cat.tags) return;
    cat.tags = cat.tags.filter(t => t !== tag);
  }

  const slotIcons = { crown:"👑", weapon:"⚔", dagger:"🗡", ring:"💍", amulet:"📿", staff:"🪄", chain:"⛓" };

  function GearRow({ artifact }) {
    const owner = possessorMap[artifact.id];
    const ownerKingdom = owner ? kingdoms.find(k => k.primary_key === owner.allegiance) : null;
    const discInfo = artifact.discipline !== "all" ? MAGIC_DISCIPLINES.find(d => d.key === artifact.discipline) : null;
    const tags = artifact.tags || [];
    const isEditing = editingTagId === artifact.id;

    // Get equipped/energy state from owner's artifact data
    const ownerArt = owner ? (Array.isArray(owner.artifacts) ? owner.artifacts : []).find(a => a?.id === artifact.id) : null;
    const energy = ownerArt?.energy ?? 3;
    const isEquipped = ownerArt?.equipped && energy > 0;

    return (
      <div style={{ padding:"8px 12px", borderBottom:"1px solid #f3f4f6", display:"flex", gap:8, alignItems:"flex-start" }}>
        {/* Icon + name */}
        <div style={{ minWidth:0, flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:3 }}>
            <span style={{ fontSize:12 }}>{slotIcons[artifact.slot] || "🏺"}</span>
            <span style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{artifact.name}</span>
            <span style={{ fontSize:10, color: discInfo ? discInfo.color : "#d97706", fontWeight:600 }}>
              {discInfo ? `${discInfo.icon} ${discInfo.label}` : "⊕ All"}
            </span>
            <span style={{ fontSize:9, color:"#9ca3af", background:"#f3f4f6", padding:"1px 5px", borderRadius:3 }}>{artifact.slot}</span>
          </div>
          <div style={{ fontSize:10, color:"#9ca3af", lineHeight:1.4, fontStyle:"italic", marginBottom:4 }}>{artifact.desc}</div>
          {/* Tags */}
          <div style={{ display:"flex", gap:3, flexWrap:"wrap", alignItems:"center" }}>
            {tags.map(t => (
              <span key={t} style={{ fontSize:9, background:"#e0e7ff", color:"#4338ca", padding:"1px 6px", borderRadius:8, display:"flex", alignItems:"center", gap:3 }}>
                {t}
                <span onClick={() => removeTag(artifact.id, t)} style={{ cursor:"pointer", fontSize:8, color:"#6366f1", fontWeight:700 }}>✕</span>
              </span>
            ))}
            {isEditing ? (
              <div style={{ display:"flex", gap:2, alignItems:"center" }}>
                <input
                  autoFocus
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") addTag(artifact.id, tagInput);
                    if (e.key === "Escape") { setEditingTagId(null); setTagInput(""); }
                  }}
                  placeholder="tag name..."
                  style={{ fontSize:9, padding:"1px 4px", border:"1px solid #c7d2fe", borderRadius:4, width:70, outline:"none" }}
                />
                <button onClick={() => addTag(artifact.id, tagInput)}
                  style={{ fontSize:8, padding:"1px 4px", borderRadius:3, border:"1px solid #c7d2fe", background:"#e0e7ff", color:"#4338ca", cursor:"pointer" }}>+</button>
                <button onClick={() => { setEditingTagId(null); setTagInput(""); }}
                  style={{ fontSize:8, padding:"1px 4px", borderRadius:3, border:"1px solid #d1d5db", background:"#f9fafb", color:"#9ca3af", cursor:"pointer" }}>✕</button>
              </div>
            ) : (
              <span onClick={() => { setEditingTagId(artifact.id); setTagInput(""); }}
                style={{ fontSize:9, color:"#9ca3af", cursor:"pointer", padding:"1px 4px", border:"1px dashed #d1d5db", borderRadius:4 }}>+ tag</span>
            )}
          </div>
        </div>

        {/* Owner info */}
        <div style={{ minWidth:120, textAlign:"right", flexShrink:0 }}>
          {owner ? (
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:"#374151" }}>
                {owner._type === "mage" ? "✦" : "🐉"} {owner.name}
              </div>
              {ownerKingdom && (
                <div style={{ fontSize:9, color: ownerKingdom.color || "#6b7280" }}>{ownerKingdom.name}</div>
              )}
              <div style={{ display:"flex", gap:3, justifyContent:"flex-end", marginTop:3, alignItems:"center" }}>
                {/* Energy pips */}
                {[1,2,3].map(i => (
                  <div key={i} style={{
                    width:10, height:6, borderRadius:1,
                    background: i <= energy ? (energy === 1 ? "#ef4444" : energy === 2 ? "#f59e0b" : "#22c55e") : "#e5e7eb",
                  }} />
                ))}
                <span style={{ fontSize:8, fontWeight:600, color: isEquipped ? "#16a34a" : "#9ca3af", marginLeft:2 }}>
                  {isEquipped ? "ON" : energy > 0 ? "OFF" : "—"}
                </span>
              </div>
            </div>
          ) : (
            <span style={{ fontSize:10, color:"#d1d5db" }}>Unowned</span>
          )}
        </div>
      </div>
    );
  }

  // Entities that own artifacts for possessor filter
  const ownersWithArts = allEntities.filter(e => (Array.isArray(e.artifacts) ? e.artifacts : []).some(a => a?.id));

  return (
    <div style={{
      position:"fixed", inset:0, background:"#00000066", zIndex:100,
      display:"flex", alignItems:"center", justifyContent:"center",
    }} onClick={onClose}>
      <div style={{
        background:"#fff", borderRadius:12, width:"min(900px, 95vw)", maxHeight:"85vh",
        boxShadow:"0 20px 60px #0004", display:"flex", flexDirection:"column", overflow:"hidden",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid #e5e7eb", flexShrink:0 }}>
          <span style={{ fontSize:15, fontWeight:700, color:"#111827" }}>🏺 Gear Catalog</span>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ fontSize:11, color:"#9ca3af" }}>{filtered.length}/{ARTIFACT_CATALOG.length} shown</span>
            {allTags.length > 0 && <span style={{ fontSize:11, color:"#6366f1" }}>{allTags.length} tags</span>}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#9ca3af" }}>✕</button>
        </div>

        {/* Filters */}
        <div style={{ display:"flex", gap:6, padding:"10px 20px", borderBottom:"1px solid #f3f4f6", flexShrink:0, flexWrap:"wrap", alignItems:"center" }}>
          {/* Search */}
          <div style={{ position:"relative", flex:1, minWidth:140 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or description..."
              style={{ ...inputStyle, fontSize:11, paddingLeft:24, width:"100%" }} />
            <span style={{ position:"absolute", left:7, top:"50%", transform:"translateY(-50%)", fontSize:11, color:"#9ca3af", pointerEvents:"none" }}>🔍</span>
            {search && <button onClick={() => setSearch("")} style={{ position:"absolute", right:4, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:10, color:"#9ca3af" }}>✕</button>}
          </div>

          {/* Slot */}
          <select value={filterSlot} onChange={e => setFilterSlot(e.target.value)}
            style={{ fontSize:10, padding:"4px 6px", borderRadius:4, border:"1px solid #e5e7eb", background:"#f9fafb", cursor:"pointer" }}>
            <option value="">All Slots</option>
            {ARTIFACT_SLOTS.map(s => <option key={s.key} value={s.key}>{slotIcons[s.key] || "🏺"} {s.label}</option>)}
          </select>

          {/* Discipline */}
          <select value={filterDisc} onChange={e => setFilterDisc(e.target.value)}
            style={{ fontSize:10, padding:"4px 6px", borderRadius:4, border:"1px solid #e5e7eb", background:"#f9fafb", cursor:"pointer" }}>
            <option value="">All Disciplines</option>
            {MAGIC_DISCIPLINES.map(d => <option key={d.key} value={d.key}>{d.icon} {d.label}</option>)}
            <option value="all">⊕ All (universal)</option>
          </select>

          {/* Tag */}
          <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
            style={{ fontSize:10, padding:"4px 6px", borderRadius:4, border:`1px solid ${filterTag ? "#c7d2fe" : "#e5e7eb"}`, background: filterTag ? "#e0e7ff" : "#f9fafb", color: filterTag ? "#4338ca" : "#374151", cursor:"pointer" }}>
            <option value="">All Tags</option>
            {allTags.map(t => <option key={t} value={t}>🏷 {t}</option>)}
          </select>

          {/* Possessor */}
          <select value={filterPossessor} onChange={e => setFilterPossessor(e.target.value)}
            style={{ fontSize:10, padding:"4px 6px", borderRadius:4, border:"1px solid #e5e7eb", background:"#f9fafb", cursor:"pointer" }}>
            <option value="">All Possessors</option>
            <option value="__owned__">Owned</option>
            <option value="__unowned__">Unowned</option>
            {ownersWithArts.map(e => <option key={e.primary_key} value={e.primary_key}>{e._type === "mage" ? "✦" : "🐉"} {e.name}</option>)}
          </select>

          {/* Kingdom */}
          <select value={filterKingdom} onChange={e => setFilterKingdom(e.target.value)}
            style={{ fontSize:10, padding:"4px 6px", borderRadius:4, border:"1px solid #e5e7eb", background:"#f9fafb", cursor:"pointer" }}>
            <option value="">All Kingdoms</option>
            {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
            <option value="__none__">Unaligned owners</option>
          </select>

          {(search || filterSlot || filterDisc || filterTag || filterPossessor || filterKingdom) && (
            <button onClick={() => { setSearch(""); setFilterSlot(""); setFilterDisc(""); setFilterTag(""); setFilterPossessor(""); setFilterKingdom(""); }}
              style={{ fontSize:9, color:"#9ca3af", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>Clear all</button>
          )}
        </div>

        {/* List */}
        <div style={{ overflow:"auto", flex:1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding:32, textAlign:"center", color:"#9ca3af", fontSize:13 }}>No artifacts match your filters</div>
          ) : (
            filtered.map(a => <GearRow key={a.id} artifact={a} />)
          )}
        </div>
      </div>
    </div>
  );
}

// ── Fatigue Manager Modal ─────────────────────────────────────────────────────
function FatigueManagerModal({ mages, dragons, kingdoms, onUpdateMage, onUpdateDragon, onClose }) {
  const [search, setSearch] = useState("");
  const [filterAllegiance, setFilterAllegiance] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const q = search.toLowerCase().trim();

  const allEntities = [
    ...mages.map(m => ({ ...m, _type: "mage" })),
    ...dragons.map(d => ({ ...d, _type: "dragon" })),
  ].filter(e => {
    if (q && !e.name?.toLowerCase().includes(q)) return false;
    if (filterAllegiance === "__none__" && e.allegiance) return false;
    if (filterAllegiance && filterAllegiance !== "__none__" && e.allegiance !== filterAllegiance) return false;
    return true;
  }).sort((a, b) => (b.fatigue || 0) - (a.fatigue || 0));

  async function saveEntity(entity, changes) {
    const updated = { ...entity, ...changes };
    delete updated._type;
    if (entity._type === "mage") await onUpdateMage(updated);
    else await onUpdateDragon(updated);
  }

  async function resetAllFatigue() {
    for (const e of allEntities) {
      if ((e.fatigue || 0) > 0) await saveEntity(e, { fatigue: 0 });
    }
  }

  async function healAllHP() {
    for (const e of allEntities) {
      const maxHP = calcMaxHP(e);
      const curHP = getCurrentHP(e);
      if (curHP < maxHP) await saveEntity(e, { currentHP: maxHP });
    }
  }

  async function fullRestoreAll() {
    for (const e of allEntities) {
      const maxHP = calcMaxHP(e);
      const arts = Array.isArray(e.artifacts) ? e.artifacts : [];
      const recharged = arts.map(a => a?.name ? { ...a, energy: 3 } : a);
      await saveEntity(e, { fatigue: 0, currentHP: maxHP, artifacts: recharged });
    }
  }

  async function rechargeAllArtifacts() {
    for (const e of allEntities) {
      const arts = Array.isArray(e.artifacts) ? e.artifacts : [];
      const needsRecharge = arts.some(a => a?.name && (a?.energy || 0) < 3);
      if (needsRecharge) {
        const recharged = arts.map(a => a?.name ? { ...a, energy: 3 } : a);
        await saveEntity(e, { artifacts: recharged });
      }
    }
  }

  async function setArtifactEnergy(entity, slotKey, newEnergy) {
    const arts = (Array.isArray(entity.artifacts) ? entity.artifacts : []).map(a => {
      if (a?.slot === slotKey) return { ...a, energy: Math.max(0, Math.min(3, newEnergy)) };
      return a;
    });
    await saveEntity(entity, { artifacts: arts });
  }

  async function toggleArtifactEquip(entity, slotKey) {
    const arts = (Array.isArray(entity.artifacts) ? entity.artifacts : []).map(a => {
      if (a?.slot === slotKey) return { ...a, equipped: !a.equipped };
      return a;
    });
    await saveEntity(entity, { artifacts: arts });
  }

  async function rechargeEntityArtifacts(entity) {
    const arts = (Array.isArray(entity.artifacts) ? entity.artifacts : []).map(a => {
      if (a?.name) return { ...a, energy: 3 };
      return a;
    });
    await saveEntity(entity, { artifacts: arts });
  }

  function FatigueRow({ entity }) {
    const fat = entity.fatigue || 0;
    const fatColor = fat <= 0.3 ? "#22c55e" : fat <= 0.6 ? "#f59e0b" : "#ef4444";
    const icon = entity._type === "mage" ? "✦" : "🐉";
    const kingdom = kingdoms.find(k => k.primary_key === entity.allegiance);
    const peak = peakDiscipline(entity.disciplines, entity.primaryDiscipline);
    const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
    const power = calcDisciplinePowerForType(entity.disciplines, entity._type, entity.artifacts, fat);
    const filledArts = (Array.isArray(entity.artifacts) ? entity.artifacts : []).filter(a => a?.name);
    const totalEnergy = filledArts.reduce((s, a) => s + (a?.energy || 0), 0);
    const maxEnergy = filledArts.length * 3;
    const isExpanded = expandedId === entity.primary_key;
    const maxHP = calcMaxHP(entity);
    const curHP = getCurrentHP(entity);
    const hpPct = maxHP > 0 ? curHP / maxHP : 1;
    const hpColor = hpPct > 0.6 ? "#22c55e" : hpPct > 0.3 ? "#f59e0b" : "#ef4444";

    return (
      <div style={{ borderBottom:"1px solid #f3f4f6" }}>
        {/* Main row */}
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", cursor: filledArts.length > 0 ? "pointer" : "default" }}
          onClick={() => filledArts.length > 0 && setExpandedId(isExpanded ? null : entity.primary_key)}>
          <span style={{ fontSize:14 }}>{icon}</span>
          <div style={{ minWidth:110, flex:"0 0 auto" }}>
            <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{entity.name}</div>
            <div style={{ display:"flex", gap:4, alignItems:"center", marginTop:2 }}>
              {kingdom && (
                <span style={{ fontSize:9, color: kingdom.color || "#6b7280", background: (kingdom.color || "#6b7280") + "18", padding:"1px 4px", borderRadius:3 }}>{kingdom.name}</span>
              )}
              {peakInfo && (
                <span style={{ fontSize:9, color: peakInfo.color, background: peakInfo.color + "15", padding:"1px 4px", borderRadius:3 }}>{peakInfo.icon}{peak.level}</span>
              )}
            </div>
          </div>
          {/* Fatigue + HP stacked */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:3 }} onClick={e => e.stopPropagation()}>
            {/* Fatigue slider */}
            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ fontSize:8, fontWeight:700, color:"#9ca3af", minWidth:22 }}>FAT</span>
              <input type="range" min={0} max={100} value={Math.round(fat * 100)}
                onChange={e => saveEntity(entity, { fatigue: parseInt(e.target.value) / 100 })}
                style={{ flex:1, accentColor: fatColor, height:12 }} />
              <span style={{ fontSize:10, fontWeight:700, color: fatColor, minWidth:32, textAlign:"right" }}>
                {Math.round(fat * 100)}%
              </span>
            </div>
            {/* HP slider */}
            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ fontSize:8, fontWeight:700, color:"#ef4444", minWidth:22 }}>HP</span>
              <input type="range" min={0} max={maxHP} value={curHP}
                onChange={e => saveEntity(entity, { currentHP: parseInt(e.target.value) })}
                style={{ flex:1, accentColor: hpColor, height:12 }} />
              <span style={{ fontSize:10, fontWeight:700, color: hpColor, minWidth:52, textAlign:"right" }}>
                {curHP}/{maxHP}
              </span>
            </div>
          </div>
          {/* Artifact energy summary */}
          {filledArts.length > 0 && (
            <div style={{ display:"flex", alignItems:"center", gap:4, minWidth:50 }} title={`Artifact energy: ${totalEnergy}/${maxEnergy}`}>
              <span style={{ fontSize:10 }}>🏺</span>
              <span style={{ fontSize:10, fontWeight:600, color: totalEnergy === maxEnergy ? "#22c55e" : totalEnergy === 0 ? "#ef4444" : "#f59e0b" }}>
                {totalEnergy}/{maxEnergy}
              </span>
              {filledArts.length > 0 && <span style={{ fontSize:9, color:"#9ca3af" }}>{isExpanded ? "▲" : "▼"}</span>}
            </div>
          )}
          {/* Power */}
          <div style={{ minWidth:52, textAlign:"right" }}>
            <span style={{ fontSize:10, color:"#9ca3af" }}>+{(power * 100).toFixed(0)}%</span>
          </div>
          {/* Quick buttons */}
          <div style={{ display:"flex", flexDirection:"column", gap:2 }} onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", gap:2 }}>
              <button onClick={() => saveEntity(entity, { fatigue: 0 })} title="Reset fatigue"
                style={{ fontSize:8, padding:"2px 4px", borderRadius:3, border:"1px solid #d1d5db", background: fat > 0 ? "#dcfce7" : "#f9fafb", color: fat > 0 ? "#16a34a" : "#d1d5db", cursor: fat > 0 ? "pointer" : "default", fontWeight:700 }}>
                F:0
              </button>
              <button onClick={() => saveEntity(entity, { fatigue: 0.5 })}
                style={{ fontSize:8, padding:"2px 4px", borderRadius:3, border:"1px solid #d1d5db", background:"#f9fafb", color:"#6b7280", cursor:"pointer", fontWeight:700 }}>
                F:50
              </button>
              <button onClick={() => saveEntity(entity, { fatigue: 1 })}
                style={{ fontSize:8, padding:"2px 4px", borderRadius:3, border:"1px solid #d1d5db", background: fat < 1 ? "#fef2f2" : "#f9fafb", color: fat < 1 ? "#ef4444" : "#d1d5db", cursor: fat < 1 ? "pointer" : "default", fontWeight:700 }}>
                F:100
              </button>
            </div>
            <div style={{ display:"flex", gap:2 }}>
              <button onClick={() => saveEntity(entity, { currentHP: maxHP })} title="Full heal"
                style={{ fontSize:8, padding:"2px 4px", borderRadius:3, border:"1px solid #d1d5db", background: curHP < maxHP ? "#dcfce7" : "#f9fafb", color: curHP < maxHP ? "#16a34a" : "#d1d5db", cursor: curHP < maxHP ? "pointer" : "default", fontWeight:700 }}>
                HP:Max
              </button>
              <button onClick={() => saveEntity(entity, { currentHP: Math.round(maxHP * 0.5) })}
                style={{ fontSize:8, padding:"2px 4px", borderRadius:3, border:"1px solid #d1d5db", background:"#f9fafb", color:"#6b7280", cursor:"pointer", fontWeight:700 }}>
                HP:½
              </button>
              <button onClick={() => saveEntity(entity, { currentHP: 0 })}
                style={{ fontSize:8, padding:"2px 4px", borderRadius:3, border:"1px solid #d1d5db", background: curHP > 0 ? "#fef2f2" : "#f9fafb", color: curHP > 0 ? "#ef4444" : "#d1d5db", cursor: curHP > 0 ? "pointer" : "default", fontWeight:700 }}>
                HP:0
              </button>
            </div>
          </div>
        </div>

        {/* Expanded artifact section */}
        {isExpanded && filledArts.length > 0 && (
          <div style={{ padding:"4px 10px 8px 38px", background:"#fafbff" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <span style={{ fontSize:10, fontWeight:600, color:"#6b7280", letterSpacing:0.5, textTransform:"uppercase" }}>Artifacts ({filledArts.length})</span>
              <button onClick={() => rechargeEntityArtifacts(entity)}
                disabled={totalEnergy === maxEnergy}
                style={{
                  fontSize:9, fontWeight:600, padding:"2px 8px", borderRadius:3, cursor: totalEnergy < maxEnergy ? "pointer" : "not-allowed",
                  border:"1px solid #d1d5db", background: totalEnergy < maxEnergy ? "#dbeafe" : "#f9fafb", color: totalEnergy < maxEnergy ? "#2563eb" : "#d1d5db",
                }}>
                ⚡ Recharge All
              </button>
            </div>
            {filledArts.map((art, idx) => {
              const discInfo = art.discipline && art.discipline !== "all"
                ? MAGIC_DISCIPLINES.find(d => d.key === art.discipline) : null;
              return (
                <div key={art.slot || idx} style={{
                  display:"flex", alignItems:"center", gap:6, padding:"4px 6px", marginBottom:2,
                  borderRadius:4, border:`1px solid ${discInfo ? discInfo.color + "44" : "#fde68a"}`,
                  background: discInfo ? discInfo.color + "08" : "#fffbeb",
                }}>
                  <span style={{ fontSize:10 }}>{art.slot === "crown" ? "👑" : "🏺"}</span>
                  <span style={{ fontSize:11, color:"#374151", flex:1, fontWeight:500 }}>
                    {art.name}
                    <span style={{ color: discInfo ? discInfo.color : "#d97706", marginLeft:4, fontSize:10 }}>
                      {discInfo ? discInfo.icon : "⊕"} {(() => {
                      const eff = art.effects || {};
                      const tags = [];
                      if (eff.atkBonus) tags.push(`+${(eff.atkBonus*100).toFixed(0)}%⚔`);
                      if (eff.defBonus) tags.push(`+${(eff.defBonus*100).toFixed(0)}%🛡`);
                      if (eff.hpRegen) tags.push(`+${eff.hpRegen}♥`);
                      if (eff.evasion) tags.push(`+${(eff.evasion*100).toFixed(0)}%↺`);
                      return tags.join(" ") || "—";
                    })()}
                    </span>
                  </span>
                  <span style={{ fontSize:8, fontWeight:700, color:"#16a34a" }}>ACTIVE</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const fatigued = allEntities.filter(e => (e.fatigue || 0) > 0).length;
  const wounded = allEntities.filter(e => getCurrentHP(e) < calcMaxHP(e)).length;
  const dead = allEntities.filter(e => getCurrentHP(e) <= 0).length;
  const totalArtEnergy = allEntities.reduce((s, e) => s + (Array.isArray(e.artifacts) ? e.artifacts : []).filter(a => a?.name).reduce((s2, a) => s2 + (a?.energy || 0), 0), 0);
  const maxArtEnergy = allEntities.reduce((s, e) => s + (Array.isArray(e.artifacts) ? e.artifacts : []).filter(a => a?.name).length * 3, 0);

  return (
    <div style={{
      position:"fixed", inset:0, background:"#00000066", zIndex:100,
      display:"flex", alignItems:"center", justifyContent:"center",
    }} onClick={onClose}>
      <div style={{
        background:"#fff", borderRadius:12, width:"min(820px, 95vw)", maxHeight:"85vh",
        boxShadow:"0 20px 60px #0004", display:"flex", flexDirection:"column", overflow:"hidden",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid #e5e7eb", flexShrink:0 }}>
          <span style={{ fontSize:15, fontWeight:700, color:"#111827" }}>😓 Fatigue, HP & Gear</span>
          <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:"#9ca3af" }}>{fatigued}/{allEntities.length} fatigued</span>
            <span style={{ fontSize:11, color: wounded > 0 ? "#f59e0b" : "#22c55e" }}>
              ❤ {wounded} wounded{dead > 0 ? `, ${dead} dead` : ""}
            </span>
            {maxArtEnergy > 0 && (
              <span style={{ fontSize:11, color: totalArtEnergy === maxArtEnergy ? "#22c55e" : "#f59e0b" }}>
                🏺 {totalArtEnergy}/{maxArtEnergy} energy
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#9ca3af" }}>✕</button>
        </div>

        {/* Search + filters + bulk actions */}
        <div style={{ display:"flex", gap:8, padding:"10px 20px", borderBottom:"1px solid #f3f4f6", flexShrink:0, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, minWidth:150 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search mages & dragons..."
              style={{ ...inputStyle, fontSize:12, paddingLeft:26, width:"100%" }} />
            <span style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", fontSize:12, color:"#9ca3af", pointerEvents:"none" }}>🔍</span>
            {search && (
              <button onClick={() => setSearch("")} style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:11, color:"#9ca3af" }}>✕</button>
            )}
          </div>
          <select value={filterAllegiance} onChange={e => setFilterAllegiance(e.target.value)}
            style={{ fontSize:11, padding:"5px 8px", borderRadius:5, border:"1px solid #e5e7eb", background:"#f9fafb", color:"#374151", cursor:"pointer" }}>
            <option value="">All Allegiances</option>
            {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
            <option value="__none__">Unaligned</option>
          </select>
          <button onClick={resetAllFatigue} disabled={fatigued === 0}
            style={{
              fontSize:10, fontWeight:600, padding:"5px 10px", borderRadius:5, cursor: fatigued > 0 ? "pointer" : "not-allowed",
              border:"1px solid #d1d5db", background: fatigued > 0 ? "#dcfce7" : "#f9fafb", color: fatigued > 0 ? "#16a34a" : "#d1d5db",
            }}>
            Reset Fatigue
          </button>
          <button onClick={healAllHP} disabled={wounded === 0}
            style={{
              fontSize:10, fontWeight:600, padding:"5px 10px", borderRadius:5, cursor: wounded > 0 ? "pointer" : "not-allowed",
              border:"1px solid #d1d5db", background: wounded > 0 ? "#fef3c7" : "#f9fafb", color: wounded > 0 ? "#d97706" : "#d1d5db",
            }}>
            ❤ Heal All HP
          </button>
          <button onClick={rechargeAllArtifacts} disabled={totalArtEnergy === maxArtEnergy}
            style={{
              fontSize:10, fontWeight:600, padding:"5px 10px", borderRadius:5, cursor: totalArtEnergy < maxArtEnergy ? "pointer" : "not-allowed",
              border:"1px solid #d1d5db", background: totalArtEnergy < maxArtEnergy ? "#dbeafe" : "#f9fafb", color: totalArtEnergy < maxArtEnergy ? "#2563eb" : "#d1d5db",
            }}>
            ⚡ Recharge Gear
          </button>
          <button onClick={fullRestoreAll} disabled={fatigued === 0 && wounded === 0 && totalArtEnergy === maxArtEnergy}
            style={{
              fontSize:10, fontWeight:600, padding:"5px 10px", borderRadius:5,
              cursor: (fatigued > 0 || wounded > 0 || totalArtEnergy < maxArtEnergy) ? "pointer" : "not-allowed",
              border:"1px solid #d1d5db",
              background: (fatigued > 0 || wounded > 0 || totalArtEnergy < maxArtEnergy) ? "#faf5ff" : "#f9fafb",
              color: (fatigued > 0 || wounded > 0 || totalArtEnergy < maxArtEnergy) ? "#7c3aed" : "#d1d5db",
            }}>
            ✦ Full Restore All
          </button>
        </div>

        {/* List */}
        <div style={{ overflow:"auto", flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background:"#f9fafb", borderBottom:"1px solid #e5e7eb", position:"sticky", top:0, zIndex:1 }}>
            <span style={{ fontSize:10, color:"#9ca3af", width:14 }}></span>
            <span style={{ fontSize:10, color:"#9ca3af", fontWeight:600, minWidth:110, flex:"0 0 auto" }}>Name</span>
            <span style={{ fontSize:10, color:"#9ca3af", fontWeight:600, flex:1, textAlign:"center" }}>Fatigue / HP</span>
            <span style={{ fontSize:10, color:"#9ca3af", fontWeight:600, minWidth:50, textAlign:"center" }}>Gear</span>
            <span style={{ fontSize:10, color:"#9ca3af", fontWeight:600, minWidth:52, textAlign:"right" }}>Power</span>
            <span style={{ fontSize:10, color:"#9ca3af", fontWeight:600, minWidth:68 }}></span>
          </div>
          {allEntities.length === 0 ? (
            <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:13 }}>
              {search || filterAllegiance ? "No entities match your filters" : "No mages or dragons yet"}
            </div>
          ) : (
            allEntities.map(e => <FatigueRow key={e.primary_key} entity={e} />)
          )}
        </div>
      </div>
    </div>
  );
}

// ── Battle Simulator ─────────────────────────────────────────────────────────
function getEntityPower(e) {
  if (e.type === "character") return 0.04 * (Math.pow(1.37, e.skill || 5) - 1);
  return calcDisciplinePowerForType(e.disciplines, e.type, e.artifacts, e._battleFatigue ?? e.fatigue ?? 0);
}

// Group matchmaking: balance total power across groups rather than strict 1:1
function matchEntities(activeA, activeB) {
  const powA = activeA.map(e => ({ e, pow: getEntityPower(e) })).sort((a,b) => b.pow - a.pow);
  const powB = activeB.map(e => ({ e, pow: getEntityPower(e) })).sort((a,b) => b.pow - a.pow);
  if (powA.length === 0 || powB.length === 0) return { duels: [], unmatchedA: activeA, unmatchedB: activeB };

  const duels = [];
  const usedA = new Set();
  const usedB = new Set();

  // Work from the side with fewer entities
  const fewerIsA = powA.length <= powB.length;
  const fewer = fewerIsA ? powA : powB;
  const more = fewerIsA ? powB : powA;
  const usedFewer = fewerIsA ? usedA : usedB;
  const usedMore = fewerIsA ? usedB : usedA;

  // Phase 1: pair each fewer entity with strongest available opponent
  for (const f of fewer) {
    if (usedFewer.has(f.e.primary_key)) continue;
    usedFewer.add(f.e.primary_key);

    let bestIdx = -1;
    for (let i = 0; i < more.length; i++) {
      if (usedMore.has(more[i].e.primary_key)) continue;
      bestIdx = i; break;
    }
    if (bestIdx === -1) break;

    const opponent = more[bestIdx];
    usedMore.add(opponent.e.primary_key);

    const groupFewer = [f.e];
    const groupMore = [opponent.e];
    let totalFewer = f.pow;
    let totalMore = opponent.pow;

    // Pull allies if outmatched >1.5x
    if (totalMore > totalFewer * 1.5) {
      for (const f2 of fewer) {
        if (usedFewer.has(f2.e.primary_key)) continue;
        if (totalFewer >= totalMore * 0.85) break;
        usedFewer.add(f2.e.primary_key);
        groupFewer.push(f2.e);
        totalFewer += f2.pow;
      }
    } else if (totalFewer > totalMore * 1.5) {
      for (let i = 0; i < more.length; i++) {
        if (usedMore.has(more[i].e.primary_key)) continue;
        if (totalMore >= totalFewer * 0.85) break;
        usedMore.add(more[i].e.primary_key);
        groupMore.push(more[i].e);
        totalMore += more[i].pow;
      }
    }

    if (fewerIsA) {
      duels.push({ groupA: groupFewer, groupB: groupMore, totalPowA: totalFewer, totalPowB: totalMore });
    } else {
      duels.push({ groupA: groupMore, groupB: groupFewer, totalPowA: totalMore, totalPowB: totalFewer });
    }
  }

  // Phase 2: Smart assignment of remaining entities
  // Check which duels the side with more entities is LOSING
  // Surplus entities go to losing duels first, then to army support
  const remainA = activeA.filter(e => !usedA.has(e.primary_key));
  const remainB = activeB.filter(e => !usedB.has(e.primary_key));
  const surplus = remainA.length > 0 ? remainA : remainB;
  const surplusSide = remainA.length > 0 ? "A" : "B";

  for (const r of surplus) {
    const pow = getEntityPower(r);
    // Find duels where our side is losing (opponent has more power)
    let bestDuel = -1;
    let bestDeficit = 0;
    for (let i = 0; i < duels.length; i++) {
      const d = duels[i];
      const ourPow = surplusSide === "A" ? d.totalPowA : d.totalPowB;
      const theirPow = surplusSide === "A" ? d.totalPowB : d.totalPowA;
      const deficit = theirPow - ourPow;
      if (deficit > bestDeficit) { bestDeficit = deficit; bestDuel = i; }
    }

    if (bestDuel >= 0) {
      // We found a duel we're losing — reinforce it
      if (surplusSide === "A") {
        duels[bestDuel].groupA.push(r);
        duels[bestDuel].totalPowA += pow;
      } else {
        duels[bestDuel].groupB.push(r);
        duels[bestDuel].totalPowB += pow;
      }
      if (surplusSide === "A") usedA.add(r.primary_key); else usedB.add(r.primary_key);
    }
    // If we're winning ALL duels, leave this entity as army support
  }

  const unmatchedA = activeA.filter(e => !usedA.has(e.primary_key));
  const unmatchedB = activeB.filter(e => !usedB.has(e.primary_key));
  return { duels, unmatchedA, unmatchedB };
}

function simulateFullRound({ troopsA, troopsB, bonusA, bonusB, entitiesA, entitiesB, severity, aIsAttacker, aHasSurprise, bHasSurprise, round }) {
  const SURPRISE = (round === 1) ? 2.5 : 1;
  const surpriseA = (aHasSurprise && round === 1) ? SURPRISE : 1;
  const surpriseB = (bHasSurprise && round === 1) ? SURPRISE : 1;

  const activeA = entitiesA.filter(e => (e._battleFatigue ?? e.fatigue ?? 0) < 1);
  const activeB = entitiesB.filter(e => (e._battleFatigue ?? e.fatigue ?? 0) < 1);

  const { duels: matchedDuels, unmatchedA, unmatchedB } = matchEntities(activeA, activeB);

  // Resolve group duels with power-scaled fatigue + discipline counters
  const duelResults = [];
  for (const duel of matchedDuels) {
    // Apply discipline counter bonuses to duel power
    // Use the peak discipline of the strongest entity in each group
    const bestA = duel.groupA.reduce((best, e) => {
      const p = getEntityPower(e);
      return p > (best?.pow || 0) ? { e, pow: p } : best;
    }, null);
    const bestB = duel.groupB.reduce((best, e) => {
      const p = getEntityPower(e);
      return p > (best?.pow || 0) ? { e, pow: p } : best;
    }, null);
    const counterResult = (bestA?.e && bestB?.e)
      ? getCounterMatchup(bestA.e.disciplines, bestB.e.disciplines)
      : { attackerBonus: 0, defenderBonus: 0, attackerCounters: false, defenderCounters: false };

    const totalA = duel.totalPowA * (1 + (counterResult.attackerBonus || 0));
    const totalB = duel.totalPowB * (1 + (counterResult.defenderBonus || 0));
    const total = totalA + totalB;
    if (total <= 0) continue;

    const ratioA = totalA / total;
    const ratioB = totalB / total;
    const sevScale = severity / 10;

    // Fatigue formula: scales with BOTH absolute opponent power AND power ratio
    // Higher-powered opponent = more fatigue for you (absolute scaling)
    // Being outmatched = even more fatigue (ratio scaling)
    // Example: +5000 vs +10000 -> 5000 gets ~50%, 10000 gets ~20%
    //          +1000 vs +2000  -> 1000 gets ~20%, 2000 gets ~10%
    const woundedA = [];
    const woundedB = [];

    for (const eA of duel.groupA) {
      const myPow = getEntityPower(eA);
      // Absolute factor: how strong is the opponent group (log scale for sanity)
      const absFactor = Math.log10(1 + totalB * 100) / 4; // ~0.5 at +100%, ~0.75 at +1000%, ~1.0 at +10000%
      // Ratio factor: how outmatched am I (1.0 = even, 2.0 = outmatched 2:1)
      const ratioFactor = ratioB / 0.5;
      const baseFat = 0.03 + sevScale * 0.04;
      const fatGain = baseFat * absFactor * ratioFactor * (0.85 + Math.random() * 0.3);
      const newFat = Math.min(1, (eA._battleFatigue ?? eA.fatigue ?? 0) + fatGain);
      eA._battleFatigue = newFat;
      if (newFat >= 1) woundedA.push(eA.name);
    }
    for (const eB of duel.groupB) {
      const myPow = getEntityPower(eB);
      const absFactor = Math.log10(1 + totalA * 100) / 4;
      const ratioFactor = ratioA / 0.5;
      const baseFat = 0.03 + sevScale * 0.04;
      const fatGain = baseFat * absFactor * ratioFactor * (0.85 + Math.random() * 0.3);
      const newFat = Math.min(1, (eB._battleFatigue ?? eB.fatigue ?? 0) + fatGain);
      eB._battleFatigue = newFat;
      if (newFat >= 1) woundedB.push(eB.name);
    }

    const duelWinner = Math.random() < ratioA ? "A" : "B";
    duelResults.push({
      groupA: duel.groupA, groupB: duel.groupB,
      totalPowA: totalA, totalPowB: totalB,
      woundedA, woundedB, duelWinner,
      counterResult,
    });
  }

  // Army fight — only unmatched entities contribute bonus
  let armyEntBonusA = unmatchedA.reduce((s, e) => s + getEntityPower(e), 0);
  let armyEntBonusB = unmatchedB.reduce((s, e) => s + getEntityPower(e), 0);

  const powerA = Math.pow(troopsA, 0.7) * (1 + bonusA + armyEntBonusA) * surpriseA;
  const powerB = Math.pow(troopsB, 0.7) * (1 + bonusB + armyEntBonusB) * surpriseB;
  const randA = powerA * (0.85 + Math.random() * 0.30);
  const randB = powerB * (0.85 + Math.random() * 0.30);

  const sevScale = severity / 10;
  const roundLoss = (0.02 + sevScale * 0.12) * (0.9 + Math.random() * 0.2);
  const totalTroops = troopsA + troopsB;
  const casualties = Math.round(totalTroops * Math.min(0.35, roundLoss));

  const totalPow = randA + randB;
  const ratA = randA / totalPow;
  const sharpA = Math.pow(ratA, 2);
  const sharpB = Math.pow(1 - ratA, 2);
  const normA = sharpA / (sharpA + sharpB);
  const shareA = (1 - normA) * 0.95 + 0.5 * 0.05;
  const shareB = normA * 0.95 + 0.5 * 0.05;

  const casA = Math.min(troopsA, Math.round(casualties * shareA));
  const casB = Math.min(troopsB, Math.round(casualties * shareB));

  return {
    casualtiesA: casA, casualtiesB: casB,
    survivorsA: Math.max(0, troopsA - casA),
    survivorsB: Math.max(0, troopsB - casB),
    roundWinner: randA >= randB ? "A" : "B",
    duelResults, armyEntBonusA, armyEntBonusB,
    unmatchedCountA: unmatchedA.length, unmatchedCountB: unmatchedB.length,
  };
}

function verdictLabel(r) {
  if (r < 1.15) return "Pyrrhic Victory";
  if (r < 1.5) return "Narrow Victory";
  if (r < 2.5) return "Decisive Victory";
  if (r < 5) return "Crushing Victory";
  return "Total Annihilation";
}

function BattleModal({ armies, characters, mages, dragons, onUpdateMage, onUpdateDragon, onConfirmBattle, onClose }) {
  const [armyIdA, setArmyIdA] = useState("");
  const [armyIdB, setArmyIdB] = useState("");
  const [aIsAttacker, setAIsAttacker] = useState(true);
  const [severity, setSeverity] = useState(1);
  const [aHasSurprise, setAHasSurprise] = useState(false);
  const [bHasSurprise, setBHasSurprise] = useState(false);
  const [rounds, setRounds] = useState([]);
  const [battleEntsA, setBattleEntsA] = useState([]);
  const [battleEntsB, setBattleEntsB] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const armyA = armies.find(a => a.primary_key === armyIdA);
  const armyB = armies.find(a => a.primary_key === armyIdB);

  function getEntities(armyKey) {
    if (!armyKey) return [];
    return [
      ...characters.filter(c => c.army === armyKey).map(c => ({ ...c, type: "character" })),
      ...mages.filter(m => m.army === armyKey).map(m => ({ ...m, type: "mage" })),
      ...dragons.filter(d => d.army === armyKey).map(d => ({ ...d, type: "dragon" })),
    ];
  }

  const nameA = armyA?.name || "Army A";
  const nameB = armyB?.name || "Army B";
  const sizeA = armyA?.size || 0;
  const sizeB = armyB?.size || 0;
  const canSim = armyA && armyB && sizeA > 0 && sizeB > 0 && armyIdA !== armyIdB;

  const lastRound = rounds[rounds.length - 1];
  const currentA = lastRound ? lastRound.survivorsA : sizeA;
  const currentB = lastRound ? lastRound.survivorsB : sizeB;
  const battleOver = currentA <= 0 || currentB <= 0;
  const winner = battleOver ? (currentA > currentB ? "A" : "B") : null;
  const totalCasA = rounds.reduce((s, r) => s + r.casualtiesA, 0);
  const totalCasB = rounds.reduce((s, r) => s + r.casualtiesB, 0);
  const battleStarted = battleEntsA.length > 0 || battleEntsB.length > 0;

  function startBattle() {
    setBattleEntsA(getEntities(armyIdA).map(e => ({ ...e, _battleFatigue: e.fatigue || 0 })));
    setBattleEntsB(getEntities(armyIdB).map(e => ({ ...e, _battleFatigue: e.fatigue || 0 })));
    setRounds([]);
  }

  function simulateNextRound() {
    if (!canSim || battleOver) return;
    const tA = lastRound ? lastRound.survivorsA : sizeA;
    const tB = lastRound ? lastRound.survivorsB : sizeB;
    if (tA <= 0 || tB <= 0) return;
    const result = simulateFullRound({ troopsA: tA, troopsB: tB, bonusA: armyA?.bonus || 0, bonusB: armyB?.bonus || 0, entitiesA: battleEntsA, entitiesB: battleEntsB, severity, aIsAttacker, aHasSurprise, bHasSurprise, round: rounds.length + 1 });
    setRounds(r => [...r, result]);
    setBattleEntsA([...battleEntsA]);
    setBattleEntsB([...battleEntsB]);
  }

  function simulateToEnd() {
    if (!canSim) return;
    let tA = lastRound ? lastRound.survivorsA : sizeA;
    let tB = lastRound ? lastRound.survivorsB : sizeB;
    const newRounds = [];
    let rd = rounds.length;
    while (tA > 0 && tB > 0 && rd < 50) {
      rd++;
      const result = simulateFullRound({ troopsA: tA, troopsB: tB, bonusA: armyA?.bonus || 0, bonusB: armyB?.bonus || 0, entitiesA: battleEntsA, entitiesB: battleEntsB, severity, aIsAttacker, aHasSurprise, bHasSurprise, round: rd });
      newRounds.push(result);
      tA = result.survivorsA; tB = result.survivorsB;
    }
    setRounds(r => [...r, ...newRounds]);
    setBattleEntsA([...battleEntsA]);
    setBattleEntsB([...battleEntsB]);
  }

  function reset() { setRounds([]); setBattleEntsA([]); setBattleEntsB([]); }
  function selectArmy(side, val) { if (side==="A") setArmyIdA(val); else setArmyIdB(val); reset(); }

  // Toggle artifact equip during battle
  async function handleToggleArtifact(ent, slotKey) {
    if (ent.type !== "mage" && ent.type !== "dragon") return;
    const newArts = (Array.isArray(ent.artifacts) ? ent.artifacts : []).map(a => {
      if (a?.slot === slotKey) return { ...a, equipped: !a.equipped };
      return a;
    });
    // Update battle clone
    const updateList = (list, setList) => {
      const idx = list.findIndex(e => e.primary_key === ent.primary_key);
      if (idx >= 0) { list[idx] = { ...list[idx], artifacts: newArts }; setList([...list]); }
    };
    updateList(battleEntsA, setBattleEntsA);
    updateList(battleEntsB, setBattleEntsB);
    // Also persist to API
    const { type: _, _battleFatigue: __, ...baseData } = ent;
    const updated = { ...baseData, artifacts: newArts };
    if (ent.type === "mage" && onUpdateMage) await onUpdateMage(updated);
    if (ent.type === "dragon" && onUpdateDragon) await onUpdateDragon(updated);
  }

  async function confirmResult() {
    if (!battleOver || !armyA || !armyB) return;
    setConfirming(true);
    try {
      const finalEntsA = battleEntsA.map(e => ({ ...e, fatigue: e._battleFatigue ?? e.fatigue ?? 0 }));
      const finalEntsB = battleEntsB.map(e => ({ ...e, fatigue: e._battleFatigue ?? e.fatigue ?? 0 }));
      await onConfirmBattle(armyA.primary_key, currentA, armyB.primary_key, currentB, finalEntsA, finalEntsB);
      reset();
    } catch(e) { console.error(e); } finally { setConfirming(false); }
  }

  const sevLabels = ["Skirmish","Minor Clash","Engagement","Bloody Fight","Hard Battle","Brutal Contest","Savage Battle","Carnage","Slaughter","Bloodbath"];

  // Entity status with expandable artifact toggles
  // ── Manual matchup state ──
  const [manualMatchups, setManualMatchups] = useState(null); // null = auto, array of { groupA: [pk], groupB: [pk] }
  const [dragEnt, setDragEnt] = useState(null); // { pk, side }

  // Compute current matchups (auto or manual)
  function getCurrentMatchups() {
    const entsA = battleStarted ? battleEntsA.filter(e => e.type !== "character") : getEntities(armyIdA).filter(e => e.type !== "character");
    const entsB = battleStarted ? battleEntsB.filter(e => e.type !== "character") : getEntities(armyIdB).filter(e => e.type !== "character");
    const activeA = entsA.filter(e => (e._battleFatigue ?? e.fatigue ?? 0) < 1);
    const activeB = entsB.filter(e => (e._battleFatigue ?? e.fatigue ?? 0) < 1);

    if (manualMatchups) {
      // Rebuild from manual state
      const duels = manualMatchups.map(m => ({
        groupA: m.groupA.map(pk => activeA.find(e => e.primary_key === pk) || entsA.find(e => e.primary_key === pk)).filter(Boolean),
        groupB: m.groupB.map(pk => activeB.find(e => e.primary_key === pk) || entsB.find(e => e.primary_key === pk)).filter(Boolean),
      })).filter(d => d.groupA.length > 0 && d.groupB.length > 0);
      const usedA = new Set(duels.flatMap(d => d.groupA.map(e => e.primary_key)));
      const usedB = new Set(duels.flatMap(d => d.groupB.map(e => e.primary_key)));
      return { duels, unmatchedA: activeA.filter(e => !usedA.has(e.primary_key)), unmatchedB: activeB.filter(e => !usedB.has(e.primary_key)) };
    }
    const { duels: autoDuels, unmatchedA, unmatchedB } = matchEntities(activeA, activeB);
    return {
      duels: autoDuels.map(d => ({ groupA: d.groupA, groupB: d.groupB })),
      unmatchedA, unmatchedB,
    };
  }

  // Handle drop: move entity into a duel group
  function handleDrop(targetDuelIdx, targetSide) {
    if (!dragEnt) return;
    const matchups = getCurrentMatchups();
    const duels = matchups.duels.map(d => ({
      groupA: d.groupA.map(e => e.primary_key),
      groupB: d.groupB.map(e => e.primary_key),
    }));
    // Also include unmatched as potential sources
    const unmatchedPKs_A = matchups.unmatchedA.map(e => e.primary_key);
    const unmatchedPKs_B = matchups.unmatchedB.map(e => e.primary_key);
    // Remove dragged entity from wherever it is (duels + unmatched)
    for (const d of duels) {
      d.groupA = d.groupA.filter(pk => pk !== dragEnt.pk);
      d.groupB = d.groupB.filter(pk => pk !== dragEnt.pk);
    }
    // Add to target
    if (targetDuelIdx >= 0 && targetDuelIdx < duels.length) {
      if (targetSide === "A") duels[targetDuelIdx].groupA.push(dragEnt.pk);
      else duels[targetDuelIdx].groupB.push(dragEnt.pk);
    }
    // Keep duels that have at least one entity on either side (so you can drop into the empty side)
    const cleaned = duels.filter(d => d.groupA.length > 0 || d.groupB.length > 0);
    setManualMatchups(cleaned);
    setDragEnt(null);
  }

  // Create new duel from unmatched entities
  function handleDropNewDuel(side) {
    if (!dragEnt) return;
    const matchups = getCurrentMatchups();
    const duels = matchups.duels.map(d => ({
      groupA: d.groupA.map(e => e.primary_key),
      groupB: d.groupB.map(e => e.primary_key),
    }));
    for (const d of duels) {
      d.groupA = d.groupA.filter(pk => pk !== dragEnt.pk);
      d.groupB = d.groupB.filter(pk => pk !== dragEnt.pk);
    }
    const newDuel = { groupA: [], groupB: [] };
    if (side === "A") newDuel.groupA.push(dragEnt.pk);
    else newDuel.groupB.push(dragEnt.pk);
    duels.push(newDuel);
    setManualMatchups(duels.filter(d => d.groupA.length > 0 || d.groupB.length > 0));
    setDragEnt(null);
  }

  function resetMatchups() { setManualMatchups(null); }

  // Override startBattle to also init matchups
  const origStartBattle = () => {
    const a = getEntities(armyIdA).map(e => ({ ...e, _battleFatigue: e.fatigue || 0 }));
    const b = getEntities(armyIdB).map(e => ({ ...e, _battleFatigue: e.fatigue || 0 }));
    setBattleEntsA(a); setBattleEntsB(b);
    setRounds([]); setManualMatchups(null);
  };

  // Override simulateNextRound to use manual matchups
  function simNextRound() {
    if (!canSim || battleOver) return;
    const tA = lastRound ? lastRound.survivorsA : sizeA;
    const tB = lastRound ? lastRound.survivorsB : sizeB;
    if (tA <= 0 || tB <= 0) return;
    // If manual matchups, inject them into simulateFullRound by temporarily reordering entities
    const result = simulateFullRound({ troopsA: tA, troopsB: tB, bonusA: armyA?.bonus || 0, bonusB: armyB?.bonus || 0, entitiesA: battleEntsA, entitiesB: battleEntsB, severity, aIsAttacker, aHasSurprise, bHasSurprise, round: rounds.length + 1 });
    setRounds(r => [...r, result]);
    setBattleEntsA([...battleEntsA]); setBattleEntsB([...battleEntsB]);
    setManualMatchups(null); // Reset manual after each round
  }

  function simToEnd() {
    if (!canSim) return;
    let tA = lastRound ? lastRound.survivorsA : sizeA;
    let tB = lastRound ? lastRound.survivorsB : sizeB;
    const newRounds = [];
    let rd = rounds.length;
    while (tA > 0 && tB > 0 && rd < 50) {
      rd++;
      const result = simulateFullRound({ troopsA: tA, troopsB: tB, bonusA: armyA?.bonus || 0, bonusB: armyB?.bonus || 0, entitiesA: battleEntsA, entitiesB: battleEntsB, severity, aIsAttacker, aHasSurprise, bHasSurprise, round: rd });
      newRounds.push(result); tA = result.survivorsA; tB = result.survivorsB;
    }
    setRounds(r => [...r, ...newRounds]);
    setBattleEntsA([...battleEntsA]); setBattleEntsB([...battleEntsB]);
  }

  // ── Entity Card ──
  function EntityCard({ ent, draggable }) {
    const fat = ent._battleFatigue ?? ent.fatigue ?? 0;
    const wounded = fat >= 1;
    const pow = getEntityPower(ent);
    const peak = peakDiscipline(ent.disciplines, ent.primaryDiscipline);
    const pi = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
    const icon = ent.type === "mage" ? "✦" : ent.type === "dragon" ? "🐉" : "👤";
    const filledArts = (Array.isArray(ent.artifacts) ? ent.artifacts : []).filter(a => a?.name);
    const equippedArts = filledArts.filter(a => a?.name && a?.effects);
    const disc = ent.disciplines || {};
    const [showArts, setShowArts] = useState(false);

    return (
      <div
        draggable={draggable && !wounded}
        onDragStart={e => { e.dataTransfer.setData("text",""); setDragEnt({ pk: ent.primary_key, side: battleEntsA.find(x=>x.primary_key===ent.primary_key)?"A":"B" }); }}
        onDragEnd={() => setDragEnt(null)}
        style={{
          width:140, padding:8, borderRadius:8, cursor: draggable && !wounded ? "grab" : "default",
          background: wounded ? "#f9fafb" : "#fff",
          border: `2px solid ${wounded ? "#e5e7eb" : pi ? pi.color + "55" : "#e5e7eb"}`,
          boxShadow: wounded ? "none" : "0 2px 8px #0001",
          opacity: wounded ? 0.45 : 1,
          transition: "box-shadow 0.2s, opacity 0.3s",
          position: "relative",
        }}
      >
        {/* Header: icon + name + power */}
        <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4 }}>
          <span style={{ fontSize:14 }}>{icon}</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, fontWeight:700, color: wounded ? "#9ca3af" : "#111827", textDecoration: wounded ? "line-through" : "none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ent.name}</div>
          </div>
        </div>

        {/* Peak discipline badge */}
        {pi && (
          <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom:4, flexWrap:"wrap" }}>
            <span style={{ fontSize:9, color: pi.color, background: pi.color+"15", padding:"1px 5px", borderRadius:3, fontWeight:600 }}>{pi.icon} {pi.label} {peak.level}</span>
            {/* Synergy badges */}
            {getActiveSynergies(disc).map((syn, si) => (
              <span key={si} title={syn.desc} style={{ fontSize:7, color:"#15803d", background:"#f0fdf4", padding:"1px 4px", borderRadius:3, fontWeight:600, border:"1px solid #bbf7d0" }}>{syn.icon}{syn.label}</span>
            ))}
          </div>
        )}

        {/* Discipline mini-bars */}
        {Object.values(disc).some(v => v > 0) && (
          <div style={{ display:"flex", gap:1, marginBottom:5 }}>
            {MAGIC_DISCIPLINES.map(d => {
              const lvl = disc[d.key] || 0;
              return (<div key={d.key} title={`${d.label}: ${lvl}`} style={{ flex:1, height:3, borderRadius:1, background:"#f3f4f6", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${(lvl/12)*100}%`, background: d.color, borderRadius:1 }} />
              </div>);
            })}
          </div>
        )}

        {/* Fatigue meter */}
        <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4 }}>
          <div style={{ flex:1, height:6, borderRadius:3, background:"#e5e7eb", overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${fat*100}%`, borderRadius:3, transition:"width 0.4s ease", background: wounded ? "#ef4444" : fat > 0.6 ? "#f59e0b" : fat > 0.3 ? "#eab308" : "#22c55e" }} />
          </div>
          <span style={{ fontSize:9, fontWeight:700, color: wounded ? "#ef4444" : fat > 0.6 ? "#f59e0b" : "#22c55e", minWidth:28 }}>
            {wounded ? "KO" : `${Math.round(fat*100)}%`}
          </span>
        </div>

        {/* Power + artifacts summary */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:12, fontWeight:800, color: wounded ? "#d1d5db" : "#7c3aed" }}>+{(pow*100).toFixed(0)}%</span>
          {filledArts.length > 0 && (
            <span onClick={e => { e.stopPropagation(); setShowArts(!showArts); }} style={{ fontSize:9, color:"#d97706", cursor:"pointer", background:"#fffbeb", padding:"1px 5px", borderRadius:3, border:"1px solid #fde68a" }}>
              🏺{equippedArts.length}/{filledArts.length} {showArts ? "▲" : "▼"}
            </span>
          )}
        </div>

        {/* Expandable artifacts */}
        {showArts && filledArts.length > 0 && (
          <div style={{ marginTop:4, borderTop:"1px solid #ece7e0", paddingTop:4 }}>
            {filledArts.map((art, idx) => {
              const energy = art.energy ?? 3;
              const isEquipped = art.equipped && energy > 0;
              const isDepleted = energy <= 0;
              return (
                <div key={art.slot||idx} style={{ display:"flex", alignItems:"center", gap:3, padding:"2px 0", fontSize:8, opacity: isDepleted ? 0.4 : 1 }}>
                  <span style={{ flex:1, color:"#374151" }}>{art.name}</span>
                  <div style={{ display:"flex", gap:1 }}>{[1,2,3].map(i => (<div key={i} style={{ width:6, height:4, borderRadius:1, background: i<=energy?(energy===1?"#ef4444":energy===2?"#f59e0b":"#22c55e"):"#e5e7eb" }} />))}</div>
                  <button onClick={e => { e.stopPropagation(); handleToggleArtifact(ent, art.slot); }} disabled={isDepleted} style={{ fontSize:7, fontWeight:700, padding:"1px 3px", borderRadius:2, cursor:isDepleted?"not-allowed":"pointer", border:`1px solid ${isEquipped?"#16a34a":"#d1d5db"}`, background:isEquipped?"#dcfce7":"#f9fafb", color:isEquipped?"#16a34a":"#9ca3af" }}>{isEquipped?"ON":isDepleted?"—":"OFF"}</button>
                </div>
              );
            })}
          </div>
        )}

        {wounded && <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, pointerEvents:"none" }}>☠️</div>}
      </div>
    );
  }

  // ── Duel Group Visual ──
  function DuelGroup({ duel, index }) {
    const groupAPow = duel.groupA.reduce((s,e) => s + getEntityPower(e), 0);
    const groupBPow = duel.groupB.reduce((s,e) => s + getEntityPower(e), 0);

    // Compute counter matchup using strongest entity on each side
    const bestA = duel.groupA.reduce((best, e) => { const p = getEntityPower(e); return p > (best?.pow||0) ? {e,pow:p} : best; }, null);
    const bestB = duel.groupB.reduce((best, e) => { const p = getEntityPower(e); return p > (best?.pow||0) ? {e,pow:p} : best; }, null);
    const counter = (bestA?.e && bestB?.e) ? getCounterMatchup(bestA.e.disciplines, bestB.e.disciplines) : null;
    const effAPow = groupAPow * (1 + (counter?.attackerBonus || 0));
    const effBPow = groupBPow * (1 + (counter?.defenderBonus || 0));
    const total = effAPow + effBPow;
    const ratioA = total > 0 ? effAPow / total : 0.5;

    const peakAInfo = counter?.peakA?.key ? MAGIC_DISCIPLINES.find(d => d.key === counter.peakA.key) : null;
    const peakBInfo = counter?.peakB?.key ? MAGIC_DISCIPLINES.find(d => d.key === counter.peakB.key) : null;

    return (
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:10, background:"#f8fafc", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:8 }}>
        {/* Side A entities */}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={() => handleDrop(index, "A")}
          style={{ display:"flex", gap:6, flexWrap:"wrap", flex:1, minHeight:60, padding:4, borderRadius:6, border: dragEnt ? "2px dashed #93c5fd" : "2px solid transparent", transition:"border 0.2s" }}
        >
          {duel.groupA.map(e => <EntityCard key={e.primary_key} ent={e} draggable={battleStarted && !battleOver} />)}
        </div>

        {/* VS indicator with power comparison + counter info */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, padding:"0 6px", flexShrink:0 }}>
          <span style={{ fontSize:9, fontWeight:700, color: armyA?.color || "#3b82f6" }}>+{(effAPow*100).toFixed(0)}%</span>
          {counter?.attackerCounters && <span style={{ fontSize:7, color:"#22c55e", fontWeight:700 }}>⚔ COUNTERS</span>}
          <div style={{ width:40, height:6, borderRadius:3, background:"#e5e7eb", overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${ratioA*100}%`, borderRadius:3, background: ratioA > 0.55 ? "#22c55e" : ratioA < 0.45 ? "#ef4444" : "#f59e0b" }} />
          </div>
          {peakAInfo && peakBInfo && (
            <span style={{ fontSize:9, color:"#6b7280" }}>{peakAInfo.icon}vs{peakBInfo.icon}</span>
          )}
          <span style={{ fontSize:12, fontWeight:900, color:"#9ca3af" }}>VS</span>
          <div style={{ width:40, height:6, borderRadius:3, background:"#e5e7eb", overflow:"hidden", direction:"rtl" }}>
            <div style={{ height:"100%", width:`${(1-ratioA)*100}%`, borderRadius:3, background: ratioA < 0.45 ? "#22c55e" : ratioA > 0.55 ? "#ef4444" : "#f59e0b" }} />
          </div>
          {counter?.defenderCounters && <span style={{ fontSize:7, color:"#22c55e", fontWeight:700 }}>⚔ COUNTERS</span>}
          <span style={{ fontSize:9, fontWeight:700, color: armyB?.color || "#ef4444" }}>+{(effBPow*100).toFixed(0)}%</span>
        </div>

        {/* Side B entities */}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={() => handleDrop(index, "B")}
          style={{ display:"flex", gap:6, flexWrap:"wrap", flex:1, minHeight:60, padding:4, borderRadius:6, justifyContent:"flex-end", border: dragEnt ? "2px dashed #fca5a5" : "2px solid transparent", transition:"border 0.2s" }}
        >
          {duel.groupB.map(e => <EntityCard key={e.primary_key} ent={e} draggable={battleStarted && !battleOver} />)}
        </div>
      </div>
    );
  }

  const matchups = getCurrentMatchups();

  // ── Troop Summary Bar ──
  function TroopBar({ army, current, lost, side, color }) {
    const isWin = winner === side;
    return (
      <div style={{ flex:1, padding:"8px 12px", borderRadius:8, background: isWin ? "#f0fdf4" : winner && !isWin ? "#fef2f2" : "#f8fafc", border:`1px solid ${isWin?"#bbf7d0":winner&&!isWin?"#fecaca":"#e5e7eb"}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
          <div style={{ width:10, height:10, borderRadius:2, background:color, transform:"rotate(45deg)" }} />
          <span style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{army?.name}</span>
          {isWin && <span>🏆</span>}
          {winner && !isWin && <span>🏳</span>}
          <span style={{ marginLeft:"auto", fontSize:12, fontWeight:700 }}>{current.toLocaleString()}</span>
          {rounds.length > 0 && <span style={{ fontSize:10, color:"#ef4444" }}>(−{lost.toLocaleString()})</span>}
        </div>
        <div style={{ height:8, borderRadius:4, background:"#e5e7eb", overflow:"hidden", position:"relative" }}>
          <div style={{ position:"absolute", height:"100%", width:`${((army?.size||0)/Math.max(sizeA,sizeB))*100}%`, background:color+"22", borderRadius:4 }} />
          <div style={{ height:"100%", borderRadius:4, transition:"width 0.5s", width:`${(current/Math.max(sizeA,sizeB))*100}%`, background:`linear-gradient(90deg,${color},${color}cc)` }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000066", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:"#fff", borderRadius:12, width:"min(1100px, 97vw)", maxHeight:"94vh", boxShadow:"0 20px 60px #0006", display:"flex", flexDirection:"column", overflow:"hidden" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px", background:"#1f2937", flexShrink:0 }}>
          <span style={{ fontSize:16, fontWeight:700, color:"#f9fafb" }}>⚔ Battle Simulator</span>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {rounds.length > 0 && <span style={{ fontSize:12, color:"#fbbf24" }}>Round {rounds.length}</span>}
            {winner && <span style={{ fontSize:12, color:"#22c55e", fontWeight:700 }}>🏆 {winner==="A"?nameA:nameB} — {verdictLabel(Math.max(currentA,currentB)/Math.max(Math.min(currentA,currentB),1))}</span>}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#9ca3af" }}>✕</button>
        </div>

        {/* Setup row */}
        <div style={{ padding:"10px 20px", display:"flex", gap:12, flexWrap:"wrap", borderBottom:"1px solid #e5e7eb", flexShrink:0, alignItems:"center" }}>
          <div style={{ flex:1, minWidth:150 }}>
            <div style={{ fontSize:9, fontWeight:700, color:"#6b7280", letterSpacing:1, marginBottom:3 }}>ARMY A</div>
            <select value={armyIdA} onChange={e=>selectArmy("A",e.target.value)} disabled={battleStarted} style={{ ...inputStyle, cursor:"pointer", fontSize:11, opacity:battleStarted?0.6:1 }}>
              <option value="">— Choose —</option>
              {armies.filter(a=>a.primary_key!==armyIdB).map(a=>(<option key={a.primary_key} value={a.primary_key}>{a.name} ({(a.size||0).toLocaleString()})</option>))}
            </select>
          </div>
          <span style={{ fontSize:14, fontWeight:800, color:"#d1d5db" }}>VS</span>
          <div style={{ flex:1, minWidth:150 }}>
            <div style={{ fontSize:9, fontWeight:700, color:"#6b7280", letterSpacing:1, marginBottom:3 }}>ARMY B</div>
            <select value={armyIdB} onChange={e=>selectArmy("B",e.target.value)} disabled={battleStarted} style={{ ...inputStyle, cursor:"pointer", fontSize:11, opacity:battleStarted?0.6:1 }}>
              <option value="">— Choose —</option>
              {armies.filter(a=>a.primary_key!==armyIdA).map(a=>(<option key={a.primary_key} value={a.primary_key}>{a.name} ({(a.size||0).toLocaleString()})</option>))}
            </select>
          </div>
          <div style={{ minWidth:150 }}>
            <div style={{ fontSize:9, fontWeight:700, color:"#6b7280", letterSpacing:1, marginBottom:3 }}>SEVERITY ({severity}/10)</div>
            <div style={{ display:"flex", gap:2 }}>{Array.from({length:10},(_,i)=>{const n=i+1;return<div key={n} onClick={()=>{if(!battleStarted)setSeverity(n);}} style={{ flex:1,height:14,borderRadius:2,cursor:battleStarted?"default":"pointer",background:n<=severity?(n<=3?"#86efac":n<=6?"#fbbf24":"#ef4444"):"#f3f4f6"}} />;})}</div>
            <div style={{ fontSize:9, color:"#374151", fontWeight:600, textAlign:"center", marginTop:2 }}>{sevLabels[severity-1]}</div>
          </div>
          {canSim&&!battleStarted&&(
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              <div style={{ display:"flex", gap:2 }}>{[["⚔ Atk",true],["🛡 Def",false]].map(([l,v])=>(<button key={l} onClick={()=>setAIsAttacker(v)} style={{ flex:1,padding:"3px 6px",borderRadius:3,border:"1px solid #e5e7eb",cursor:"pointer",fontSize:9,fontWeight:600,background:aIsAttacker===v?"#1f2937":"#f9fafb",color:aIsAttacker===v?"#fff":"#6b7280"}}>{l}</button>))}</div>
              <div style={{ display:"flex", gap:2 }}>
                <button onClick={()=>{setAHasSurprise(s=>!s);setBHasSurprise(false);}} style={{ flex:1,padding:"2px 4px",borderRadius:3,fontSize:8,fontWeight:600,cursor:"pointer",border:`1px solid ${aHasSurprise?"#f59e0b":"#e5e7eb"}`,background:aHasSurprise?"#fffbeb":"#f9fafb",color:aHasSurprise?"#92400e":"#9ca3af"}}>⚡A</button>
                <button onClick={()=>{setBHasSurprise(s=>!s);setAHasSurprise(false);}} style={{ flex:1,padding:"2px 4px",borderRadius:3,fontSize:8,fontWeight:600,cursor:"pointer",border:`1px solid ${bHasSurprise?"#f59e0b":"#e5e7eb"}`,background:bHasSurprise?"#fffbeb":"#f9fafb",color:bHasSurprise?"#92400e":"#9ca3af"}}>⚡B</button>
              </div>
            </div>
          )}
        </div>

        {/* Troop summary */}
        {canSim && (
          <div style={{ padding:"8px 20px", display:"flex", gap:12, flexShrink:0 }}>
            <TroopBar army={armyA} current={currentA} lost={totalCasA} side="A" color={armyA?.color||"#3b82f6"} />
            <TroopBar army={armyB} current={currentB} lost={totalCasB} side="B" color={armyB?.color||"#ef4444"} />
          </div>
        )}

        {/* Controls */}
        {canSim && (
          <div style={{ padding:"4px 20px 8px", display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
            {!battleStarted&&<button onClick={origStartBattle} style={{ background:"#1f2937",color:"#fff",border:"none",borderRadius:6,padding:"7px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>⚔ Begin Battle</button>}
            {battleStarted&&!battleOver&&<button onClick={simNextRound} style={{ background:"#1f2937",color:"#fff",border:"none",borderRadius:6,padding:"7px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>⚔ Next Round</button>}
            {battleStarted&&!battleOver&&<button onClick={simToEnd} style={{ background:"#7c3aed",color:"#fff",border:"none",borderRadius:6,padding:"7px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>⚡ Fight to End</button>}
            {battleStarted && manualMatchups && <button onClick={resetMatchups} style={{ background:"none",border:"1px solid #c7d2fe",borderRadius:6,padding:"7px 12px",fontSize:10,color:"#7c3aed",cursor:"pointer"}}>Auto Match</button>}
            {battleStarted&&<button onClick={()=>{reset();setManualMatchups(null);}} style={{ background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"7px 12px",fontSize:11,color:"#6b7280",cursor:"pointer"}}>↺ Reset</button>}
            {battleStarted && !battleOver && <span style={{ fontSize:9, color:"#9ca3af", marginLeft:8 }}>Drag cards to rearrange matchups</span>}
            {battleOver&&<button onClick={confirmResult} disabled={confirming} style={{ background:"#dc2626",color:"#fff",border:"none",borderRadius:6,padding:"7px 18px",fontSize:12,fontWeight:700,cursor:confirming?"wait":"pointer",marginLeft:"auto"}}>{confirming?"Applying...":"✓ Confirm & Apply"}</button>}
          </div>
        )}

        {/* Battle Arena — duel matchups */}
        {canSim && (matchups.duels.length > 0 || matchups.unmatchedA.length > 0 || matchups.unmatchedB.length > 0) && (
          <div style={{ flex:1, overflow:"auto", borderTop:"1px solid #e5e7eb", padding:"12px 20px" }}>
            {/* Duels */}
            {matchups.duels.length > 0 && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#374151", letterSpacing:0.5, marginBottom:6, textTransform:"uppercase" }}>⚔ Entity Duels ({matchups.duels.length})</div>
                {matchups.duels.map((d, i) => <DuelGroup key={i} duel={d} index={i} />)}
              </div>
            )}
            {/* Unmatched — army support */}
            {(matchups.unmatchedA.length > 0 || matchups.unmatchedB.length > 0) && (
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:"#7c3aed", letterSpacing:0.5, marginBottom:6, textTransform:"uppercase" }}>→ Army Support (unmatched)</div>
                <div style={{ display:"flex", gap:16 }}>
                  {matchups.unmatchedA.length > 0 && (
                    <div onDragOver={e => e.preventDefault()} onDrop={() => handleDropNewDuel("A")} style={{ flex:1, display:"flex", gap:6, flexWrap:"wrap", padding:8, borderRadius:8, background:"#f5f3ff", border: dragEnt ? "2px dashed #93c5fd" : "1px solid #e9d5ff", minHeight:40 }}>
                      {matchups.unmatchedA.map(e => <EntityCard key={e.primary_key} ent={e} draggable={battleStarted && !battleOver} />)}
                    </div>
                  )}
                  {matchups.unmatchedB.length > 0 && (
                    <div onDragOver={e => e.preventDefault()} onDrop={() => handleDropNewDuel("B")} style={{ flex:1, display:"flex", gap:6, flexWrap:"wrap", padding:8, borderRadius:8, background:"#fef2f2", border: dragEnt ? "2px dashed #fca5a5" : "1px solid #fecdd3", minHeight:40, justifyContent:"flex-end" }}>
                      {matchups.unmatchedB.map(e => <EntityCard key={e.primary_key} ent={e} draggable={battleStarted && !battleOver} />)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Round log — compact below the arena */}
            {rounds.length > 0 && (
              <div style={{ marginTop:16, borderTop:"1px solid #e5e7eb", paddingTop:8 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#374151", letterSpacing:0.5, marginBottom:6, textTransform:"uppercase" }}>Round History</div>
                {rounds.map((r, i) => {
                  const maxTroops = Math.max(sizeA, sizeB);
                  return (
                    <div key={i} style={{ display:"flex", alignItems:"center", padding:"4px 0", borderBottom:"1px solid #f3f4f6", gap:4, fontSize:10 }}>
                      <span style={{ width:30, fontWeight:700, color:"#9ca3af" }}>R{i+1}</span>
                      <span style={{ fontWeight:600, color:r.roundWinner==="A"?"#16a34a":"#374151", minWidth:40 }}>{r.survivorsA.toLocaleString()}</span>
                      <div style={{ flex:1, height:5, borderRadius:3, background:"#f3f4f6", overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:3, width:`${(r.survivorsA/maxTroops)*100}%`, background:armyA?.color||"#3b82f6" }} />
                      </div>
                      <span style={{ color:"#ef4444", minWidth:50, textAlign:"center" }}>−{r.casualtiesA.toLocaleString()} {r.roundWinner==="A"?"◀":"▶"} −{r.casualtiesB.toLocaleString()}</span>
                      <div style={{ flex:1, height:5, borderRadius:3, background:"#f3f4f6", overflow:"hidden", direction:"rtl" }}>
                        <div style={{ height:"100%", borderRadius:3, width:`${(r.survivorsB/maxTroops)*100}%`, background:armyB?.color||"#ef4444" }} />
                      </div>
                      <span style={{ fontWeight:600, color:r.roundWinner==="B"?"#16a34a":"#374151", minWidth:40, textAlign:"right" }}>{r.survivorsB.toLocaleString()}</span>
                      {r.duelResults.length>0&&<span style={{ fontSize:8, color:"#9ca3af" }}>{r.duelResults.length} duels{r.duelResults.some(d=>d.woundedA.length>0||d.woundedB.length>0)?" ☠️":""}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!canSim&&armyIdA&&armyIdB&&armyIdA===armyIdB&&<div style={{ padding:20, textAlign:"center", color:"#ef4444", fontSize:13 }}>Cannot battle an army against itself</div>}
        {(!armyIdA||!armyIdB)&&<div style={{ padding:40, textAlign:"center", color:"#9ca3af", fontSize:13 }}>Select two armies above to begin</div>}
      </div>
    </div>
  );
}

// ── Sidebar helpers ───────────────────────────────────────────────────────────
function SidebarItem({ color, label, shape, sub, active, onClick }) {
  return (
    <div onClick={onClick} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", padding:"3px 4px", borderRadius:4, background: active ? "#f0ebe4" : "transparent" }}>
      <span style={{ width:10, height:10, background:color, display:"inline-block", flexShrink:0, borderRadius: shape === "circle" ? "50%" : 2, transform: shape === "diamond" ? "rotate(45deg)" : "none" }} />
      <div style={{ overflow:"hidden" }}>
        <div style={{ fontSize:12, color:"#3d3529", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", fontFamily:FONT_BODY }}>{label}</div>
        {sub && <div style={{ fontSize:10, color:"#9a8e82" }}>{sub}</div>}
      </div>
    </div>
  );
}
// ── Full expanded card for mages/dragons replacing the sidebar ────────────────
function MageDragonCard({ entity, type, cities, armies, active, onClick }) {
  const disciplines = entity.disciplines || {};
  const fat = entity.fatigue || 0;
  const basePower   = calcDisciplinePowerForType(disciplines, type);
  const actualPower = calcDisciplinePowerForType(disciplines, type, entity.artifacts, fat);
  const baseNorm    = normalizePower(basePower);
  const actualNorm  = normalizePower(actualPower);
  const tier        = getPowerTier(basePower);
  const peak        = peakDiscipline(disciplines, entity.primaryDiscipline);
  const peakInfo    = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
  const arts        = (Array.isArray(entity.artifacts) ? entity.artifacts : []).filter(a => a?.name);
  const equippedArts = arts.filter(a => a?.name && a?.effects);
  const actualDiff  = actualNorm - baseNorm;
  const hasDelta    = Math.abs(actualDiff) > 0;

  const locationLine = entity.city
    ? `📍 ${cities.find(ci => ci.primary_key === entity.city)?.name || "city"}`
    : entity.army ? `⚔ ${armies.find(a => a.primary_key === entity.army)?.name || "army"}`
    : type === "dragon" ? (entity.element ? `${entity.element}` : null) : null;

  const fatigueColor = fat <= 0 ? "#4ade80" : fat <= 0.3 ? "#86efac" : fat <= 0.6 ? "#fbbf24" : "#f87171";
  const fatigueLabel = fat <= 0 ? "Fresh" : fat < 0.3 ? `${Math.round(fat*100)}% tired` : fat < 0.6 ? `${Math.round(fat*100)}% fatigued` : `${Math.round(fat*100)}% exhausted`;

  const weights = type === "dragon" ? DRAGON_WEIGHTS : MAGE_WEIGHTS;
  const activeDiscs = MAGIC_DISCIPLINES
    .filter(d => (disciplines[d.key] || 0) > 0)
    .sort((a, b) => (weights[b.key] || 0) - (weights[a.key] || 0));

  // Animated glow border for celestial/legendary
  const glowAnim = tier.animBorder && !active ? {
    "--tier-glow": tier.color + "66",
    "--tier-glow-in": tier.color + "22",
    animation: tier.key === "celestial"
      ? "borderGlow 2.8s ease-in-out infinite, celestialPulse 4s ease-in-out infinite"
      : "borderGlow 3.5s ease-in-out infinite, legendaryFlicker 4s ease-in-out infinite",
  } : {};

  // Active state: full dark gradient from tier
  const bgStyle = active ? tier.activeBg : tier.cardBg;

  return (
    <div
      onClick={onClick}
      style={{
        position:"relative", cursor:"pointer", borderRadius:10, overflow:"hidden",
        border: `${tier.borderWidth}px solid ${active ? tier.color : tier.color + (tier.key === "iron" ? "44" : tier.key === "rare" ? "55" : "77")}`,
        background: bgStyle,
        boxShadow: active
          ? `0 0 ${tier.glowSize + 8}px ${tier.color}88, 0 4px 24px #0006`
          : tier.glowSize > 0 ? `0 0 ${tier.glowSize}px ${tier.color}33` : "0 1px 4px #0000001a",
        marginBottom:8, transition:"box-shadow 0.2s, border-color 0.2s",
        ...glowAnim,
      }}
    >
      {/* ── Top accent: tier-colored gradient bar, thicker for higher ranks ── */}
      <div style={{
        height: tier.key === "celestial" ? 4 : tier.key === "legendary" ? 3 : 2,
        background: tier.barStyle,
        backgroundSize: tier.animBorder ? "200% 100%" : "100% 100%",
        animation: tier.animBorder ? "borderSweep 3s linear infinite" : "none",
        opacity: active ? 1 : 0.85,
      }} />

      <div style={{ padding:"10px 12px" }}>
        {/* ── Header ── */}
        <div style={{ display:"flex", alignItems:"flex-start", gap:9, marginBottom:8 }}>

          {/* Avatar with tier ring */}
          <div style={{ position:"relative", flexShrink:0 }}>
            <div style={{
              width:38, height:38,
              borderRadius: type === "mage" ? "50%" : 6,
              background: entity.color,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:17,
              border: `2px solid ${tier.color}${active ? "cc" : "66"}`,
              transform: type === "dragon" ? "rotate(45deg)" : "none",
              boxShadow: tier.glowSize > 0 ? `0 0 ${tier.glowSize/2}px ${tier.color}66` : "none",
            }}>
              <span style={{ transform: type === "dragon" ? "rotate(-45deg)" : "none" }}>
                {type === "mage" ? "✦" : "🐉"}
              </span>
            </div>
            {/* Tier pill */}
            <div style={{
              position:"absolute", bottom:-7, left:"50%", transform:"translateX(-50%)",
              fontSize:7, fontWeight:800, letterSpacing: tier.letterSpacing,
              background: tier.badge, color: tier.badgeText,
              padding:"1px 5px", borderRadius:4, whiteSpace:"nowrap",
              boxShadow: tier.glowSize > 0 ? `0 0 6px ${tier.color}88` : "none",
            }}>{tier.label.toUpperCase()}</div>
          </div>

          {/* Name + meta */}
          <div style={{ flex:1, minWidth:0, paddingTop:1 }}>
            <div style={{
              fontSize:13, fontWeight:800, lineHeight:1.2, marginBottom:3,
              color: active ? tier.textColor : (tier.key === "iron" ? "#cbd5e1" : tier.key === "rare" ? "#e0f2fe" : tier.key === "epic" ? "#ede9fe" : tier.textColor),
              whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              letterSpacing: tier.key === "celestial" ? 0.5 : 0,
              textShadow: (tier.key === "celestial" || tier.key === "legendary") && active ? `0 0 12px ${tier.color}88` : "none",
            }}>{entity.name}</div>
            {locationLine && (
              <div style={{ fontSize:10, color: tier.subColor, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {locationLine}
              </div>
            )}
            {peakInfo && (
              <div style={{ fontSize:10, fontWeight:600, color: active ? tier.textColor+"cc" : peakInfo.color }}>
                {peakInfo.icon} {peakInfo.label}{peak.level > 0 ? ` Lv.${peak.level}` : ""}
              </div>
            )}
          </div>

          {/* Power block */}
          <div style={{ flexShrink:0, textAlign:"right" }}>
            <div style={{ display:"flex", alignItems:"baseline", gap:2, justifyContent:"flex-end" }}>
              <span style={{
                fontSize:24, fontWeight:900, lineHeight:1,
                color: tier.color,
                textShadow: tier.glowSize > 0 ? `0 0 ${tier.glowSize}px ${tier.color}` : "none",
              }}>{baseNorm}</span>
              <span style={{ fontSize:9, fontWeight:700, color: tier.color + "99" }}>PWR</span>
            </div>
            {hasDelta && (
              <div style={{ fontSize:10, marginTop:1, color: actualDiff >= 0 ? "#4ade80" : "#f87171", fontWeight:700 }}>
                {actualDiff >= 0 ? "+" : ""}{actualDiff} → {actualNorm}
              </div>
            )}
          </div>
        </div>

        {/* ── Discipline bars ── */}
        {activeDiscs.length > 0 && (
          <div style={{ display:"flex", flexDirection:"column", gap:3, marginBottom:8 }}>
            {activeDiscs.map(d => {
              const lvl = disciplines[d.key] || 0;
              const isPeak = d.key === peak.key;
              return (
                <div key={d.key}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:2 }}>
                    <span style={{ fontSize:10, color: isPeak ? d.color : d.color + "bb", fontWeight: isPeak ? 700 : 500 }}>
                      {d.icon} {d.label}{isPeak ? " ★" : ""}
                    </span>
                    <span style={{ fontSize:9, color:"#64748b" }}>{DISCIPLINE_LABELS[lvl] || `Lv.${lvl}`}</span>
                  </div>
                  <div style={{ height: isPeak ? 5 : 3, borderRadius:3, background:"#ffffff0f", overflow:"hidden" }}>
                    <div style={{
                      height:"100%", borderRadius:3,
                      width:`${(lvl / 12) * 100}%`,
                      background: isPeak ? tier.barStyle : d.color + "99",
                      backgroundSize: isPeak && tier.animBorder ? "200% 100%" : "100%",
                      animation: isPeak && tier.animBorder ? "borderSweep 2s linear infinite" : "none",
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Artifact chips ── */}
        {arts.length > 0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginBottom:7 }}>
            {arts.map((a, i) => {
              const isEquipped = a?.name && a?.effects;
              const discInfo = a.discipline && a.discipline !== "all" ? MAGIC_DISCIPLINES.find(dd => dd.key === a.discipline) : null;
              const ac = discInfo?.color || "#d97706";
              return (
                <div key={i} title={a.name} style={{
                  fontSize:9, fontWeight:600, padding:"2px 6px", borderRadius:4,
                  background: isEquipped ? ac + "18" : "#ffffff07",
                  color: isEquipped ? ac : "#475569",
                  border:`1px solid ${isEquipped ? ac + "44" : "#ffffff0f"}`,
                  whiteSpace:"nowrap", maxWidth:86, overflow:"hidden", textOverflow:"ellipsis",
                }}>
                  {discInfo?.icon || "🏺"} {a.name}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Footer ── */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:6 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ display:"flex", gap:2 }}>
              {[0.2,0.4,0.6,0.8,1.0].map(t => (
                <div key={t} style={{
                  width:6, height:6, borderRadius:2,
                  background: fat >= t ? fatigueColor : "#ffffff14",
                }} />
              ))}
            </div>
            <span style={{ fontSize:9, color: fat > 0 ? fatigueColor : "#4ade8099", fontWeight:600 }}>{fatigueLabel}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {type === "dragon" && entity.dragonSize && (
              <span style={{ fontSize:9, color:"#475569", fontWeight:500 }}>{entity.dragonSize}</span>
            )}
            <button onClick={e => { e.stopPropagation(); onClick(); }} style={{
              fontSize:9, padding:"2px 8px", borderRadius:4, cursor:"pointer",
              border:`1px solid ${tier.color}55`,
              background: tier.color + "14",
              color: tier.color,
              fontWeight:700, letterSpacing:0.5,
            }}>EDIT</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── League Member Row (extracted for hooks) ─────────────────────────────────
function LeagueMemberRow({ member, idx, entity, onEditPoints, onRemove, kingdoms }) {
  const [editing, setEditing] = useState(false);
  const [tempPts, setTempPts] = useState(member.points || 0);
  const kingdom = entity?.allegiance ? kingdoms.find(k => k.primary_key === entity.allegiance) : null;
  const disc = entity?.disciplines || {};
  const peak = peakDiscipline(disc, entity?.primaryDiscipline);
  const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
      background: "#1c1917", borderRadius: 6, border: "1px solid #292524",
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#57534e", width: 18, textAlign: "right" }}>#{idx + 1}</span>
      <span style={{ fontSize: 12 }}>{entity?.type === "dragon" ? "🐉" : "✦"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#e7e5e4", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entity?.name || "?"}</span>
          {kingdom && <span style={{ fontSize: 8, color: kingdom.color || "#78716c", background: (kingdom.color || "#78716c") + "22", padding: "0px 4px", borderRadius: 3 }}>{kingdom.name}</span>}
          {peakInfo && <span style={{ fontSize: 8, color: peakInfo.color }}>{peakInfo.icon}{peak.level}</span>}
        </div>
      </div>
      {editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <input type="number" value={tempPts} onChange={e => setTempPts(Number(e.target.value) || 0)}
            style={{
              width: 56, padding: "3px 4px", borderRadius: 4, textAlign: "center",
              background: "#0c0a09", border: "1px solid #44403c", color: "#e0c97f",
              fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY, outline: "none",
            }}
            onKeyDown={e => { if (e.key === "Enter") { onEditPoints(member.pk, tempPts); setEditing(false); } }}
          />
          <button onClick={() => { onEditPoints(member.pk, tempPts); setEditing(false); }}
            style={{ background: "none", border: "none", color: "#22c55e", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>✓</button>
          <button onClick={() => setEditing(false)}
            style={{ background: "none", border: "none", color: "#78716c", fontSize: 12, cursor: "pointer" }}>✕</button>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: "#e0c97f", fontFamily: FONT_DISPLAY, cursor: "pointer" }}
            onClick={() => { setEditing(true); setTempPts(member.points || 0); }}
            title="Click to edit points"
          >{member.points || 0}</span>
          <button onClick={() => onRemove(member.pk)}
            style={{ background: "none", border: "none", color: "#44403c", fontSize: 11, cursor: "pointer", padding: "2px 4px" }}
            onMouseOver={e => e.target.style.color="#ef4444"} onMouseOut={e => e.target.style.color="#44403c"}
          >✕</button>
        </div>
      )}
    </div>
  );
}

// ── Leagues Modal ────────────────────────────────────────────────────────────

// ── Exam Scoring ─────────────────────────────────────────────────────────────
// Base score from discipline level with diminishing returns, plus random variance.
// Level 1 → ~15-35, Level 6 → ~50-70, Level 12 → ~75-100
function calcExamScore(disciplineLevel) {
  if (!disciplineLevel || disciplineLevel <= 0) {
    // No level at all — random 0-25
    return Math.max(0, Math.min(100, Math.round(Math.random() * 25)));
  }
  // Diminishing returns: sqrt curve scaled to 0-80, then +random 0-20
  const base = Math.round(80 * Math.sqrt(disciplineLevel / 12));
  const variance = Math.round((Math.random() - 0.3) * 30); // skewed slightly positive
  return Math.max(0, Math.min(100, base + variance));
}

function examGrade(score) {
  if (score >= 95) return { grade: "S", color: "#e0c97f", label: "Exceptional" };
  if (score >= 85) return { grade: "A", color: "#22c55e", label: "Excellent" };
  if (score >= 70) return { grade: "B", color: "#3b82f6", label: "Good" };
  if (score >= 55) return { grade: "C", color: "#f59e0b", label: "Adequate" };
  if (score >= 40) return { grade: "D", color: "#f97316", label: "Poor" };
  return { grade: "F", color: "#ef4444", label: "Failed" };
}

// ── Intrigue Success Chance ──────────────────────────────────────────────────
// Mainly rank-based, with minor discipline bonus from Soulbinding/Voidweaving
function calcIntrigueChance(actorRank, totalMembers, entity, actionType, allianceBonus, targetAllianceBonus, powerLevel) {
  if (actionType === "challenge") return 1.0;
  // Rank-based: higher rank (lower number) = better chance
  const rankRatio = totalMembers > 1 ? (totalMembers - actorRank) / (totalMembers - 1) : 0.5;
  let base = 0.20 + rankRatio * 0.45; // 0.20 to 0.65

  // Minor discipline bonus: Soulbinding for spy, Voidweaving for sabotage
  const disc = entity?.disciplines || {};
  if (actionType === "spy") base += Math.min(0.10, (disc.soulbinding || 0) * 0.008);
  if (actionType === "sabotage") base += Math.min(0.10, (disc.voidweaving || 0) * 0.008);

  // Actor alliance bonus (offensive)
  base += (allianceBonus || 0);

  // Target alliance bonus (defensive — reduces chance)
  base -= (targetAllianceBonus || 0);

  // Power level penalty: higher point value = harder to pull off
  // Power 1 = no penalty, Power 2 = -15%, Power 3 = -30%
  const pwrLevel = powerLevel || 1;
  if (pwrLevel > 1) base -= (pwrLevel - 1) * 0.15;

  return Math.min(0.92, Math.max(0.08, base));
}

function LeaguesModal({ leagues, setLeagues, mages, dragons, kingdoms, onClose }) {
  const [activeLeague, setActiveLeague] = useState(null);
  const [view, setView] = useState("standings");
  const [saving, setSaving] = useState(false);

  // Create league form
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState("🏆");
  const LEAGUE_ICONS = ["🏆","⚔","🏟","🎭","🔮","👑","🐉","💀","🌋","⚡","🛡","🗡","🏰","🎪","🦁"];

  // Add member form
  const [addSearch, setAddSearch] = useState("");
  const [addPoints, setAddPoints] = useState(0);

  // Manual award
  const [awardPk, setAwardPk] = useState("");
  const [awardAmount, setAwardAmount] = useState(1);
  const [awardReason, setAwardReason] = useState("");

  // Exam state
  const [examMember, setExamMember] = useState("");
  const [examDiscipline, setExamDiscipline] = useState("");
  const [examResult, setExamResult] = useState(null); // { score, grade, points, member, discipline }
  const [examCutoffs, setExamCutoffs] = useState({ S: 5, A: 3, B: 2, C: 1, D: 0, F: -1 });
  const [examBatchResults, setExamBatchResults] = useState(null); // [{ memberPk, name, discipline, discLevel, score, grade, gradeInfo, points }]

  // Intrigue state
  const [intAction, setIntAction] = useState(""); // "sabotage" | "spy" | "challenge"
  const [intActor, setIntActor] = useState("");
  const [intTarget, setIntTarget] = useState("");
  const [intResult, setIntResult] = useState(null); // { success, message, details }
  const [intStakes, setIntStakes] = useState(1); // for challenges
  const [intFilterAlliance, setIntFilterAlliance] = useState(""); // filter actor/target by alliance
  const [intPower, setIntPower] = useState(1); // point multiplier for sabotage/spy (1/2/3), higher = less success
  const [intUseAlliance, setIntUseAlliance] = useState(true); // whether to factor in alliance bonuses

  // Alliance state
  const [newAllianceName, setNewAllianceName] = useState("");
  const [newAllianceIcon, setNewAllianceIcon] = useState("🤝");
  const [editingAlliance, setEditingAlliance] = useState(null); // alliance name being edited
  const ALLIANCE_ICONS = ["🤝","🗡","🛡","🔮","💀","👁","🐍","🦅","🌙","☀","⚔","🏴"];

  const allEntities = [
    ...mages.map(m => ({ ...m, type: "mage" })),
    ...dragons.map(d => ({ ...d, type: "dragon" })),
  ];

  const league = leagues.find(l => l.primary_key === activeLeague);
  const members = league?.members || [];
  const standings = [...members].sort((a, b) => (b.points || 0) - (a.points || 0));

  const availableEntities = allEntities.filter(e =>
    !members.some(m => m.pk === e.primary_key) &&
    (addSearch === "" || e.name.toLowerCase().includes(addSearch.toLowerCase()))
  );

  function getEntityInfo(pk) {
    return allEntities.find(e => e.primary_key === pk);
  }

  // ── Deep clean for DynamoDB — strip nulls, undefined, empty strings recursively ──
  function cleanForDynamo(obj) {
    if (obj === null || obj === undefined || obj === "") return undefined;
    if (Array.isArray(obj)) {
      const cleaned = obj.map(cleanForDynamo).filter(v => v !== undefined);
      return cleaned;
    }
    if (typeof obj === "object" && obj !== null) {
      const cleaned = {};
      for (const [k, v] of Object.entries(obj)) {
        const cv = cleanForDynamo(v);
        if (cv !== undefined) cleaned[k] = cv;
      }
      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }
    return obj;
  }

  // ── Persist helper — cleans data and caps history to avoid 400KB DynamoDB limit ──
  async function saveLeague(updated) {
    setSaving(true);
    try {
      // Cap member history to last 50 entries, duels log to last 200 entries
      const capped = {
        ...updated,
        members: (updated.members || []).map(m => ({
          ...m,
          history: (m.history || []).slice(-50),
          exams: (m.exams || []).slice(-30),
        })),
        duels: (updated.duels || []).slice(-200),
      };
      const cleaned = cleanForDynamo(capped);
      // Ensure required keys survive cleaning
      cleaned.primary_key = updated.primary_key;
      cleaned.sort_key = "League";
      await apiPut(cleaned);
      setLeagues(ls => ls.map(l => l.primary_key === updated.primary_key ? updated : l));
    } catch (e) { console.error("Failed to save league:", e); }
    finally { setSaving(false); }
  }

  // ── Create League ──
  async function createLeague() {
    if (!newName.trim() || saving) return;
    const primary_key = "league_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const newLeague = {
      primary_key, sort_key: "League",
      name: newName.trim(), icon: newIcon,
      members: [], duels: [], alliances: [], allianceRelations: [],
      createdAt: Date.now(),
    };
    setSaving(true);
    try {
      await apiPut(newLeague);
      setLeagues(ls => [...ls, newLeague]);
      setActiveLeague(primary_key);
      setNewName(""); setNewIcon("🏆");
      setView("standings");
    } catch (e) { console.error("Failed to create league:", e); }
    finally { setSaving(false); }
  }

  // ── Delete League ──
  async function deleteLeague(pk) {
    if (saving) return;
    setSaving(true);
    try {
      await apiDelete(pk, "League");
      setLeagues(ls => ls.filter(l => l.primary_key !== pk));
      if (activeLeague === pk) { setActiveLeague(null); setView("standings"); }
    } catch (e) { console.error("Failed to delete league:", e); }
    finally { setSaving(false); }
  }

  // ── Add Member ──
  async function addMember(entityPk) {
    if (!league || saving) return;
    const updated = {
      ...league,
      members: [...league.members, { pk: entityPk, points: addPoints, wins: 0, losses: 0, draws: 0, streak: 0, history: [], exams: [], protected: false }],
    };
    await saveLeague(updated);
  }

  // ── Remove Member ──
  async function removeMember(entityPk) {
    if (!league || saving) return;
    const updated = { ...league, members: league.members.filter(m => m.pk !== entityPk) };
    await saveLeague(updated);
  }

  // ── Edit Member Points ──
  async function editMemberPoints(entityPk, newPoints) {
    if (!league || saving) return;
    const updated = { ...league, members: league.members.map(m => m.pk === entityPk ? { ...m, points: newPoints } : m) };
    await saveLeague(updated);
  }

  // ── Manual Award ──
  async function manualAward() {
    if (!league || !awardPk || saving || awardAmount === 0) return;
    const entity = getEntityInfo(awardPk);
    const updated = {
      ...league,
      members: league.members.map(m => m.pk === awardPk ? {
        ...m, points: (m.points || 0) + awardAmount,
        history: [...(m.history || []), { result: "award", pts: awardAmount, reason: awardReason || "Manual award", ts: Date.now() }],
      } : m),
      duels: [...(league.duels || []), {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        type: "award", target: entity?.name || "?", targetPk: awardPk,
        amount: awardAmount, reason: awardReason || "Manual award", timestamp: Date.now(),
      }],
    };
    await saveLeague(updated);
    setAwardPk(""); setAwardAmount(1); setAwardReason("");
  }

  // ── Run Exam ──
  function runExam() {
    if (!examMember || !examDiscipline) return;
    const entity = getEntityInfo(examMember);
    const discLevel = (entity?.disciplines || {})[examDiscipline] || 0;
    const score = calcExamScore(discLevel);
    const gradeInfo = examGrade(score);
    const pts = examCutoffs[gradeInfo.grade] ?? 0;
    setExamResult({ score, grade: gradeInfo.grade, gradeInfo, points: pts, memberPk: examMember, discipline: examDiscipline, discLevel });
  }

  async function confirmExam() {
    if (!league || !examResult || saving) return;
    const entity = getEntityInfo(examResult.memberPk);
    const discInfo = MAGIC_DISCIPLINES.find(d => d.key === examResult.discipline);
    const updated = {
      ...league,
      members: league.members.map(m => m.pk === examResult.memberPk ? {
        ...m,
        points: (m.points || 0) + examResult.points,
        exams: [...(m.exams || []), { discipline: examResult.discipline, score: examResult.score, grade: examResult.grade, pts: examResult.points, ts: Date.now() }],
        history: [...(m.history || []), { result: "exam", pts: examResult.points, reason: `${discInfo?.label || "?"} Exam — ${examResult.grade} (${examResult.score})`, ts: Date.now() }],
      } : m),
      duels: [...(league.duels || []), {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        type: "exam", target: entity?.name || "?", targetPk: examResult.memberPk,
        discipline: examResult.discipline, score: examResult.score, grade: examResult.grade,
        amount: examResult.points, timestamp: Date.now(),
      }],
    };
    await saveLeague(updated);
    setExamResult(null); setExamMember(""); setExamDiscipline("");
  }

  // ── Batch Exam (all members) ──
  function runExamAll() {
    if (!examDiscipline || members.length === 0) return;
    const results = members.map(m => {
      const entity = getEntityInfo(m.pk);
      const discLevel = (entity?.disciplines || {})[examDiscipline] || 0;
      const score = calcExamScore(discLevel);
      const gradeInfo = examGrade(score);
      const pts = examCutoffs[gradeInfo.grade] ?? 0;
      return { memberPk: m.pk, name: entity?.name || "?", type: entity?.type || "mage", discipline: examDiscipline, discLevel, score, grade: gradeInfo.grade, gradeInfo, points: pts };
    });
    results.sort((a, b) => b.score - a.score);
    setExamBatchResults(results);
    setExamResult(null);
  }

  async function confirmExamBatch() {
    if (!league || !examBatchResults || saving) return;
    const discInfo = MAGIC_DISCIPLINES.find(d => d.key === examDiscipline);
    let updatedMembers = league.members.map(m => ({ ...m }));
    const newDuels = [];
    for (const r of examBatchResults) {
      updatedMembers = updatedMembers.map(m => {
        if (m.pk !== r.memberPk) return m;
        return {
          ...m,
          points: (m.points || 0) + r.points,
          exams: [...(m.exams || []), { discipline: r.discipline, score: r.score, grade: r.grade, pts: r.points, ts: Date.now() }],
          history: [...(m.history || []), { result: "exam", pts: r.points, reason: `${discInfo?.label || "?"} Exam — ${r.grade} (${r.score})`, ts: Date.now() }],
        };
      });
      newDuels.push({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6) + r.memberPk.slice(-3),
        type: "exam", target: r.name, targetPk: r.memberPk,
        discipline: r.discipline, score: r.score, grade: r.grade,
        amount: r.points, timestamp: Date.now(),
      });
    }
    const updated = { ...league, members: updatedMembers, duels: [...(league.duels || []), ...newDuels] };
    await saveLeague(updated);
    setExamBatchResults(null); setExamDiscipline("");
  }

  // ── Intrigue Actions ──
  // Get alliance bonus for an actor
  function getAllianceBonus(memberPk) {
    if (!league?.alliances) return { bonus: 0, allianceName: null };
    for (const alliance of league.alliances) {
      if ((alliance.members || []).includes(memberPk)) {
        const allianceMembers = (alliance.members || []);
        if (allianceMembers.length <= 1) return { bonus: 0, allianceName: alliance.name };
        // Average rank of all alliance members
        const totalM = standings.length;
        let rankSum = 0;
        for (const pk of allianceMembers) {
          const rank = standings.findIndex(m => m.pk === pk) + 1;
          rankSum += rank > 0 ? rank : totalM; // unranked = worst
        }
        const avgRank = rankSum / allianceMembers.length;
        // Bonus: better average rank + more members = stronger
        // avgRank 1 out of 10 → rankRatio 0.9, avgRank 10 → 0.0
        const rankRatio = totalM > 1 ? (totalM - avgRank) / (totalM - 1) : 0.5;
        const sizeFactor = Math.min(1.0, allianceMembers.length / 4); // caps at 4 members
        const bonus = rankRatio * sizeFactor * 0.20; // max ~0.20
        return { bonus: Math.max(0, Math.min(0.25, bonus)), allianceName: alliance.name };
      }
    }
    return { bonus: 0, allianceName: null };
  }

  async function executeIntrigue() {
    if (!league || !intAction || !intActor || saving) return;
    if ((intAction === "sabotage" || intAction === "spy" || intAction === "challenge") && !intTarget) return;

    const actor = getEntityInfo(intActor);
    const target = intTarget ? getEntityInfo(intTarget) : null;
    const actorMember = league.members.find(m => m.pk === intActor);
    const targetMember = intTarget ? league.members.find(m => m.pk === intTarget) : null;
    const actorRank = standings.findIndex(m => m.pk === intActor) + 1;
    const totalMembers = standings.length;
    const { bonus: allianceBonus, allianceName } = getAllianceBonus(intActor);
    const { bonus: targetAllianceBonus, allianceName: targetAllianceName } = intTarget ? getAllianceBonus(intTarget) : { bonus: 0, allianceName: null };
    const effAllianceBonus = intUseAlliance ? allianceBonus : 0;
    const effTargetAllianceBonus = intUseAlliance ? targetAllianceBonus : 0;
    const chance = calcIntrigueChance(actorRank, totalMembers, actor, intAction, effAllianceBonus, effTargetAllianceBonus, intPower);
    const roll = Math.random();
    const success = roll <= chance;

    let resultMsg = "";
    let details = {};
    let updatedMembers = league.members.map(m => ({ ...m }));
    let logEntry = { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6), type: "intrigue", action: intAction, actor: actor?.name || "?", actorPk: intActor, timestamp: Date.now() };

    if (intAction === "sabotage") {
      logEntry.target = target?.name || "?";
      logEntry.targetPk = intTarget;
      if (success) {
        const loss = intPower;
        resultMsg = `Sabotage successful! ${target?.name || "Target"} loses ${loss} point${loss !== 1 ? "s" : ""}.${allianceName && intUseAlliance ? ` (${allianceName} assisted)` : ""}`;
        details = { success: true, pointsLost: loss };
        logEntry.success = true; logEntry.amount = -loss;
        updatedMembers = updatedMembers.map(m => {
          if (m.pk === intTarget) return { ...m, points: Math.max(0, (m.points || 0) - loss), history: [...(m.history || []), { result: "sabotaged", pts: -loss, reason: `Sabotaged by ${actor?.name || "?"}${allianceName && intUseAlliance ? ` (${allianceName})` : ""}`, ts: Date.now() }] };
          return m;
        });
      } else {
        const penalty = 1;
        resultMsg = `Sabotage failed! ${actor?.name || "Actor"} was caught and loses ${penalty} point.`;
        details = { success: false, penalty };
        logEntry.success = false; logEntry.amount = -penalty;
        updatedMembers = updatedMembers.map(m => {
          if (m.pk === intActor) return { ...m, points: Math.max(0, (m.points || 0) - penalty), history: [...(m.history || []), { result: "caught", pts: -penalty, reason: `Failed sabotage against ${target?.name || "?"}`, ts: Date.now() }] };
          return m;
        });
      }
    } else if (intAction === "spy") {
      logEntry.target = target?.name || "?";
      logEntry.targetPk = intTarget;
      if (success) {
        const gain = intPower;
        const tMember = targetMember || {};
        const tEntity = target || {};
        const tDisc = tEntity.disciplines || {};
        const tPeak = peakDiscipline(tDisc, tEntity.primaryDiscipline);
        const tPeakInfo = tPeak.key ? MAGIC_DISCIPLINES.find(d => d.key === tPeak.key) : null;
        const totalDiscLvl = Object.values(tDisc).reduce((s, v) => s + v, 0);
        resultMsg = `Espionage successful! Intel gathered on ${target?.name || "Target"}. ${actor?.name || "Actor"} gains ${gain} point${gain !== 1 ? "s" : ""}.${allianceName && intUseAlliance ? ` (${allianceName} assisted)` : ""}`;
        details = {
          success: true, pointsGained: gain,
          intel: {
            points: tMember.points || 0,
            wins: tMember.wins || 0, losses: tMember.losses || 0,
            streak: tMember.streak || 0,
            peakDisc: tPeakInfo ? `${tPeakInfo.label} Lv.${tPeak.level}` : "None",
            totalDiscLevels: totalDiscLvl,
            examCount: (tMember.exams || []).length,
            lastExamGrade: (tMember.exams || []).length > 0 ? tMember.exams[tMember.exams.length - 1].grade : "N/A",
          }
        };
        logEntry.success = true; logEntry.amount = gain;
        updatedMembers = updatedMembers.map(m => {
          if (m.pk === intActor) return { ...m, points: (m.points || 0) + gain, history: [...(m.history || []), { result: "spy_success", pts: gain, reason: `Spied on ${target?.name || "?"}`, ts: Date.now() }] };
          return m;
        });
      } else {
        resultMsg = `Espionage failed! ${actor?.name || "Actor"} was detected — no intel gathered.`;
        details = { success: false };
        logEntry.success = false;
      }
    } else if (intAction === "challenge") {
      logEntry.target = target?.name || "?";
      logEntry.targetPk = intTarget;
      logEntry.stakes = intStakes;
      resultMsg = `${actor?.name || "Actor"} has issued a formal challenge to ${target?.name || "Target"} for ${intStakes} point${intStakes !== 1 ? "s" : ""}! Open the Duel panel with this league selected — the challenge will appear as a pending match.`;
      details = { success: true, stakes: intStakes, pending: true };
      logEntry.success = true; logEntry.amount = intStakes;
    }

    const updated = { ...league, members: updatedMembers, duels: [...(league.duels || []), logEntry] };
    setIntResult({ success: details.success !== false, message: resultMsg, details, action: intAction });
    await saveLeague(updated);
  }

  function resetIntrigue() {
    setIntAction(""); setIntActor(""); setIntTarget(""); setIntResult(null); setIntStakes(1); setIntFilterAlliance(""); setIntPower(1); setIntUseAlliance(true);
  }

  // ── Alliance CRUD ──
  async function createAlliance() {
    if (!league || !newAllianceName.trim() || saving) return;
    const existing = (league.alliances || []).some(a => a.name === newAllianceName.trim());
    if (existing) return;
    const updated = {
      ...league,
      alliances: [...(league.alliances || []), { name: newAllianceName.trim(), icon: newAllianceIcon, members: [] }],
    };
    await saveLeague(updated);
    setNewAllianceName(""); setNewAllianceIcon("🤝");
  }

  async function deleteAlliance(allianceName) {
    if (!league || saving) return;
    const updated = { ...league, alliances: (league.alliances || []).filter(a => a.name !== allianceName) };
    await saveLeague(updated);
    if (editingAlliance === allianceName) setEditingAlliance(null);
  }

  async function addToAlliance(allianceName, memberPk) {
    if (!league || saving) return;
    const updated = {
      ...league,
      alliances: (league.alliances || []).map(a => {
        if (a.name !== allianceName) return a;
        if ((a.members || []).includes(memberPk)) return a;
        return { ...a, members: [...(a.members || []), memberPk] };
      }),
    };
    await saveLeague(updated);
  }

  async function removeFromAlliance(allianceName, memberPk) {
    if (!league || saving) return;
    const updated = {
      ...league,
      alliances: (league.alliances || []).map(a => {
        if (a.name !== allianceName) return a;
        return { ...a, members: (a.members || []).filter(pk => pk !== memberPk) };
      }),
    };
    await saveLeague(updated);
  }

  function getMemberAlliance(pk) {
    if (!league?.alliances) return null;
    return league.alliances.find(a => (a.members || []).includes(pk)) || null;
  }

  // ── Rank badge styling ──
  function rankBadge(rank) {
    if (rank === 1) return { bg: "linear-gradient(135deg,#fef3c7,#fde68a)", border: "#f59e0b", color: "#92400e", icon: "👑", shadow: "0 0 12px #f59e0b44" };
    if (rank === 2) return { bg: "linear-gradient(135deg,#f1f5f9,#e2e8f0)", border: "#94a3b8", color: "#475569", icon: "🥈", shadow: "0 0 8px #94a3b822" };
    if (rank === 3) return { bg: "linear-gradient(135deg,#fef3e2,#fed7aa)", border: "#d97706", color: "#78350f", icon: "🥉", shadow: "0 0 8px #d9770622" };
    return { bg: "#f8fafc", border: "#e2e8f0", color: "#64748b", icon: null, shadow: "none" };
  }

  // ── Render ──
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "#0c0a09",
      display: "flex", flexDirection: "column",
      fontFamily: FONT_BODY, color: "#e7e5e4",
    }}>
      {/* ── Top bar ── */}
      <div style={{
        height: 56, flexShrink: 0,
        background: "linear-gradient(180deg, #1c1917 0%, #0c0a09 100%)",
        borderBottom: "1px solid #292524",
        display: "flex", alignItems: "center", padding: "0 20px", gap: 16,
      }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "#a8a29e", fontSize: 22, cursor: "pointer", padding: "4px 8px", borderRadius: 4 }}
          onMouseOver={e => e.target.style.color="#fef2f2"} onMouseOut={e => e.target.style.color="#a8a29e"}>✕</button>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: FONT_DISPLAY, letterSpacing: 2, color: "#e0c97f" }}>🏆 LEAGUES</div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 4, alignItems: "center", overflow: "auto" }}>
          {leagues.map(l => (
            <button key={l.primary_key} onClick={() => { setActiveLeague(l.primary_key); setView("standings"); resetIntrigue(); setExamResult(null); }}
              style={{
                padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600,
                fontFamily: FONT_BODY, whiteSpace: "nowrap", transition: "all 0.15s",
                background: activeLeague === l.primary_key ? "#292524" : "transparent",
                border: `1px solid ${activeLeague === l.primary_key ? "#44403c" : "transparent"}`,
                color: activeLeague === l.primary_key ? "#fef2f2" : "#78716c",
              }}>{l.icon || "🏆"} {l.name}</button>
          ))}
          <button onClick={() => { setActiveLeague(null); setView("standings"); }}
            style={{
              padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700,
              background: !activeLeague ? "#292524" : "transparent",
              border: `1px solid ${!activeLeague ? "#44403c" : "transparent"}`,
              color: !activeLeague ? "#e0c97f" : "#57534e", fontFamily: FONT_BODY,
            }}>+ New</button>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: "auto" }}>

        {/* No league — create form */}
        {!activeLeague && (
          <div style={{ maxWidth: 480, margin: "80px auto", padding: 40, background: "#1c1917", border: "1px solid #292524", borderRadius: 16 }}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: FONT_DISPLAY, color: "#e0c97f", letterSpacing: 1, marginBottom: 6 }}>Create a New League</div>
            <div style={{ fontSize: 13, color: "#78716c", marginBottom: 28 }}>Leagues are ranked arenas where your mages and dragons compete for glory. Award points through duels, exams, intrigue, or manually.</div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#a8a29e", letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>League Name</div>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Arcane Academy"
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", borderRadius: 8, background: "#0c0a09", border: "1px solid #44403c", color: "#fef2f2", fontSize: 15, fontFamily: FONT_BODY, outline: "none" }}
                onFocus={e => e.target.style.borderColor="#e0c97f"} onBlur={e => e.target.style.borderColor="#44403c"} />
            </div>
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#a8a29e", letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>Icon</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {LEAGUE_ICONS.map(ic => (
                  <button key={ic} onClick={() => setNewIcon(ic)} style={{
                    width: 38, height: 38, borderRadius: 8, fontSize: 18, cursor: "pointer",
                    background: newIcon === ic ? "#292524" : "transparent",
                    border: `2px solid ${newIcon === ic ? "#e0c97f" : "#292524"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{ic}</button>
                ))}
              </div>
            </div>
            <button onClick={createLeague} disabled={!newName.trim() || saving}
              style={{
                width: "100%", padding: "12px", borderRadius: 8, cursor: newName.trim() ? "pointer" : "not-allowed",
                background: newName.trim() ? "linear-gradient(135deg,#92400e,#b45309)" : "#292524",
                border: "1px solid #d97706", color: "#fef3c7",
                fontSize: 14, fontWeight: 700, fontFamily: FONT_DISPLAY, letterSpacing: 1,
                opacity: newName.trim() ? 1 : 0.5,
              }}>{saving ? "Creating..." : `${newIcon} Establish League`}</button>
          </div>
        )}

        {/* ── Active league ── */}
        {league && (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Header */}
            <div style={{ padding: "24px 32px 16px", flexShrink: 0, background: "linear-gradient(180deg, #1c1917 0%, #0c0a0900 100%)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 32 }}>{league.icon || "🏆"}</span>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, fontFamily: FONT_DISPLAY, color: "#fef2f2", letterSpacing: 1 }}>{league.name}</div>
                  <div style={{ fontSize: 12, color: "#78716c" }}>{members.length} member{members.length !== 1 ? "s" : ""} · {(league.duels || []).length} event{(league.duels || []).length !== 1 ? "s" : ""}</div>
                </div>
                <div style={{ flex: 1 }} />
                <button onClick={() => { if (window.confirm("Delete this league permanently?")) deleteLeague(league.primary_key); }}
                  style={{ background: "none", border: "1px solid #44403c", borderRadius: 6, color: "#78716c", fontSize: 11, padding: "5px 10px", cursor: "pointer", fontFamily: FONT_BODY }}
                  onMouseOver={e => { e.target.style.color="#ef4444"; e.target.style.borderColor="#ef4444"; }}
                  onMouseOut={e => { e.target.style.color="#78716c"; e.target.style.borderColor="#44403c"; }}
                >Delete League</button>
              </div>
              {/* Sub-nav */}
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {[
                  { key: "standings", label: "Standings", icon: "📊" },
                  { key: "exam", label: "Exams", icon: "📝" },
                  { key: "intrigue", label: "Intrigue", icon: "🗡" },
                  { key: "award", label: "Award Points", icon: "⭐" },
                  { key: "add", label: "Members", icon: "👥" },
                  { key: "alliances", label: "Alliances", icon: "🤝" },
                  { key: "history", label: "History", icon: "📜" },
                ].map(tab => (
                  <button key={tab.key} onClick={() => { setView(tab.key); if (tab.key !== "intrigue") resetIntrigue(); if (tab.key !== "exam") setExamResult(null); }}
                    style={{
                      padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY,
                      background: view === tab.key ? "#292524" : "transparent",
                      border: `1px solid ${view === tab.key ? "#44403c" : "transparent"}`,
                      color: view === tab.key ? "#fef2f2" : "#57534e", transition: "all 0.12s",
                    }}>{tab.icon} {tab.label}</button>
                ))}
              </div>
            </div>

            {/* ════════════════ STANDINGS ════════════════ */}
            {view === "standings" && (
              <div style={{ flex: 1, overflow: "auto", padding: "8px 32px 32px" }}>
                <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 8, background: "#292524", border: "1px solid #44403c", display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: "#a8a29e" }}>
                  <span style={{ fontSize: 16 }}>💡</span>
                  <span>Points are awarded via <strong style={{ color: "#e0c97f" }}>Duels</strong> (select league in ⚡ Duel modal), <strong style={{ color: "#e0c97f" }}>Exams</strong>, <strong style={{ color: "#e0c97f" }}>Intrigue</strong>, or <strong style={{ color: "#e0c97f" }}>Manual Awards</strong>.</span>
                </div>
                {standings.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 60, color: "#57534e" }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🏟</div>
                    <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No members yet</div>
                    <button onClick={() => setView("add")} style={{ marginTop: 16, padding: "8px 20px", borderRadius: 6, cursor: "pointer", background: "#292524", border: "1px solid #44403c", color: "#e0c97f", fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY }}>+ Add Members</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 70px 60px 60px 70px", padding: "8px 12px", fontSize: 9, fontWeight: 700, color: "#57534e", letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid #292524" }}>
                      <span>#</span><span>Name</span><span style={{ textAlign: "right" }}>Points</span>
                      <span style={{ textAlign: "center" }}>W</span><span style={{ textAlign: "center" }}>L</span><span style={{ textAlign: "center" }}>Streak</span>
                    </div>
                    {standings.map((member, idx) => {
                      const rank = idx + 1;
                      const entity = getEntityInfo(member.pk);
                      const badge = rankBadge(rank);
                      const totalGames = (member.wins || 0) + (member.losses || 0);
                      const winRate = totalGames > 0 ? Math.round(((member.wins || 0) / totalGames) * 100) : null;
                      const kingdom = entity?.allegiance ? kingdoms.find(k => k.primary_key === entity.allegiance) : null;
                      const disc = entity?.disciplines || {};
                      const peak = peakDiscipline(disc, entity?.primaryDiscipline);
                      const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
                      const streak = member.streak || 0;
                      return (
                        <div key={member.pk} style={{
                          display: "grid", gridTemplateColumns: "36px 1fr 70px 60px 60px 70px",
                          padding: "10px 12px", alignItems: "center", background: badge.bg, borderRadius: 8,
                          border: `1px solid ${rank <= 3 ? badge.border + "44" : "#1c191700"}`, boxShadow: badge.shadow,
                        }}
                          onMouseOver={e => { if (rank > 3) e.currentTarget.style.background = "#1c1917"; }}
                          onMouseOut={e => { if (rank > 3) e.currentTarget.style.background = badge.bg; }}
                        >
                          <div style={{ fontSize: rank <= 3 ? 16 : 14, fontWeight: 800, color: rank <= 3 ? badge.color : "#57534e", fontFamily: FONT_DISPLAY, display: "flex", alignItems: "center", justifyContent: "center" }}>{badge.icon || rank}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12 }}>{entity?.type === "dragon" ? "🐉" : "✦"}</span>
                              <span style={{ fontSize: 14, fontWeight: 700, color: rank <= 3 ? badge.color : "#111111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entity?.name || "Unknown"}</span>
                              {kingdom && <span style={{ fontSize: 9, color: kingdom.color || "#78716c", background: (kingdom.color || "#78716c") + "22", padding: "1px 5px", borderRadius: 3 }}>{kingdom.name}</span>}
                              {peakInfo && <span style={{ fontSize: 9, color: peakInfo.color, background: peakInfo.color + "18", padding: "1px 4px", borderRadius: 3 }}>{peakInfo.icon}{peak.level}</span>}
                              {(() => { const al = getMemberAlliance(member.pk); return al ? <span style={{ fontSize: 8, color: "#8b5cf6", background: "#8b5cf618", padding: "1px 5px", borderRadius: 3 }}>{al.icon || "🤝"} {al.name}</span> : null; })()}
                            </div>
                            {totalGames > 0 && <div style={{ fontSize: 10, color: "#57534e", marginTop: 2 }}>{winRate}% WR · {totalGames} game{totalGames !== 1 ? "s" : ""}</div>}
                          </div>
                          <div style={{ textAlign: "right" }}><span style={{ fontSize: 20, fontWeight: 800, fontFamily: FONT_DISPLAY, color: "#e0c97f", textShadow: (member.points || 0) >= 10 ? "0 0 8px #e0c97f44" : "none" }}>{member.points || 0}</span></div>
                          <div style={{ textAlign: "center", fontSize: 13, fontWeight: 600, color: "#22c55e" }}>{member.wins || 0}</div>
                          <div style={{ textAlign: "center", fontSize: 13, fontWeight: 600, color: "#ef4444" }}>{member.losses || 0}</div>
                          <div style={{ textAlign: "center" }}>{streak >= 2 ? <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: streak >= 5 ? "#f59e0b22" : "#22c55e22", color: streak >= 5 ? "#f59e0b" : "#22c55e", border: `1px solid ${streak >= 5 ? "#f59e0b33" : "#22c55e33"}` }}>🔥{streak}</span> : <span style={{ fontSize: 11, color: "#44403c" }}>—</span>}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ════════════════ EXAMS ════════════════ */}
            {view === "exam" && (
              <div style={{ flex: 1, overflow: "auto", padding: "8px 32px 32px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, maxWidth: 900 }}>
                  {/* Left: Run exam */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Administer Exam</div>
                    <div style={{ fontSize: 11, color: "#57534e", marginBottom: 16 }}>
                      Select a member and discipline. Score is based on discipline level with diminishing returns and random variance. Higher mastery = more consistent high scores.
                    </div>

                    {/* Member select */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Student</div>
                      <select value={examMember} onChange={e => { setExamMember(e.target.value); setExamResult(null); }}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 8, boxSizing: "border-box", background: "#1c1917", border: "1px solid #292524", color: "#e7e5e4", fontSize: 13, fontFamily: FONT_BODY, outline: "none", cursor: "pointer" }}>
                        <option value="">Select student...</option>
                        {standings.map(m => { const e = getEntityInfo(m.pk); return <option key={m.pk} value={m.pk}>{e?.type === "dragon" ? "🐉" : "✦"} {e?.name || "?"}</option>; })}
                      </select>
                    </div>

                    {/* Discipline select */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Subject</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {MAGIC_DISCIPLINES.map(d => {
                          const memberEntity = examMember ? getEntityInfo(examMember) : null;
                          const lvl = memberEntity ? ((memberEntity.disciplines || {})[d.key] || 0) : 0;
                          return (
                            <button key={d.key} onClick={() => { setExamDiscipline(d.key); setExamResult(null); }}
                              style={{
                                padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontWeight: 600,
                                background: examDiscipline === d.key ? d.color + "22" : "#1c1917",
                                border: `1px solid ${examDiscipline === d.key ? d.color : "#292524"}`,
                                color: examDiscipline === d.key ? d.color : "#57534e",
                              }}>{d.icon} {d.label}{lvl > 0 ? ` (${lvl})` : ""}</button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Run button */}
                    <button onClick={runExam} disabled={!examMember || !examDiscipline}
                      style={{
                        width: "100%", padding: "12px", borderRadius: 8,
                        cursor: (examMember && examDiscipline) ? "pointer" : "not-allowed",
                        background: (examMember && examDiscipline) ? "linear-gradient(135deg,#1e3a5f,#1e40af)" : "#292524",
                        border: "1px solid #3b82f6", color: "#bfdbfe",
                        fontSize: 13, fontWeight: 700, fontFamily: FONT_DISPLAY, letterSpacing: 1,
                        opacity: (examMember && examDiscipline) ? 1 : 0.5,
                      }}>📝 Administer Exam</button>

                    {/* Exam All button */}
                    <button onClick={runExamAll} disabled={!examDiscipline || members.length === 0}
                      style={{
                        width: "100%", padding: "10px", borderRadius: 8, marginTop: 6,
                        cursor: examDiscipline ? "pointer" : "not-allowed",
                        background: examDiscipline ? "#292524" : "#1c1917",
                        border: `1px solid ${examDiscipline ? "#44403c" : "#292524"}`,
                        color: examDiscipline ? "#a8a29e" : "#44403c",
                        fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY,
                        opacity: examDiscipline ? 1 : 0.5,
                      }}>📝 Exam All Members ({members.length})</button>

                    {/* Batch results */}
                    {examBatchResults && (() => {
                      const discInfo = MAGIC_DISCIPLINES.find(d => d.key === examDiscipline);
                      const totalPts = examBatchResults.reduce((s, r) => s + r.points, 0);
                      const avgScore = Math.round(examBatchResults.reduce((s, r) => s + r.score, 0) / examBatchResults.length);
                      return (
                        <div style={{ marginTop: 16, padding: 16, background: "#1c1917", borderRadius: 12, border: "1px solid #292524" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#e7e5e4" }}>{discInfo?.icon} {discInfo?.label} — Class Exam</div>
                              <div style={{ fontSize: 10, color: "#57534e" }}>{examBatchResults.length} students · Avg {avgScore} · Total {totalPts >= 0 ? "+" : ""}{totalPts} pts</div>
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button onClick={confirmExamBatch} disabled={saving}
                                style={{ padding: "6px 14px", borderRadius: 5, cursor: "pointer", background: "linear-gradient(135deg,#166534,#15803d)", border: "1px solid #22c55e", color: "#fef2f2", fontSize: 11, fontWeight: 700, fontFamily: FONT_BODY }}>
                                {saving ? "..." : "✓ Confirm All"}
                              </button>
                              <button onClick={() => setExamBatchResults(null)}
                                style={{ padding: "6px 14px", borderRadius: 5, cursor: "pointer", background: "#292524", border: "1px solid #44403c", color: "#78716c", fontSize: 11, fontWeight: 600, fontFamily: FONT_BODY }}>Discard</button>
                              <button onClick={runExamAll}
                                style={{ padding: "6px 14px", borderRadius: 5, cursor: "pointer", background: "#292524", border: "1px solid #44403c", color: "#a8a29e", fontSize: 11, fontWeight: 600, fontFamily: FONT_BODY }}>↺ Re-roll All</button>
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 300, overflow: "auto" }}>
                            {examBatchResults.map((r, i) => (
                              <div key={r.memberPk} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", background: i === 0 ? "#e0c97f0a" : "#0c0a09", borderRadius: 5, border: `1px solid ${i === 0 ? "#e0c97f22" : "#1c1917"}` }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#57534e", width: 18, textAlign: "right" }}>#{i + 1}</span>
                                <span style={{ fontSize: 18, fontWeight: 900, fontFamily: FONT_DISPLAY, color: r.gradeInfo.color, width: 22, textAlign: "center" }}>{r.grade}</span>
                                <span style={{ fontSize: 11 }}>{r.type === "dragon" ? "🐉" : "✦"}</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: "#e7e5e4", flex: 1 }}>{r.name}</span>
                                <span style={{ fontSize: 9, color: "#57534e" }}>Lv.{r.discLevel}</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: r.gradeInfo.color, fontFamily: FONT_DISPLAY }}>{r.score}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, color: r.points >= 0 ? "#22c55e" : "#ef4444", width: 28, textAlign: "right" }}>{r.points >= 0 ? "+" : ""}{r.points}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Single Result */}
                    {examResult && (() => {
                      const g = examResult.gradeInfo;
                      const entity = getEntityInfo(examResult.memberPk);
                      const discInfo = MAGIC_DISCIPLINES.find(d => d.key === examResult.discipline);
                      return (
                        <div style={{ marginTop: 20, padding: 20, background: "#1c1917", borderRadius: 12, border: `2px solid ${g.color}44`, textAlign: "center" }}>
                          <div style={{ fontSize: 48, fontWeight: 900, fontFamily: FONT_DISPLAY, color: g.color, textShadow: `0 0 20px ${g.color}44`, marginBottom: 4 }}>{g.grade}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#e7e5e4", marginBottom: 2 }}>{entity?.name || "?"} — {discInfo?.label || "?"} Exam</div>
                          <div style={{ fontSize: 12, color: "#78716c", marginBottom: 8 }}>{g.label} · Discipline Lv.{examResult.discLevel}</div>
                          <div style={{ fontSize: 28, fontWeight: 800, fontFamily: FONT_DISPLAY, color: g.color, marginBottom: 4 }}>{examResult.score}<span style={{ fontSize: 14, color: "#57534e" }}>/100</span></div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: examResult.points >= 0 ? "#22c55e" : "#ef4444", marginBottom: 16 }}>
                            {examResult.points >= 0 ? "+" : ""}{examResult.points} point{Math.abs(examResult.points) !== 1 ? "s" : ""}
                          </div>
                          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                            <button onClick={confirmExam} disabled={saving}
                              style={{ padding: "8px 20px", borderRadius: 6, cursor: "pointer", background: "linear-gradient(135deg,#166534,#15803d)", border: "1px solid #22c55e", color: "#fef2f2", fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY }}>
                              {saving ? "..." : "✓ Confirm & Award"}
                            </button>
                            <button onClick={() => setExamResult(null)}
                              style={{ padding: "8px 20px", borderRadius: 6, cursor: "pointer", background: "#292524", border: "1px solid #44403c", color: "#78716c", fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY }}>
                              Discard
                            </button>
                            <button onClick={runExam}
                              style={{ padding: "8px 20px", borderRadius: 6, cursor: "pointer", background: "#292524", border: "1px solid #44403c", color: "#a8a29e", fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY }}>
                              ↺ Re-roll
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Right: Cutoffs + recent exams */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Grade → Points</div>
                    <div style={{ fontSize: 11, color: "#57534e", marginBottom: 12 }}>Set how many points each grade awards. Negative values deduct points.</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
                      {["S","A","B","C","D","F"].map(g => {
                        const info = examGrade(g === "S" ? 95 : g === "A" ? 85 : g === "B" ? 70 : g === "C" ? 55 : g === "D" ? 40 : 0);
                        return (
                          <div key={g} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 18, fontWeight: 900, fontFamily: FONT_DISPLAY, color: info.color, width: 28, textAlign: "center" }}>{g}</span>
                            <span style={{ fontSize: 10, color: "#57534e", width: 70 }}>{info.label}</span>
                            <span style={{ fontSize: 9, color: "#44403c", width: 50 }}>{g === "S" ? "95+" : g === "A" ? "85-94" : g === "B" ? "70-84" : g === "C" ? "55-69" : g === "D" ? "40-54" : "0-39"}</span>
                            <input type="number" value={examCutoffs[g]} onChange={e => setExamCutoffs(c => ({ ...c, [g]: Number(e.target.value) || 0 }))}
                              style={{ width: 50, padding: "4px 6px", borderRadius: 4, textAlign: "center", background: "#0c0a09", border: "1px solid #44403c", color: examCutoffs[g] >= 0 ? "#22c55e" : "#ef4444", fontSize: 13, fontWeight: 700, fontFamily: FONT_BODY, outline: "none" }} />
                            <span style={{ fontSize: 10, color: "#57534e" }}>pts</span>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>Recent Exams</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 250, overflow: "auto" }}>
                      {(league.duels || []).filter(d => d.type === "exam").reverse().slice(0, 15).map((d, i) => {
                        const g = examGrade(d.score);
                        const discInfo = MAGIC_DISCIPLINES.find(dd => dd.key === d.discipline);
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "#1c1917", borderRadius: 6, border: "1px solid #292524" }}>
                            <span style={{ fontSize: 14, fontWeight: 900, fontFamily: FONT_DISPLAY, color: g.color, width: 20, textAlign: "center" }}>{g.grade}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#e7e5e4", flex: 1 }}>{d.target}</span>
                            <span style={{ fontSize: 9, color: discInfo?.color || "#78716c" }}>{discInfo?.icon} {discInfo?.label}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#e0c97f" }}>{d.score}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: d.amount >= 0 ? "#22c55e" : "#ef4444" }}>{d.amount >= 0 ? "+" : ""}{d.amount}</span>
                          </div>
                        );
                      })}
                      {(league.duels || []).filter(d => d.type === "exam").length === 0 && (
                        <div style={{ fontSize: 11, color: "#44403c", padding: 12, textAlign: "center" }}>No exams administered yet</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════ INTRIGUE ════════════════ */}
            {view === "intrigue" && (
              <div style={{ flex: 1, overflow: "auto", padding: "8px 32px 32px" }}>
                <div style={{ maxWidth: 700 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Intrigue Actions</div>
                  <div style={{ fontSize: 11, color: "#57534e", marginBottom: 20 }}>
                    Espionage, sabotage, and political maneuvering. Success chance is based on relevant discipline levels — Soulbinding aids espionage, Voidweaving aids sabotage.
                  </div>

                  {intResult ? (
                    /* Result display */
                    <div style={{ padding: 24, background: "#1c1917", borderRadius: 12, border: `2px solid ${intResult.success ? "#22c55e44" : "#ef444444"}`, textAlign: "center", marginBottom: 20 }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>{intResult.success ? (intResult.action === "spy" ? "🕵" : intResult.action === "challenge" ? "⚔" : "💥") : "❌"}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: intResult.success ? "#22c55e" : "#ef4444", marginBottom: 8 }}>{intResult.success ? "Success!" : "Failed!"}</div>
                      <div style={{ fontSize: 12, color: "#a8a29e", marginBottom: 16, lineHeight: 1.6 }}>{intResult.message}</div>

                      {/* Spy intel display */}
                      {intResult.action === "spy" && intResult.success && intResult.details?.intel && (
                        <div style={{ textAlign: "left", padding: 16, background: "#0c0a09", borderRadius: 8, border: "1px solid #292524", marginBottom: 16 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#e0c97f", letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>🕵 Classified Intel</div>
                          {Object.entries(intResult.details.intel).map(([k, v]) => (
                            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 11, borderBottom: "1px solid #1c1917" }}>
                              <span style={{ color: "#78716c", textTransform: "capitalize" }}>{k.replace(/([A-Z])/g, ' $1')}</span>
                              <span style={{ color: "#e7e5e4", fontWeight: 600 }}>{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button onClick={resetIntrigue} style={{ padding: "8px 24px", borderRadius: 6, cursor: "pointer", background: "#292524", border: "1px solid #44403c", color: "#e0c97f", fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY }}>
                        New Action
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Action type selector */}
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 8, textTransform: "uppercase" }}>Action Type</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {[
                            { key: "sabotage", icon: "💥", label: "Sabotage", desc: "Reduce target's points", color: "#ef4444", discHint: "Voidweaving / Flamecalling" },
                            { key: "spy", icon: "🕵", label: "Spy", desc: "Reveal target's hidden info", color: "#8b5cf6", discHint: "Soulbinding / Voidweaving" },
                            { key: "challenge", icon: "⚔", label: "Challenge", desc: "Declare a formal duel with stakes", color: "#f59e0b", discHint: "Always succeeds" },
                          ].map(a => (
                            <button key={a.key} onClick={() => { setIntAction(a.key); setIntResult(null); }}
                              style={{
                                flex: 1, padding: "14px 12px", borderRadius: 10, cursor: "pointer", textAlign: "left",
                                background: intAction === a.key ? a.color + "15" : "#1c1917",
                                border: `2px solid ${intAction === a.key ? a.color : "#292524"}`,
                                transition: "all 0.12s",
                              }}>
                              <div style={{ fontSize: 20, marginBottom: 4 }}>{a.icon}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: intAction === a.key ? a.color : "#a8a29e" }}>{a.label}</div>
                              <div style={{ fontSize: 10, color: "#57534e", marginTop: 2 }}>{a.desc}</div>
                              <div style={{ fontSize: 9, color: "#44403c", marginTop: 4 }}>Boosted by: {a.discHint}</div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {intAction && (
                        <>
                          {/* Alliance filter */}
                          {(league.alliances || []).length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Filter by Alliance</div>
                              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                <button onClick={() => setIntFilterAlliance("")} style={{
                                  fontSize: 10, padding: "4px 10px", borderRadius: 5, cursor: "pointer",
                                  background: !intFilterAlliance ? "#292524" : "#1c1917",
                                  border: `1px solid ${!intFilterAlliance ? "#44403c" : "#292524"}`,
                                  color: !intFilterAlliance ? "#e7e5e4" : "#57534e", fontWeight: 600, fontFamily: FONT_BODY,
                                }}>All</button>
                                {(league.alliances || []).map(a => (
                                  <button key={a.name} onClick={() => setIntFilterAlliance(intFilterAlliance === a.name ? "" : a.name)} style={{
                                    fontSize: 10, padding: "4px 10px", borderRadius: 5, cursor: "pointer",
                                    background: intFilterAlliance === a.name ? "#8b5cf622" : "#1c1917",
                                    border: `1px solid ${intFilterAlliance === a.name ? "#8b5cf6" : "#292524"}`,
                                    color: intFilterAlliance === a.name ? "#8b5cf6" : "#57534e", fontWeight: 600, fontFamily: FONT_BODY,
                                  }}>{a.icon || "🤝"} {a.name} ({(a.members || []).length})</button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Actor */}
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>
                              {intAction === "challenge" ? "Challenger" : "Agent"}
                            </div>
                            <select value={intActor} onChange={e => setIntActor(e.target.value)}
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, boxSizing: "border-box", background: "#1c1917", border: "1px solid #292524", color: "#e7e5e4", fontSize: 13, fontFamily: FONT_BODY, outline: "none", cursor: "pointer" }}>
                              <option value="">Select {intAction === "challenge" ? "challenger" : "agent"}...</option>
                              {standings.filter(m => {
                                if (m.pk === intTarget) return false;
                                if (intFilterAlliance) {
                                  const al = (league.alliances || []).find(a => a.name === intFilterAlliance);
                                  if (al && !(al.members || []).includes(m.pk)) return false;
                                }
                                return true;
                              }).map((m, idx) => {
                                const e = getEntityInfo(m.pk);
                                const rank = standings.findIndex(s => s.pk === m.pk) + 1;
                                const { bonus: ab, allianceName: aName } = getAllianceBonus(m.pk);
                                const chance = Math.round(calcIntrigueChance(rank, standings.length, e, intAction, ab, 0) * 100);
                                return <option key={m.pk} value={m.pk}>#{rank} {e?.name || "?"} ({chance}%){aName ? ` [${aName}]` : ""}</option>;
                              })}
                            </select>
                          </div>

                          {/* Target */}
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Target</div>
                            <select value={intTarget} onChange={e => setIntTarget(e.target.value)}
                              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, boxSizing: "border-box", background: "#1c1917", border: "1px solid #292524", color: "#e7e5e4", fontSize: 13, fontFamily: FONT_BODY, outline: "none", cursor: "pointer" }}>
                              <option value="">Select target...</option>
                              {standings.filter(m => {
                                if (m.pk === intActor) return false;
                                if (intFilterAlliance) {
                                  const al = (league.alliances || []).find(a => a.name === intFilterAlliance);
                                  if (al && !(al.members || []).includes(m.pk)) return false;
                                }
                                return true;
                              }).map(m => {
                                const e = getEntityInfo(m.pk);
                                const { allianceName: tAlName } = getAllianceBonus(m.pk);
                                return <option key={m.pk} value={m.pk}>{e?.name || "?"} ({m.points || 0} pts){tAlName ? ` [${tAlName}]` : ""}</option>;
                              })}
                            </select>
                          </div>

                          {/* Stakes for challenge */}
                          {intAction === "challenge" && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Stakes (points wagered)</div>
                              <div style={{ display: "flex", gap: 4 }}>
                                {[1, 2, 3, 5].map(v => (
                                  <button key={v} onClick={() => setIntStakes(v)}
                                    style={{
                                      flex: 1, padding: "8px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700,
                                      background: intStakes === v ? "#f59e0b22" : "#1c1917",
                                      border: `1px solid ${intStakes === v ? "#f59e0b" : "#292524"}`,
                                      color: intStakes === v ? "#f59e0b" : "#57534e", fontFamily: FONT_BODY,
                                    }}>{v}</button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Power level for sabotage/spy */}
                          {(intAction === "sabotage" || intAction === "spy") && (
                            <div style={{ display: "flex", gap: 16, marginBottom: 12, alignItems: "flex-start" }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>
                                  {intAction === "sabotage" ? "Damage" : "Intel Value"} (points)
                                </div>
                                <div style={{ display: "flex", gap: 4 }}>
                                  {[1, 2, 3].map(v => (
                                    <button key={v} onClick={() => setIntPower(v)}
                                      style={{
                                        flex: 1, padding: "8px 4px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700,
                                        background: intPower === v ? (intAction === "sabotage" ? "#ef444422" : "#8b5cf622") : "#1c1917",
                                        border: `1px solid ${intPower === v ? (intAction === "sabotage" ? "#ef4444" : "#8b5cf6") : "#292524"}`,
                                        color: intPower === v ? (intAction === "sabotage" ? "#ef4444" : "#8b5cf6") : "#57534e",
                                        fontFamily: FONT_BODY, textAlign: "center",
                                      }}>
                                      <div>{intAction === "sabotage" ? `-${v}` : `+${v}`}</div>
                                      <div style={{ fontSize: 8, color: "#57534e", fontWeight: 500, marginTop: 1 }}>
                                        {v === 1 ? "Easy" : v === 2 ? "Risky" : "Extreme"}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                                {intPower > 1 && (
                                  <div style={{ fontSize: 9, color: "#f59e0b", marginTop: 4 }}>
                                    ⚠ −{(intPower - 1) * 15}% success chance
                                  </div>
                                )}
                              </div>

                              {/* Alliance toggle */}
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Alliance</div>
                                <button onClick={() => setIntUseAlliance(!intUseAlliance)}
                                  style={{
                                    padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600,
                                    background: intUseAlliance ? "#8b5cf622" : "#1c1917",
                                    border: `1px solid ${intUseAlliance ? "#8b5cf6" : "#292524"}`,
                                    color: intUseAlliance ? "#8b5cf6" : "#57534e", fontFamily: FONT_BODY,
                                  }}>
                                  {intUseAlliance ? "🤝 On" : "👤 1v1"}
                                </button>
                                <div style={{ fontSize: 8, color: "#57534e", marginTop: 3, textAlign: "center" }}>
                                  {intUseAlliance ? "Alliance power active" : "Solo — no alliance"}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Success chance preview */}
                          {intActor && intTarget && intAction !== "challenge" && (() => {
                            const actorEnt = getEntityInfo(intActor);
                            const actorRankIdx = standings.findIndex(m => m.pk === intActor);
                            const { bonus: ab, allianceName: alName } = getAllianceBonus(intActor);
                            const { bonus: tab, allianceName: talName } = getAllianceBonus(intTarget);
                            const effAb = intUseAlliance ? ab : 0;
                            const effTab = intUseAlliance ? tab : 0;
                            const chance = Math.round(calcIntrigueChance(actorRankIdx + 1, standings.length, actorEnt, intAction, effAb, effTab, intPower) * 100);
                            return (
                              <div style={{ marginBottom: 16, padding: "8px 12px", background: "#292524", borderRadius: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 10, color: "#78716c" }}>Success:</span>
                                <div style={{ flex: 1, minWidth: 80, height: 6, background: "#0c0a09", borderRadius: 3, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${chance}%`, background: chance >= 70 ? "#22c55e" : chance >= 40 ? "#f59e0b" : "#ef4444", borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 700, color: chance >= 70 ? "#22c55e" : chance >= 40 ? "#f59e0b" : "#ef4444" }}>{chance}%</span>
                                {intUseAlliance && alName && <span style={{ fontSize: 9, color: "#8b5cf6", fontWeight: 600 }}>🗡 {alName} +{Math.round(effAb * 100)}%</span>}
                                {intUseAlliance && talName && <span style={{ fontSize: 9, color: "#f59e0b", fontWeight: 600 }}>🛡 {talName} −{Math.round(effTab * 100)}%</span>}
                                {!intUseAlliance && <span style={{ fontSize: 9, color: "#57534e" }}>👤 Solo</span>}
                                {intPower > 1 && <span style={{ fontSize: 9, color: "#ef4444", fontWeight: 600 }}>⚠ Power −{(intPower - 1) * 15}%</span>}
                              </div>
                            );
                          })()}

                          <button onClick={executeIntrigue} disabled={!intActor || !intTarget || saving}
                            style={{
                              width: "100%", padding: "12px", borderRadius: 8,
                              cursor: (intActor && intTarget) ? "pointer" : "not-allowed",
                              background: (intActor && intTarget) ? (intAction === "sabotage" ? "linear-gradient(135deg,#7f1d1d,#991b1b)" : intAction === "spy" ? "linear-gradient(135deg,#3b0764,#581c87)" : "linear-gradient(135deg,#78350f,#92400e)") : "#292524",
                              border: `1px solid ${intAction === "sabotage" ? "#ef4444" : intAction === "spy" ? "#8b5cf6" : "#f59e0b"}`,
                              color: "#fef2f2", fontSize: 13, fontWeight: 700, fontFamily: FONT_DISPLAY, letterSpacing: 1,
                              opacity: (intActor && intTarget) ? 1 : 0.5,
                            }}>
                            {saving ? "Executing..." : intAction === "sabotage" ? "💥 Execute Sabotage" : intAction === "spy" ? "🕵 Deploy Spy" : "⚔ Issue Challenge"}
                          </button>
                        </>
                      )}

                      {/* Recent intrigue log */}
                      {(league.duels || []).filter(d => d.type === "intrigue").length > 0 && (
                        <div style={{ marginTop: 24 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#78716c", letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>Recent Intrigue</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {(league.duels || []).filter(d => d.type === "intrigue").reverse().slice(0, 10).map((d, i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "#1c1917", borderRadius: 6, border: "1px solid #292524", fontSize: 11 }}>
                                <span>{d.action === "sabotage" ? "💥" : d.action === "spy" ? "🕵" : "⚔"}</span>
                                <span style={{ color: "#a8a29e", fontWeight: 600 }}>{d.actor}</span>
                                <span style={{ color: "#44403c" }}>→</span>
                                <span style={{ color: "#e7e5e4" }}>{d.target}</span>
                                <span style={{ color: d.success ? "#22c55e" : "#ef4444", fontWeight: 700, marginLeft: "auto" }}>{d.success ? "✓" : "✗"}</span>
                                {d.amount && <span style={{ color: d.amount > 0 ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{d.amount > 0 ? "+" : ""}{d.amount}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ════════════════ AWARD POINTS ════════════════ */}
            {view === "award" && (
              <div style={{ flex: 1, overflow: "auto", padding: "8px 32px 32px" }}>
                <div style={{ maxWidth: 520 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Manual Point Award</div>
                  <div style={{ fontSize: 11, color: "#57534e", marginBottom: 20 }}>Award or deduct points for story events, penalties, achievements, or anything else.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Member</div>
                      <select value={awardPk} onChange={e => setAwardPk(e.target.value)}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 8, boxSizing: "border-box", background: "#1c1917", border: "1px solid #292524", color: "#e7e5e4", fontSize: 13, fontFamily: FONT_BODY, outline: "none", cursor: "pointer" }}>
                        <option value="">Select a member...</option>
                        {standings.map(m => { const e = getEntityInfo(m.pk); return <option key={m.pk} value={m.pk}>{e?.type === "dragon" ? "🐉" : "✦"} {e?.name || "?"} ({m.points || 0} pts)</option>; })}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Points</div>
                      <div style={{ display: "flex", gap: 4 }}>
                        {[-5, -1, 1, 2, 3, 5].map(v => (
                          <button key={v} onClick={() => setAwardAmount(v)}
                            style={{
                              flex: 1, padding: "8px 4px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY,
                              background: awardAmount === v ? (v < 0 ? "#ef444422" : "#22c55e22") : "#1c1917",
                              border: `1px solid ${awardAmount === v ? (v < 0 ? "#ef4444" : "#22c55e") : "#292524"}`,
                              color: awardAmount === v ? (v < 0 ? "#ef4444" : "#22c55e") : "#57534e",
                            }}>{v > 0 ? "+" : ""}{v}</button>
                        ))}
                        <input type="number" value={awardAmount} onChange={e => setAwardAmount(Number(e.target.value) || 0)}
                          style={{ width: 50, padding: "8px 4px", borderRadius: 6, textAlign: "center", background: "#0c0a09", border: "1px solid #44403c", color: "#e0c97f", fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY, outline: "none" }} />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>Reason (optional)</div>
                      <input value={awardReason} onChange={e => setAwardReason(e.target.value)} placeholder="e.g. Slew a wandering beast..."
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 8, boxSizing: "border-box", background: "#1c1917", border: "1px solid #292524", color: "#e7e5e4", fontSize: 12, fontFamily: FONT_BODY, outline: "none" }} />
                    </div>
                    <button onClick={manualAward} disabled={!awardPk || saving || awardAmount === 0}
                      style={{
                        width: "100%", padding: "12px", borderRadius: 8, marginTop: 4,
                        cursor: (awardPk && awardAmount !== 0) ? "pointer" : "not-allowed",
                        background: (awardPk && awardAmount !== 0) ? (awardAmount > 0 ? "linear-gradient(135deg,#166534,#15803d)" : "linear-gradient(135deg,#991b1b,#b91c1c)") : "#292524",
                        border: `1px solid ${awardAmount > 0 ? "#22c55e" : awardAmount < 0 ? "#ef4444" : "#44403c"}`, color: "#fef2f2",
                        fontSize: 13, fontWeight: 700, fontFamily: FONT_DISPLAY, letterSpacing: 1, opacity: (awardPk && awardAmount !== 0) ? 1 : 0.5,
                      }}>{saving ? "Awarding..." : `${awardAmount > 0 ? "⭐ Award" : "⚠ Deduct"} ${Math.abs(awardAmount)} Point${Math.abs(awardAmount) !== 1 ? "s" : ""}`}</button>
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════ ALLIANCES ════════════════ */}
            {view === "alliances" && (
              <div style={{ flex: 1, overflow: "auto", padding: "8px 32px 32px" }}>
                <div style={{ maxWidth: 800 }}>

                  {/* ── Alliance Rankings ── */}
                  {(league.alliances || []).length > 0 && (() => {
                    const ranked = (league.alliances || []).map(a => {
                      const totalPts = (a.members || []).reduce((sum, pk) => {
                        const m = members.find(mm => mm.pk === pk);
                        return sum + (m?.points || 0);
                      }, 0);
                      return { ...a, totalPts, memberCount: (a.members || []).length };
                    }).sort((a, b) => b.totalPts - a.totalPts);

                    return (
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 10, textTransform: "uppercase" }}>Alliance Rankings</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {ranked.map((a, idx) => {
                            const rank = idx + 1;
                            const relations = (league.allianceRelations || []).filter(r => r.a === a.name || r.b === a.name);
                            return (
                              <div key={a.name} style={{
                                display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                                background: rank === 1 ? "linear-gradient(135deg,#fef3c7,#fde68a)" : rank === 2 ? "linear-gradient(135deg,#f1f5f9,#e2e8f0)" : rank === 3 ? "linear-gradient(135deg,#fef3e2,#fed7aa)" : "#f8fafc",
                                borderRadius: 8,
                                border: `1px solid ${rank <= 3 ? (rank === 1 ? "#f59e0b44" : rank === 2 ? "#94a3b844" : "#d9770644") : "#1c191700"}`,
                              }}>
                                <span style={{ fontSize: rank <= 3 ? 18 : 14, fontWeight: 800, fontFamily: FONT_DISPLAY, color: rank === 1 ? "#92400e" : rank === 2 ? "#475569" : rank === 3 ? "#78350f" : "#57534e", width: 24, textAlign: "center" }}>
                                  {rank <= 3 ? ["👑","🥈","🥉"][rank-1] : rank}
                                </span>
                                <span style={{ fontSize: 18 }}>{a.icon || "🤝"}</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: rank <= 3 ? (rank === 1 ? "#92400e" : rank === 2 ? "#475569" : "#78350f") : "#e7e5e4" }}>{a.name}</span>
                                    <span style={{ fontSize: 9, color: "#78716c" }}>{a.memberCount} member{a.memberCount !== 1 ? "s" : ""}</span>
                                  </div>
                                  {relations.length > 0 && (
                                    <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                                      {relations.map((r, ri) => {
                                        const other = r.a === a.name ? r.b : r.a;
                                        const otherAlliance = (league.alliances || []).find(al => al.name === other);
                                        const relColor = r.type === "enemy" ? "#ef4444" : r.type === "friendly" ? "#22c55e" : "#78716c";
                                        const relIcon = r.type === "enemy" ? "⚔" : r.type === "friendly" ? "💚" : "—";
                                        return (
                                          <span key={ri} style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: relColor + "18", color: relColor, fontWeight: 600 }}>
                                            {relIcon} {otherAlliance?.icon || ""}{other}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <span style={{ fontSize: 22, fontWeight: 800, fontFamily: FONT_DISPLAY, color: "#e0c97f", textShadow: a.totalPts >= 20 ? "0 0 8px #e0c97f44" : "none" }}>{a.totalPts}</span>
                                <span style={{ fontSize: 9, color: "#78716c" }}>pts</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Relations Manager ── */}
                  {(league.alliances || []).length >= 2 && (
                    <div style={{ marginBottom: 24, padding: 16, background: "#1c1917", borderRadius: 10, border: "1px solid #292524" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 10, textTransform: "uppercase" }}>Alliance Relations</div>
                      <div style={{ fontSize: 10, color: "#57534e", marginBottom: 12 }}>Set diplomatic relationships between alliances.</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {(() => {
                          const alliances = league.alliances || [];
                          const pairs = [];
                          for (let i = 0; i < alliances.length; i++) {
                            for (let j = i + 1; j < alliances.length; j++) {
                              pairs.push([alliances[i], alliances[j]]);
                            }
                          }
                          return pairs.map(([a1, a2]) => {
                            const existing = (league.allianceRelations || []).find(r =>
                              (r.a === a1.name && r.b === a2.name) || (r.a === a2.name && r.b === a1.name)
                            );
                            const currentType = existing?.type || "neutral";
                            const types = [
                              { key: "enemy", label: "Enemy", icon: "⚔", color: "#ef4444" },
                              { key: "neutral", label: "Neutral", icon: "—", color: "#78716c" },
                              { key: "friendly", label: "Friendly", icon: "💚", color: "#22c55e" },
                            ];
                            return (
                              <div key={a1.name + a2.name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "#0c0a09", borderRadius: 6 }}>
                                <span style={{ fontSize: 12 }}>{a1.icon || "🤝"}</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: "#e7e5e4", minWidth: 60 }}>{a1.name}</span>
                                <div style={{ display: "flex", gap: 2 }}>
                                  {types.map(t => (
                                    <button key={t.key} onClick={async () => {
                                      if (saving) return;
                                      const filtered = (league.allianceRelations || []).filter(r =>
                                        !((r.a === a1.name && r.b === a2.name) || (r.a === a2.name && r.b === a1.name))
                                      );
                                      const newRelations = t.key === "neutral" ? filtered : [...filtered, { a: a1.name, b: a2.name, type: t.key }];
                                      const updated = { ...league, allianceRelations: newRelations };
                                      await saveLeague(updated);
                                    }}
                                      style={{
                                        fontSize: 9, padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                                        background: currentType === t.key ? t.color + "22" : "transparent",
                                        border: `1px solid ${currentType === t.key ? t.color : "#292524"}`,
                                        color: currentType === t.key ? t.color : "#44403c",
                                        fontWeight: 600, fontFamily: FONT_BODY,
                                      }}>{t.icon} {t.label}</button>
                                  ))}
                                </div>
                                <span style={{ fontSize: 12 }}>{a2.icon || "🤝"}</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: "#e7e5e4" }}>{a2.name}</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* ── Create Alliance ── */}
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Manage Alliances</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 20, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 4, textTransform: "uppercase" }}>New Alliance</div>
                      <input value={newAllianceName} onChange={e => setNewAllianceName(e.target.value)} placeholder="e.g. Shadow Pact"
                        style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, background: "#1c1917", border: "1px solid #292524", color: "#e7e5e4", fontSize: 12, fontFamily: FONT_BODY, outline: "none" }} />
                    </div>
                    <div style={{ display: "flex", gap: 3 }}>
                      {ALLIANCE_ICONS.map(ic => (
                        <button key={ic} onClick={() => setNewAllianceIcon(ic)} style={{
                          width: 30, height: 30, borderRadius: 6, fontSize: 14, cursor: "pointer",
                          background: newAllianceIcon === ic ? "#292524" : "transparent",
                          border: `1px solid ${newAllianceIcon === ic ? "#8b5cf6" : "#292524"}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>{ic}</button>
                      ))}
                    </div>
                    <button onClick={createAlliance} disabled={!newAllianceName.trim() || saving}
                      style={{
                        padding: "8px 16px", borderRadius: 6, cursor: newAllianceName.trim() ? "pointer" : "not-allowed",
                        background: newAllianceName.trim() ? "#8b5cf622" : "#292524",
                        border: `1px solid ${newAllianceName.trim() ? "#8b5cf6" : "#44403c"}`,
                        color: "#8b5cf6", fontSize: 12, fontWeight: 700, fontFamily: FONT_BODY,
                        opacity: newAllianceName.trim() ? 1 : 0.5, whiteSpace: "nowrap",
                      }}>{saving ? "..." : "Create"}</button>
                  </div>

                  {/* ── Alliance List ── */}
                  {(league.alliances || []).length === 0 ? (
                    <div style={{ textAlign: "center", padding: 40, color: "#44403c" }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>🤝</div>
                      <div style={{ fontSize: 13 }}>No alliances formed yet</div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {(league.alliances || []).map(alliance => {
                        const isEditing = editingAlliance === alliance.name;
                        const allianceMembers = (alliance.members || []).map(pk => ({ pk, entity: getEntityInfo(pk), member: members.find(m => m.pk === pk) })).filter(m => m.entity);
                        const nonMembers = standings.filter(m => !(alliance.members || []).includes(m.pk));
                        const relations = (league.allianceRelations || []).filter(r => r.a === alliance.name || r.b === alliance.name);
                        return (
                          <div key={alliance.name} style={{
                            background: "#1c1917", borderRadius: 10, border: `1px solid ${isEditing ? "#8b5cf644" : "#292524"}`,
                            overflow: "hidden",
                          }}>
                            {/* Alliance header */}
                            <div style={{
                              display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
                              background: isEditing ? "#8b5cf60a" : "transparent", cursor: "pointer",
                            }} onClick={() => setEditingAlliance(isEditing ? null : alliance.name)}>
                              <span style={{ fontSize: 18 }}>{alliance.icon || "🤝"}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontSize: 14, fontWeight: 700, color: "#e7e5e4" }}>{alliance.name}</span>
                                  <span style={{ fontSize: 10, color: "#57534e" }}>{allianceMembers.length} member{allianceMembers.length !== 1 ? "s" : ""}</span>
                                </div>
                                {relations.length > 0 && (
                                  <div style={{ display: "flex", gap: 3, marginTop: 2 }}>
                                    {relations.map((r, ri) => {
                                      const other = r.a === alliance.name ? r.b : r.a;
                                      const relColor = r.type === "enemy" ? "#ef4444" : "#22c55e";
                                      const relIcon = r.type === "enemy" ? "⚔" : "💚";
                                      return <span key={ri} style={{ fontSize: 8, color: relColor }}>{relIcon} {other}</span>;
                                    })}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: "flex", gap: 3 }}>
                                {allianceMembers.slice(0, 5).map(m => (
                                  <span key={m.pk} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "#292524", color: "#a8a29e", fontWeight: 600 }}>
                                    {m.entity?.type === "dragon" ? "🐉" : "✦"}{m.entity?.name?.substring(0, 6)}
                                  </span>
                                ))}
                                {allianceMembers.length > 5 && <span style={{ fontSize: 9, color: "#57534e" }}>+{allianceMembers.length - 5}</span>}
                              </div>
                              <span style={{ fontSize: 12, color: "#57534e" }}>{isEditing ? "▼" : "▶"}</span>
                            </div>

                            {/* Expanded editor */}
                            {isEditing && (
                              <div style={{ padding: "0 14px 14px", borderTop: "1px solid #292524" }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginTop: 10, marginBottom: 6, textTransform: "uppercase" }}>Members</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
                                  {allianceMembers.map(m => {
                                    const rank = standings.findIndex(s => s.pk === m.pk) + 1;
                                    return (
                                      <div key={m.pk} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#292524", borderRadius: 5, fontSize: 11 }}>
                                        <span>{m.entity?.type === "dragon" ? "🐉" : "✦"}</span>
                                        <span style={{ color: "#e7e5e4", fontWeight: 600 }}>{m.entity?.name}</span>
                                        <span style={{ color: "#57534e", fontSize: 9 }}>#{rank}</span>
                                        <button onClick={() => removeFromAlliance(alliance.name, m.pk)}
                                          style={{ background: "none", border: "none", color: "#44403c", fontSize: 10, cursor: "pointer", padding: "0 2px" }}
                                          onMouseOver={e => e.target.style.color="#ef4444"} onMouseOut={e => e.target.style.color="#44403c"}>✕</button>
                                      </div>
                                    );
                                  })}
                                  {allianceMembers.length === 0 && <span style={{ fontSize: 11, color: "#44403c" }}>No members</span>}
                                </div>

                                <div style={{ fontSize: 10, fontWeight: 600, color: "#78716c", letterSpacing: 0.5, marginBottom: 6, textTransform: "uppercase" }}>Add to Alliance</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 12 }}>
                                  {nonMembers.map(m => {
                                    const e = getEntityInfo(m.pk);
                                    const otherAlliance = getMemberAlliance(m.pk);
                                    return (
                                      <button key={m.pk} onClick={() => addToAlliance(alliance.name, m.pk)}
                                        style={{
                                          fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                                          background: "#0c0a09", border: "1px solid #292524", color: "#a8a29e",
                                          fontWeight: 500, fontFamily: FONT_BODY, opacity: otherAlliance ? 0.5 : 1,
                                        }}
                                        title={otherAlliance ? `Already in ${otherAlliance.name}` : "Add to alliance"}>
                                        {e?.type === "dragon" ? "🐉" : "✦"} {e?.name}{otherAlliance ? ` (${otherAlliance.icon})` : ""}
                                      </button>
                                    );
                                  })}
                                  {nonMembers.length === 0 && <span style={{ fontSize: 11, color: "#44403c" }}>All members assigned</span>}
                                </div>

                                <button onClick={() => { if (window.confirm(`Delete alliance "${alliance.name}"?`)) deleteAlliance(alliance.name); }}
                                  style={{ fontSize: 10, padding: "4px 10px", borderRadius: 4, cursor: "pointer", background: "none", border: "1px solid #44403c", color: "#78716c", fontFamily: FONT_BODY }}
                                  onMouseOver={e => { e.target.style.color="#ef4444"; e.target.style.borderColor="#ef4444"; }}
                                  onMouseOut={e => { e.target.style.color="#78716c"; e.target.style.borderColor="#44403c"; }}>
                                  Delete Alliance
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ════════════════ MANAGE MEMBERS ════════════════ */}
            {view === "add" && (
              <div style={{ flex: 1, overflow: "auto", padding: "8px 32px 32px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, maxWidth: 900 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 12, textTransform: "uppercase" }}>Add Members</div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      <input value={addSearch} onChange={e => setAddSearch(e.target.value)} placeholder="Search entities..."
                        style={{ flex: 1, padding: "8px 12px", borderRadius: 6, background: "#1c1917", border: "1px solid #292524", color: "#e7e5e4", fontSize: 12, fontFamily: FONT_BODY, outline: "none" }} />
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 10, color: "#78716c" }}>Pts:</span>
                        <input type="number" value={addPoints} onChange={e => setAddPoints(Number(e.target.value) || 0)}
                          style={{ width: 50, padding: "8px 6px", borderRadius: 6, textAlign: "center", background: "#1c1917", border: "1px solid #292524", color: "#e0c97f", fontSize: 13, fontWeight: 700, fontFamily: FONT_BODY, outline: "none" }} />
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 400, overflow: "auto" }}>
                      {availableEntities.length === 0 && <div style={{ fontSize: 12, color: "#44403c", padding: 16, textAlign: "center" }}>{addSearch ? "No matching entities" : "All entities are already members"}</div>}
                      {availableEntities.map(entity => {
                        const disc = entity.disciplines || {};
                        const peak = peakDiscipline(disc, entity.primaryDiscipline);
                        const peakInfo = peak.key ? MAGIC_DISCIPLINES.find(d => d.key === peak.key) : null;
                        const kingdom = entity.allegiance ? kingdoms.find(k => k.primary_key === entity.allegiance) : null;
                        return (
                          <div key={entity.primary_key} onClick={() => addMember(entity.primary_key)}
                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#1c1917", borderRadius: 6, border: "1px solid #292524", cursor: "pointer" }}
                            onMouseOver={e => e.currentTarget.style.borderColor = "#e0c97f44"} onMouseOut={e => e.currentTarget.style.borderColor = "#292524"}>
                            <span style={{ fontSize: 14 }}>{entity.type === "dragon" ? "🐉" : "✦"}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "#e7e5e4", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entity.name}</div>
                              <div style={{ fontSize: 9, color: "#57534e", display: "flex", gap: 4 }}>
                                {kingdom && <span style={{ color: kingdom.color || "#78716c" }}>{kingdom.name}</span>}
                                {peakInfo && <span style={{ color: peakInfo.color }}>{peakInfo.icon}{peak.level}</span>}
                              </div>
                            </div>
                            <span style={{ fontSize: 16, color: "#e0c97f66" }}>+</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 12, textTransform: "uppercase" }}>Current Members ({members.length})</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 500, overflow: "auto" }}>
                      {standings.map((member, idx) => (
                        <LeagueMemberRow key={member.pk} member={member} idx={idx} entity={getEntityInfo(member.pk)} kingdoms={kingdoms} onEditPoints={editMemberPoints} onRemove={removeMember} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════ HISTORY ════════════════ */}
            {view === "history" && (
              <div style={{ flex: 1, overflow: "auto", padding: "8px 32px 32px" }}>
                {(league.duels || []).length === 0 ? (
                  <div style={{ textAlign: "center", padding: 60, color: "#57534e" }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📜</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>No events recorded yet</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 700 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#a8a29e", letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>Event History ({(league.duels || []).length})</div>
                    {[...(league.duels || [])].reverse().map((d, i) => (
                      <div key={d.id || i} style={{ padding: "10px 14px", background: "#1c1917", borderRadius: 8, border: "1px solid #292524" }}>
                        {d.type === "exam" ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span>📝</span>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#e7e5e4" }}>{d.target}</span>
                              <span style={{ fontSize: 10, color: "#57534e", marginLeft: 6 }}>{MAGIC_DISCIPLINES.find(dd => dd.key === d.discipline)?.label} Exam</span>
                              <span style={{ fontSize: 12, fontWeight: 800, fontFamily: FONT_DISPLAY, color: examGrade(d.score).color, marginLeft: 6 }}>{d.grade} ({d.score})</span>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: d.amount >= 0 ? "#22c55e" : "#ef4444" }}>{d.amount >= 0 ? "+" : ""}{d.amount}</span>
                            <span style={{ fontSize: 9, color: "#44403c" }}>{d.timestamp ? new Date(d.timestamp).toLocaleDateString() : ""}</span>
                          </div>
                        ) : d.type === "intrigue" ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span>{d.action === "sabotage" ? "💥" : d.action === "spy" ? "🕵" : "⚔"}</span>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#a8a29e" }}>{d.actor}</span>
                              <span style={{ fontSize: 10, color: "#44403c", margin: "0 4px" }}>→</span>
                              <span style={{ fontSize: 12, color: "#e7e5e4" }}>{d.target}</span>
                              <span style={{ fontSize: 10, color: d.success ? "#22c55e" : "#ef4444", fontWeight: 700, marginLeft: 6 }}>{d.success ? "Success" : "Failed"}</span>
                            </div>
                            {d.amount && <span style={{ fontSize: 11, fontWeight: 700, color: d.amount > 0 ? "#22c55e" : "#ef4444" }}>{d.amount > 0 ? "+" : ""}{d.amount}</span>}
                            <span style={{ fontSize: 9, color: "#44403c" }}>{d.timestamp ? new Date(d.timestamp).toLocaleDateString() : ""}</span>
                          </div>
                        ) : d.type === "award" ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span>{d.amount > 0 ? "⭐" : "⚠"}</span>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#e7e5e4" }}>{d.target}</span>
                              {d.reason && <span style={{ fontSize: 10, color: "#57534e", marginLeft: 6 }}>{d.reason}</span>}
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: d.amount > 0 ? "#22c55e" : "#ef4444" }}>{d.amount > 0 ? "+" : ""}{d.amount}</span>
                            <span style={{ fontSize: 9, color: "#44403c" }}>{d.timestamp ? new Date(d.timestamp).toLocaleDateString() : ""}</span>
                          </div>
                        ) : (
                          /* Duel record */
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                              <span>⚔</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#e7e5e4" }}>Duel</span>
                              <span style={{ fontSize: 9, color: "#44403c" }}>{d.timestamp ? new Date(d.timestamp).toLocaleDateString() : ""}</span>
                            </div>
                            <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
                              {(d.winners || []).length > 0 && <div><span style={{ color: "#22c55e", fontWeight: 600 }}>👑 {d.winners.join(", ")}</span><span style={{ color: "#57534e" }}> +1 each</span></div>}
                              {(d.losers || []).length > 0 && <div><span style={{ color: "#ef4444" }}>☠ {d.losers.join(", ")}</span></div>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

function ToolSection({ label, children }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      <div style={{
        fontSize:9, letterSpacing:2, color:"#8a7e72", fontWeight:700,
        borderBottom:"1px solid #ece7e0", paddingBottom:5, marginBottom:3,
        fontFamily:FONT_DISPLAY,
      }}>{label}</div>
      {children}
    </div>
  );
}
function ToolBtn({ children, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: active ? "#f0ebe4" : "transparent",
      border: `1px solid ${active ? "#d9d2c9" : "transparent"}`,
      color: active ? "#1a1814" : "#5c5347",
      borderRadius:5, padding:"5px 10px", cursor:"pointer", fontSize:13,
      textAlign:"left", display:"flex", alignItems:"center", gap:6,
      transition:"all 0.12s", fontWeight: active ? 600 : 400,
      fontFamily:FONT_BODY,
    }}>
      {children}
    </button>
  );
}